import { initializeApp } from 'firebase/app';
import {
  createStoreApiFetcher,
  FetchHtmlError,
  type FetchHtmlProfile,
} from './storeApiFetch';
import type { WebMatchProvenance } from '../utils/webMatchProvenance';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  collectionGroup,
  doc,
  documentId,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  getDocsFromServer,
  getDocFromServer,
  onSnapshot,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
  deleteField,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  arrayRemove,
  increment,
  runTransaction,
} from 'firebase/firestore';
import {
  getStorage,
  getBlob,
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { getDatabase } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { resizeIfOversize } from '../utils/imageConversion';
import { withoutUndefined } from '../utils/withoutUndefined';
import { buildWinderImagePath } from './winderImagePath';
import { buildLookupValueId } from './lookupValueId';
import { resolveSafeSpecsWrite } from './winderSafeSpecs';
import { resolveDisplayCaseSpecsWrite } from './winderDisplayCaseSpecs';
import { referenceNeedsAiEnrichment } from './referenceListQuery';
import { imageContentType, imageFileExtension } from './imageFileExtension';
import {
  capabilitiesOf,
  resolveCapabilitiesWrite,
  type ApplianceCapabilities,
} from './winderApplianceKind';
import { dedupeProductUrls } from '../utils/productUrls';
import { deriveIsVariant } from '../utils/references';
// calibreResolution only imports TYPES from this module, so this is not a
// runtime cycle.
import { foldUnicodeDashes, normalizeCalibreKey } from './calibreResolution';
// algolia.ts imports only types + ./siblingReferences, so this is not a cycle.
import {
  fetchManufacturerCatalogue,
  fetchWinderCatalogue,
  getBrandSiblingReferences,
  isAlgoliaConfigured,
  type ManufacturerCatalogueEntry,
} from './algolia';

/**
 * Re-exported so a consumer of `getManufacturerCatalogue` can name its state
 * type from the same module it calls, rather than reaching into `algolia.ts`
 * for a type it only holds because of where the data comes from.
 */
export type { ManufacturerCatalogueEntry } from './algolia';
import {
  applyManufacturerOverlay,
  notifyManufacturerCatalogueChanged,
  recordCreatedManufacturer,
  recordDeletedManufacturer,
} from './manufacturerCatalogueOverlay';
import {
  MAX_SIBLING_REFERENCES,
  resolveSiblingReferences,
  type SiblingReference,
  type SiblingSubject,
} from './siblingReferences';
import { siblingReferencesFor } from './notableVariantCandidates';
import {
  LOOKUP_INITIAL_VALUES,
  getLookupCollectionConfig,
} from '../config/lookupCollectionsConfig';
import type {
  PendingReferenceApproval,
  WatchBrand,
  WatchReference,
  LookupValue,
  AdminUser,
  ApprovalStatus,
  BrandMembership,
  BrandMembershipRole,
  MergeOptions,
  FirestoreUser,
  UserPlan,
  Article,
  CuratedCollection,
  BrandImportSources,
  ImportSourceConfig,
  ShopifyImportSource,
  GenericUrlImportSource,
  ImportRunRecord,
  ImportRunSummary,
  MovementManufacturer,
  ParentOrganization,
  ParentOrgOwnershipEvent,
  ElectronicModule,
  WinderEntry,
  AdminBanner,
  AdminBannerInput,
  RoadmapItem,
  RoadmapComment,
  RoadmapAttachment,
  RoadmapAuthor,
  SaveRoadmapItemInput,
  SaveRoadmapItemOutput,
  AddRoadmapCommentOutput,
  ListRoadmapItemsInput,
  DeleteRoadmapAttachmentOutput,
} from '../types';
import type { ImageInfo, TierVocabularyEntry } from '@watchkeeper/shared-types';
import { validateTierVocabulary } from '../utils/tierVocabulary';
import { mapFirestoreUser } from './userDocMapper';
import type {
  CalibreAttributionSource,
  CalibreEntitySource,
  CalibreImageSource,
} from './calibreSources';
import type {
  ScanReferenceDuplicatesOptions,
  ScanReferenceDuplicatesResult,
  MergeReferenceDuplicatesOptions,
  MergeReferenceDuplicatesResult,
  BulkMergeReferenceDuplicatesOptions,
  BulkMergeReferenceDuplicatesResult,
} from '../types/referenceDedup';
import type {
  BackfillDialColorOptions,
  BackfillDialColorResult,
} from '../types/dialColorBackfill';
import { deriveBrandSlug } from './brandSlug';
import { calibreClearableUpdate } from './calibreClearableFields';
import { deriveWinderDocId, slugifyWinderPart } from './winderDocId';
import {
  resolveTurnsPerDayWrite,
  type TurnsPerDayMode,
} from './winderTurnsPerDay';
import { fetchImageBlobWith, ReferenceImageFetchError } from './imageBlobFetch';
import { resolveHeroImage, type ResolvedHeroImage } from './heroImageResolve';

// Firebase configuration from environment variables.
// databaseURL defaults to the standard `<projectId>-default-rtdb.firebaseio.com`
// pattern when VITE_FIREBASE_DATABASE_URL isn't explicitly set — that matches
// the URL the RN app currently reads (e.g. /configuration/watchCharts).
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  databaseURL:
    import.meta.env.VITE_FIREBASE_DATABASE_URL ||
    (projectId ? `https://${projectId}-default-rtdb.firebaseio.com` : undefined),
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize App Check
// Enable debug token for local development (prints token to console)
if (import.meta.env.DEV) {
  (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider(import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY || ''),
  isTokenAutoRefreshEnabled: true,
});

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const rtdb = getDatabase(app);

// Collection names
const PENDING_APPROVALS_COLLECTION = 'pendingReferenceApprovals';
const WATCH_BRANDS_COLLECTION = 'watchBrands';
const REFERENCES_SUBCOLLECTION = 'references';
const SUBMISSIONS_INDEX_COLLECTION = 'submissions_index';

// ----------------------------------------------------------------------------
// Submission dedup index helpers
// computeContentKey MUST mirror the watchlock Cloud Function
// (referenceApproval.ts) so the contentKey computed here for read-only UI
// surfacing matches the one written server-side. All index *writes* go through
// the admin* Cloud Function callables, never directly from here.
// ----------------------------------------------------------------------------

/** Normalize a reference number for identity. Mirrors the function/RN helper. */
export function normalizeReference(reference: string): string {
  return (reference ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

async function sha1Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-1', data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Stable content key = sha1(`${brandId}:${normalizedRef}`). Read-only UI use. */
export async function computeContentKey(
  brandId: string,
  reference: string,
): Promise<string> {
  return sha1Hex(`${brandId}:${normalizeReference(reference)}`);
}

const USERS_COLLECTION = 'users';
const BRAND_IMPORT_SOURCES_COLLECTION = 'brand_import_sources';
const BRAND_IMPORT_RUNS_SUBCOLLECTION = 'runs';

// ============================================================================
// Storage Helpers
// ============================================================================

export async function getImageUrl(storagePath: string): Promise<string> {
  const storageRef = ref(storage, storagePath);
  return getDownloadURL(storageRef);
}

// ============================================================================
// Authentication
// ============================================================================

export async function loginWithEmail(
  email: string,
  password: string,
): Promise<User> {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
}

export async function logout(): Promise<void> {
  await signOut(auth);
}

/**
 * Create a brand-new Firebase Auth account (used by the Brand Portal accept-invite
 * landing page so an invitee can self-register before consuming their invite).
 * This signs the new user in immediately. We send a verification email straight
 * away because `consumeBrandInvite` gates on a verified email server-side.
 */
export async function signUpWithEmail(
  email: string,
  password: string,
): Promise<User> {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  try {
    await sendEmailVerification(result.user);
  } catch (err) {
    // Non-fatal: the user can resend from the verify screen.
    console.warn('Failed to send verification email on signup (non-fatal):', err);
  }
  return result.user;
}

/** Resend the email-verification link to the currently signed-in user. */
export async function resendEmailVerification(): Promise<void> {
  if (!auth.currentUser) throw new Error('Not signed in');
  await sendEmailVerification(auth.currentUser);
}

/**
 * Refresh the signed-in user's auth state from the server, then force a fresh
 * ID token. Call this before retrying `consumeBrandInvite` after the user has
 * clicked their verification link, so the server sees the updated
 * `email_verified` claim rather than the stale token minted at sign-in.
 */
export async function refreshCurrentUserToken(): Promise<void> {
  if (!auth.currentUser) return;
  await auth.currentUser.reload();
  await auth.currentUser.getIdToken(true);
}

export function onAuthChange(
  callback: (user: User | null) => void,
): () => void {
  return onAuthStateChanged(auth, callback);
}

/** Coerce the raw user-doc `brandMemberships` field into a typed array. */
function parseBrandMemberships(raw: unknown): BrandMembership[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (m): m is BrandMembership =>
      !!m &&
      typeof (m as BrandMembership).brandId === 'string' &&
      ((m as BrandMembership).role === 'brandAdmin' ||
        (m as BrandMembership).role === 'brandMember'),
  );
}

export async function checkUserRole(uid: string): Promise<{
  isAdmin: boolean;
  isContributor: boolean;
  brandMemberships: BrandMembership[];
}> {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      // Check both IsAdmin and isAdmin (case variations)
      const isAdmin = data?.IsAdmin === true || data?.isAdmin === true;
      const isContributor = data?.isContributor === true;
      const brandMemberships = parseBrandMemberships(data?.brandMemberships);
      console.log(
        'Role check for uid:',
        uid,
        'isAdmin:',
        isAdmin,
        'isContributor:',
        isContributor,
        'brandMemberships:',
        brandMemberships.length,
      );
      return { isAdmin, isContributor, brandMemberships };
    }
    console.log('User document does not exist for uid:', uid);
    return { isAdmin: false, isContributor: false, brandMemberships: [] };
  } catch (error) {
    console.error('Error checking user role:', error);
    return { isAdmin: false, isContributor: false, brandMemberships: [] };
  }
}

export async function getCurrentAdminUser(): Promise<AdminUser | null> {
  const user = auth.currentUser;
  if (!user) return null;

  const { isAdmin, isContributor, brandMemberships } = await checkUserRole(
    user.uid,
  );
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    isAdmin,
    isContributor,
    brandMemberships,
  };
}

// ============================================================================
// Pending Reference Approvals
// ============================================================================

export async function getPendingApprovals(
  status?: ApprovalStatus,
): Promise<PendingReferenceApproval[]> {
  let q = query(
    collection(db, PENDING_APPROVALS_COLLECTION),
    orderBy('submittedAt', 'desc'),
    limit(100),
  );

  if (status) {
    q = query(
      collection(db, PENDING_APPROVALS_COLLECTION),
      where('status', '==', status),
      orderBy('submittedAt', 'desc'),
      limit(100),
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as PendingReferenceApproval[];
}

export function subscribeToPendingApprovals(
  callback: (approvals: PendingReferenceApproval[]) => void,
  status?: ApprovalStatus,
): () => void {
  let q = query(
    collection(db, PENDING_APPROVALS_COLLECTION),
    orderBy('submittedAt', 'desc'),
    limit(100),
  );

  if (status) {
    q = query(
      collection(db, PENDING_APPROVALS_COLLECTION),
      where('status', '==', status),
      orderBy('submittedAt', 'desc'),
      limit(100),
    );
  }

  return onSnapshot(q, (snapshot) => {
    const approvals = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as PendingReferenceApproval[];
    callback(approvals);
  });
}

export async function getPendingApprovalById(
  id: string,
): Promise<PendingReferenceApproval | null> {
  const docRef = doc(db, PENDING_APPROVALS_COLLECTION, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as PendingReferenceApproval;
  }
  return null;
}

/**
 * Write the terminal lock on submissions_index for a resolved submission.
 * Mutates the pending doc AND the index entry in one transaction so the lock
 * and the approval/rejection can never diverge.
 */
// All submission-lifecycle index mutations are server-only (admin-token Cloud
// Functions) so the submissions_index rules can stay read-only for clients.

export async function approveReference(id: string): Promise<void> {
  // adminResolveSubmission sets verified=true (→ processApprovedReference) and
  // locks submissions_index → 'approved' in one server transaction.
  const fn = httpsCallable<
    { submissionId: string; resolution: 'approved' },
    { success: boolean }
  >(functions, 'adminResolveSubmission');
  await fn({ submissionId: id, resolution: 'approved' });
}

export async function rejectReference(
  id: string,
  reason?: string,
): Promise<void> {
  const fn = httpsCallable<
    { submissionId: string; resolution: 'rejected'; reason?: string },
    { success: boolean }
  >(functions, 'adminResolveSubmission');
  await fn({ submissionId: id, resolution: 'rejected', reason });
}

/**
 * Unlock a terminally-locked index entry (approved/rejected) back to pending.
 * For approved entries, optionally delete the catalogue ref that was created.
 */
export async function unlockSubmission(
  contentKey: string,
  deleteCatalogueRef = false,
): Promise<{ deletedCatalogueRef: boolean }> {
  const fn = httpsCallable<
    { contentKey: string; deleteCatalogueRef: boolean },
    { success: boolean; deletedCatalogueRef: boolean }
  >(functions, 'adminUnlockSubmission');
  const res = await fn({ contentKey, deleteCatalogueRef });
  return { deletedCatalogueRef: res.data.deletedCatalogueRef };
}

/** Re-run catalogue materialisation for an approved-but-errored submission. */
export async function retrySubmissionProcessing(
  submissionId: string,
): Promise<void> {
  const fn = httpsCallable<{ submissionId: string }, { success: boolean }>(
    functions,
    'retrySubmissionProcessing',
  );
  await fn({ submissionId });
}

/**
 * Resolve a submission as "already in the catalogue" — the reviewer found the
 * reference there, so nothing is created and the submission simply closes.
 *
 * TWO WRITES, IN THIS ORDER, AND THE ORDER IS THE POINT.
 *
 * 1. `adminLockSubmissionForMerge` — the same server callable the merge path
 *    uses, and for the same reason: the submitted content has been resolved into
 *    a reference that ALREADY EXISTED. It locks the `submissions_index` ledger
 *    without recording an approved-ref pointer, so a later unlock can never
 *    delete a catalogue reference this submission did not create. Skipping it
 *    would leave the ledger at `pending` with `currentSubmissionId` naming a
 *    resolved doc, and the same content could re-enter the queue.
 * 2. The pending doc's own status and audit fields.
 *
 * A lock failure THROWS and the status is never written, unlike
 * `mergeIntoExistingReference`, which logs and carries on. A visible failure the
 * reviewer can retry beats a queue and a ledger that quietly disagree.
 *
 * The pending doc is kept, not deleted — again unlike the merge path. The whole
 * point of the audit fields is to be able to ask later which reference a
 * dismissal pointed at, and a deleted doc cannot answer.
 */
export async function dismissApprovalAsDuplicate(
  id: string,
  match: { brandId: string; referenceId: string },
  resolvedByUid?: string,
): Promise<void> {
  const lockFn = httpsCallable<{ submissionId: string }, { success: boolean }>(
    functions,
    'adminLockSubmissionForMerge',
  );
  await lockFn({ submissionId: id });

  await updatePendingApproval(id, {
    status: 'dismissed_duplicate',
    matchedBrandId: match.brandId,
    matchedRefId: match.referenceId,
    resolvedAt: Timestamp.now(),
    ...(resolvedByUid ? { resolvedByUid } : {}),
  });
}

export async function updatePendingApproval(
  id: string,
  updates: Partial<PendingReferenceApproval>,
): Promise<void> {
  const docRef = doc(db, PENDING_APPROVALS_COLLECTION, id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

// ============================================================================
// Watch Brands & References
// ============================================================================

export async function getWatchBrands(): Promise<WatchBrand[]> {
  const snapshot = await getDocs(collection(db, WATCH_BRANDS_COLLECTION));
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as WatchBrand[];
}

/**
 * Firestore's ceiling on an `in` filter's value list.
 */
const DOC_ID_IN_CHUNK = 30;

/**
 * Read specific brand documents by id, in chunks of thirty.
 *
 * The brands listing renders from Algolia hits, which carry a projection of each
 * brand rather than the document; this fills in the rest for the rows actually on
 * screen (see `BRAND_FIELDS_NOT_INDEXED`). A page is 25–100 brands, so this is
 * one to four queries — against the 561-document collection read it replaces.
 *
 * Ids that don't resolve are simply absent from the result; the caller keeps its
 * listing row for those.
 */
export async function getWatchBrandsByIds(
  ids: string[],
): Promise<WatchBrand[]> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (unique.length === 0) return [];
  const brands: WatchBrand[] = [];
  for (let i = 0; i < unique.length; i += DOC_ID_IN_CHUNK) {
    const chunk = unique.slice(i, i + DOC_ID_IN_CHUNK);
    const snapshot = await getDocs(
      query(
        collection(db, WATCH_BRANDS_COLLECTION),
        where(documentId(), 'in', chunk),
      ),
    );
    for (const docSnap of snapshot.docs) {
      brands.push({ id: docSnap.id, ...docSnap.data() } as WatchBrand);
    }
  }
  return brands;
}

/**
 * Every brand flagged `isDisabled`.
 *
 * The Algolia indexer EVICTS disabled brands from `watchkeeper_brands`, so a
 * listing built from the index alone would silently drop them — and the admin
 * brands page is the only place a disabled brand can be found and re-enabled.
 * Small by construction (the index currently accounts for all 561 enabled
 * brands), and an equality filter needs only the automatic single-field index.
 */
export async function getDisabledWatchBrands(): Promise<WatchBrand[]> {
  const snapshot = await getDocs(
    query(
      collection(db, WATCH_BRANDS_COLLECTION),
      where('isDisabled', '==', true),
    ),
  );
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as WatchBrand[];
}

/**
 * Every brand still carrying the legacy `shopifyEnabled` flag.
 *
 * `brandHasShopifySource` prefers the brand's import-sources document and only
 * falls back to the legacy fields when there is none. Neither legacy field is
 * indexed in Algolia, so without this the Shopify stat pill would under-count —
 * and the green row tint and Last Import column would go with it — for exactly
 * the brands that never got a sources document.
 */
export async function getLegacyShopifyWatchBrands(): Promise<WatchBrand[]> {
  const snapshot = await getDocs(
    query(
      collection(db, WATCH_BRANDS_COLLECTION),
      where('shopifyEnabled', '==', true),
    ),
  );
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as WatchBrand[];
}

/**
 * Every brand carrying an `acquisitions` array.
 *
 * `orderBy` on a field returns only documents where that field EXISTS, which is
 * the cheapest way to reach exactly the lineage-bearing brands — a few dozen out
 * of 561. The listing needs them up front because `acquisitions` is not indexed
 * in Algolia and two surfaces read it across the WHOLE catalogue rather than
 * per-row: the ⚘ Acquisitions badge, and the editor's "N brands list this as an
 * acquisition" warning, which is the guard against quietly orphaning a lineage
 * link while changing a brand's status.
 *
 * Best-effort: array fields are ordered by their elements, and if that ever
 * needs an index exemption the caller degrades to badges on hydrated rows only,
 * which is why this is called inside a try/catch rather than gating the load.
 */
export async function getWatchBrandsWithAcquisitions(): Promise<WatchBrand[]> {
  const snapshot = await getDocs(
    query(collection(db, WATCH_BRANDS_COLLECTION), orderBy('acquisitions')),
  );
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as WatchBrand[];
}

export interface BulkSetShowProductLinksResult {
  /** Total brand docs examined. */
  brandsScanned: number;
  /** Brand docs whose `showProductLinks` was written. */
  updated: number;
  /** Brands whose batch write failed; surfaced for retry. */
  errors: { id: string; error: string }[];
}

/**
 * Set `showProductLinks` to a single value on every watch brand. One-shot admin
 * tool for retroactively flipping the explore "View product page" gate across
 * all brands. Batched at the Firestore 500-write cap (BATCH_SIZE=400 like the
 * rest of the codebase); a failed batch is recorded per-brand and does not abort
 * the remaining batches.
 */
export async function bulkSetShowProductLinks(
  value: boolean,
): Promise<BulkSetShowProductLinksResult> {
  const snapshot = await getDocs(collection(db, WATCH_BRANDS_COLLECTION));
  const ids = snapshot.docs.map((d) => d.id);
  const result: BulkSetShowProductLinksResult = {
    brandsScanned: ids.length,
    updated: 0,
    errors: [],
  };
  const BATCH_SIZE = 400;
  const now = Timestamp.now();
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const slice = ids.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);
    for (const id of slice) {
      batch.update(doc(db, WATCH_BRANDS_COLLECTION, id), {
        showProductLinks: value,
        updatedAt: now,
      });
    }
    try {
      await batch.commit();
      result.updated += slice.length;
    } catch (err) {
      console.error('bulkSetShowProductLinks batch failed:', err);
      for (const id of slice) {
        result.errors.push({
          id,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }
  return result;
}

/**
 * Lightweight fetch: returns reference number → {id, reference} for duplicate detection.
 * Downloads all docs but the caller only uses reference + id fields.
 * Consider storing refCount on the brand doc to avoid needing this for counts.
 */
export async function getExistingReferenceMap(
  brandId: string,
): Promise<Map<string, WatchReference>> {
  const q = query(
    collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
  );
  const snapshot = await getDocs(q);
  const map = new Map<string, WatchReference>();
  for (const docSnap of snapshot.docs) {
    const data = { id: docSnap.id, brandId, ...docSnap.data() } as WatchReference;
    if (data.reference) {
      map.set(data.reference, data);
    }
  }
  return map;
}

/**
 * Like {@link getExistingReferenceMap} but keyed by Firestore DOC ID instead of
 * the `reference` string. Doc ids are unique, so — unlike the ref-keyed map —
 * this never drops docs that share a `reference` value (the Shopify
 * composite-id model deliberately allows multiple docs per SKU). The bulk
 * importer uses it to find a Shopify ref's existing doc by its composite id
 * (`shopify__{productId}__{variantId}`) for stable, ref-independent upserts.
 */
export async function getExistingReferenceMapById(
  brandId: string,
): Promise<Map<string, WatchReference>> {
  const q = query(
    collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
  );
  const snapshot = await getDocs(q);
  const map = new Map<string, WatchReference>();
  for (const docSnap of snapshot.docs) {
    map.set(docSnap.id, {
      id: docSnap.id,
      brandId,
      ...docSnap.data(),
    } as WatchReference);
  }
  return map;
}

/**
 * Fetch a brand's references by EXACT Firestore doc id, keyed by doc id.
 *
 * The targeted counterpart to {@link getExistingReferenceMapById}: that one
 * reads the brand's whole `references` subcollection (1,400 docs on a big
 * brand) and is only affordable once per import run. The bulk-import PREVIEW
 * needs the same lookup to spot a changed reference string before anything is
 * written, and Shopify doc ids are deterministic (`shopify__{pid}__{vid}`), so
 * it can ask for exactly the docs it cares about instead — N reads for N
 * previewed items rather than the whole catalogue.
 *
 * Ids that don't resolve are simply absent from the result (a brand-new product
 * has no doc yet — the expected case, not an error).
 */
export async function getReferencesByDocIds(
  brandId: string,
  ids: string[],
): Promise<Map<string, WatchReference>> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const map = new Map<string, WatchReference>();
  if (unique.length === 0) return map;
  for (let i = 0; i < unique.length; i += DOC_ID_IN_CHUNK) {
    const chunk = unique.slice(i, i + DOC_ID_IN_CHUNK);
    const snapshot = await getDocs(
      query(
        collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
        where(documentId(), 'in', chunk),
      ),
    );
    for (const docSnap of snapshot.docs) {
      map.set(docSnap.id, {
        id: docSnap.id,
        brandId,
        ...docSnap.data(),
      } as WatchReference);
    }
  }
  return map;
}

/**
 * EVERY reference belonging to one brand, unpaginated. Distinct from
 * {@link getReferencesForBrand}, which caps at a small `limit` for previews —
 * this reads the whole `references` subcollection (typically hundreds of docs).
 * Used by the Data Health brand drill-down to scan a brand's refs for those whose
 * calibre resolves into a given manufacturer's calibre set.
 */
export async function getAllReferencesForBrand(
  brandId: string,
): Promise<WatchReference[]> {
  const snapshot = await getDocs(
    collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
  );
  return snapshot.docs.map(
    (docSnap) => ({ id: docSnap.id, brandId, ...docSnap.data() }) as WatchReference,
  );
}

export { MAX_SIBLING_REFERENCES };

/**
 * The `siblingReferences` list for an AI enrich snapshot, from references
 * already in memory: the OTHER consumer-facing references in the subject's
 * collection, as `{ reference, model?, nickname? }`.
 *
 * This list is the enrichReference prompt's ground truth for "Notable
 * Variants" — the model may name only what is on it. The rules (same
 * collection, no variants, not the subject, at least two or nothing) live in
 * `notableVariantCandidates.ts`; every enrich path, bulk or single, ends up
 * there, so they cannot drift.
 *
 * `subject` is the reference being enriched. Pass its doc id when it has one;
 * its reference string is matched too, for callers enriching a ref that is not
 * saved yet and because two docs can share a reference string.
 */
export function siblingReferencesFrom(
  refs: readonly WatchReference[],
  subject: SiblingSubject,
): SiblingReference[] {
  return siblingReferencesFor(subject, refs);
}

/**
 * Fetch a brand's references from FIRESTORE and compute the sibling list.
 *
 * This is the expensive path — an unbounded read of the brand's whole
 * `references` subcollection (~1,400 docs for Rolex) — and exists only as the
 * fallback behind {@link getSiblingReferences}. Do not call it directly.
 *
 * Reads the DOC-ID-keyed map: a ref-string-keyed one collapses docs that share
 * a `reference` value, which can drop a legitimate parent when a Shopify
 * variant shares its SKU — and the subject's id needs every doc present to
 * match on.
 */
async function getSiblingReferencesFromFirestore(
  brandId: string,
  subject: SiblingSubject,
): Promise<SiblingReference[]> {
  const map = await getExistingReferenceMapById(brandId);
  const siblings = siblingReferencesFrom(Array.from(map.values()), subject);
  console.info(
    `[siblings] Firestore fallback: ${map.size} docs read → ${siblings.length} siblings`,
  );
  return siblings;
}

/**
 * The sibling list for an AI enrich snapshot, via Algolia with a Firestore
 * fallback.
 *
 * Algolia first because the Firestore path bills one read per reference on the
 * brand EVERY time an admin clicks Suggest with AI — 1,400 reads a click on
 * Rolex, and the audit measured 4-8K reads for a single enrichment once the
 * other cache-warm reads are counted. The equivalent Algolia search costs
 * nothing against the Firestore quota.
 *
 * A subject with no collection has no siblings, and nothing is read at all.
 *
 * Falls back to Firestore when Algolia is unconfigured, errors, OR has no
 * record at all for this brand and collection — not even the subject. Refs of a
 * DISABLED brand are evicted from the index, and an unsaved ref is not in it
 * yet, so nothing-at-all means "not indexed", not "no siblings". When the index
 * DOES hold the collection and it is simply too small, that answer is final: a
 * collection of one or two is common, and paying a whole-brand read every time
 * to confirm it would undo what the Algolia path is for.
 */
export async function getSiblingReferences(
  brandId: string,
  subject: SiblingSubject,
): Promise<SiblingReference[]> {
  return resolveSiblingReferences({
    subject,
    fromIndex: isAlgoliaConfigured()
      ? () => getBrandSiblingReferences(brandId, subject)
      : undefined,
    fromDatabase: () => getSiblingReferencesFromFirestore(brandId, subject),
    warn: (message, error) =>
      error === undefined ? console.warn(message) : console.warn(message, error),
  });
}

/**
 * Server-side count of EVERY reference on the brand, variants included. This is
 * the "how many docs are actually stored?" number — what a full wipe would
 * delete, and what tells you a brand is safe to delete. For the number that
 * matches the parents-only listing, use
 * {@link getParentReferencesCountForBrand}.
 */
export async function getReferencesCountForBrand(brandId: string): Promise<number> {
  const collectionRef = collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION);
  const countSnapshot = await getCountFromServer(collectionRef);
  return countSnapshot.data().count;
}

/**
 * Server-side count of the brand's PARENTS AND STANDALONES — the population the
 * reference listing shows. Same `isVariant == false` predicate the listing
 * itself uses (see {@link getReferencesForBrandPaginated}), so the brand list's
 * "234 refs" and the rows an admin actually gets on opening that brand are the
 * same number rather than differing by however many variants the brand holds.
 *
 * Equality on a single field, so the automatic index covers it — no composite
 * index, and the aggregation downloads no documents.
 *
 * Deliberately a separate function rather than an option on
 * {@link getReferencesCountForBrand}: parents-only is opt-in, and the total is
 * still the right answer for the delete-all wipe count and the brand portal.
 */
export async function getParentReferencesCountForBrand(
  brandId: string,
): Promise<number> {
  const q = query(
    collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
    where('isVariant', '==', false),
  );
  const countSnapshot = await getCountFromServer(q);
  return countSnapshot.data().count;
}

/**
 * Server-side count of the brand's VARIANT references — those grouped under a
 * parent via `parentReferenceId`. A non-zero count means the brand already holds
 * Shopify parent-child relationships, which is the signal the ShopifySourceEditor
 * uses to warn before toggling `ignoreParentChildRelationships` (flipping the flag
 * mid-lifecycle would leave these refs inconsistent with the new import intent).
 * Uses an aggregation query (`parentReferenceId != null`, single-field index) so
 * no documents are downloaded.
 */
export async function getParentChildReferenceCountForBrand(
  brandId: string,
): Promise<number> {
  const q = query(
    collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
    where('parentReferenceId', '!=', null),
  );
  const countSnapshot = await getCountFromServer(q);
  return countSnapshot.data().count;
}

/**
 * Server-side count of custom calibres scoped to one brand. Uses an aggregation
 * query (no docs downloaded) filtered by `brandId`, so it stays within the brand
 * portal's read scope — never loads the full `custom_calibres` collection.
 */
export async function getCalibresCountForBrand(brandId: string): Promise<number> {
  const q = query(
    collection(db, CUSTOM_CALIBRES_COLLECTION),
    where('brandId', '==', brandId),
  );
  const countSnapshot = await getCountFromServer(q);
  return countSnapshot.data().count;
}

/**
 * The N most recently-updated references for a brand (default 10), sorted by
 * `updatedAt` descending. Backs the brand portal's "Recent activity" list — caps
 * the read so the dashboard never loads a whole catalogue into memory. Refs
 * without an `updatedAt` are omitted by the orderBy.
 */
export async function getRecentReferencesForBrand(
  brandId: string,
  limitCount: number = 10,
): Promise<WatchReference[]> {
  const q = query(
    collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
    orderBy('updatedAt', 'desc'),
    limit(limitCount),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    brandId,
    ...docSnap.data(),
  })) as WatchReference[];
}

export async function getWatchBrandById(
  id: string,
): Promise<WatchBrand | null> {
  const docRef = doc(db, WATCH_BRANDS_COLLECTION, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as WatchBrand;
  }
  return null;
}

export async function updateWatchBrand(
  brandId: string,
  updates: Partial<Omit<WatchBrand, 'id'>>,
): Promise<void> {
  const docRef = doc(db, WATCH_BRANDS_COLLECTION, brandId);
  await updateDoc(docRef, {
    ...stripUndefined(updates as Record<string, unknown>),
    updatedAt: Timestamp.now(),
  });
}

/** What deleting a brand removes. Mirrors the `deleteWatchBrand` callable's response. */
export interface WatchBrandDeletePreview {
  dryRun: boolean;
  brandId: string;
  brandName: string;
  /** Identifies this exact list; a delete must send it back. */
  fingerprint: string;
  /** True total, parents and variants. */
  referenceCount: number;
  /** Reasons the delete is refused. Empty when it can proceed. */
  blockers: string[];
  blockingTiers: { calibreId: string; calibreName: string; tierName: string }[];
  calibreOwners: { id: string; name: string }[];
  calibreAliases: { id: string; name: string; removed: string[] }[];
  brandAcquisitions: { id: string; name: string; removed: number }[];
  manufacturers: { id: string; name: string; fields: string[] }[];
  parentOrgs: { id: string; name: string; removed: number }[];
  users: { uid: string; email: string }[];
  inviteCount: number;
  importSource: boolean;
  subcollections: string[];
  storagePaths: string[];
  /** Reference images exist under the brand and are left in place. */
  referenceImagesKept: boolean;
  deleted: boolean;
  /** Non-fatal problems after the brand document was deleted. */
  warnings: string[];
}

async function callDeleteWatchBrand(input: {
  brandId: string;
  dryRun: boolean;
  fingerprint?: string;
}): Promise<WatchBrandDeletePreview> {
  const fn = httpsCallable<typeof input, WatchBrandDeletePreview>(
    functions,
    'deleteWatchBrand',
    { timeout: 300000 },
  );
  return (await fn(input)).data;
}

/**
 * Everything deleting this brand would remove, and whether it is allowed.
 * Writes nothing. Calls the admin `deleteWatchBrand` v1 callable as a dry run.
 */
export async function previewWatchBrandDelete(brandId: string): Promise<WatchBrandDeletePreview> {
  return callDeleteWatchBrand({ brandId, dryRun: true });
}

/**
 * Delete an empty brand together with its Storage images and every link other
 * documents hold to it. `fingerprint` comes from {@link previewWatchBrandDelete};
 * the server refuses if what would be deleted has changed since that preview.
 */
export async function deleteWatchBrand(
  brandId: string,
  fingerprint: string,
): Promise<WatchBrandDeletePreview> {
  return callDeleteWatchBrand({ brandId, dryRun: false, fingerprint });
}

// ============================================================================
// WatchCharts brand mapping (Phase A) — server-backed callables.
// The brand list and the 1:1 mapping write are both Cloud Functions (admin
// SDK) so the WatchCharts API key stays server-side and 1:1 uniqueness is
// enforced authoritatively. See watchlock functions/src/watchcharts.ts.
// ============================================================================

export interface WatchChartsBrand {
  uuid: string;
  name: string;
  watch_count: number | null;
  has_index: boolean;
}

/**
 * Fetch the WatchCharts brand list (24h cached server-side). Pass
 * forceRefresh to bypass the cache (e.g. a "Refresh" button).
 */
export async function getWatchChartsBrands(
  forceRefresh = false,
): Promise<{ brands: WatchChartsBrand[]; cached: boolean; lastFetchedAt: number | null }> {
  const fn = httpsCallable<
    { forceRefresh?: boolean },
    { brands: WatchChartsBrand[]; cached: boolean; lastFetchedAt: number | null }
  >(functions, 'getWatchChartsBrands');
  const res = await fn({ forceRefresh });
  return res.data;
}

/**
 * Map (or unmap) one of our brands to a WatchCharts brand. Passing null
 * uuid clears the mapping. 1:1 uniqueness is enforced server-side; a conflict
 * rejects with an `already-exists` error naming the conflicting brand.
 */
export async function mapWatchChartsBrand(
  brandId: string,
  watchChartsBrandUuid: string | null,
  watchChartsBrandName: string | null,
): Promise<void> {
  const fn = httpsCallable<
    {
      brandId: string;
      watchChartsBrandUuid: string | null;
      watchChartsBrandName: string | null;
    },
    { success: boolean }
  >(functions, 'mapWatchChartsBrand');
  await fn({ brandId, watchChartsBrandUuid, watchChartsBrandName });
}

export async function createWatchBrand(
  brand: Omit<WatchBrand, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  // Generate a URL-friendly ID from the brand name
  const brandId = deriveBrandSlug(brand.name);

  const docRef = doc(db, WATCH_BRANDS_COLLECTION, brandId);

  // Check if brand already exists
  const existing = await getDoc(docRef);
  if (existing.exists()) {
    throw new Error(`Brand with ID "${brandId}" already exists`);
  }

  await setDoc(docRef, {
    ...stripUndefined(brand as Record<string, unknown>),
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });

  return brandId;
}

export async function checkBrandIdExists(brandId: string): Promise<boolean> {
  const docRef = doc(db, WATCH_BRANDS_COLLECTION, brandId);
  const snapshot = await getDoc(docRef);
  return snapshot.exists();
}

/**
 * A capped page of a brand's references, ordered by reference string.
 *
 * `options.parentsOnly` adds a server-side `isVariant == false` filter, so the
 * `limitCount` cap is spent entirely on parents/standalones. Without it a
 * variant-heavy brand can burn the whole cap on variant docs and TRUNCATE the
 * parents a caller then filters for — which is why every parents-only caller
 * must opt in rather than filter client-side. Opt-in (not the default) because
 * callers that legitimately sweep every doc still exist.
 *
 * Filtered form needs the (isVariant, reference) composite index.
 */
export async function getReferencesForBrand(
  brandId: string,
  limitCount: number = 50,
  options: { parentsOnly?: boolean } = {},
): Promise<WatchReference[]> {
  const q = query(
    collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
    ...(options.parentsOnly ? [where('isVariant', '==', false)] : []),
    orderBy('reference'),
    limit(limitCount),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    brandId,
    ...docSnap.data(),
  })) as WatchReference[];
}

/**
 * How many references in this brand point at `parentId` via `parentReferenceId`?
 *
 * One aggregation query — no documents downloaded — so the ReferenceEditor can
 * ask it for every ref it opens. A non-zero count means the doc being edited is a
 * variant PARENT, which gates both the "Variants" panel (whose header badge shows
 * this count) and the editable "Variant Aggregates" section. Replaces the older
 * `limit(1)` existence check, which cost an actual doc read to learn strictly
 * less.
 *
 * Equality-only filter ⇒ automatic single-field index, no composite index.
 */
export async function getVariantCountForParent(
  brandId: string,
  parentId: string,
): Promise<number> {
  const q = query(
    collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
    where('parentReferenceId', '==', parentId),
  );
  const countSnapshot = await getCountFromServer(q);
  return countSnapshot.data().count;
}

/**
 * Every variant reference grouped under `parentId`, sorted by reference string.
 *
 * Deliberately has NO Firestore `orderBy`: an `orderBy('reference')` alongside
 * the equality filter would require a composite index, and a parent's variant
 * count is small (a handful typically, ~70 at the observed maximum), so sorting
 * in memory is free. Fetched lazily — only when the editor's Variants panel is
 * expanded.
 */
export async function getVariantsForParent(
  brandId: string,
  parentId: string,
): Promise<WatchReference[]> {
  const q = query(
    collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
    where('parentReferenceId', '==', parentId),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((docSnap) => ({
      id: docSnap.id,
      brandId,
      ...docSnap.data(),
    }) as WatchReference)
    .sort((a, b) => (a.reference || '').localeCompare(b.reference || ''));
}

export interface PaginatedReferencesResult {
  references: WatchReference[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  totalCount: number;
}

/**
 * Sort applied to a brand's reference list (Brand Portal 2.2d chunk iii).
 *
 * All three fields are single-field indexes (automatic in Firestore — no
 * `firestore.indexes.json` entry needed) and there is no `where` clause, so no
 * composite index is required.
 *
 * ⚠️ `createdAt` caveat: Firestore `orderBy('createdAt', …)` returns ONLY docs
 * that actually carry the field. Portal-created refs set it server-side
 * (`createWatchReference`), but any legacy ref written without a `createdAt`
 * will be *excluded* from a createdAt-sorted page (it can't be ordered to the
 * end — Firestore drops it). `reference` and `model` are required fields and
 * carry no such risk.
 */
export interface ReferenceSort {
  field: 'createdAt' | 'reference' | 'model';
  direction: 'asc' | 'desc';
}

const DEFAULT_REFERENCE_SORT: ReferenceSort = {
  field: 'reference',
  direction: 'asc',
};

/**
 * One page of a brand's references.
 *
 * `options.parentsOnly` restricts the page to parents and standalones with a
 * server-side `where('isVariant', '==', false)`. That is what makes pagination
 * honest: previously the page was unfiltered and callers dropped variants
 * client-side, so a 50-doc page of a variant-heavy brand could surface only a
 * handful of actual rows (and "load more" walked variant docs the user never
 * saw). Filtering server-side means a page of 50 is 50 real rows.
 *
 * `totalCount` uses the SAME filter, so a parents-only caller's header count
 * can never disagree with the list beneath it. Callers that need the true
 * whole-subcollection total (e.g. a delete-all wipe, which really does remove
 * variants too) must ask for it separately via getReferencesCountForBrand.
 *
 * Opt-in rather than default: BackfillProductUrls sweeps EVERY ref in every
 * brand through this function, and a blanket filter would silently hide
 * variants from it.
 *
 * `isVariant` is set on write by every admin path, by watchlock's writers, and
 * by a self-healing trigger, and was backfilled across the catalogue — so the
 * equality filter (which skips docs missing the field) is safe.
 *
 * Filtered form needs an (isVariant, <sort.field>) composite index.
 */
export async function getReferencesForBrandPaginated(
  brandId: string,
  pageSize: number = 50,
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null,
  sort: ReferenceSort = DEFAULT_REFERENCE_SORT,
  options: { parentsOnly?: boolean } = {},
): Promise<PaginatedReferencesResult> {
  const collectionRef = collection(
    db,
    WATCH_BRANDS_COLLECTION,
    brandId,
    REFERENCES_SUBCOLLECTION,
  );

  const parentsFilter = options.parentsOnly
    ? [where('isVariant', '==', false)]
    : [];

  // Total count, under the same filter as the page below.
  const countSnapshot = await getCountFromServer(
    options.parentsOnly ? query(collectionRef, ...parentsFilter) : collectionRef,
  );
  const totalCount = countSnapshot.data().count;

  // Build query with optional cursor. The cursor is the previous page's last
  // doc snapshot; `startAfter` reads whatever the active `orderBy` field is
  // straight off that snapshot, so changing the sort field automatically
  // re-bases the cursor (callers reset `lastDoc` to null on sort change).
  let q = query(
    collectionRef,
    ...parentsFilter,
    orderBy(sort.field, sort.direction),
    limit(pageSize),
  );

  if (lastDoc) {
    q = query(
      collectionRef,
      ...parentsFilter,
      orderBy(sort.field, sort.direction),
      startAfter(lastDoc),
      limit(pageSize),
    );
  }

  const snapshot = await getDocs(q);
  const references = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    brandId,
    ...docSnap.data(),
  })) as WatchReference[];

  const newLastDoc =
    snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

  return {
    references,
    lastDoc: newLastDoc,
    hasMore: snapshot.docs.length === pageSize,
    totalCount,
  };
}

export async function searchReferencesInBrand(
  brandId: string,
  searchTerm: string,
  limitCount: number = 50,
): Promise<WatchReference[]> {
  // Firestore doesn't support full-text search, so we fetch and filter client-side
  // For production, you'd use Algolia or similar
  const refs = await getReferencesForBrand(brandId, 500);
  const term = searchTerm.toLowerCase();
  return refs
    .filter(
      (ref) =>
        ref.reference?.toLowerCase().includes(term) ||
        ref.model?.toLowerCase().includes(term) ||
        ref.productName?.toLowerCase().includes(term) ||
        ref.collection?.toLowerCase().includes(term),
    )
    .slice(0, limitCount);
}

export async function getWatchReference(
  brandId: string,
  referenceId: string,
): Promise<WatchReference | null> {
  const docRef = doc(
    db,
    WATCH_BRANDS_COLLECTION,
    brandId,
    REFERENCES_SUBCOLLECTION,
    referenceId,
  );
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as WatchReference;
  }
  return null;
}

export async function updateWatchReference(
  brandId: string,
  referenceId: string,
  updates: Partial<WatchReference>,
): Promise<void> {
  const docRef = doc(
    db,
    WATCH_BRANDS_COLLECTION,
    brandId,
    REFERENCES_SUBCOLLECTION,
    referenceId,
  );

  const updatesRecord = updates as Record<string, unknown>;
  const patch: Record<string, unknown> = {
    ...stripUndefined(updatesRecord),
    updatedAt: Timestamp.now(),
  };

  // Keep the denormalized `isVariant` flag in step whenever this patch actually
  // changes parentage. Only then: `isVariant` backs the parents-only listing
  // query, so writing it on an unrelated field edit could flip a row out of the
  // listing on stale input.
  //
  // Gated on the key being PRESENT and not undefined. `stripUndefined` above
  // drops `parentReferenceId: undefined`, which callers use to mean "don't
  // touch this field" — deriving from it would wrongly stamp isVariant: false.
  // A `deleteField()` sentinel is not a string, so unlinking correctly derives
  // false. No admin path mutates parentage outside the bulk importer today;
  // this is here so the next one that does can't get it wrong.
  if (
    'parentReferenceId' in updatesRecord &&
    updatesRecord.parentReferenceId !== undefined
  ) {
    patch.isVariant = deriveIsVariant(updatesRecord.parentReferenceId);
  }

  // Keep `referenceNormalized` in sync / self-heal legacy dirty docs.
  if (typeof updatesRecord.reference === 'string') {
    // The update changes the reference itself — recompute the normalized form.
    patch.referenceNormalized = normalizeReference(updatesRecord.reference);
  } else if (!('referenceNormalized' in updatesRecord)) {
    // The partial doesn't touch `reference` or `referenceNormalized`. Read the
    // doc once and backfill the normalized field if it's missing, so any admin
    // edit / bulk op routed through here repairs legacy docs written before
    // `referenceNormalized` was persisted. Conservative: never overwrites an
    // existing value, only fills a genuine gap.
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data() as Record<string, unknown>;
      if (
        !data.referenceNormalized &&
        typeof data.reference === 'string' &&
        data.reference
      ) {
        patch.referenceNormalized = normalizeReference(data.reference);
      }
    }
  }

  await updateDoc(docRef, patch);
}

export async function createWatchReference(
  brandId: string,
  reference: Omit<WatchReference, 'id' | 'brandId' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  // Invariant: every reference doc must have a non-empty `referenceNormalized`.
  // A ref whose number normalizes to empty (whitespace-only or all
  // non-alphanumeric) is structurally junk — it can't be searched, dedup'd, or
  // linked to. Refuse to create it before any Firestore reads. Mirrors the
  // backend create-gates (submitReference / create|updateReferenceFromPortal).
  if (!normalizeReference(reference.reference ?? '')) {
    throw new Error(
      'reference is required and must contain at least one alphanumeric character',
    );
  }

  // Generate hash-based ID similar to C# implementation
  const refNum = reference.reference || '';
  const model = reference.model || '';
  const diameterMm =
    reference.case?.diameterMm?.toString() ||
    reference.case?.diameter?.toString() ||
    '';
  const caseMaterial = reference.case?.material || '';
  const startYear = reference.production?.startYear?.toString() || '';
  const endYear = reference.production?.endYear?.toString() || '';

  const key = `${brandId}|${refNum}|${model}|${diameterMm}|${caseMaterial}|${startYear}|${endYear}`;
  const encoder = new TextEncoder();
  const data = encoder.encode(key);
  const hashBuffer = await crypto.subtle.digest('SHA-1', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  const shortHash = hashHex.slice(0, 10).toLowerCase();

  // Clean reference for use in ID
  const cleanRef = refNum
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const referenceId = `${cleanRef}-${shortHash}`;

  // Brand-scoped duplicate precheck on `referenceNormalized`. Two refs whose
  // numbers normalize to the same value (e.g. "16610 LN" and "16610LN") are
  // treated as duplicates even when their other specs differ and would
  // otherwise produce distinct hash-based doc IDs.
  //
  // The normalized value uses the canonical backend formula (`normalizeReference`
  // above, which mirrors watchlock/functions/src/referenceApproval.ts —
  // uppercase, strip non-alphanumerics) so that admin-created docs dedup against
  // portal/backend/backfilled docs that all store the same canonical format.
  //
  // Known coverage gap: Firestore equality queries skip docs missing the field,
  // so legacy refs written before `referenceNormalized` existed won't match here.
  // The exact-doc-ID check below still catches identical-spec collisions, and the
  // backfillReferenceNormalized callable patches the rest.
  const referenceNormalized = normalizeReference(refNum);
  if (referenceNormalized) {
    const dupSnap = await getDocs(
      query(
        collection(
          db,
          WATCH_BRANDS_COLLECTION,
          brandId,
          REFERENCES_SUBCOLLECTION,
        ),
        where('referenceNormalized', '==', referenceNormalized),
        limit(1),
      ),
    );
    if (!dupSnap.empty) {
      const existingId = dupSnap.docs[0].id;
      const err = new Error(
        'A reference with this number already exists for this brand. Edit the existing reference instead.',
      ) as Error & { existingReferenceId?: string };
      err.existingReferenceId = existingId;
      throw err;
    }
  }

  const docRef = doc(
    db,
    WATCH_BRANDS_COLLECTION,
    brandId,
    REFERENCES_SUBCOLLECTION,
    referenceId,
  );

  // Belt-and-braces: exact hash-based doc-ID collision check. Catches
  // identical-spec duplicates and legacy docs without `referenceNormalized`.
  const existing = await getDoc(docRef);
  if (existing.exists()) {
    throw new Error(
      `Reference with ID "${referenceId}" already exists for this brand`,
    );
  }

  await setDoc(docRef, {
    ...stripUndefined(reference as Record<string, unknown>),
    // Persist the normalized ref so future duplicate prechecks (and the brand
    // portal callable) can match this doc. Previously this was computed above
    // for the dup query but never written — the root cause of the legacy
    // duplicate clusters (e.g. the IWC Mark XX cluster). Must stay in sync with
    // watchlock/functions/src/referenceApproval.ts normalizeReference.
    //
    // Always written, even when the source ref is whitespace-only (empty
    // string). This guarantees the field is present so backfill/dedup logic can
    // reason about it, rather than silently omitting it for blank refs.
    referenceNormalized,
    // Denormalized parentage flag, always written so the doc is listable from
    // the moment it exists. Everything created through here is a parent or
    // standalone in practice (the bulk importer builds variants with a
    // parentReferenceId on the payload), so derive rather than hardcode.
    isVariant: deriveIsVariant(
      (reference as Record<string, unknown>).parentReferenceId,
    ),
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });

  return referenceId;
}

export async function uploadReferenceImage(
  brandId: string,
  referenceId: string,
  file: File,
): Promise<string> {
  const extension = imageFileExtension(file, file.name);
  const timestamp = Date.now();
  const storagePath = `watchBrands/${brandId}/references/${referenceId}/images/${timestamp}.${extension}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: imageContentType(file, extension),
  });

  // Return the storage path, not the download URL
  return storagePath;
}

export async function deleteReferenceImage(storagePath: string): Promise<void> {
  const storageRef = ref(storage, storagePath);
  await deleteObject(storageRef);
}

/**
 * Copy a user-submitted image into ref-owned Storage and return the new path.
 *
 * User submissions carry images that live under the submitting user's own
 * Storage path (the watch in their collection). Historically approval/merge
 * wrote that path straight onto the catalogue ref, so the ref and the user's
 * watch shared one underlying file — and a later admin "delete image" on the
 * ref (`deleteReferenceImage` → `deleteObject`) destroyed the user's image too.
 *
 * This re-homes the bytes to `watchBrands/{brandId}/references/{referenceId}/
 * images/{timestamp}.{ext}` (mirrors {@link uploadReferenceImage}) so the ref
 * owns its own copy and the user's original file is never touched.
 *
 * `sourceStoragePath` may be a Storage path or an already-resolved http(s) URL.
 * Throws on failure; callers decide whether to fall back to the source path.
 */
export async function copySubmissionImageToReference(
  brandId: string,
  referenceId: string,
  sourceStoragePath: string,
): Promise<string> {
  // Resolve a fetchable URL: Storage paths → download URL; http(s) → as-is.
  const isHttpUrl = /^https?:\/\//i.test(sourceStoragePath);
  const downloadUrl = isHttpUrl
    ? sourceStoragePath
    : await getImageUrl(sourceStoragePath);

  const res = await fetch(downloadUrl);
  if (!res.ok) {
    throw new Error(
      `Failed to fetch source image (${res.status}): ${sourceStoragePath}`,
    );
  }
  const blob = await res.blob();
  const contentType = blob.type || 'image/jpeg';

  // From the bytes first, then the source path's extension if it is a real
  // image one, else jpg — see imageFileExtension. The path used to win here
  // unchecked, so copying a misnamed source would have copied its bad name.
  const extension = imageFileExtension(blob, sourceStoragePath);

  const timestamp = Date.now();
  const storagePath = `watchBrands/${brandId}/references/${referenceId}/images/${timestamp}.${extension}`;
  await uploadBytes(ref(storage, storagePath), blob, { contentType });

  return storagePath;
}

export async function uploadBrandHeroImage(
  brandId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  const sourceName = fileName || (file instanceof File ? file.name : '') || 'image.jpg';
  const extension = imageFileExtension(file, sourceName);
  const timestamp = Date.now();
  const storagePath = `brands/${brandId}/hero/${timestamp}.${extension}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: imageContentType(file, extension),
  });

  return storagePath;
}

export async function uploadCalibreImage(
  calibreDocId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  const sourceName = fileName || (file instanceof File ? file.name : '') || 'image.jpg';
  const extension = imageFileExtension(file, sourceName);
  const timestamp = Date.now();
  const storagePath = `calibres/${calibreDocId}/images/${timestamp}.${extension}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: imageContentType(file, extension),
  });

  return storagePath;
}

/**
 * COPY a calibre's images onto another calibre's Storage prefix, for a tier
 * that a reclassified calibre becomes. Copy, not move: the source keeps its
 * images (and its own Storage objects) in case the reclassify is reverted, and
 * sharing one Storage object between two documents would let deleting either
 * delete the other's image.
 *
 *  - Storage-backed images: bytes downloaded and re-uploaded under
 *    `calibres/{targetCalibreId}/images/`, each with a unique name (timestamp +
 *    random suffix — `uploadCalibreImage`'s timestamp-only name collides when
 *    several are copied in the same millisecond).
 *  - Legacy URL-only images: carried over as they are (nothing to copy).
 *
 * An image that fails to copy is skipped and counted; the rest still copy.
 */
export async function copyCalibreImagesToCalibre(
  images: CalibreImage[],
  targetCalibreId: string,
): Promise<{ images: CalibreImage[]; failed: number }> {
  const out: CalibreImage[] = [];
  let failed = 0;
  for (const img of images) {
    if (!img.storagePath) {
      if (img.url) out.push({ ...img });
      continue;
    }
    try {
      const blob = await getBlob(ref(storage, img.storagePath));
      const extension = img.storagePath.match(/\.([a-z0-9]{2,5})$/i)?.[1]?.toLowerCase() ?? 'jpg';
      const storagePath = `calibres/${targetCalibreId}/images/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${extension}`;
      await uploadBytes(ref(storage, storagePath), blob, { contentType: blob.type || imageContentType(blob, extension) });
      const copy: CalibreImage = { ...img, storagePath };
      delete copy.url;
      out.push(copy);
    } catch (error) {
      console.error(`[reclassify] could not copy calibre image ${img.storagePath}:`, error);
      failed += 1;
    }
  }
  return { images: out, failed };
}

/**
 * Upload bytes under `pathPrefix` with a timestamped filename, and return the
 * STORAGE PATH.
 *
 * The four timeline/hero uploaders below were the same eight lines four times.
 * They differ only in prefix, so the prefix is the argument.
 *
 * Timestamped filename, NEVER an index: timeline events sort by the year parsed
 * out of their free-form `date`, so inserting an earlier event renumbers every
 * later one and an index-keyed path would leave files misfiled against events
 * they no longer belong to.
 *
 * Returns the path rather than a download URL, because `TimelineEventImage`
 * documents `storagePath` as preferred over `url`: a stored path lets a
 * renderer re-sign a fresh, tokened URL at display time instead of persisting
 * one that a rules change can silently turn into a 403.
 */
async function uploadTimestampedImage(
  pathPrefix: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  const sourceName = fileName || (file instanceof File ? file.name : '') || 'image.jpg';
  const extension = imageFileExtension(file, sourceName);
  const storagePath = `${pathPrefix}${Date.now()}.${extension}`;

  await uploadBytes(ref(storage, storagePath), file, {
    contentType: imageContentType(file, extension),
  });

  return storagePath;
}

/**
 * Upload one BRAND timeline-event image and return its storage path.
 *
 * STORAGE RULES, corrected. A previous version of this comment claimed
 * `brands/{id}/**` needed no rules change because it already carried hero,
 * logo, glyph and reference images. That was wrong, and it cost a 403 and a
 * watchlock fix (#1057): the catch-all `match /brands/{allPaths=**}` grants
 * READ only, and every brand write path needs its own explicit block. Those
 * four subpaths each have one — which is what made the wildcard look total.
 * Adding a new `brands/**` subpath means adding a rule for it.
 */
export async function uploadBrandTimelineImage(
  brandId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  return uploadTimestampedImage(`brands/${brandId}/timeline/`, file, fileName);
}

/**
 * Upload a BRAND history hero image — the banner leading the history screen —
 * and return its storage path.
 *
 * Its own prefix, never shared with the event images: the hero replaces and
 * deletes its own bytes, and a shared path would make one delete destroy the
 * other's picture.
 *
 * Needs `match /brands/{brandId}/history-hero/{fileName}` in watchlock's
 * storage.rules — see the note on {@link uploadBrandTimelineImage}. Until that
 * ships, uploads here 403.
 */
export async function uploadBrandHistoryHeroImage(
  brandId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  return uploadTimestampedImage(`brands/${brandId}/history-hero/`, file, fileName);
}

/**
 * Upload a MANUFACTURER history hero image and return its storage path.
 *
 * No rules change needed, unlike the brand mirror above:
 * `match /manufacturers/{manufacturerId}/{field}/{filename}` already allows an
 * authenticated write to any single-segment `{field}`, and `history-hero` is
 * one.
 */
export async function uploadManufacturerHistoryHeroImage(
  manufacturerId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  return uploadTimestampedImage(`manufacturers/${manufacturerId}/history-hero/`, file, fileName);
}

/**
 * Upload one MANUFACTURER timeline-event image and return its storage path.
 * See {@link uploadTimestampedImage} for why it is a path and not a URL.
 */
export async function uploadManufacturerTimelineImage(
  manufacturerId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  return uploadTimestampedImage(`manufacturers/${manufacturerId}/timeline/`, file, fileName);
}

/**
 * Upload a manufacturer logo or hero image and return its public download URL.
 * The manufacturer doc stores these as plain URLs (consumed by the app and the
 * entity index), so — unlike calibre images — we resolve the URL here rather
 * than storing a storage path.
 */
export async function uploadManufacturerImage(
  manufacturerId: string,
  field: 'logo' | 'hero',
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  const sourceName = fileName || (file instanceof File ? file.name : '') || 'image.jpg';
  const extension = imageFileExtension(file, sourceName);
  const timestamp = Date.now();
  const storagePath = `manufacturers/${manufacturerId}/${field}/${timestamp}.${extension}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: imageContentType(file, extension),
  });

  return getDownloadURL(storageRef);
}

/**
 * Upload a brand logo and return its public download URL. Brand docs store the
 * logo as a plain URL (consumed by the RN app), so — like manufacturer images —
 * we resolve the download URL here rather than storing a storage path.
 */
export async function uploadBrandLogo(
  brandId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  const sourceName = fileName || (file instanceof File ? file.name : '') || 'logo.jpg';
  const extension = imageFileExtension(file, sourceName);
  const timestamp = Date.now();
  const storagePath = `brands/${brandId}/logo/${timestamp}.${extension}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: imageContentType(file, extension),
  });

  return getDownloadURL(storageRef);
}

/**
 * Upload a brand dial-glyph image and return its public download URL. Stored as
 * a URL (not a storage path) because `WatchBrand.glyphImageUrl` is a URL field,
 * matching the logo flow rather than the hero flow.
 */
export async function uploadBrandGlyphImage(
  brandId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  const sourceName = fileName || (file instanceof File ? file.name : '') || 'glyph.jpg';
  const extension = imageFileExtension(file, sourceName);
  const timestamp = Date.now();
  const storagePath = `brands/${brandId}/glyph/${timestamp}.${extension}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: imageContentType(file, extension),
  });

  return getDownloadURL(storageRef);
}

// ---------------------------------------------------------------------------
// Rich-text body images
// ---------------------------------------------------------------------------

/** Storage prefix for images embedded in a rich-text body. Mirrored by watchlock storage.rules. */
export const RICH_TEXT_IMAGE_STORAGE_PREFIX = 'admin/richTextImages';

/**
 * Hard cap per image, applied to the bytes that actually go up rather than to
 * the file the curator picked — a camera original is resized first, so what
 * matters is what survives that. Mirrored by the size check in storage.rules.
 *
 * Half the brand-banner cap: a banner is the one image on a screen, where a
 * body can carry a dozen, and every one of them ships to RN clients over
 * mobile data.
 */
export const RICH_TEXT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Widest image we put in a body. Anything wider is downscaled in the browser
 * before upload. RichTextBody renders into a WebView the width of a phone, so
 * this only has to beat the largest plausible device render at 3x — a 6000px
 * original is mobile data spent on pixels nobody sees.
 */
export const RICH_TEXT_IMAGE_MAX_WIDTH = 1600;

/**
 * Upload an image a curator is embedding in a rich-text body — an article, a
 * curated collection landing body, a generic page — and return its public
 * download URL, which is what goes into the `<img src>` the editor inserts.
 *
 * The path is a single shared prefix rather than one scoped to the document,
 * because there may not be a document yet: an image can be inserted while the
 * article or collection is still being created and has no id. The trade-off is
 * that deleting the document orphans the images in its body, which is already
 * true of collection header images.
 *
 * Direct to Storage rather than through a callable, for the same reason as
 * roadmap attachments and brand banner fallbacks: routing multi-megabyte images
 * through a function means base64 in a JSON payload. The rules gate on
 * `isAdmin()` and re-assert the caps enforced here.
 */
export async function uploadRichTextImage(
  file: File,
  options: { onProgress?: (percent: number) => void } = {},
): Promise<{
  storagePath: string;
  downloadUrl: string;
  /** True when the image was downscaled before upload. */
  resized: boolean;
  /** Dimensions of the file the curator picked, or `0` if undecodable. */
  originalWidth: number;
  originalHeight: number;
}> {
  const { onProgress } = options;

  if (!file.type.startsWith('image/')) {
    throw new Error(`"${file.name}" is not an image`);
  }

  const {
    file: upload,
    width: originalWidth,
    height: originalHeight,
    resized,
  } = await resizeIfOversize(file, RICH_TEXT_IMAGE_MAX_WIDTH);

  if (upload.size > RICH_TEXT_IMAGE_MAX_BYTES) {
    const mb = (upload.size / (1024 * 1024)).toFixed(1);
    const limitMb = Math.round(RICH_TEXT_IMAGE_MAX_BYTES / (1024 * 1024));
    const note = resized ? ' even after resizing' : '';
    throw new Error(`"${file.name}" is ${mb} MB${note} — the limit is ${limitMb} MB`);
  }

  // Read the extension off the bytes being uploaded, not the file picked: a
  // resized PNG is JPEG bytes now, and storing those under `.png` would leave
  // the object lying about itself.
  const extension = imageFileExtension(upload, upload.name);

  // Timestamp plus a random suffix, because two images dropped in the same
  // millisecond is not a hypothetical when a curator picks several at once,
  // and the second one silently replacing the first would be invisible until
  // the body was read back.
  const unique = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const storagePath = `${RICH_TEXT_IMAGE_STORAGE_PREFIX}/${unique}.${extension}`;
  const task = uploadBytesResumable(ref(storage, storagePath), upload, {
    contentType: upload.type,
  });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => {
        if (!onProgress || snapshot.totalBytes === 0) return;
        onProgress(
          Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
        );
      },
      (err) => reject(err),
      () => resolve(),
    );
  });

  const downloadUrl = await getDownloadURL(task.snapshot.ref);
  return { storagePath, downloadUrl, resized, originalWidth, originalHeight };
}

/** True for URLs that already live in our own Firebase Storage. */
function isOurStorageUrl(url: string): boolean {
  return /firebasestorage\.googleapis\.com|storage\.googleapis\.com/.test(url);
}

/** Best-effort delete of a Storage object addressed by its download URL. */
async function deleteStorageObjectByUrl(url: string): Promise<void> {
  try {
    await deleteObject(ref(storage, url));
  } catch (err) {
    // Object may already be gone, or the URL may not be ours — non-fatal.
    console.warn('Failed to delete prior logo from Storage (non-fatal):', err);
  }
}

/**
 * Thrown by {@link resolveLogoToStorage} when the source image can't be fetched
 * (CORS, 404, etc.). Carries the source URL so the caller can still persist
 * provenance while leaving the canonical logo empty.
 */
export class LogoFetchError extends Error {
  readonly logoSourceUrl: string;
  constructor(logoSourceUrl: string, options?: { cause?: unknown }) {
    super('Failed to fetch the logo image from its source URL');
    this.name = 'LogoFetchError';
    this.logoSourceUrl = logoSourceUrl;
    if (options?.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.cause;
    }
  }
}

export interface ResolvedLogo {
  /** Canonical logo value to write — a Storage URL, or '' to clear. */
  logo: string;
  /** External provenance URL to write, or '' to clear. */
  logoSourceUrl: string;
}

/**
 * Pull a brand/manufacturer logo into our own Firebase Storage, mirroring how
 * heroes live in our infra. Given what the admin entered for the logo (`input`,
 * which may be an external URL, one of our own Storage URLs, or empty), the
 * manually-entered source URL, and the previously-saved values, returns the
 * `{logo, logoSourceUrl}` to write to the doc.
 *
 * - empty input → clears both fields, deletes the prior Storage object (best effort)
 * - already-our-Storage URL (e.g. admin uploaded a file) → kept as canonical
 * - same external source as before + a working Storage logo already present → no-op
 * - new external URL → fetch via {@link fetchImageBlob} + re-upload to Storage
 * - fetch fails → throws {@link LogoFetchError} carrying the source URL
 */
export async function resolveLogoToStorage(params: {
  entity: 'brands' | 'manufacturers';
  id: string;
  input: string;
  manualSourceUrl?: string;
  prev?: { logo?: string; logoSourceUrl?: string };
}): Promise<ResolvedLogo> {
  const input = (params.input || '').trim();
  const manualSourceUrl = (params.manualSourceUrl || '').trim();
  const prevLogo = (params.prev?.logo || '').trim();
  const prevSource = (params.prev?.logoSourceUrl || '').trim();

  // 1. Cleared → drop the prior Storage object and clear both fields.
  if (!input) {
    if (prevLogo && isOurStorageUrl(prevLogo)) {
      await deleteStorageObjectByUrl(prevLogo);
    }
    return { logo: '', logoSourceUrl: '' };
  }

  // 2. Already one of our Storage URLs (admin uploaded a file) → keep as
  //    canonical; preserve whatever provenance was entered/stored.
  if (isOurStorageUrl(input)) {
    return { logo: input, logoSourceUrl: manualSourceUrl || prevSource };
  }

  // 3. Idempotent: same external source as before and we already hold a working
  //    Storage logo → don't re-fetch / re-upload.
  if (prevLogo && isOurStorageUrl(prevLogo) && prevSource === input) {
    return { logo: prevLogo, logoSourceUrl: input };
  }

  // 4. A new external URL → fetch it and re-upload into our Storage.
  let blob: Blob;
  try {
    blob = await fetchImageBlob(input);
  } catch (err) {
    throw new LogoFetchError(input, { cause: err });
  }
  const fileName = input.split('/').pop()?.split('?')[0] || 'logo.jpg';

  // Replacing an older Storage logo → clean it up (best effort).
  if (prevLogo && isOurStorageUrl(prevLogo)) {
    await deleteStorageObjectByUrl(prevLogo);
  }

  const logoUrl =
    params.entity === 'manufacturers'
      ? await uploadManufacturerImage(params.id, 'logo', blob, fileName)
      : await uploadBrandLogo(params.id, blob, fileName);

  return { logo: logoUrl, logoSourceUrl: input };
}

/**
 * Pull a pasted hero image URL into our own Storage on save. See
 * services/heroImageResolve.ts — same shape as {@link resolveLogoToStorage},
 * except a failed fetch keeps the external URL (with `fetchError`) instead of
 * clearing the hero.
 */
export async function resolveHeroToStorage(params: {
  entity: 'brands' | 'manufacturers';
  id: string;
  input: string;
  manualSourceUrl?: string;
  prev?: { heroImage?: string; heroImageSourceUrl?: string };
}): Promise<ResolvedHeroImage> {
  return resolveHeroImage(params, {
    fetchBlob: fetchImageBlob,
    isOurStorageUrl,
    upload: (blob, fileName) =>
      params.entity === 'manufacturers'
        ? uploadManufacturerImage(params.id, 'hero', blob, fileName)
        : uploadBrandHeroImage(params.id, blob, fileName),
  });
}

export async function uploadCuratedCollectionImage(
  collectionKey: string,
  file: File | Blob,
  fileName?: string,
): Promise<{ storagePath: string; downloadUrl: string }> {
  const sourceName = fileName || (file instanceof File ? file.name : '') || 'header.jpg';
  const extension = imageFileExtension(file, sourceName);
  const storagePath = `collections/${collectionKey}/header.${extension}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: imageContentType(file, extension),
  });

  const downloadUrl = await getDownloadURL(storageRef);
  return { storagePath, downloadUrl };
}

export async function deleteCalibreImage(storagePath: string): Promise<void> {
  const storageRef = ref(storage, storagePath);
  await deleteObject(storageRef);
}

/**
 * Uploads the home "identify with AI" intro-CTA background image and returns its
 * public download URL (the URL is what the RN app reads from
 * `configuration/aiImageRecognition.introCtaBackgroundUrl`). Timestamped path so
 * swapping the image busts any CDN/client cache.
 */
export async function uploadIntroCtaBackground(
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  const sourceName = fileName || (file instanceof File ? file.name : '') || 'background.jpg';
  const extension = imageFileExtension(file, sourceName);
  const timestamp = Date.now();
  const storagePath = `admin/aiImageRecognition/introCtaBackground/${timestamp}.${extension}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: imageContentType(file, extension),
  });

  return getDownloadURL(storageRef);
}

// ---------------------------------------------------------------------------
// Brand banner fallbacks
// ---------------------------------------------------------------------------

/** Storage prefix for the pool objects. Mirrored by watchlock storage.rules. */
export const BRAND_BANNER_FALLBACK_STORAGE_PREFIX = 'admin/brandBannerFallbacks';

/** Images only. Mirrored by the `image/*` check in storage.rules. */
export const BRAND_BANNER_FALLBACK_ACCEPT = 'image/*';

/**
 * Hard cap per file. Mirrored by the size check in storage.rules. Lower than
 * the 25 MB roadmap cap because every one of these ships to RN clients over
 * mobile data, where roadmap attachments never leave the admin.
 */
export const BRAND_BANNER_FALLBACK_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Widest image we put in the pool. Anything wider is downscaled in the browser
 * before upload — RN renders these into a container a few hundred points wide,
 * so a 6000px original is pure mobile-data cost for pixels nobody sees.
 * Deliberately generous: it only has to beat the largest plausible device
 * render, not match the source.
 *
 * Scoped to this pool. Hero images and logos upload at their original size.
 */
export const BRAND_BANNER_FALLBACK_MAX_WIDTH = 2400;

/**
 * Uploads one image into the brand-banner fallback pool and returns its public
 * download URL (the URL is what RN reads from
 * `configuration/brandBannerFallbacks/images/{id}.url`).
 *
 * Deliberately not a callable, for the same reason as roadmap attachments:
 * routing multi-megabyte images through a function would mean base64 in a JSON
 * payload. The client writes straight to Storage and the rules gate on
 * `isAdmin()` and re-assert the type/size caps enforced here.
 *
 * The object is named after the RTDB key the caller has already minted, so the
 * two halves are reconcilable by id alone. Uses `uploadBytesResumable` rather
 * than `uploadBytes` so a batch drop can show per-file progress.
 *
 * Images wider than {@link BRAND_BANNER_FALLBACK_MAX_WIDTH} are downscaled
 * here, silently — an admin picking a camera original shouldn't have to think
 * about it. The size cap is applied to the bytes that actually go up, so a
 * 14 MB 6000px photo now uploads (as a ~400 KB JPEG) instead of being turned
 * away for being 4 MB over a limit the resize was always going to solve.
 */
export async function uploadBrandBannerFallback(
  id: string,
  file: File,
  options: { onProgress?: (percent: number) => void } = {},
): Promise<{
  storagePath: string;
  downloadUrl: string;
  /** True when the image was downscaled before upload. */
  resized: boolean;
  /** Dimensions of the file the admin picked, or `0` if undecodable. */
  originalWidth: number;
  originalHeight: number;
}> {
  if (!id) throw new Error('id is required');
  return uploadPublicAdminImage(BRAND_BANNER_FALLBACK_STORAGE_PREFIX, id, file, {
    maxWidth: BRAND_BANNER_FALLBACK_MAX_WIDTH,
    maxBytes: BRAND_BANNER_FALLBACK_MAX_BYTES,
    onProgress: options.onProgress,
  });
}

/**
 * The upload half of {@link uploadBrandBannerFallback}, for any public admin
 * image prefix: downscale past `maxWidth`, enforce `maxBytes` on the bytes that
 * actually go up, name the object `{prefix}/{name}.{ext}` with the extension of
 * the uploaded bytes, and return its download URL.
 *
 * The prefix must have a matching `storage.rules` block (watchlock) that allows
 * admin writes and repeats the same size and content-type caps.
 */
export async function uploadPublicAdminImage(
  prefix: string,
  name: string,
  file: File,
  options: { maxWidth: number; maxBytes: number; onProgress?: (percent: number) => void },
): Promise<{
  storagePath: string;
  downloadUrl: string;
  resized: boolean;
  originalWidth: number;
  originalHeight: number;
}> {
  const { onProgress, maxWidth, maxBytes } = options;

  if (!prefix || !name) throw new Error('prefix and name are required');
  if (!file.type.startsWith('image/')) {
    throw new Error(`"${file.name}" is not an image`);
  }

  const {
    file: upload,
    width: originalWidth,
    height: originalHeight,
    resized,
  } = await resizeIfOversize(file, maxWidth);

  if (upload.size > maxBytes) {
    const mb = (upload.size / (1024 * 1024)).toFixed(1);
    const limitMb = Math.round(maxBytes / (1024 * 1024));
    const note = resized ? ' even after resizing' : '';
    throw new Error(`"${file.name}" is ${mb} MB${note} — the limit is ${limitMb} MB`);
  }

  // Read the extension off the file being uploaded, not the one picked: a
  // resized PNG is JPEG bytes now, and storing those under `.png` would leave
  // the object lying about itself.
  const extension = imageFileExtension(upload, upload.name);

  const storagePath = `${prefix.replace(/\/+$/, '')}/${name}.${extension}`;
  const task = uploadBytesResumable(ref(storage, storagePath), upload, {
    contentType: upload.type,
  });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => {
        if (!onProgress || snapshot.totalBytes === 0) return;
        onProgress(
          Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
        );
      },
      (err) => reject(err),
      () => resolve(),
    );
  });

  const downloadUrl = await getDownloadURL(task.snapshot.ref);
  return { storagePath, downloadUrl, resized, originalWidth, originalHeight };
}

/**
 * Best-effort removal of a banner-fallback object.
 *
 * Resolves `false` instead of throwing when the object is already gone (or the
 * row never recorded a path): the RTDB key is dropped either way, and a
 * missing object must not block the admin from clearing a broken row. The
 * caller uses the result only to word the toast honestly.
 */
export async function deleteBrandBannerFallbackObject(
  storagePath: string,
): Promise<boolean> {
  if (!storagePath) return false;
  try {
    await deleteObject(ref(storage, storagePath));
    return true;
  } catch (err) {
    console.warn(`Could not delete banner fallback object ${storagePath}:`, err);
    return false;
  }
}

export async function deleteWatchReference(
  brandId: string,
  referenceId: string,
  imageStoragePaths: string[],
): Promise<void> {
  // Delete all associated images from Storage first (best effort)
  await Promise.allSettled(
    imageStoragePaths.map((path) => deleteObject(ref(storage, path))),
  );

  // Delete the Firestore document
  const docRef = doc(
    db,
    WATCH_BRANDS_COLLECTION,
    brandId,
    REFERENCES_SUBCOLLECTION,
    referenceId,
  );
  await deleteDoc(docRef);
}

/**
 * Delete EVERY reference doc under a brand in batched Firestore writes.
 *
 * References live in the `watchBrands/{brandId}/references` subcollection, so
 * "all refs for the brand" is simply the whole subcollection — parents,
 * Shopify variants, and standalones alike. We delete everything matching;
 * the server-side `cascadeDeleteVariants` trigger (which removes variant docs
 * when their parent is deleted) is a harmless no-op on variants we're already
 * removing here, and keeps things correct if the trigger hasn't deployed yet.
 *
 * Storage images are NOT cleaned up here (that would mean one Storage call per
 * image — far too slow for a brand-wide wipe). This is intended for the
 * delete-and-re-import migration flow; orphaned image cleanup is separate.
 *
 * @param onProgress called after each committed batch with (deleted, total)
 * @returns the number of reference docs deleted
 */
export async function deleteAllReferencesForBrand(
  brandId: string,
  onProgress?: (deleted: number, total: number) => void,
): Promise<number> {
  const collectionRef = collection(
    db,
    WATCH_BRANDS_COLLECTION,
    brandId,
    REFERENCES_SUBCOLLECTION,
  );

  // Pull every doc id up front so we can report an accurate total.
  const snapshot = await getDocs(collectionRef);
  const ids = snapshot.docs.map((d) => d.id);
  const total = ids.length;
  if (total === 0) {
    onProgress?.(0, 0);
    return 0;
  }

  // Firestore caps a batch at 500 writes; stay under it like the rest of the
  // codebase (BATCH_SIZE=400).
  const BATCH_SIZE = 400;
  let deleted = 0;
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const slice = ids.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);
    for (const id of slice) {
      batch.delete(
        doc(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION, id),
      );
    }
    await batch.commit();
    deleted += slice.length;
    onProgress?.(deleted, total);
  }

  return deleted;
}

export async function updateReferenceImage(
  brandId: string,
  referenceId: string,
  file: File,
): Promise<void> {
  // Upload the new image and get storage path
  const storagePath = await uploadReferenceImage(brandId, referenceId, file);

  // Update the reference with the new image.
  await updateWatchReference(brandId, referenceId, {
    images: [
      {
        storagePath,
        contentType: file.type || 'image/jpeg',
        index: 0,
      },
    ],
  });
}

// ============================================================================
// Merge Functionality
// ============================================================================

export async function mergeIntoExistingReference(
  pendingId: string,
  options: MergeOptions,
): Promise<void> {
  const pending = await getPendingApprovalById(pendingId);
  if (!pending) throw new Error('Pending approval not found');

  const existingRef = await getWatchReference(
    options.targetBrandId,
    options.targetReferenceId,
  );
  if (!existingRef) throw new Error('Target reference not found');

  // Build update object from selected fields
  const updates: Partial<WatchReference> = {};

  for (const field of options.fieldsToUpdate) {
    const value = (pending as unknown as Record<string, unknown>)[field];
    if (value !== undefined && value !== null && value !== '') {
      (updates as Record<string, unknown>)[field] = value;
    }
  }
  // A description merged in from a user submission is outside copy, not the
  // curator's: tagged 'imported' so the provenance says who wrote the text now
  // stored (see CuratedWatches/descriptionSource.ts).
  if (typeof updates.description === 'string' && updates.description.trim()) {
    updates.descriptionSource = 'imported';
  }

  // Handle image strategy - convert pending imageUrls to ImageInfo objects.
  // Note: pending.imageUrls may be URLs or paths depending on the source.
  //
  // Each user-submitted image is COPIED into ref-owned Storage first (see
  // copySubmissionImageToReference) so the ref never shares a file with the
  // submitting user's watch — otherwise a later admin "delete image" on the ref
  // would destroy the user's image too. Paths already under `watchBrands/` are
  // ref-owned, so they're left as-is. A single failed copy falls back to the
  // source path (logged) rather than aborting the whole merge.
  //
  // Tag with 'user_submitted' so the RN side can attribute these images
  // (e.g. suppress brand-name caption fallback). Pending approvals originate
  // from user submissions, so all merged-in images get the tag.
  const buildRefOwnedImages = async (paths: string[], startIndex: number) =>
    Promise.all(
      paths.map(async (path, i) => {
        let storagePath = path;
        if (!path.startsWith('watchBrands/')) {
          try {
            storagePath = await copySubmissionImageToReference(
              options.targetBrandId,
              options.targetReferenceId,
              path,
            );
          } catch (err) {
            console.warn(
              `Failed to copy submission image to ref-owned storage; keeping source path: ${path}`,
              err,
            );
          }
        }
        return {
          storagePath,
          contentType: 'image/jpeg',
          index: startIndex + i,
          tags: ['user_submitted'],
        };
      }),
    );

  if (options.imageStrategy === 'replace' && pending.imageUrls?.length) {
    updates.images = await buildRefOwnedImages(pending.imageUrls, 0);
  } else if (
    options.imageStrategy === 'add_as_additional' &&
    pending.imageUrls?.length
  ) {
    const existingImages = existingRef.images || [];
    const newImages = await buildRefOwnedImages(
      pending.imageUrls,
      existingImages.length,
    );
    updates.images = [...existingImages, ...newImages];
  }
  // 'keep_existing' means we don't update images

  // Update the existing reference
  if (Object.keys(updates).length > 0) {
    await updateWatchReference(
      options.targetBrandId,
      options.targetReferenceId,
      updates,
    );
  }

  // Merging resolves the submission into an existing catalogue ref — lock the
  // dedup index (server-side; no approved-ref pointer so unlock never deletes
  // the pre-existing target) before removing the pending doc.
  try {
    const lockFn = httpsCallable<{ submissionId: string }, { success: boolean }>(
      functions,
      'adminLockSubmissionForMerge',
    );
    await lockFn({ submissionId: pendingId });
  } catch (err) {
    console.error('Failed to lock submissions_index on merge:', err);
  }

  // Delete the pending approval (since we're handling it manually, not via the trigger)
  await deleteDoc(doc(db, PENDING_APPROVALS_COLLECTION, pendingId));
}

// ============================================================================
// Submission dedup index (read + one-time backfill)
// ============================================================================

export interface SubmissionIndexEntry {
  contentKey: string;
  status: 'pending' | 'approved' | 'rejected';
  currentSubmissionId?: string | null;
  brandId?: string;
  referenceNormalized?: string;
  firstSubmittedBy?: string;
  firstSubmittedAt?: Timestamp;
  lockedAt?: Timestamp;
  lastUpdated?: Timestamp;
  approvedBrandId?: string;
  approvedReferenceId?: string;
}

/** Read a submissions_index entry by contentKey (for admin UI surfacing). */
export async function getSubmissionIndexEntry(
  contentKey: string,
): Promise<SubmissionIndexEntry | null> {
  const snap = await getDoc(doc(db, SUBMISSIONS_INDEX_COLLECTION, contentKey));
  if (!snap.exists()) return null;
  return { contentKey, ...(snap.data() as Omit<SubmissionIndexEntry, 'contentKey'>) };
}

/** List submissions_index entries, optionally filtered by status (read-only). */
export async function getSubmissionsIndex(
  status?: 'pending' | 'approved' | 'rejected',
): Promise<SubmissionIndexEntry[]> {
  const col = collection(db, SUBMISSIONS_INDEX_COLLECTION);
  const q = status ? query(col, where('status', '==', status)) : query(col);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    contentKey: d.id,
    ...(d.data() as Omit<SubmissionIndexEntry, 'contentKey'>),
  }));
}

export interface SubmissionIndexDetail {
  entry: SubmissionIndexEntry;
  pending: PendingReferenceApproval | null;
  catalogueRefExists: boolean;
}

/** Full detail for one index entry: the entry + linked pending + catalogue ref. */
export async function getSubmissionIndexDetail(
  contentKey: string,
): Promise<SubmissionIndexDetail | null> {
  const entry = await getSubmissionIndexEntry(contentKey);
  if (!entry) return null;

  let pending: PendingReferenceApproval | null = null;
  if (entry.currentSubmissionId) {
    pending = await getPendingApprovalById(entry.currentSubmissionId);
  }

  let catalogueRefExists = false;
  if (entry.approvedBrandId && entry.approvedReferenceId) {
    const refSnap = await getDoc(
      doc(
        db,
        WATCH_BRANDS_COLLECTION,
        entry.approvedBrandId,
        REFERENCES_SUBCOLLECTION,
        entry.approvedReferenceId,
      ),
    );
    catalogueRefExists = refSnap.exists();
  }

  return { entry, pending, catalogueRefExists };
}

export interface OrphanIndexEntry {
  contentKey: string;
  status: SubmissionIndexEntry['status'];
  reason: 'dangling_submission' | 'approved_without_catalogue_ref';
  brandId?: string;
  referenceNormalized?: string;
}

/**
 * Read-only scan for index entries that look orphaned:
 *  - pending entries whose currentSubmissionId points at a missing pending doc
 *  - approved entries whose recorded catalogue ref no longer exists
 * Surfaces for admin review (no auto-fix).
 */
export async function scanOrphanIndexEntries(): Promise<OrphanIndexEntry[]> {
  const entries = await getSubmissionsIndex();
  const orphans: OrphanIndexEntry[] = [];

  for (const e of entries) {
    if (e.status === 'pending' && e.currentSubmissionId) {
      const p = await getPendingApprovalById(e.currentSubmissionId);
      if (!p) {
        orphans.push({
          contentKey: e.contentKey,
          status: e.status,
          reason: 'dangling_submission',
          brandId: e.brandId,
          referenceNormalized: e.referenceNormalized,
        });
      }
    } else if (e.status === 'approved' && e.approvedBrandId && e.approvedReferenceId) {
      const refSnap = await getDoc(
        doc(
          db,
          WATCH_BRANDS_COLLECTION,
          e.approvedBrandId,
          REFERENCES_SUBCOLLECTION,
          e.approvedReferenceId,
        ),
      );
      if (!refSnap.exists()) {
        orphans.push({
          contentKey: e.contentKey,
          status: e.status,
          reason: 'approved_without_catalogue_ref',
          brandId: e.brandId,
          referenceNormalized: e.referenceNormalized,
        });
      }
    }
  }

  return orphans;
}

export interface BackfillResult {
  scanned: number;
  pendingPatched: number;
  indexWritten: number;
  collisions: string[];
}

/**
 * One-time migration (server-side, admin-token): ensure every pending doc has a
 * submissionContentKey and matching submissions_index entry. Idempotent.
 */
export async function backfillSubmissionsIndex(): Promise<BackfillResult> {
  const fn = httpsCallable<unknown, BackfillResult>(
    functions,
    'adminBackfillSubmissionsIndex',
  );
  const res = await fn({});
  return res.data;
}

export interface BackfillReferenceNormalizedError {
  brandId: string;
  referenceId: string;
  error: string;
}

export interface BackfillReferenceNormalizedResult {
  scanned: number;
  missingField: number;
  updated: number;
  skipped: number;
  /**
   * Per-reference failures from the backend callable. Each entry names the
   * brand/reference that failed and why — typically refs whose `reference`
   * field is empty (a separate data-hygiene issue), not a transient error.
   */
  errors: BackfillReferenceNormalizedError[];
}

export interface BackfillReferenceNormalizedOptions {
  /** When true, scan & report counts only — no writes. */
  dryRun: boolean;
  /** Limit the run to a single brand. Omit to scan all brands. */
  brandId?: string;
}

/**
 * One-shot maintenance (server-side, admin-token): scan watch references for
 * docs missing `referenceNormalized` and backfill the field using the canonical
 * backend formula (uppercase, strip non-alphanumerics — see
 * watchlock/functions/src/referenceApproval.ts normalizeReference). Idempotent.
 *
 * Counterpart to the admin-side fix in {@link createWatchReference}, which now
 * writes `referenceNormalized` on every new doc; this callable patches legacy
 * docs created before that field was persisted.
 */
export async function backfillReferenceNormalized(
  options: BackfillReferenceNormalizedOptions,
): Promise<BackfillReferenceNormalizedResult> {
  const fn = httpsCallable<
    BackfillReferenceNormalizedOptions,
    BackfillReferenceNormalizedResult
  >(functions, 'backfillReferenceNormalized', { timeout: 540000 });
  const res = await fn({
    dryRun: options.dryRun,
    ...(options.brandId ? { brandId: options.brandId } : {}),
  });
  return res.data;
}

export interface ReclassifyCoDevelopedCalibresSample {
  calibreId: string;
  calibreName: string;
  /** The single brand the manufacturer set implies (the value that would be set). */
  brandId: string;
  /** The non-generic co-developer(s) tying the calibre to the brand. */
  viaManufacturerIds: string[];
}

export interface ReclassifyCoDevelopedCalibresError {
  calibreId: string;
  error: string;
}

export interface ReclassifyCoDevelopedCalibresResult {
  dryRun: boolean;
  scanned: number;
  /** Already had a brandId — left untouched (fill-empty-only). */
  alreadyScoped: number;
  /** Brand-less, set implies exactly one brand — updated (or would be). */
  updated: number;
  /** Brand-less, set implies TWO+ distinct brands — skipped for human review. */
  ambiguousMultiBrand: number;
  /** Brand-less, no non-generic maker ties it to a brand — nothing to do. */
  noBrandLink: number;
  samples: ReclassifyCoDevelopedCalibresSample[];
  errors: ReclassifyCoDevelopedCalibresError[];
}

/**
 * One-shot maintenance (server-side, admin-token): persist the BRAND association
 * of co-developed calibres. For each brand-less custom_calibres doc whose
 * manufacturer set (primary + additionalManufacturerIds) implies exactly one
 * brand via its non-generic makers, sets that `brandId` so the calibre surfaces
 * in brand-scoped queries / Algolia / RN. Fill-empty-only (never overwrites an
 * existing brandId); multi-brand sets are reported as ambiguous and skipped.
 *
 * Note: the generic/non-generic CLASSIFICATION itself is derived live from the
 * manufacturer set (see services/calibreClassification.ts) and needs no backfill.
 * `dryRun` defaults true — pass `dryRun: false` to write.
 */
export async function reclassifyCoDevelopedCalibres(options: {
  dryRun: boolean;
}): Promise<ReclassifyCoDevelopedCalibresResult> {
  const fn = httpsCallable<
    { dryRun: boolean },
    ReclassifyCoDevelopedCalibresResult
  >(functions, 'reclassifyCoDevelopedCalibres', { timeout: 540000 });
  const res = await fn({ dryRun: options.dryRun });
  return res.data;
}

export interface DeriveManufacturerBrandsSample {
  mfrId: string;
  mfrName: string;
  /** Stored derived set before this run (sorted). */
  before: string[];
  /** Computed derived set this run would write (sorted). */
  after: string[];
}

export interface DeriveManufacturerBrandsResult {
  dryRun: boolean;
  /** References scanned across the whole collection group. */
  scanned: number;
  /** Refs with no `brandId` — skipped. */
  refsWithoutBrand: number;
  /** Refs whose calibre resolved to no curated calibre — skipped. */
  refsWithoutCalibre: number;
  /** Manufacturers whose derived set changed (written, or would be). */
  manufacturersUpdated: number;
  /** Manufacturers whose derived set was already correct. */
  manufacturersUnchanged: number;
  samples: DeriveManufacturerBrandsSample[];
}

/**
 * Maintenance (server-side, admin-token): recompute the
 * `brandIdsDerivedFromRefs` cache on every movement manufacturer from ref usage.
 * For each ref, its `brandId` is added to the derived set of every manufacturer
 * in the ref's resolved calibre's set (primary + additionalManufacturerIds).
 *
 * Unlike the fill-empty backfills this is a fully-derived cache: each run
 * recomputes from scratch and replaces the stored value for any manufacturer
 * whose set changed (including shrinking to empty). `dryRun` defaults true —
 * pass `dryRun: false` to write.
 */
export async function deriveManufacturerBrands(options: {
  dryRun: boolean;
}): Promise<DeriveManufacturerBrandsResult> {
  const fn = httpsCallable<
    { dryRun: boolean },
    DeriveManufacturerBrandsResult
  >(functions, 'deriveManufacturerBrands', { timeout: 540000 });
  const res = await fn({ dryRun: options.dryRun });
  return res.data;
}

export interface BackfillReferenceImagesFromUserStorageParams {
  /** When true (default), scan & report only — no Storage copies or doc writes. */
  dryRun?: boolean;
  /** Limit the run to a single brand. Omit to scan all brands. */
  brandId?: string;
  /** Cap on the number of refs to process. Omit for unlimited. */
  limit?: number;
}

export interface BackfillReferenceImagesFromUserStorageResult {
  dryRun: boolean;
  refsScanned: number;
  refsAffected: number;
  imagesAffected: number;
  /** 0 when dryRun. */
  imagesCopied: number;
  /** User-pathed file that no longer exists in Storage. */
  imagesOrphaned: number;
  /** 0 when dryRun. */
  refsUpdated: number;
  sampleRefIds: string[];
  sampleOrphanedPaths: string[];
  /** Audit-log doc id in `image_backfill_runs/{autoId}`. */
  logId: string;
}

/**
 * Maintenance (server-side, admin-token): scan watch references for image URLs
 * still pointing at per-user Storage paths and copy those files into ref-owned
 * storage, rewriting the reference docs to the new URLs. Dry-run (default) scans
 * & reports counts only — no Storage copies or doc writes. Each run writes an
 * audit log to `image_backfill_runs/{autoId}`.
 *
 * Counterpart to the approval-time fix in watchlock PR #725, which copies
 * user-submitted images into ref-owned storage on merge; this callable patches
 * legacy refs whose images were merged before that fix landed.
 */
export async function callBackfillReferenceImagesFromUserStorage(
  params: BackfillReferenceImagesFromUserStorageParams,
): Promise<BackfillReferenceImagesFromUserStorageResult> {
  const fn = httpsCallable<
    BackfillReferenceImagesFromUserStorageParams,
    BackfillReferenceImagesFromUserStorageResult
  >(functions, 'backfillReferenceImagesFromUserStorage', { timeout: 540000 });
  const res = await fn({
    dryRun: params.dryRun ?? true,
    ...(params.brandId ? { brandId: params.brandId } : {}),
    ...(params.limit != null ? { limit: params.limit } : {}),
  });
  return res.data;
}

/**
 * One-shot maintenance (server-side, admin-token): scan watch references with an
 * empty `dialAndHands.color` and populate it from color tokens found in the
 * `model`/`productName` strings, resolved against `lookup_dial_colors`
 * (value / displayName / synonym). Single-token matches are written; refs with
 * multiple candidate tokens are reported as `ambiguous` (need manual attention),
 * refs with no token as `noMatch`. Idempotent.
 *
 * ⚠️ Depends on the `backfillDialColorFromModel` Cloud Function being deployed
 * (parallel watchlock task) — `firebase deploy --only
 * functions:backfillDialColorFromModel`. Until then this call returns a
 * `not-found`/`internal` error. The 540s client timeout mirrors the callable's
 * server-side `timeoutSeconds: 540`.
 */
export async function backfillDialColorFromModel(
  options: BackfillDialColorOptions,
): Promise<BackfillDialColorResult> {
  const fn = httpsCallable<
    BackfillDialColorOptions,
    BackfillDialColorResult
  >(functions, 'backfillDialColorFromModel', { timeout: 540000 });
  const res = await fn({
    dryRun: options.dryRun,
    ...(options.brandId ? { brandId: options.brandId } : {}),
    ...(options.overwriteExisting ? { overwriteExisting: true } : {}),
    ...(options.fields && options.fields !== 'all' ? { fields: options.fields } : {}),
  });
  return res.data;
}

/** Result of the `backfillParentVariantAggregates` callable. */
export interface BackfillParentVariantAggregatesResult {
  /** Parents (docs with at least one variant) examined. */
  scanned: number;
  /** Parents whose aggregated arrays were rewritten. */
  parentsUpdated: number;
  /** Parents already up to date (no write). */
  parentsUnchanged: number;
  /** Per-parent errors (capped sample). */
  errors: { brandId: string; parentId: string; error: string }[];
}

/**
 * One-shot maintenance (server-side, admin-token): recompute each parent
 * reference's aggregated variant arrays — `dialColorVariants`,
 * `movementVariants`, `strapTypeVariants`, `caseMaterialVariants` — from the
 * current values on the parent + all its variants. Fixes parents whose arrays
 * drifted stale because a later variant edit (manual, dialColor backfill, etc.)
 * never propagated (only the Shopify importer wrote them historically). The
 * `propagateVariantAggregatesToParent` trigger keeps them in sync going forward;
 * this callable repairs the existing backlog. Writes a parent only when its
 * arrays actually change. Idempotent.
 *
 * Pass `brandId` to scope to one brand (recommended for iteration / to bound the
 * timeout); omit to sweep every brand.
 *
 * ⚠️ Depends on the `backfillParentVariantAggregates` Cloud Function being
 * deployed (parallel watchlock task) — `firebase deploy --only
 * functions:backfillParentVariantAggregates`. Until then this call returns a
 * `not-found`/`internal` error. The 540s client timeout mirrors the callable's
 * server-side `timeoutSeconds: 540`.
 */
export async function backfillParentVariantAggregates(
  options: { brandId?: string } = {},
): Promise<BackfillParentVariantAggregatesResult> {
  const fn = httpsCallable<
    { brandId?: string },
    BackfillParentVariantAggregatesResult
  >(functions, 'backfillParentVariantAggregates', { timeout: 540000 });
  const res = await fn(options.brandId ? { brandId: options.brandId } : {});
  return res.data;
}

// ============================================================================
// Reference Cleanup — legacy duplicate detection (Phase 1, read-only)
// ============================================================================

/**
 * Scan `watch_references` for legacy duplicate clusters — docs with different
 * doc IDs whose `reference` collapses to the same identity key under
 * `normalizeReference` (see LEGACY_REF_DEDUP_PLAN.md §1).
 *
 * Read-only: returns candidate clusters with per-doc scoring, divergence
 * signatures, and downstream-pointer counts. No writes, no merge — Phase 1.
 *
 * Pass `brandId` to scope the scan to a single brand (recommended for fast
 * iteration and to bound the callable's timeout); omit it to scan everything.
 *
 * ⚠️ Depends on the `scanReferenceDuplicates` Cloud Function being deployed
 * (`firebase deploy --only functions:scanReferenceDuplicates`). Until then this
 * call returns a `not-found`/`internal` error. The 540s client timeout mirrors
 * the callable's server-side `timeoutSeconds: 540`.
 */
export async function scanReferenceDuplicates(
  options: ScanReferenceDuplicatesOptions = {},
): Promise<ScanReferenceDuplicatesResult> {
  const fn = httpsCallable<
    ScanReferenceDuplicatesOptions,
    ScanReferenceDuplicatesResult
  >(functions, 'scanReferenceDuplicates', { timeout: 540000 });
  const payload: ScanReferenceDuplicatesOptions = {};
  if (options.brandId) payload.brandId = options.brandId;
  if (options.includePrefixVariants) payload.includePrefixVariants = true;
  const res = await fn(payload);
  return res.data;
}

/**
 * Merge a single cluster of legacy duplicate references into one canonical doc
 * (Reference Cleanup — Phase 2). The callable writes the merged canonical,
 * hard-deletes the duplicate docs, and best-effort rewrites every downstream
 * pointer (bookmarks, wishlists, curated collections, submissions_index),
 * recording the operation in a `referenceMergeTombstones` doc for audit/rollback.
 * Algolia cleanup is automatic via the existing `syncReferenceToAlgolia` trigger.
 *
 * Pass `dryRun: true` to get the merge plan + rewrite counts without writing.
 *
 * For a `variant_excluded` cluster the callable requires `variantOverrideReason`;
 * if omitted it throws a `failed-precondition` `FunctionsError` whose `.details`
 * is `{ code: 'variant_override_required', conflictField }` — the dialog catches
 * that specifically and prompts for the reason inline.
 *
 * ⚠️ Depends on the `mergeReferenceDuplicates` Cloud Function + the
 * `referenceMergeTombstones` Firestore rule being deployed (parallel watchlock
 * task). Until then this call returns a `not-found`/`internal` error. Deploy
 * with `firebase deploy --only functions:mergeReferenceDuplicates,firestore:rules`.
 * The 540s client timeout mirrors the callable's server-side `timeoutSeconds: 540`.
 */
export async function mergeReferenceDuplicates(
  options: MergeReferenceDuplicatesOptions,
): Promise<MergeReferenceDuplicatesResult> {
  const fn = httpsCallable<
    MergeReferenceDuplicatesOptions,
    MergeReferenceDuplicatesResult
  >(functions, 'mergeReferenceDuplicates', { timeout: 540000 });
  const res = await fn(options);
  return res.data;
}

/**
 * Bulk high-confidence auto-merge of legacy duplicate references for a single
 * brand (Reference Cleanup — Phase 3). The callable scans the brand, picks the
 * algorithmic canonical per cluster, and merges every cluster in the chosen
 * confidence band up to `maxClusters`, reporting per-cluster outcomes. Same
 * downstream-rewrite + tombstone semantics as the per-cluster Phase 2 merge.
 *
 * Pass `dryRun: true` to get the full plan (which clusters would merge, rewrite
 * counts, skips/failures) without writing anything. The UI gates the real run
 * behind a successful dry-run on the same brand+confidence+maxClusters combo.
 *
 * ⚠️ Two-part gate, both owned by James:
 *   (a) the `bulkMergeReferenceDuplicates` Cloud Function must be deployed
 *       (`firebase deploy --only functions:bulkMergeReferenceDuplicates`); and
 *   (b) the RTDB flag `configuration/admin/bulkMergeEnabled` must be `true`.
 * When the flag is false the callable throws `failed-precondition` with message
 * "bulk merge is not enabled" — the panel catches that and shows a flag banner.
 * The 540s client timeout mirrors the callable's server-side `timeoutSeconds`.
 */
export async function bulkMergeReferenceDuplicates(
  options: BulkMergeReferenceDuplicatesOptions,
): Promise<BulkMergeReferenceDuplicatesResult> {
  const fn = httpsCallable<
    BulkMergeReferenceDuplicatesOptions,
    BulkMergeReferenceDuplicatesResult
  >(functions, 'bulkMergeReferenceDuplicates', { timeout: 540000 });
  const res = await fn(options);
  return res.data;
}

export interface BackfillSwatchMissingReferenceError {
  referenceId: string;
  error: string;
}

/** Where the backfilled `reference` value was sourced from per doc. */
export type SwatchReferenceSource = 'model' | 'docIdSlug';

export interface BackfillSwatchMissingReferenceSample {
  referenceId: string;
  /** The `reference` value the callable would (or did) write. */
  extractedValue: string;
  source: SwatchReferenceSource;
}

export interface BackfillSwatchMissingReferenceResult {
  scanned: number;
  missingReference: number;
  updated: number;
  skipped: number;
  errors: BackfillSwatchMissingReferenceError[];
  /** How many docs got their `reference` from each source. */
  bySource: { model: number; docIdSlug: number };
  /** Up to 50 sample rows showing what would be / was written per doc. */
  samples: BackfillSwatchMissingReferenceSample[];
  dryRun: boolean;
}

export interface BackfillSwatchMissingReferenceOptions {
  /** When true, scan & report counts/samples only — no writes. */
  dryRun: boolean;
}

/**
 * One-shot maintenance (server-side, admin-token): scan
 * `watchBrands/swatch/references` for docs missing a `reference` field and
 * backfill it. The callable prefers the doc's `model` field, falling back to a
 * humanized form of the doc-ID slug. Hardcoded to Swatch — no brand filter.
 *
 * Returns a per-source breakdown (`bySource`) and up to 50 `samples` of exactly
 * what value would be written per doc, so a dry run can be inspected before the
 * real write run.
 */
export async function backfillSwatchMissingReference(
  options: BackfillSwatchMissingReferenceOptions,
): Promise<BackfillSwatchMissingReferenceResult> {
  const fn = httpsCallable<
    BackfillSwatchMissingReferenceOptions,
    BackfillSwatchMissingReferenceResult
  >(functions, 'backfillSwatchMissingReference', { timeout: 540000 });
  const res = await fn({ dryRun: options.dryRun });
  return res.data;
}

export interface PruneOrphanCollectionWatchIdsError {
  userId: string;
  error: string;
}

export interface PruneOrphanCollectionWatchIdsResult {
  usersScanned: number;
  collectionsScanned: number;
  collectionsUpdated: number;
  orphansRemoved: number;
  errors: PruneOrphanCollectionWatchIdsError[];
}

export interface PruneOrphanCollectionWatchIdsOptions {
  /** Scope the sweep to a single user. Omit / empty → all users. */
  userId?: string;
}

/**
 * Maintenance (server-side, admin-token): remove references in user collections
 * whose `watchIds` point at watches that no longer exist. Only the `watchIds`
 * arrays are modified. Pass a `userId` to scope to one user; omit it for a full
 * sweep across all users. Calls the `pruneOrphanCollectionWatchIds` callable.
 */
export async function pruneOrphanCollectionWatchIds(
  options: PruneOrphanCollectionWatchIdsOptions = {},
): Promise<PruneOrphanCollectionWatchIdsResult> {
  const fn = httpsCallable<
    PruneOrphanCollectionWatchIdsOptions,
    PruneOrphanCollectionWatchIdsResult
  >(functions, 'pruneOrphanCollectionWatchIds', { timeout: 540000 });
  // Only send `userId` when it's a non-empty string so the server treats an
  // empty input as a full sweep rather than scoping to a "" user.
  const userId = options.userId?.trim();
  const res = await fn(userId ? { userId } : {});
  return res.data;
}

export interface RecomputeCalibreReferenceCountsError {
  calibreId: string;
  error: string;
}

export interface RecomputeCalibreReferenceCountsResult {
  scanned: number;
  updated: number;
  reindexed: number;
  skipped: number;
  errors: RecomputeCalibreReferenceCountsError[];
}

/**
 * Maintenance (server-side): recompute the denormalised `referenceCount` on
 * every calibre from current watch-reference state. Run after a bulk import, if
 * counts drift, or if the Algolia index is stale; this sweep force-re-indexes
 * every calibre to Algolia (even ones whose count is unchanged), so it also
 * repairs search-index drift. Calls the `recomputeAllCalibreReferenceCounts`
 * v2 callable (no parameters).
 */
export async function recomputeAllCalibreReferenceCounts(): Promise<RecomputeCalibreReferenceCountsResult> {
  const fn = httpsCallable<unknown, RecomputeCalibreReferenceCountsResult>(
    functions,
    'recomputeAllCalibreReferenceCounts',
    { timeout: 540000 },
  );
  const res = await fn({});
  return res.data;
}

// ---------------------------------------------------------------------------
// Calibre spelling normalization (Caliber → Calibre)
// ---------------------------------------------------------------------------

export interface SpellingFieldDiff {
  before: string;
  after: string;
}

export interface CalibreSpellingChange {
  id: string;
  brandId?: string;
  name?: SpellingFieldDiff;
  description?: SpellingFieldDiff;
}

/**
 * A calibre whose rewritten name would collide with a name another doc already
 * owns — one of the duplicate pairs where both spellings exist as separate
 * docs. Skipped whole (description included) and reported for a manual merge.
 */
export interface CalibreSpellingSkip {
  sourceId: string;
  /** The doc that already owns the target name. */
  targetId: string;
  /** The name the source doc would have been renamed to. */
  name: string;
  brandId?: string;
}

export interface NormalizeCalibreSpellingResult {
  dryRun: boolean;
  includeDescriptions: boolean;
  scanned: number;
  /** Docs that WOULD be written (dry run only; 0 on a real run). */
  wouldWrite: number;
  /** Docs actually written (real run only; 0 on a dry run). */
  written: number;
  nameChanges: number;
  descriptionChanges: number;
  changes: CalibreSpellingChange[];
  changesTruncated: boolean;
  skipped: CalibreSpellingSkip[];
  skippedTruncated: boolean;
  errors: Array<{ calibreId: string; error: string }>;
}

/**
 * Fold the American spelling onto the British one in the stored calibre corpus:
 * `name` always, `description` only on an explicit `includeDescriptions`. AKAs
 * and doc ids are never touched, "Caliber Corner" is exempt, and docs whose
 * rename would collide with an existing name are skipped and reported.
 *
 * Safe and idempotent; `dryRun` defaults TRUE. Calls `normalizeCalibreSpelling`.
 */
export async function normalizeCalibreSpelling(opts: {
  dryRun?: boolean;
  includeDescriptions?: boolean;
}): Promise<NormalizeCalibreSpellingResult> {
  const fn = httpsCallable<
    { dryRun: boolean; includeDescriptions: boolean },
    NormalizeCalibreSpellingResult
  >(functions, 'normalizeCalibreSpelling', { timeout: 540000 });
  const res = await fn({
    dryRun: opts.dryRun !== false,
    includeDescriptions: opts.includeDescriptions === true,
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Tier types — Phase 1 backfill + Phase 3 reclassify migration
// ---------------------------------------------------------------------------

export interface BackfillTierTypesResult {
  scanned: number;
  /** Calibres that had at least one tier stamped. */
  calibresUpdated: number;
  /** Total individual tiers stamped with tierType: 'general'. */
  tiersStamped: number;
  dryRun: boolean;
  samples: Array<{ calibreId: string; calibreName: string; tiersStamped: number }>;
  errors: Array<{ calibreId: string; error: string }>;
}

/**
 * Phase 1 backfill: stamp `tierType: 'general'` on every existing calibre tier
 * that lacks one (fill-empty-only — never touches a tier already typed). Safe,
 * idempotent, `dryRun` defaults TRUE. Calls `backfillTierTypesGeneral`.
 */
export async function backfillTierTypesGeneral(
  dryRun = true,
): Promise<BackfillTierTypesResult> {
  const fn = httpsCallable<{ dryRun: boolean }, BackfillTierTypesResult>(
    functions,
    'backfillTierTypesGeneral',
    { timeout: 540000 },
  );
  const res = await fn({ dryRun });
  return res.data;
}

/** What a SCOPED backfill run did to one calibre. */
export type BackfillCalibreTiersStatus =
  | 'enriched'
  | 'no-tiers'
  | 'skipped-has-tiers'
  | 'would-enrich'
  | 'not-found'
  | 'error';

export interface BackfillCalibreTiersEntry {
  calibreId: string;
  calibreName: string;
  status: BackfillCalibreTiersStatus;
  tiersAdded: number;
  tierNames: string[];
  /** True when this run overwrote (or would overwrite) an existing tier array. */
  overwrote: boolean;
  /** True when an earlier run already attempted this calibre. */
  attemptedBefore: boolean;
  error?: string;
}

export interface BackfillCalibreTiersResult {
  dryRun: boolean;
  /** True when the run was scoped to an explicit `calibreIds` list. */
  scoped: boolean;
  /** True when a scoped run was allowed to overwrite populated tiers. */
  force: boolean;
  /** Docs read this invocation. */
  scanned: number;
  /** Eligible calibres seen this invocation — unscoped: empty tiers + unmarked;
   *  scoped: the calibres the plan would enrich. */
  candidates: number;
  /** Calibres actually enriched this invocation (real runs only). */
  processed: number;
  /** Of those processed, how many received at least one tier. */
  calibresUpdated: number;
  /** Total individual tiers written this invocation. */
  tiersAdded: number;
  /** True when more work remains — call again to continue. */
  hasMore: boolean;
  /** SCOPED cursor: ids this invocation did not reach. Re-send exactly these. */
  remainingIds: string[];
  /** Scoped runs only: the per-calibre outcome. Empty on the unscoped sweep. */
  results: BackfillCalibreTiersEntry[];
  /** Scoped runs only: calibres left alone because they already have tiers. */
  skippedWithTiers: number;
  samples: Array<{
    calibreId: string;
    calibreName: string;
    tiersAdded: number;
    tierNames: string[];
  }>;
  errors: Array<{ calibreId: string; error: string }>;
}

/** Ids accepted per scoped invocation — mirrors MAX_CALIBRE_IDS on the callable. */
export const BACKFILL_TIERS_MAX_IDS = 500;

/**
 * Backfill `data.tiers` on custom calibres by re-running the same enrichCalibre
 * core per calibre and auto-accepting the returned tiers.
 *
 * Two modes. Omit `calibreIds` for the whole-collection sweep (fill-empty only:
 * calibres that already have tiers are never visited, and an internal marker
 * stops an already-attempted calibre being re-enriched forever). Pass
 * `calibreIds` to scope the run to a selection — there the marker is ignored,
 * because picking rows out of the listing is a deliberate re-ask, and `force`
 * additionally re-enriches calibres that already have tiers, OVERWRITING them.
 *
 * Each calibre is a paid AI call, so a run enriches at most `limit` calibres per
 * invocation. Unscoped runs loop on `hasMore`; scoped runs re-send
 * `remainingIds`. `dryRun` defaults TRUE (plan only; no AI, no writes).
 */
export async function backfillCalibreTiers(
  opts: {
    dryRun?: boolean;
    limit?: number;
    calibreIds?: string[];
    force?: boolean;
  } = {},
): Promise<BackfillCalibreTiersResult> {
  const fn = httpsCallable<
    { dryRun: boolean; limit?: number; calibreIds?: string[]; force?: boolean },
    BackfillCalibreTiersResult
  >(functions, 'backfillCalibreTiers', { timeout: 540000 });
  const res = await fn({
    dryRun: opts.dryRun !== false,
    limit: opts.limit,
    ...(opts.calibreIds?.length ? { calibreIds: opts.calibreIds } : {}),
    ...(opts.force ? { force: true } : {}),
  });
  return res.data;
}

export interface MigrateStandaloneToTierRequest {
  sourceCalibreId: string;
  targetCalibreId: string;
  /** Required for a real run; may be '' on a dry-run preview (tier not yet made). */
  targetTierId: string;
  /**
   * Scope brand for AKA + legacy-string matching during a dry-run preview that
   * runs before the tier exists. On a real run the target tier's brand is
   * authoritative; pass the wizard's selected brand so the two agree.
   */
  brandId?: string;
  dryRun?: boolean;
}

export interface MigrateStandaloneToTierResult {
  dryRun: boolean;
  /** Set when the migration refused (pre-flight guard failed); no writes done. */
  refused?: {
    reason:
      | 'same-calibre'
      | 'source-deprecated'
      | 'target-deprecated'
      | 'target-missing'
      | 'source-missing'
      | 'tier-missing'
      | 'multi-brand-refs'
      | 'multi-tier-source';
    message: string;
    /** For multi-brand-refs: the distinct brand ids found on the source's refs. */
    brandIds?: string[];
  };
  /** Refs re-pointed from source → target (or that WOULD be, on a dry run). */
  refsRepointed: number;
  /** Of `refsRepointed`, those linked by the `calibreId` FK. */
  refsRepointedById: number;
  /** Of `refsRepointed`, those matched by the legacy `calibre` STRING. */
  refsRepointedByLegacyString: number;
  /** Distinct brand ids owning the source's refs (echoed for the preview). */
  refBrandIds: string[];
  /** True if any of the source's aliases were moved onto the target. */
  akaAdded: boolean;
  /** How many alias rows moved from the source onto the target. */
  akasTransferred: number;
  /** The moved names, capped, for the preview. */
  akaNamesTransferred: string[];
  /** Aliases removed from the source once they had landed on the target. */
  sourceAkasStripped: number;
  /** The source's new canonical name — `X (deprecated)` — when it was renamed. */
  sourceRenamedTo?: string;
  /** True once the source calibre was marked deprecated + redirected. */
  sourceDeprecated: boolean;
  sampleRefIds: string[];
  errors: Array<{ refId: string; error: string }>;
}

/**
 * Phase 3 migration: re-point every ref linking the source calibre onto the
 * target calibre + brand-specific tier, move the source's WHOLE alias set onto
 * the target (canonical name, flat `alsoKnownAs` and every `aliasDetails` row,
 * deduplicated), then strip those aliases from the source, rename it
 * `X (deprecated)` and mark it deprecated (permanent redirect alias — never
 * hard-deleted). The transfer is load-bearing: the calibre resolver refuses to
 * match a deprecated calibre by any name, so a name left on the source stops
 * resolving. Guards refuse multi-brand / multi-tier sources. `dryRun` defaults
 * TRUE — the wizard previews with it before confirming. Calls
 * `migrateStandaloneToTier`.
 */
export async function migrateStandaloneToTier(
  req: MigrateStandaloneToTierRequest,
): Promise<MigrateStandaloneToTierResult> {
  const fn = httpsCallable<
    MigrateStandaloneToTierRequest,
    MigrateStandaloneToTierResult
  >(functions, 'migrateStandaloneToTier', { timeout: 540000 });
  const res = await fn({ dryRun: true, ...req });
  return res.data;
}

/** One calibre repaired by {@link backfillDeprecatedCalibreHygiene}. */
export interface DeprecatedCalibreHygieneSample {
  calibreId: string;
  calibreName: string;
  targetCalibreId: string;
  /** Alias names moved onto the redirect target. */
  transferred: string[];
  /** Aliases removed from the source (both storage shapes). */
  stripped: number;
  /** The source's new canonical name, when it was renamed. */
  renamedTo?: string;
}

export interface DeprecatedCalibreHygieneResult {
  dryRun: boolean;
  scanned: number;
  repaired: number;
  akasTransferred: number;
  sourcesStripped: number;
  sourcesRenamed: number;
  /** True when the cap or the deadline stopped the sweep — run it again. */
  stoppedEarly: boolean;
  stoppedReason?: 'maxCalibres' | 'deadline';
  samples: DeprecatedCalibreHygieneSample[];
  skipped: Array<{
    calibreId: string;
    calibreName: string;
    reason: 'no-redirect-target' | 'target-missing' | 'target-deprecated' | 'already-clean';
  }>;
  errors: Array<{ calibreId: string; error: string }>;
}

/**
 * Retroactive repair for calibres deprecated before the reclassify wizard moved
 * a whole alias set: transfers whatever aliases are still stranded on a
 * soft-deleted calibre onto its redirect target, snapshots and strips them, and
 * applies the ` (deprecated)` rename. Idempotent — a second run over the same
 * calibres writes nothing — so hitting `maxCalibres` just means running it
 * again. `dryRun` defaults TRUE.
 */
export async function backfillDeprecatedCalibreHygiene(
  opts: { dryRun?: boolean; maxCalibres?: number; calibreId?: string } = {},
): Promise<DeprecatedCalibreHygieneResult> {
  const fn = httpsCallable<
    { dryRun: boolean; maxCalibres?: number; calibreId?: string },
    DeprecatedCalibreHygieneResult
  >(functions, 'backfillDeprecatedCalibreHygiene', { timeout: 540000 });
  const res = await fn({
    dryRun: opts.dryRun !== false,
    maxCalibres: opts.maxCalibres,
    calibreId: opts.calibreId,
  });
  return res.data;
}

export interface BackfillCalibreMovementTypeSample {
  calibreId: string;
  calibreName: string;
  oldType: string;
  /** The rule proposal, or '' when there is no clean match (includeAll rows only). */
  newType: string;
  /** True when an existing specific value disagrees with the proposal (not written). */
  conflict?: boolean;
  /** True when data.movementTypeReviewedAt is set (only surfaced with includeReviewed). */
  reviewed?: boolean;
}

export interface BackfillCalibreMovementTypeError {
  calibreId: string;
  error: string;
}

/** Result of the rule-match PROPOSAL pass — never writes. */
export interface BackfillCalibreMovementTypesResult {
  scanned: number;
  ruleMatched: number;
  /** Empty/generic value with a clean match that differs — proposed for upgrade. */
  updated: number;
  unchanged: number;
  conflicts: number;
  noMatch: number;
  /** Skipped because data.movementTypeReviewedAt is set (and includeReviewed=false). */
  reviewedSkipped: number;
  samples: BackfillCalibreMovementTypeSample[];
  errors: BackfillCalibreMovementTypeError[];
}

/** Options for the rule-match proposal pass. All independent, all default off. */
export interface BackfillCalibreMovementTypesOptions {
  /** Surface EVERY scanned calibre as a row, not just rule-flagged ones. */
  includeAll?: boolean;
  /** Do NOT skip calibres already stamped data.movementTypeReviewedAt. */
  includeReviewed?: boolean;
  /** Restrict the scan to calibres with this top-level manufacturerId FK. */
  manufacturerId?: string;
}

/**
 * Maintenance (server-side, admin-gated): PROPOSE `data.movementType` upgrades
 * for every custom calibre using ONLY the deterministic rule match — scanning
 * name + alsoKnownAs against the `lookup_movement_types` synonyms. Never calls AI
 * and NEVER writes; the human reviews each proposal on the page and applies the
 * ones they accept via {@link applyCalibreMovementTypeUpdates}. Returns proposed
 * upgrades and flagged conflicts (existing specific value disagrees). Pass
 * options to show all calibres, include already-reviewed ones, and/or filter by
 * manufacturer (see {@link BackfillCalibreMovementTypesOptions}).
 */
export async function backfillCalibreMovementTypesRule(
  options: BackfillCalibreMovementTypesOptions = {},
): Promise<BackfillCalibreMovementTypesResult> {
  const fn = httpsCallable<
    BackfillCalibreMovementTypesOptions,
    BackfillCalibreMovementTypesResult
  >(functions, 'backfillCalibreMovementTypesRule', { timeout: 120000 });
  const res = await fn(options);
  return res.data;
}

export interface AuditCalibreDescriptionSample {
  calibreId: string;
  name: string;
  /** The calibre's current data.movementType (may be empty). */
  currentType: string;
  /** Slug the rule pass found in the DESCRIPTION only. */
  descriptionSays: string;
  /** True when descriptionSays differs from currentType (a red contradiction). */
  contradictsCurrent: boolean;
  /** True when data.descriptionReviewedAt is set (only surfaced with includeReviewed). */
  reviewed?: boolean;
}

/** Result of the Description Audit pass — never writes, never calls AI. */
export interface AuditCalibreDescriptionsResult {
  scanned: number;
  contradictions: number;
  matches: number;
  noMention: number;
  /** Skipped because data.descriptionReviewedAt is set (and includeReviewed=false). */
  reviewedSkipped: number;
  samples: AuditCalibreDescriptionSample[];
  errors: BackfillCalibreMovementTypeError[];
}

/** Options for the Description Audit pass. Mirrors the rule pass for consistency. */
export interface AuditCalibreDescriptionsOptions {
  /** Do NOT skip calibres already stamped data.descriptionReviewedAt. */
  includeReviewed?: boolean;
  /** Restrict the scan to calibres with this top-level manufacturerId FK. */
  manufacturerId?: string;
}

/**
 * Maintenance (server-side, admin-gated): runs the deterministic rule match
 * against each calibre's DESCRIPTION ONLY and compares it to the stored
 * `data.movementType`, surfacing contradictions (description disagrees) and
 * matches (description confirms). Diagnoses bad descriptions that would mislead
 * the "Ask AI" path. Never writes; never calls AI. Calls `auditCalibreDescriptions`.
 */
export async function auditCalibreDescriptions(
  options: AuditCalibreDescriptionsOptions = {},
): Promise<AuditCalibreDescriptionsResult> {
  const fn = httpsCallable<
    AuditCalibreDescriptionsOptions,
    AuditCalibreDescriptionsResult
  >(functions, 'auditCalibreDescriptions', { timeout: 120000 });
  const res = await fn(options);
  return res.data;
}

export interface SuggestCalibreMovementTypeResult {
  calibreId: string;
  currentType: string;
  /** Empty string when AI is not confident enough to pick a slug. */
  suggestedSlug: string;
  reasoning?: string;
}

/**
 * Maintenance (server-side, admin-gated): AI suggestion for ONE calibre, invoked
 * from a row's "Ask AI" button. Classifies the full text (incl. description) with
 * Sonnet and returns a single slug (or empty) plus brief reasoning. Never writes
 * — the human decides whether to accept it. Calls `suggestCalibreMovementType`.
 */
export async function suggestCalibreMovementType(
  calibreId: string,
): Promise<SuggestCalibreMovementTypeResult> {
  const fn = httpsCallable<{ calibreId: string }, SuggestCalibreMovementTypeResult>(
    functions,
    'suggestCalibreMovementType',
    { timeout: 70000 },
  );
  const res = await fn({ calibreId });
  return res.data;
}

export interface DeepVerifyCalibreMovementTypeResult {
  calibreId: string;
  currentType: string;
  /** Slug from lookup, "unknown" if no clear source, or null if no search was done. */
  webFinding: string | null;
  webSources: string[];
  /** Slug from lookup, or null if the description states no movement type. */
  descriptionFinding: string | null;
  recommendation: 'current_correct' | 'web_correct' | 'description_correct' | 'ambiguous';
  reasoning: string;
}

/**
 * Maintenance (server-side, admin-gated): per-row "Deep verify" for ONE calibre.
 * Runs Sonnet WITH web search enabled to cross-check what authoritative web
 * sources say against the description and the current stored type. Diagnostic
 * only — never writes; the human decides. Slow (web search adds a few seconds per
 * search). Calls `deepVerifyCalibreMovementType`.
 */
export async function deepVerifyCalibreMovementType(
  calibreId: string,
): Promise<DeepVerifyCalibreMovementTypeResult> {
  const fn = httpsCallable<{ calibreId: string }, DeepVerifyCalibreMovementTypeResult>(
    functions,
    'deepVerifyCalibreMovementType',
    { timeout: 70000 },
  );
  const res = await fn({ calibreId });
  return res.data;
}

export interface ApplyCalibreMovementTypeUpdate {
  calibreId: string;
  newSlug: string;
}

export interface ApplyCalibreMovementTypeFailure {
  calibreId: string;
  error: string;
}

export interface ApplyCalibreMovementTypeUpdatesResult {
  applied: number;
  failed: ApplyCalibreMovementTypeFailure[];
}

/**
 * Maintenance (server-side, admin-gated): the ONLY write path. Takes the explicit
 * list of { calibreId, newSlug } the human accepted/overrode, validates each slug
 * against `lookup_movement_types`, and writes `data.movementType` + a
 * `lastBackfillAt` timestamp (re-firing the watchlock Algolia trigger). Capped at
 * 500 updates per call. Calls `applyCalibreMovementTypeUpdates`.
 */
export async function applyCalibreMovementTypeUpdates(
  updates: ApplyCalibreMovementTypeUpdate[],
): Promise<ApplyCalibreMovementTypeUpdatesResult> {
  const fn = httpsCallable<
    { updates: ApplyCalibreMovementTypeUpdate[] },
    ApplyCalibreMovementTypeUpdatesResult
  >(functions, 'applyCalibreMovementTypeUpdates', { timeout: 120000 });
  const res = await fn({ updates });
  return res.data;
}

export interface ConfirmCalibreMovementTypeFailure {
  calibreId: string;
  error: string;
}

export interface ConfirmCalibreMovementTypeResult {
  confirmed: number;
  failed: ConfirmCalibreMovementTypeFailure[];
}

/**
 * Maintenance (server-side, admin-gated): "leave as-is" path. Stamps ONLY
 * `data.movementTypeReviewedAt` on each given calibre (no type change, no Algolia
 * reindex), so a deliberate "this is already correct" decision stops the row
 * re-surfacing in the default rule pass. Capped at 500 per call. Calls
 * `confirmCalibreMovementType`.
 */
export async function confirmCalibreMovementType(
  calibreIds: string[],
  options: { markDescriptionReviewed?: boolean } = {},
): Promise<ConfirmCalibreMovementTypeResult> {
  const fn = httpsCallable<
    { calibreIds: string[]; markDescriptionReviewed?: boolean },
    ConfirmCalibreMovementTypeResult
  >(functions, 'confirmCalibreMovementType', { timeout: 120000 });
  const res = await fn({ calibreIds, markDescriptionReviewed: options.markDescriptionReviewed });
  return res.data;
}

export interface RecomputeAcquiredByBrandIdsError {
  brandId: string;
  error: string;
}

export interface RecomputeAcquiredByBrandIdsResult {
  scanned: number;
  updated: number;
  cleared: number;
  errors: RecomputeAcquiredByBrandIdsError[];
}

/**
 * Maintenance (server-side): recompute the inverse `acquiredByBrandIds` field on
 * every brand by scanning all `acquisitions` arrays. Run after a bulk acquisition
 * import or if the inverse field drifts; each updated brand re-indexes to Algolia
 * via the existing brand trigger. Calls the `recomputeAllAcquiredByBrandIds`
 * v2 callable (no parameters).
 */
export async function recomputeAllAcquiredByBrandIds(): Promise<RecomputeAcquiredByBrandIdsResult> {
  const fn = httpsCallable<unknown, RecomputeAcquiredByBrandIdsResult>(
    functions,
    'recomputeAllAcquiredByBrandIds',
    { timeout: 540000 },
  );
  const res = await fn({});
  return res.data;
}

export interface RecomputeWatchBrandReferenceCountsError {
  brandId: string;
  error: string;
}

export interface RecomputeWatchBrandReferenceCountsResult {
  scanned: number;
  updated: number;
  unchanged: number;
  skipped: number;
  /** Server caps this list at the first 100 failures. */
  errors: RecomputeWatchBrandReferenceCountsError[];
}

/**
 * Maintenance (server-side): recompute the denormalised `parentReferenceCount`
 * on every brand from its current listable (parent) references. Run once after
 * the counter deployed to seed existing brands, or if counts drift — the
 * reference trigger keeps them fresh thereafter. Idempotent: a second run
 * reports everything `unchanged`. The 540s client timeout mirrors the
 * callable's server-side `timeoutSeconds: 540`. Calls the
 * `recomputeAllWatchBrandReferenceCounts` v2 callable (no parameters).
 */
export async function recomputeAllWatchBrandReferenceCounts(): Promise<RecomputeWatchBrandReferenceCountsResult> {
  const fn = httpsCallable<unknown, RecomputeWatchBrandReferenceCountsResult>(
    functions,
    'recomputeAllWatchBrandReferenceCounts',
    { timeout: 540000 },
  );
  const res = await fn({});
  return res.data;
}

/** Fields `familyFilter` may match on (watchlock `FAMILY_FILTER_FIELDS`). */
export type MoveRefsFamilyFilterField = 'series' | 'collection' | 'productName' | 'family';

export interface MoveWatchReferencesBetweenBrandsInput {
  sourceBrandId: string;
  targetBrandId: string;
  /** Preview only — count what would move without writing. */
  dryRun?: boolean;
  /*
   * SELECTORS (watchlock #1078) — at most one. None means the original
   * all-or-nothing move. Any selection is widened server-side to whole
   * parent/variant groups before it moves.
   */
  /** Whole-value regex on `reference`, compiled `^(?:…)$`, CASE-SENSITIVE, ≤200 chars. */
  refPattern?: string;
  /** Explicit ref doc ids — trimmed and de-duplicated server-side, capped at 500. */
  referenceIds?: string[];
  familyFilter?: {
    field: MoveRefsFamilyFilterField;
    /** Exactly one of `equals` (trimmed, case-insensitive) or `matches` (anchored, case-sensitive). */
    equals?: string;
    matches?: string;
  };
  /**
   * Required for a selective WET run. The server only checks it is non-empty —
   * it is not bound to a dry run, so it is not a safety check by itself.
   */
  confirmationToken?: string;
}

export interface MoveWatchReferencesBetweenBrandsResult {
  refsMoved: number;
  calibresMoved: number;
  /** Per-collection counts for any other entities re-pointed (keyed by entity). */
  otherEntitiesMoved: Record<string, number>;
  /** Always true — the callable never deletes the source brand. */
  sourceBrandStillExists: true;
  dryRun: boolean;
  auditLogId?: string;
  /** DRY RUNS ONLY: the first 20 refs that would move. Absent on a wet run. */
  sampleRefsMoved?: string[];
  /** Selective moves: refs added because they share a parent/variant group with a selected ref. */
  refsAddedByFamilyClosure?: number;
  /** `referenceIds` selector: requested ids with no ref under the source brand. */
  referenceIdsNotFound?: string[];
}

/**
 * Rescue tool (server-side, admin-gated): move ALL watch references (and related
 * entities — calibres, etc.) from `sourceBrandId` onto `targetBrandId`. Used to
 * reconcile a duplicate/mistaken brand doc that has stray refs (e.g. an orphan
 * `iwc-schaffhausen` ref that belongs on `iwc`). Pass `dryRun: true` to preview
 * counts without writing. The source brand doc is left in place (empty of refs)
 * for manual deletion via the brand list. Calls the
 * `moveWatchReferencesBetweenBrands` v2 callable.
 */
export async function moveWatchReferencesBetweenBrands(
  input: MoveWatchReferencesBetweenBrandsInput,
): Promise<MoveWatchReferencesBetweenBrandsResult> {
  const fn = httpsCallable<
    MoveWatchReferencesBetweenBrandsInput,
    MoveWatchReferencesBetweenBrandsResult
  >(functions, 'moveWatchReferencesBetweenBrands', { timeout: 540000 });
  const res = await fn(input);
  return res.data;
}

/** One row of a parent-org backfill report — enough to find and fix the document. */
export interface ParentOrgBackfillRow {
  collection: 'watchBrands' | 'movement_manufacturers';
  id: string;
  name: string;
  parentOrg: string;
  /** Resolved: the one org. Ambiguous: every org the string matches. */
  candidateOrgIds?: string[];
  /** Flagged already-linked rows: the FK stored on the document. */
  parentOrgId?: string;
  flag?: 'fk-missing-org' | 'fk-disagrees-with-string';
}

export interface BackfillParentOrgIdsResult {
  dryRun: boolean;
  counts: {
    scanned: number;
    resolved: number;
    ambiguous: number;
    unmatched: number;
    alreadyLinked: number;
    flaggedLinked: number;
    noString: number;
    written: number;
  };
  /** First 200 resolved rows; `counts.resolved` is exact. */
  resolvedSample: ParentOrgBackfillRow[];
  ambiguous: ParentOrgBackfillRow[];
  unmatched: ParentOrgBackfillRow[];
  flaggedLinked: ParentOrgBackfillRow[];
}

export interface BackfillDescriptionSourceResult {
  dryRun: boolean;
  maxWrites: number;
  scanned: number;
  tagged: number;
  noDescription: number;
  imported: number;
  importedBySourceType: Record<string, number>;
  unknownSource: number;
  otherSource: number;
  otherSourceTypes: Record<string, number>;
  written: number;
  nextCursor?: string;
  stoppedReason?: 'maxWrites' | 'deadline';
}

/**
 * Tag legacy descriptions 'imported' where the ref's `sourceType` says bulk
 * import created it. Calls the admin `backfillDescriptionSource` v1 callable;
 * dry run by default, sliced by `maxWrites` with `startAfterPath` to continue.
 */
export async function backfillDescriptionSource(input: {
  dryRun: boolean;
  confirmationToken?: string;
  startAfterPath?: string;
  maxWrites?: number;
}): Promise<BackfillDescriptionSourceResult> {
  const fn = httpsCallable<typeof input, BackfillDescriptionSourceResult>(
    functions,
    'backfillDescriptionSource',
    { timeout: 540000 },
  );
  return (await fn(input)).data;
}

/** One calibre image stored without a real image extension. */
export interface MisnamedCalibreImage {
  oldPath: string;
  /** The name its content type implies; null when that cannot be told. */
  newPath: string | null;
  contentType: string | null;
  size: number | null;
  /** ready = copy + repoint · copied = only references remain · orphan = unreferenced · blocked = needs a person. */
  status: 'ready' | 'copied' | 'orphan' | 'blocked';
  blockReason?: string;
  references: { docId: string; field: 'images' | 'data.images'; index: number }[];
  newObjectExists: boolean;
  /** How many of the two proper thumbnails exist for the new name. */
  newVariantsPresent: number;
  junkVariants: string[];
  /** Safe to delete the old object: copied, de-referenced, both thumbnails present. */
  cleanable: boolean;
  cleanupBlockedBy?: string;
}

export interface RepairCalibreImageNamesResult {
  mode: 'dryRun' | 'repair' | 'cleanup';
  bucket: string;
  objectsListed: number;
  calibreDocsRead: number;
  misnamed: MisnamedCalibreImage[];
  /** Sanity check on correctly named originals: do both resized variants exist? */
  thumbnails: { originalsChecked: number; complete: number; missing: string[]; missingCount: number };
  repairCount: number;
  repairToken: string;
  cleanupCount: number;
  cleanupToken: string;
  /** Dry run only: the other resize-watched trees, reported and never repaired. */
  otherTrees?: {
    prefix: string;
    originalsScanned: number;
    misnamedCount: number;
    sample: string[];
    complete: boolean;
  }[];
  repaired?: {
    copied: number;
    alreadyCopied: number;
    docsUpdated: number;
    pathsUpdated: number;
    failures: { oldPath: string; error: string }[];
  };
  cleaned?: {
    originalsDeleted: number;
    variantsDeleted: number;
    failures: { oldPath: string; error: string }[];
  };
}

export interface RecomputeCalibreReferenceCountsTargetedResult {
  requested: number;
  updated: number;
  reindexed: number;
  skipped: number;
  superseded: number;
  /** The count each calibre ended the call with. */
  counts: Record<string, number>;
  errors: { calibreId: string; error: string }[];
}

/**
 * Recount NAMED calibres' `referenceCount`, once each, and re-index them.
 * Calls watchlock's `recomputeCalibreReferenceCounts` v2 callable (1–50 ids).
 *
 * The companion to writing references with a deferred recount (see
 * `pushCalibreDataToRefs`'s `deferCalibreRecount`): one deliberate recount when
 * a bulk run has finished, instead of one per reference written.
 */
export async function recomputeCalibreReferenceCountsFor(
  calibreIds: string[],
): Promise<RecomputeCalibreReferenceCountsTargetedResult> {
  const fn = httpsCallable<{ calibreIds: string[] }, RecomputeCalibreReferenceCountsTargetedResult>(
    functions,
    'recomputeCalibreReferenceCounts',
    { timeout: 540000 },
  );
  return (await fn({ calibreIds })).data;
}

/** One reference the finder thinks could belong to a calibre. */
export interface CalibreReferenceCandidate {
  brandId: string;
  refId: string;
  /** The calibre text the reference shows (cached name, else legacy string). */
  calibre: string;
  calibreId: string | null;
  isVariant: boolean;
  /** How the SERVER found it — a wide net. The browser matcher has the last word. */
  foundBy: 'link' | 'name' | 'alias';
}

export interface FindReferencesForCalibreResult {
  calibreId: string;
  calibreName: string;
  mode: 'linked' | 'scan';
  candidates: CalibreReferenceCandidate[];
  scanned: number;
  /** A scan stopped early — pass back as `startAfterPath` to continue. */
  nextCursor?: string;
  elapsedMs: number;
}

/**
 * READ-ONLY. Calls the admin `findReferencesForCalibre` v1 callable.
 *
 * - `linked`: every reference whose `calibreId` is this calibre, all brands.
 * - `scan`: references whose calibre text equals the calibre's name or any
 *   alias — one brand's with `brandId`, every brand's without. Sliced; loop
 *   while `nextCursor` comes back.
 *
 * Candidates only: "Reapply to matching references" runs the brand-side matcher
 * over them before anything is shown or written.
 */
export async function findReferencesForCalibre(input: {
  calibreId: string;
  mode: 'linked' | 'scan';
  brandId?: string;
  startAfterPath?: string;
}): Promise<FindReferencesForCalibreResult> {
  const fn = httpsCallable<typeof input, FindReferencesForCalibreResult>(
    functions,
    'findReferencesForCalibre',
    { timeout: 540000 },
  );
  return (await fn(input)).data;
}

/** One name field `stripReferenceNameMarkup` would rewrite. */
export interface ReferenceNameMarkupChange {
  field: 'model' | 'collection' | 'productName' | 'series' | 'nickname';
  before: string;
  after: string;
}

export interface StripReferenceNameMarkupResult {
  mode: 'dryRun' | 'apply';
  /** The brand filter in force; empty = every brand. */
  brandIds: string[];
  scanned: number;
  affected: number;
  fieldsToWrite: number;
  byBrand: { brandId: string; references: number; fields: Partial<Record<ReferenceNameMarkupChange['field'], number>> }[];
  samples: {
    path: string;
    brandId: string;
    refId: string;
    reference: string;
    isVariant: boolean;
    changes: ReferenceNameMarkupChange[];
  }[];
  samplesTruncated: boolean;
  /** Fields that are nothing but markup — reported, never written. */
  emptied: { path: string; fields: ReferenceNameMarkupChange['field'][] }[];
  /** Brand documents that carry markup themselves — report only. */
  brandDocsWithMarkup: { brandId: string; fields: string[] }[];
  confirmationToken?: string;
  written?: { references: number; fields: number; failures: { path: string; error: string }[] };
  elapsedMs: number;
}

/**
 * Remove HTML-like markup (`<tc>ALLIANCE</tc>`, `&amp;`) from the model,
 * collection, product name, series and nickname of existing references. Calls
 * the admin `stripReferenceNameMarkup` v1 callable.
 *
 * `dryRun` (default) writes nothing and returns a token; `apply` needs that
 * token and is refused if the references — or the brand filter — have changed
 * since. `brandIds` limits the run to those brands; omitted or empty = all.
 */
export async function stripReferenceNameMarkup(input: {
  mode: 'dryRun' | 'apply';
  brandIds?: string[];
  confirmationToken?: string;
}): Promise<StripReferenceNameMarkupResult> {
  const fn = httpsCallable<typeof input, StripReferenceNameMarkupResult>(
    functions,
    'stripReferenceNameMarkup',
    { timeout: 540000 },
  );
  return (await fn(input)).data;
}

/**
 * Find and repair calibre images stored as `{timestamp}.images` / `.image`.
 * Calls the admin `repairCalibreImageNames` v1 callable.
 *
 * - `dryRun` (default) writes nothing and returns a token for each other mode.
 * - `repair` copies each object to its proper name and repoints the calibre
 *   documents. It deletes nothing.
 * - `cleanup` deletes the old objects, only where the new one, both its
 *   thumbnails, and the absence of any remaining reference are confirmed.
 *
 * The writing modes need the matching token from a dry run; the callable
 * refuses if the bucket has changed since.
 */
export async function repairCalibreImageNames(input: {
  mode: 'dryRun' | 'repair' | 'cleanup';
  confirmationToken?: string;
}): Promise<RepairCalibreImageNamesResult> {
  const fn = httpsCallable<typeof input, RepairCalibreImageNamesResult>(
    functions,
    'repairCalibreImageNames',
    { timeout: 540000 },
  );
  return (await fn(input)).data;
}

/** One analog ref's planned write in the analog-module cleanup. */
export interface AnalogModuleCleanupRefRow {
  path: string;
  moduleId: string;
  moduleNumber: string | null;
  sharedModule: boolean;
  danglingModule: boolean;
  write: { unsetModuleId: true; calibreId?: string; calibre?: string };
  calibreOutcome: 'alreadyLinked' | 'linked' | 'seeded' | 'noNumber';
}

export interface AnalogModuleCleanupModuleRow {
  moduleId: string;
  number: string | null;
  calibreId?: string;
  unlinkedRefs: number;
}

export interface CleanupAnalogModuleLinksResult {
  dryRun: boolean;
  counts: {
    refsWithModule: number;
    refsToUnlink: number;
    calibresLinked: number;
    calibresSeeded: number;
    alreadyLinked: number;
    danglingModules: number;
    sharedModuleRefs: number;
    modulesToDelete: number;
    modulesAwaitingCalibre: number;
    refsWritten: number;
    modulesDeleted: number;
  };
  refs: AnalogModuleCleanupRefRow[];
  modulesToDelete: AnalogModuleCleanupModuleRow[];
  modulesAwaitingCalibre: AnalogModuleCleanupModuleRow[];
  sharedModules: Array<{ moduleId: string; analogRefs: number; otherRefs: number }>;
  ambiguousNumbers: string[];
  skippedUnresolvedType: number;
}

/**
 * Unlink the electronic module pre-#602 bulk import gave analog refs, link or
 * seed their calibre, and delete module docs that have become calibres. Calls
 * the admin `cleanupAnalogModuleLinks` v1 callable; dry run by default.
 */
export async function cleanupAnalogModuleLinks(input: {
  dryRun: boolean;
  confirmationToken?: string;
}): Promise<CleanupAnalogModuleLinksResult> {
  const fn = httpsCallable<typeof input, CleanupAnalogModuleLinksResult>(
    functions,
    'cleanupAnalogModuleLinks',
    { timeout: 300000 },
  );
  return (await fn(input)).data;
}

/** One ref the dial-finish migration moved, as the callable reports it. */
export interface DialFinishMigrationAction {
  path: string;
  decision: 'notStructural' | 'migrate' | 'featureAlreadyPresent';
  finish: string;
  feature: string;
  dialFeatures: string;
}

export interface MigrateDialFinishResult {
  dryRun: boolean;
  /** False ⇒ the soft deadline fired; call again with `startAfterBrandId`. */
  complete: boolean;
  lastBrandId: string | null;
  counts: {
    brandsScanned: number;
    refsScanned: number;
    refsWithFinish: number;
    toMigrate: number;
    featureAlreadyPresent: number;
    refsWritten: number;
    byFinish: Record<string, number>;
  };
  sample: DialFinishMigrationAction[];
}

/**
 * Move the structural values (skeletonized, sandwich, three_dimensional) out of
 * `dialAndHands.finish` into `dialAndHands.dialFeatures`, the field #618 split
 * off for them, and clear the finish. Dry run by default; a wet run needs a
 * non-empty `confirmationToken`.
 *
 * RESUMABLE: there is no index for "refs with a structural finish" (it is a map
 * subfield), so the run reads references brand by brand and decides in process.
 * `complete: false` means the soft deadline fired — call again passing the
 * returned `lastBrandId` as `startAfterBrandId`.
 */
export async function migrateDialFinishToFeatures(input: {
  dryRun: boolean;
  confirmationToken?: string;
  startAfterBrandId?: string;
}): Promise<MigrateDialFinishResult> {
  const fn = httpsCallable<typeof input, MigrateDialFinishResult>(
    functions,
    'migrateDialFinishToFeatures',
    { timeout: 540000 },
  );
  return (await fn(input)).data;
}

/**
 * Phase 3 of the parent-org migration: set `parentOrgId` on every brand and
 * manufacturer whose legacy `parentOrg` resolves to exactly one organization.
 * Dry run by default; a wet run needs a non-empty `confirmationToken`. Writes
 * `parentOrgId` only, and skips documents that already have one, so it is safe
 * to re-run. Calls the admin `backfillParentOrgIds` v1 callable.
 */
export async function backfillParentOrgIds(input: {
  dryRun: boolean;
  confirmationToken?: string;
}): Promise<BackfillParentOrgIdsResult> {
  const fn = httpsCallable<typeof input, BackfillParentOrgIdsResult>(
    functions,
    'backfillParentOrgIds',
    { timeout: 540000 },
  );
  const res = await fn(input);
  return res.data;
}

export interface RecomputeManufacturerAncestorsResult {
  scanned: number;
  updated: number;
  skipped: number;
  errors?: number;
}

/**
 * Maintenance (server-side): walk each manufacturer's `parentManufacturerId`
 * chain and write the resolved `manufacturerIdAncestors` array. Run once after
 * deploying the trigger, or re-run after a mid-chain re-parent if descendants'
 * ancestor arrays are stale. Idempotent and safe to re-run. Calls the
 * `recomputeAllManufacturerAncestors` v2 callable (no parameters).
 */
export async function recomputeAllManufacturerAncestors(): Promise<RecomputeManufacturerAncestorsResult> {
  const fn = httpsCallable<unknown, RecomputeManufacturerAncestorsResult>(
    functions,
    'recomputeAllManufacturerAncestors',
    { timeout: 540000 },
  );
  const res = await fn({});
  return res.data;
}

export interface BackfillShopifyParentRefsResult {
  scanned: number;
  updated: number;
  skipped: number;
  errors: { brandId: string; parentId: string; error: string }[];
}

/**
 * Maintenance (server-side): re-derive each Shopify composite parent's display
 * `reference` from its lead variant SKU and (re)write the parent's
 * `variantReferences` array (the searchable union of all variant SKUs, indexed
 * by the watchlock `buildAlgoliaRecord`). Each parent write touches `updatedAt`,
 * re-firing the Algolia reindex trigger. Run once after deploy to align parents
 * imported before the lead-variant rule shipped; the importer maintains both on
 * subsequent imports. Idempotent and safe to re-run. Calls the
 * `backfillShopifyParentRefsAndVariants` v2 callable.
 *
 * @param brandId Optional single-brand filter; omit to sweep all brands.
 */
export async function backfillShopifyParentRefsAndVariants(
  brandId?: string,
): Promise<BackfillShopifyParentRefsResult> {
  const fn = httpsCallable<
    { brandId?: string },
    BackfillShopifyParentRefsResult
  >(functions, 'backfillShopifyParentRefsAndVariants', { timeout: 540000 });
  const res = await fn(brandId ? { brandId } : {});
  return res.data;
}

/** Params for the `backfillIsVariant` callable. */
export interface BackfillIsVariantParams {
  /** Optional single-brand filter; omit to sweep all brands. */
  brandId?: string;
  /** Resume cursor — pass BOTH cursor fields back verbatim from the last result. */
  cursorBrandId?: string;
  cursorAfterReferenceId?: string;
  /** Count and log without writing. */
  dryRun?: boolean;
}

/** Result of one `backfillIsVariant` invocation (one pass, up to 540s of work). */
export interface BackfillIsVariantResult {
  brandsScanned: number;
  scanned: number;
  updated: number;
  skipped: number;
  errors: string[];
  dryRun: boolean;
  /** Present when `complete` is false — feed both back in to resume. */
  cursorBrandId?: string;
  cursorAfterReferenceId?: string;
  /** False when the 540s budget ran out mid-sweep; call again with the cursor. */
  complete: boolean;
}

/**
 * One-shot maintenance (server-side): set the `isVariant` boolean on every
 * existing watch reference, derived from whether it carries a
 * `parentReferenceId`. Backfills docs written before the field existed
 * (watchlock PR #823); the importer maintains it on subsequent writes.
 *
 * Each invocation runs for up to 540s and returns `complete: false` if it ran
 * out of budget — the caller must re-invoke with BOTH `cursorBrandId` and
 * `cursorAfterReferenceId` copied verbatim from the result to resume. The admin
 * UI (`BackfillIsVariantSection`) chains those passes automatically.
 * Idempotent and safe to re-run.
 */
export async function backfillIsVariant(
  params: BackfillIsVariantParams,
): Promise<BackfillIsVariantResult> {
  const fn = httpsCallable<BackfillIsVariantParams, BackfillIsVariantResult>(
    functions,
    'backfillIsVariant',
    { timeout: 540000 },
  );
  const res = await fn(params);
  return res.data;
}

export interface PushAlgoliaCalibreIndexSettingsResult {
  index: string;
  replicas: string[];
  replicasAdded: string[];
  updated: boolean;
}

/**
 * Maintenance (server-side): push the `watchkeeper_calibres` index settings and
 * (re)declare its sort replicas (`_name_asc`, `_name_desc`, `_refCount_desc`) to
 * Algolia. Idempotent — re-running never duplicates a replica. Calls the
 * `pushAlgoliaCalibreIndexSettings` v2 callable (no parameters).
 */
export async function pushAlgoliaCalibreIndexSettings(): Promise<PushAlgoliaCalibreIndexSettingsResult> {
  const fn = httpsCallable<unknown, PushAlgoliaCalibreIndexSettingsResult>(
    functions,
    'pushAlgoliaCalibreIndexSettings',
    { timeout: 120000 },
  );
  const res = await fn({});
  return res.data;
}

export interface SeedElectronicModuleLookupsCollectionResult {
  collection: string;
  added: number;
  updated: number;
  skipped: number;
  total: number;
}

export interface SeedElectronicModuleLookupsResult {
  overwrite: boolean;
  collections: SeedElectronicModuleLookupsCollectionResult[];
  totalAdded: number;
  totalUpdated: number;
}

/**
 * Maintenance (server-side): one-shot seed of the ElectronicModules display +
 * battery lookups (lookup_display_type / lookup_battery_type). Calls the
 * `seedElectronicModuleLookups` callable. Default (overwrite:false) is
 * non-destructive — only missing slugs are added. overwrite:true re-applies the
 * seed displayName/sortOrder to existing seed slugs and adds any missing ones;
 * curated non-seed values are never deleted.
 */
export async function seedElectronicModuleLookups(
  overwrite = false,
): Promise<SeedElectronicModuleLookupsResult> {
  const fn = httpsCallable<{ overwrite?: boolean }, SeedElectronicModuleLookupsResult>(
    functions,
    'seedElectronicModuleLookups',
    { timeout: 120000 },
  );
  const res = await fn({ overwrite });
  // Seeding happens server-side, so the AI functions' lookup caches never see
  // the write. Signal it from here, where the admin action completes.
  await bumpLookupsVersion();
  return res.data;
}

export interface SeedPocketWatchLookupsCollectionResult {
  collection: string;
  added: number;
  updated: number;
  skipped: number;
  total: number;
}

export interface SeedPocketWatchLookupsResult {
  overwrite: boolean;
  collections: SeedPocketWatchLookupsCollectionResult[];
  totalAdded: number;
  totalUpdated: number;
}

/**
 * Maintenance (server-side): one-shot seed of the pocket-watch lookups
 * (lookup_pocket_watch_case_style / _escapement / _jewel_bearing /
 * _movement_size / _adjustments). Calls the `seedPocketWatchLookups` callable.
 * Default (overwrite:false) is non-destructive — only missing slugs are added.
 * overwrite:true re-applies the seed displayName/sortOrder to existing seed
 * slugs and adds any missing ones; curated non-seed values are never deleted.
 */
export async function seedPocketWatchLookups(
  overwrite = false,
): Promise<SeedPocketWatchLookupsResult> {
  const fn = httpsCallable<{ overwrite?: boolean }, SeedPocketWatchLookupsResult>(
    functions,
    'seedPocketWatchLookups',
    { timeout: 120000 },
  );
  const res = await fn({ overwrite });
  // As above — the seed is a server-side write the AI caches can't observe.
  await bumpLookupsVersion();
  return res.data;
}

export interface PushAlgoliaCalibreFamilyIndexSettingsResult {
  index: string;
  updated: boolean;
}

/**
 * Maintenance (server-side): push the `watchkeeper_calibre_families` index
 * settings (searchable attributes, filterOnly facets, customRanking) to Algolia.
 * Unlike the calibres index, the family index declares NO sort replicas, so the
 * callable returns only `{ index, updated }`. Idempotent. Calls the
 * `pushAlgoliaCalibreFamilyIndexSettings` v2 callable (no parameters).
 * Calibre Families Phase 2.
 */
export async function pushAlgoliaCalibreFamilyIndexSettings(): Promise<PushAlgoliaCalibreFamilyIndexSettingsResult> {
  const fn = httpsCallable<unknown, PushAlgoliaCalibreFamilyIndexSettingsResult>(
    functions,
    'pushAlgoliaCalibreFamilyIndexSettings',
    { timeout: 120000 },
  );
  const res = await fn({});
  return res.data;
}

// --- Electronic Modules (Phase 2 bootstrap callables) ----------------------
//
// These wrap the `watchkeeper_electronic_modules` Algolia/recompute callables
// deployed from watchlock. The exact server return shapes aren't available in
// this repo yet, so every field below is optional and the UI reads them
// defensively (`?.`/`??`) — a future server shape change shows a wrong/blank
// value rather than throwing mid-render (there is no error boundary above the
// AdminConfig tree).

export interface PushAlgoliaElectronicModuleIndexSettingsResult {
  index?: string;
  updated?: boolean;
  /** Some entity index callables also (re)create sort replicas; read defensively. */
  replicas?: string[];
  replicasAdded?: string[];
}

/**
 * Maintenance (server-side): declare the `watchkeeper_electronic_modules`
 * Algolia index settings (searchable attributes, filter facets, custom ranking)
 * and any sort replicas. Idempotent. Short timeout — it's a single settings
 * push. Calls the `pushAlgoliaElectronicModuleIndexSettings` v2 callable (no
 * parameters). Electronic Modules Phase 2.
 */
export async function pushAlgoliaElectronicModuleIndexSettings(): Promise<PushAlgoliaElectronicModuleIndexSettingsResult> {
  const fn = httpsCallable<
    unknown,
    PushAlgoliaElectronicModuleIndexSettingsResult
  >(functions, 'pushAlgoliaElectronicModuleIndexSettings', { timeout: 120000 });
  const res = await fn({});
  return res.data;
}

export interface BackfillElectronicModulesToAlgoliaResult {
  index?: string;
  scanned?: number;
  indexed?: number;
  fullRebuild?: boolean;
  /** Optional sample of affected module IDs, if the callable returns them. */
  sampleIds?: string[];
  /** Optional audit-log doc ID, if the callable writes one. */
  auditLogId?: string;
}

/**
 * Maintenance (server-side): push every electronic-module doc to the
 * `watchkeeper_electronic_modules` Algolia index. Use after a field-shape change
 * to refresh existing records. Pass `fullRebuild` to atomically swap the index
 * and remove Algolia records for docs no longer in Firestore (settings/synonyms
 * preserved). The 540s client timeout mirrors the callable's server-side
 * `timeoutSeconds: 540`. Electronic Modules Phase 2.
 */
export async function backfillElectronicModulesToAlgolia(opts?: {
  fullRebuild?: boolean;
}): Promise<BackfillElectronicModulesToAlgoliaResult> {
  const fn = httpsCallable<
    { fullRebuild?: boolean },
    BackfillElectronicModulesToAlgoliaResult
  >(functions, 'backfillElectronicModulesToAlgolia', { timeout: 540000 });
  const res = await fn({ fullRebuild: opts?.fullRebuild === true });
  return res.data;
}

export interface PushAlgoliaWinderIndexSettingsResult {
  index?: string;
  updated?: boolean;
}

/**
 * Maintenance (server-side): declare the `watchkeeper_winders` Algolia index
 * settings (searchable attributes, filter facets, custom ranking,
 * `paginationLimitedTo`). Idempotent. Calls the
 * `pushAlgoliaWinderIndexSettings` v2 callable (no parameters).
 *
 * No `replicas` in the result, unlike its module sibling: the winders index is
 * deliberately replica-free — the listing sorts client-side, and its two
 * sort-worthy fields are strings, which `customRanking` cannot order.
 */
export async function pushAlgoliaWinderIndexSettings(): Promise<PushAlgoliaWinderIndexSettingsResult> {
  const fn = httpsCallable<unknown, PushAlgoliaWinderIndexSettingsResult>(
    functions,
    'pushAlgoliaWinderIndexSettings',
    { timeout: 120000 },
  );
  const res = await fn({});
  return res.data;
}

export interface BackfillWindersToAlgoliaResult {
  index?: string;
  scanned?: number;
  indexed?: number;
  fullRebuild?: boolean;
}

/**
 * Maintenance (server-side): push every winder doc to the `watchkeeper_winders`
 * Algolia index. Pass `fullRebuild` to atomically swap the index and remove
 * Algolia records for docs no longer in Firestore (settings/synonyms preserved).
 *
 * The callable keyset-paginates the collection to COMPLETION on the server, so
 * one invocation is the whole backfill — there is no "call again with the last
 * id" loop for the caller to get wrong. The 540s client timeout mirrors its
 * server-side `timeoutSeconds: 540`.
 */
export async function backfillWindersToAlgolia(opts?: {
  fullRebuild?: boolean;
}): Promise<BackfillWindersToAlgoliaResult> {
  const fn = httpsCallable<{ fullRebuild?: boolean }, BackfillWindersToAlgoliaResult>(
    functions,
    'backfillWindersToAlgolia',
    { timeout: 540000 },
  );
  const res = await fn({ fullRebuild: opts?.fullRebuild === true });
  return res.data;
}

export interface RecomputeElectronicModuleReferenceCountsError {
  moduleId: string;
  error: string;
}

export interface RecomputeElectronicModuleReferenceCountsResult {
  scanned?: number;
  updated?: number;
  reindexed?: number;
  skipped?: number;
  /** Optional sample of affected module IDs, if the callable returns them. */
  sampleIds?: string[];
  /** Optional audit-log doc ID, if the callable writes one. */
  auditLogId?: string;
  errors?: RecomputeElectronicModuleReferenceCountsError[];
}

/**
 * Maintenance (server-side): recompute the denormalised `referenceCount` on
 * every electronic-module doc from the live `collectionGroup('references')`
 * count. Run after a bulk import or if counts drift; each updated module
 * re-indexes to Algolia automatically. The 540s client timeout mirrors the
 * callable's server-side `timeoutSeconds: 540`. Calls the
 * `recomputeAllElectronicModuleReferenceCounts` v2 callable (no parameters).
 * Electronic Modules Phase 2.
 */
export async function recomputeAllElectronicModuleReferenceCounts(): Promise<RecomputeElectronicModuleReferenceCountsResult> {
  const fn = httpsCallable<
    unknown,
    RecomputeElectronicModuleReferenceCountsResult
  >(functions, 'recomputeAllElectronicModuleReferenceCounts', {
    timeout: 540000,
  });
  const res = await fn({});
  return res.data;
}

// ============================================================================
// Electronic Modules (entity collection) — Phase 3 admin CRUD
//
// Mirrors the custom-calibre service layer, but the module entity is the
// shared-types `ElectronicModule` written straight to the `electronicModules`
// collection (no admin-local entry wrapper). Per-ref `electronics` caches are
// kept fresh by a Cloud Function fan-out trigger — admin only writes the module
// doc and the ref's `moduleId`, never the cache.
// ============================================================================

const ELECTRONIC_MODULES_COLLECTION = 'electronicModules';

/**
 * The editable shape an admin authors in the module editor. `id` is the doc id
 * (present on edit, derived on create). Read-only fields the backend owns
 * (`searchText`, `referenceCount`) are intentionally omitted from the writer.
 */
export type ElectronicModuleInput = Pick<
  ElectronicModule,
  | 'name'
  | 'manufacturerId'
  | 'additionalManufacturerIds'
  | 'alsoKnownAs'
  | 'generation'
  | 'displayType'
  | 'lcdType'
  | 'battery'
  | 'batteryLifeMonths'
  | 'chargeTimeHours'
  | 'lightType'
  | 'displayColor'
  | 'textColor'
  | 'sensors'
  | 'displayLayout'
  | 'viewingAngle'
  | 'dstSupport'
  | 'calendarType'
  | 'stopwatchPrecision'
  | 'powerSource'
  | 'syncSource'
  | 'timeSystems'
  | 'alarmTypes'
  | 'description'
  | 'aiDescription'
  | 'source'
  | 'sourceUrl'
  | 'images'
> & { id?: string };

/** Build a stable, slug-style doc id for a new module from name (+ manufacturer). */
export function buildElectronicModuleDocId(
  name: string,
  manufacturerId?: string | null,
): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return manufacturerId
    ? `${manufacturerId.toLowerCase().replace(/[^a-z0-9]/g, '-')}--${slug}`
    : slug;
}

/** All electronic modules, name-ordered. Mirrors `getCustomCalibres`. */
export async function getElectronicModules(
  fromServer = false,
): Promise<ElectronicModule[]> {
  const q = query(
    collection(db, ELECTRONIC_MODULES_COLLECTION),
    orderBy('name'),
  );
  const snapshot = fromServer ? await getDocsFromServer(q) : await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as ElectronicModule[];
}

/** Single module by doc id, forced server read (freshest copy for the editor). */
export async function getElectronicModuleById(
  id: string,
): Promise<ElectronicModule | null> {
  const snap = await getDocFromServer(
    doc(db, ELECTRONIC_MODULES_COLLECTION, id),
  );
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as ElectronicModule;
}

/**
 * Batch exists-check for the ref editor's legacy `elec*` rows: given a set of
 * legacy string values, find any `electronicModules` doc whose `name` OR
 * `alsoKnownAs[]` matches. Returns a map keyed by the (lower-cased) legacy value
 * → the first matching module, so all legacy rows resolve in one mount-time
 * lookup rather than a query per row. Firestore can't OR across fields, so each
 * ≤30-value chunk runs a `name in` + `alsoKnownAs array-contains-any` pair;
 * matching back to the input is case-insensitive (Firestore itself is exact).
 */
export async function findModulesByLegacyValues(
  values: string[],
): Promise<Map<string, ElectronicModule>> {
  const result = new Map<string, ElectronicModule>();
  const unique = [...new Set(values.map((v) => (v || '').trim()).filter(Boolean))];
  if (unique.length === 0) return result;

  const wanted = new Map<string, string>(); // lower-cased → original
  for (const v of unique) wanted.set(v.toLowerCase(), v);

  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += 30) chunks.push(unique.slice(i, i + 30));

  const seen: ElectronicModule[] = [];
  const byId = new Set<string>();
  await Promise.all(
    chunks.map(async (chunk) => {
      const [nameSnap, akaSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, ELECTRONIC_MODULES_COLLECTION),
            where('name', 'in', chunk),
          ),
        ),
        getDocs(
          query(
            collection(db, ELECTRONIC_MODULES_COLLECTION),
            where('alsoKnownAs', 'array-contains-any', chunk),
          ),
        ),
      ]);
      for (const d of [...nameSnap.docs, ...akaSnap.docs]) {
        if (byId.has(d.id)) continue;
        byId.add(d.id);
        seen.push({ id: d.id, ...d.data() } as ElectronicModule);
      }
    }),
  );

  // Map each wanted legacy value to the first module matching by name or AKA.
  for (const [lower, original] of wanted) {
    const match = seen.find(
      (m) =>
        (m.name || '').toLowerCase() === lower ||
        (m.alsoKnownAs || []).some((a) => (a || '').toLowerCase() === lower),
    );
    if (match) result.set(original, match);
  }
  return result;
}

/**
 * Modules made by a manufacturer — primary `manufacturerId` match OR
 * `additionalManufacturerIds` co-developer match. Two queries merged + deduped
 * since Firestore can't OR across fields. Mirrors `getCalibresForManufacturer`
 * (which only needs the primary match, but modules surface under co-developers
 * too — same as the calibre Algolia scoping).
 */
export async function getElectronicModulesForManufacturer(
  manufacturerId: string,
): Promise<ElectronicModule[]> {
  const [primarySnap, additionalSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, ELECTRONIC_MODULES_COLLECTION),
        where('manufacturerId', '==', manufacturerId),
      ),
    ),
    getDocs(
      query(
        collection(db, ELECTRONIC_MODULES_COLLECTION),
        where('additionalManufacturerIds', 'array-contains', manufacturerId),
      ),
    ),
  ]);
  const byId = new Map<string, ElectronicModule>();
  for (const d of [...primarySnap.docs, ...additionalSnap.docs]) {
    byId.set(d.id, { id: d.id, ...d.data() } as ElectronicModule);
  }
  return Array.from(byId.values()).sort((a, b) =>
    (a.name || '').localeCompare(b.name || ''),
  );
}

/**
 * All references linked to an electronic module, across every brand. Runs a
 * `collectionGroup('references')` query on `moduleId == moduleId` (mirrors the
 * server-side `referenceCount` recompute). Each returned ref carries its owning
 * `brandId` (derived from the parent brand doc) so callers can group by brand.
 *
 * Used by Module Enrichment to aggregate the pre-migration inline `electronics.*`
 * values across a module's linked refs into a grounding context block for AI
 * enrichment (historical data the module doc may be missing).
 */
export async function getReferencesForModule(
  moduleId: string,
): Promise<WatchReference[]> {
  const trimmed = (moduleId || '').trim();
  if (!trimmed) return [];
  const q = query(
    collectionGroup(db, REFERENCES_SUBCOLLECTION),
    where('moduleId', '==', trimmed),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => {
    const brandId = docSnap.ref.parent.parent?.id;
    return { id: docSnap.id, brandId, ...docSnap.data() } as WatchReference;
  });
}

/**
 * Upsert an electronic module. Optional fields are written as `null` when
 * cleared (rather than omitted) so an edit that empties a field clears it on the
 * doc. `searchText` / `referenceCount` are left untouched (backend-owned).
 * Returns the doc id.
 */
export async function saveElectronicModule(
  input: ElectronicModuleInput,
): Promise<string> {
  const name = input.name.trim();
  if (!name) throw new Error('Module name is required');

  const resolvedManufacturerId =
    input.manufacturerId && input.manufacturerId.trim()
      ? input.manufacturerId.trim()
      : null;

  // Co-developers: trim, drop empties + the primary, dedupe.
  const additional = (input.additionalManufacturerIds ?? [])
    .map((id) => (id || '').trim())
    .filter((id) => id && id !== resolvedManufacturerId);
  const resolvedAdditional = [...new Set(additional)];

  const cleanArr = (a?: string[]): string[] =>
    (a ?? []).map((s) => (s || '').trim()).filter(Boolean);

  const num = (n?: number): number | null =>
    typeof n === 'number' && Number.isFinite(n) ? n : null;

  const str = (s?: string): string | null => {
    const v = (s ?? '').trim();
    return v ? v : null;
  };

  const aka = cleanArr(input.alsoKnownAs);
  const sensors = cleanArr(input.sensors);
  const timeSystems = cleanArr(input.timeSystems);
  const alarmTypes = cleanArr(input.alarmTypes);
  const images = (input.images ?? []).filter((img) => img.storagePath || img.url);

  const payload: Record<string, unknown> = {
    name,
    manufacturerId: resolvedManufacturerId,
    additionalManufacturerIds: resolvedAdditional.length ? resolvedAdditional : null,
    alsoKnownAs: aka.length ? aka : null,
    generation: str(input.generation),
    displayType: str(input.displayType),
    lcdType: str(input.lcdType),
    battery: str(input.battery),
    batteryLifeMonths: num(input.batteryLifeMonths),
    chargeTimeHours: num(input.chargeTimeHours),
    lightType: str(input.lightType),
    displayColor: str(input.displayColor),
    textColor: str(input.textColor),
    sensors: sensors.length ? sensors : null,
    // Extended module fields (v1.26.0) — plain slugs, consumer resolves via
    // LookupService and auto-creates custom lookup values on unmatched input.
    displayLayout: str(input.displayLayout),
    viewingAngle: str(input.viewingAngle),
    dstSupport: str(input.dstSupport),
    calendarType: str(input.calendarType),
    stopwatchPrecision: str(input.stopwatchPrecision),
    powerSource: str(input.powerSource),
    syncSource: str(input.syncSource),
    timeSystems: timeSystems.length ? timeSystems : null,
    alarmTypes: alarmTypes.length ? alarmTypes : null,
    description: str(input.description),
    aiDescription: str(input.aiDescription),
    source: str(input.source),
    sourceUrl: str(input.sourceUrl),
    images: images.length ? images : null,
    updatedAt: serverTimestamp(),
  };

  const docId = input.id || buildElectronicModuleDocId(name, resolvedManufacturerId);
  const docRef = doc(db, ELECTRONIC_MODULES_COLLECTION, docId);
  await setDoc(docRef, payload, { merge: true });
  // Stamp createdAt only on first write.
  const existing = await getDoc(docRef);
  if (!existing.data()?.createdAt) {
    await updateDoc(docRef, { createdAt: serverTimestamp() });
  }
  return docId;
}

export async function deleteElectronicModule(docId: string): Promise<void> {
  await deleteDoc(doc(db, ELECTRONIC_MODULES_COLLECTION, docId));
}

/**
 * Append a legacy string as an alias on a module's `alsoKnownAs[]` (idempotent
 * via `arrayUnion`). Used by the ref editor's "Add as AKA" action so a legacy
 * `elec*` value is folded into the linked module's alias set.
 */
export async function addAliasToElectronicModule(
  moduleId: string,
  alias: string,
): Promise<void> {
  const value = alias.trim();
  if (!value) return;
  await updateDoc(doc(db, ELECTRONIC_MODULES_COLLECTION, moduleId), {
    alsoKnownAs: arrayUnion(value),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Upload a module image to Storage and return its path (stored on the doc as a
 * `CalibreImage.storagePath`, resolved to a URL at render time — mirrors the
 * calibre image flow). Deletion reuses `deleteCalibreImage` (a generic
 * `deleteObject` by path).
 */
export async function uploadElectronicModuleImage(
  moduleDocId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  const sourceName =
    fileName || (file instanceof File ? file.name : '') || 'image.jpg';
  const extension = imageFileExtension(file, sourceName);
  const timestamp = Date.now();
  const storagePath = `electronicModules/${moduleDocId}/images/${timestamp}.${extension}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, {
    contentType: imageContentType(file, extension),
  });
  return storagePath;
}

/**
 * One AI-suggested value for a single module field, returned by the
 * `enrichElectronicModule` callable. The callable reads the persisted module doc
 * (hence it needs a saved `moduleId`) and returns a per-field map — a genuinely
 * different shape from the calibre enrichment result, so the module editor has
 * its own review panel (`AiElectronicModuleEnrichPanel`) rather than reusing the
 * calibre one.
 */
export interface ModuleEnrichFieldResult {
  /** Proposed value for scalar fields (string/number). */
  value?: unknown;
  /**
   * Proposed value for multi-value fields (sensors, timeSystems, alarmTypes),
   * which the callable returns under `values` (plural) rather than `value`.
   */
  values?: unknown;
  confidence?: 'high' | 'medium' | 'low' | string;
  reasoning?: string;
}

/** Per-field result set keyed by module field name (displayType, timeSystems, …). */
export type ModuleEnrichResult = Record<string, ModuleEnrichFieldResult>;

/**
 * AI enrichment for an electronic module — wraps the deployed
 * `enrichElectronicModule` callable. Requires a saved `moduleId` (the callable
 * loads the doc server-side) and returns a per-field result map
 * (`{ displayType: { value, confidence, reasoning }, … }`) covering every
 * enrichable module field. Mirrors the fixed `enrichCalibre` per-field pattern.
 */
export async function enrichElectronicModuleWithAI(
  moduleId: string,
  context?: string,
): Promise<ModuleEnrichResult> {
  const fn = httpsCallable<
    { moduleId: string; context?: string },
    ModuleEnrichResult
  >(functions, 'enrichElectronicModule', { timeout: 120000 });
  const result = await fn({ moduleId, ...(context ? { context } : {}) });
  return result.data;
}

/** Legacy inline `electronics.*` values passed to `suggestElectronicModule` for matching. */
export interface SuggestElectronicModuleLegacyValues {
  module?: string;
  display?: string;
  battery?: string;
  lightType?: string;
  lcdType?: string;
}

/** Input for the `suggestElectronicModule` callable. */
export interface SuggestElectronicModuleInput {
  brand?: string;
  manufacturer?: string;
  productName?: string;
  legacyValues?: SuggestElectronicModuleLegacyValues;
  hint?: string;
}

/** An existing-module match proposed by `suggestElectronicModule`. */
export interface SuggestElectronicModuleMatch {
  type: 'existing';
  moduleId: string;
  moduleName: string;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
}

/** A new-module suggestion proposed by `suggestElectronicModule`. */
export interface SuggestElectronicModuleSuggestion {
  type: 'new';
  name: string;
  manufacturerId?: string;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
}

/**
 * Result of `suggestElectronicModule`. Either `match` (an existing module to
 * link) or `suggestion` (a new module to create) may be present; both absent
 * (`{}`) means the model couldn't propose anything.
 */
export interface SuggestElectronicModuleResult {
  match?: SuggestElectronicModuleMatch;
  suggestion?: SuggestElectronicModuleSuggestion;
}

/**
 * AI lookup for an electronic module — wraps the `suggestElectronicModule`
 * callable. Given brand/manufacturer/product context plus any legacy inline
 * `electronics.*` values (or a free-text `hint`), the model either matches an
 * existing module (`match`) or proposes a new one to create (`suggestion`).
 * Unlike `enrichElectronicModule`, this needs no saved `moduleId` — it operates
 * purely on the supplied context. Auth is enforced server-side (`assertAdmin`).
 */
export async function suggestElectronicModule(
  input: SuggestElectronicModuleInput,
): Promise<SuggestElectronicModuleResult> {
  const fn = httpsCallable<
    SuggestElectronicModuleInput,
    SuggestElectronicModuleResult
  >(functions, 'suggestElectronicModule', { timeout: 120000 });
  const result = await fn(input);
  return result.data;
}

export interface BackfillCalibresToAlgoliaResult {
  index: string;
  scanned: number;
  indexed: number;
  fullRebuild: boolean;
}

/**
 * Maintenance (server-side): push every `custom_calibres` doc to the
 * `watchkeeper_calibres` Algolia index. Use after a field-shape change to
 * refresh existing records. Long-running (540s timeout). Calls the
 * `backfillCalibresToAlgolia` v2 callable (incremental upsert).
 */
export async function backfillCalibresToAlgolia(): Promise<BackfillCalibresToAlgoliaResult> {
  const fn = httpsCallable<unknown, BackfillCalibresToAlgoliaResult>(
    functions,
    'backfillCalibresToAlgolia',
    { timeout: 540000 },
  );
  const res = await fn({});
  return res.data;
}

// ============================================================================
// Winder catalog (entity collection) — Task #30 admin CRUD
//
// Mirrors the electronic-module service layer above: the entity is the
// shared-types `WinderEntry` written straight to the top-level `winders`
// collection, small enough to load in full from Firestore (no Algolia), with a
// slug doc id derived from make + model.
//
// One asymmetry worth knowing: `make` is itself a slug (a `lookup_winder_
// manufacturers` value), so `makeSlug` — declared in shared-types as
// `deriveSlug(make)` — is a no-op re-slug of an already-slugged value. It is
// still written, because the field is the documented query key and a
// hand-edited or legacy `make` may not be slug-clean.
//
// Users never reference these documents from their watches. They add a
// `UserWinder` instance (users/{uid}/winders/{id}) pointing here, and the watch
// references THAT. Nothing in this file writes the user-owned side.
// ============================================================================

const WINDERS_COLLECTION = 'winders';

/**
 * The editable shape an admin authors in the winder editor. `id` is the doc id
 * (present on edit, derived on create). Timestamps and `createdBy` are owned by
 * the writer below, so they're excluded here.
 */
export type WinderInput = Pick<
  WinderEntry,
  | 'applianceKind'
  | 'make'
  | 'model'
  | 'displayName'
  | 'countryOfManufacture'
  | 'slotCount'
  | 'turnsPerDay'
  | 'rotationDirection'
  | 'rotationProgramSlugs'
  | 'powerSourceSlugs'
  | 'materialSlugs'
  | 'isQuiet'
  | 'hasLock'
  | 'hasDisplayCase'
  | 'isSafe'
  | 'priceTierSlug'
  | 'description'
  | 'aliases'
  | 'sourceUrls'
  | 'aiConfidence'
  | 'isDeprecated'
  | 'imageStoragePath'
  | 'startDelayMaxHours'
  | 'safeSpecs'
  | 'displayCaseSpecs'
> & {
  id?: string;
  /**
   * The three capability checkboxes, when the caller has them.
   *
   * Optional so the non-form writers — a bulk enrichment apply, a script —
   * carry on passing the stored fields and have their capabilities derived.
   * The editor sends this because it is the one caller where the boxes ARE the
   * answer: a curator who unticks "is a winder" means it, and deriving from
   * fields the form is still holding would silently ignore them.
   */
  capabilities?: ApplianceCapabilities;
  /**
   * Which turns-per-day representation the editor is expressing. Decides which
   * of `turnsPerDay` / `turnsPerDayRange` is written and which is cleared — the
   * caller's inactive field is ignored rather than trusted, because a toggled
   * form still holds the values the admin stopped editing. Defaults to `list`,
   * so an existing caller that knows nothing about ranges keeps its behaviour.
   */
  turnsPerDayMode?: TurnsPerDayMode;
  /**
   * Looser than `WinderEntry['turnsPerDayRange']` on purpose: the editor's two
   * number inputs are independently fillable, so a half-typed range reaches
   * here as `{ min: 300, max: null }`. `resolveTurnsPerDayWrite` is what turns
   * that into a stored range or a null.
   */
  turnsPerDayRange?: { min?: number | null; max?: number | null } | null;
};


// Re-exported so winder call sites can keep importing everything they need
// from this module, the way every other entity's service surface does.
export { deriveWinderDocId };

/** All winders, model-ordered. Mirrors `getElectronicModules`. */
export async function getWinders(fromServer = false): Promise<WinderEntry[]> {
  const q = query(collection(db, WINDERS_COLLECTION), orderBy('model'));
  const snapshot = fromServer ? await getDocsFromServer(q) : await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as WinderEntry[];
}

/**
 * The winder catalog as the admin's Curated Winders tab consumes it: from
 * `watchkeeper_winders`, falling back to the Firestore collection read when
 * Algolia is unconfigured or unreachable.
 *
 * Fails SOFT, like `getManufacturerCatalogue` and unlike the calibre and brand
 * listings — an empty winders tab is a wrong answer, not a slow one.
 *
 * The fallback also carries the ROLLOUT. `syncWinderToAlgolia` and
 * `backfillWindersToAlgolia` deploy on watchlock's cadence, this tab on
 * Netlify's; between the two, and again for the seconds between a create and its
 * trigger firing, `watchkeeper_winders` is behind the collection. A zero-hit
 * response is therefore treated as "index not populated yet" and re-read from
 * Firestore, not rendered as an empty catalog. The cost of being wrong about
 * that — one collection read when the catalog is genuinely empty, on a tab whose
 * empty state is "create your first winder" — is nil.
 *
 * ⚠️ Entries are LISTING ROWS, not documents to write back — the Algolia path
 * carries only what the indexer projects, which is not every `WinderEntry`
 * field. Anything that saves a winder reads it with `getWinderById` first; see
 * the header of `winderCatalogueQuery.ts` for the data loss that rule exists to
 * prevent.
 */
export async function getWinderCatalogue(): Promise<WinderEntry[]> {
  if (isAlgoliaConfigured()) {
    try {
      const hits = await fetchWinderCatalogue();
      if (hits.length > 0) return hits;
      console.info(
        '[winders] Algolia returned no winders — falling back to Firestore (index may not be backfilled yet)',
      );
    } catch (err) {
      console.warn(
        '[winders] Algolia catalogue fetch failed, falling back to Firestore',
        err,
      );
    }
  }
  return getWinders(true);
}

/** Single winder by doc id, forced server read (freshest copy for the editor). */
export async function getWinderById(id: string): Promise<WinderEntry | null> {
  const snap = await getDocFromServer(doc(db, WINDERS_COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as WinderEntry;
}

/** Every winder under one make slug, model-ordered. */
export async function getWindersForMake(makeSlug: string): Promise<WinderEntry[]> {
  const trimmed = (makeSlug || '').trim();
  if (!trimmed) return [];
  const snapshot = await getDocs(
    query(
      collection(db, WINDERS_COLLECTION),
      where('makeSlug', '==', trimmed),
      orderBy('model'),
    ),
  );
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as WinderEntry[];
}

/**
 * Upsert a winder. Optional fields are written as `null` when cleared (rather
 * than omitted) so an edit that empties a field clears it on the doc — the same
 * contract `saveElectronicModule` uses. Returns the doc id.
 *
 * The doc id is derived from make + model on CREATE only. An edit keeps the
 * existing id even if the make or model changes, so a rename never orphans the
 * `UserWinder.winderCatalogId` FKs pointing at it.
 */
export async function saveWinder(input: WinderInput): Promise<string> {
  const make = (input.make || '').trim();
  const model = (input.model || '').trim();
  if (!make) throw new Error('Winder make is required');
  if (!model) throw new Error('Winder model is required');

  const cleanArr = (a?: string[] | null): string[] =>
    (a ?? []).map((s) => (s || '').trim()).filter(Boolean);

  const str = (s?: string | null): string | null => {
    const v = (s ?? '').trim();
    return v ? v : null;
  };

  const boolOrNull = (b?: boolean | null): boolean | null =>
    typeof b === 'boolean' ? b : null;

  const intOrNull = (n?: number | null): number | null =>
    typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : null;

  // TPD: exactly one of the list and the range is written, the other cleared.
  // See `winderTurnsPerDay.ts` for why the mode decides rather than presence.
  const { turnsPerDay, turnsPerDayRange } = resolveTurnsPerDayWrite(
    input.turnsPerDayMode ?? 'list',
    input.turnsPerDay,
    input.turnsPerDayRange,
  );

  const aliases = cleanArr(input.aliases);
  const sourceUrls = cleanArr(input.sourceUrls);
  const programs = cleanArr(input.rotationProgramSlugs);
  const powerSources = cleanArr(input.powerSourceSlugs);
  const materials = cleanArr(input.materialSlugs);

  /*
   * ONLY A WINDER WINDS, ONLY A SAFE PROTECTS, ONLY A DISPLAY CASE DISPLAYS.
   *
   * Enforced HERE rather than trusted from the form, for exactly the reason the
   * `safeSpecs` and TPD exclusions below are: the editor deliberately KEEPS the
   * hidden inputs' values, so that unticking a capability and re-ticking it does
   * not destroy what was typed. Without this, unticking "is a winder" and saving
   * would carry its turns-per-day onto a display case — and the hidden fields
   * are precisely the ones nobody would think to check.
   *
   * shared-types requires the five winding fields to be null on a `'safe'` or a
   * `'display_case'`, and the RN side nulls them at the source for the same
   * reason (watchlock #1026, #1031): a miscatalogued appliance must not be able
   * to print "Bidirectional" onto an insurance document through whichever
   * surface forgot to look.
   *
   * The capability set comes from the three checkboxes when the editor sends
   * one, and is DERIVED from the stored fields otherwise — a bulk enrichment
   * apply does not go through the form, and neither does a script.
   */
  const caps =
    input.capabilities ??
    capabilitiesOf({
      applianceKind: input.applianceKind,
      isSafe: input.isSafe,
      hasDisplayCase: input.hasDisplayCase,
      turnsPerDay: input.turnsPerDay,
      // `entryWinds` only asks whether a range is PRESENT, so the editor's
      // half-typed `{ min: 300, max: null }` is a legitimate argument here even
      // though it is not a storable range. Widened rather than validated: the
      // storable shape is `resolveTurnsPerDayWrite`'s job, not this one's.
      turnsPerDayRange: input.turnsPerDayRange as WinderEntry['turnsPerDayRange'],
      rotationDirection: input.rotationDirection,
      rotationProgramSlugs: input.rotationProgramSlugs,
      startDelayMaxHours: input.startDelayMaxHours,
    });
  const kindWrite = resolveCapabilitiesWrite(caps);
  const suppressWinding = kindWrite.suppressWinding;

  const payload: Record<string, unknown> = {
    /*
     * The PRIMARY kind — and ABSENT MEANS WINDER, which is why this writes null
     * rather than the literal 'winder'.
     *
     * shared-types is explicit that every entry catalogued before safes existed
     * is a winder, that no backfill is needed and that none should be run. The
     * editor always holds a capability set, so writing the derived value
     * straight through would stamp an explicit 'winder' onto every entry anyone
     * so much as opens and saves — inventing data the document never carried,
     * and doing it to the whole catalogue one edit at a time.
     * `buildWinderIndexRecord` declines to invent the same value for the same
     * reason.
     *
     * A safe or a display case IS written explicitly: that is the new
     * information.
     */
    applianceKind: kindWrite.applianceKind,
    make,
    makeSlug: slugifyWinderPart(make),
    model,
    displayName: str(input.displayName) ?? `${make} ${model}`,
    countryOfManufacture: str(input.countryOfManufacture),
    // Kept for a safe, where it is the watch CAPACITY rather than a count of
    // rotor slots — a figure safes publish (the Wolf Athos 20 Piece) and the
    // editor relabels rather than hides.
    slotCount: intOrNull(input.slotCount),
    turnsPerDay: suppressWinding ? null : turnsPerDay,
    turnsPerDayRange: suppressWinding ? null : turnsPerDayRange,
    rotationDirection: suppressWinding ? null : str(input.rotationDirection),
    rotationProgramSlugs: suppressWinding || !programs.length ? null : programs,
    powerSourceSlugs: powerSources.length ? powerSources : null,
    materialSlugs: materials.length ? materials : null,
    isQuiet: boolOrNull(input.isQuiet),
    hasLock: boolOrNull(input.hasLock),
    // Forced true on a display case, exactly as `isSafe` is on a safe — every
    // consumer of the display block is gated on this flag.
    hasDisplayCase: kindWrite.hasDisplayCase,
    // A plain boolean rather than the tri-state its neighbours use: `isSafe` is
    // not an AI-enriched spec, so there is no fill-empty bulk run for "unknown"
    // to protect. It follows `isDeprecated` — an untouched checkbox is `false`.
    // Forced true on a safe. shared-types requires it, and the reason is
    // practical rather than bookkeeping: every existing consumer of the security
    // block — spec resolution, this editor's conditional panel, the insurance
    // PDF — is already gated on this flag, so setting it lights all of them up
    // for safes with no change of their own.
    isSafe: kindWrite.isSafe,
    priceTierSlug: str(input.priceTierSlug),
    description: str(input.description),
    aliases: aliases.length ? aliases : null,
    sourceUrls: sourceUrls.length ? sourceUrls : null,
    isDeprecated: input.isDeprecated === true,
    // Storage PATH, not a download URL — the RN app signs it at render, the
    // same contract calibre images use. Nulled when cleared so removing the
    // image in the editor actually removes it from the document.
    imageStoragePath: str(input.imageStoragePath),
    startDelayMaxHours: suppressWinding
      ? null
      : intOrNull(input.startDelayMaxHours),
    /*
     * Security specs exist only for a winder that IS a safe.
     *
     * Enforced HERE rather than trusting the form, for the same reason the TPD
     * exclusion is: unticking "is a safe" and saving must actually clear the
     * block, and nothing downstream re-checks. `resolveSafeSpecsWrite` also
     * drops a block whose every field is null, so an opened-and-abandoned panel
     * does not leave an empty map on the document.
     */
    safeSpecs: resolveSafeSpecsWrite(kindWrite.isSafe, input.safeSpecs),
    /*
     * Display specs exist only for an appliance that IS a display case — which
     * includes a glass-fronted winder, since the gate is the FLAG and not the
     * primary kind. Same enforcement and the same two clearing cases as
     * `safeSpecs` above.
     */
    displayCaseSpecs: resolveDisplayCaseSpecsWrite(
      kindWrite.hasDisplayCase,
      input.displayCaseSpecs,
    ),
    updatedAt: serverTimestamp(),
  };

  // Only written when the caller supplies it. Every other optional field above
  // is nulled when cleared so an emptied form clears the doc, but the editor has
  // no aiConfidence input — including it unconditionally would mean every manual
  // save wiped whatever an enrichment run had recorded.
  if (input.aiConfidence !== undefined) {
    payload.aiConfidence = str(input.aiConfidence);
  }

  const docId = input.id || deriveWinderDocId(make, model);
  const docRef = doc(db, WINDERS_COLLECTION, docId);
  await setDoc(docRef, payload, { merge: true });
  // Stamp createdAt / createdBy only on first write.
  const existing = await getDoc(docRef);
  if (!existing.data()?.createdAt) {
    await updateDoc(docRef, {
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid ?? null,
    });
  }
  return docId;
}

export async function deleteWinder(docId: string): Promise<void> {
  await deleteDoc(doc(db, WINDERS_COLLECTION, docId));
}

/** Longest edge of a stored winder image. The RN hero renders ~343pt at 3x. */
const WINDER_IMAGE_MAX_WIDTH = 1600;

/**
 * Upload the curated stock image for a winder model.
 *
 * Returns the Storage path along with the dimensions of the file as picked, so
 * the editor can tell the curator what they actually chose — see
 * `winderImageSpec.ts` for what counts as on-spec.
 *
 * PATH: `admin/winders/{docId}/{timestamp}.{ext}` — see `winderImagePath.ts`,
 * which owns the rule and explains why that prefix is a cross-repo contract
 * rather than a naming preference (storage.rules, the resize extension's
 * INCLUDE_PATH_LIST, and watchlock's RESIZED_PATH_PREFIXES all agree on it).
 *
 * ⚠️ The doc id is derived from make + model, so a winder that has never been
 * saved has no id to upload against. The editor disables the field until the
 * first save rather than inventing one.
 */
export interface WinderImageUpload {
  /** Storage path of the uploaded object. */
  storagePath: string;
  /**
   * Dimensions of the file AS PICKED, before any downscale — `0` when the
   * image could not be decoded. The editor reports these, so what the curator
   * sees is what they chose rather than what we stored.
   */
  width: number;
  height: number;
  /** True when the source was wider than the cap and came back re-encoded. */
  resized: boolean;
}

export async function uploadWinderImage(
  winderDocId: string,
  file: File | Blob,
  fileName?: string,
): Promise<WinderImageUpload> {
  if (!winderDocId) throw new Error('Save the winder before adding an image');

  let upload: File | Blob = file;
  let width = 0;
  let height = 0;
  let resized = false;
  if (file instanceof File) {
    const result = await resizeIfOversize(file, WINDER_IMAGE_MAX_WIDTH);
    upload = result.file;
    width = result.width;
    height = result.height;
    resized = result.resized;
  }

  /*
   * ⚠️ The extension comes from the RETURNED file, never the source.
   *
   * `resizeIfOversize` re-encodes anything oversize to JPEG — its own docs say
   * the caller must derive the extension from what it hands back. Reading the
   * source name instead wrote JPEG bytes to a `.webp` path; harmless, since the
   * stored contentType is what every consumer honours, but wrong. Two objects
   * uploaded before this fix still carry the mismatch and are deliberately left
   * alone: re-keying them would mean a new path, a document write and a delete,
   * to change a filename nothing reads.
   */
  const uploadName =
    upload instanceof File
      ? upload.name
      : fileName || (file instanceof File ? file.name : '');
  const storagePath = buildWinderImagePath(winderDocId, uploadName, Date.now());
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, upload, {
    contentType: upload.type || 'image/jpeg',
  });

  return { storagePath, width, height, resized };
}

/**
 * Delete a winder image object. Never throws.
 *
 * Called after the document already points somewhere else, so the admin's
 * intent is satisfied; an orphaned object costs pennies, whereas surfacing a
 * failure would report an error for an action that visibly succeeded.
 */
export async function deleteWinderImage(storagePath?: string | null): Promise<void> {
  if (!storagePath) return;
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (err) {
    console.warn(`Could not delete winder image ${storagePath}:`, err);
  }
}

/**
 * One AI-suggested value for a single winder field, returned by the
 * `enrichWinder` callable. Same per-field envelope the module enrichment uses:
 * scalars arrive under `value`, multi-value fields under `values` (plural).
 */
export interface WinderEnrichFieldResult {
  /** Proposed value for scalar fields (string / number / boolean). */
  value?: unknown;
  /**
   * Proposed value for multi-value fields (turnsPerDay, rotationProgramSlugs,
   * powerSourceSlugs, materialSlugs, aliases, sourceUrls).
   */
  values?: unknown;
  confidence?: 'high' | 'medium' | 'low' | string;
  reasoning?: string;
}

/** Per-field result set keyed by winder field name (slotCount, isQuiet, …). */
export type WinderEnrichResult = Record<string, WinderEnrichFieldResult>;

/**
 * AI enrichment for a winder — wraps the deployed `enrichWinder` callable.
 *
 * Unlike `enrichElectronicModule` this does NOT require a saved doc: the
 * callable takes `make` + `model` directly, so the bulk tool can enrich a
 * selection without a per-row Firestore read, and the editor can enrich before
 * the first save. Pass `winderId` when a saved doc exists and its stored values
 * should be treated as ground truth.
 */
export async function enrichWinderWithAI(
  make: string,
  model: string,
  options?: { winderId?: string; context?: string },
): Promise<WinderEnrichResult> {
  const fn = httpsCallable<
    { make: string; model: string; winderId?: string; context?: string },
    WinderEnrichResult
  >(functions, 'enrichWinder', { timeout: 120000 });
  const result = await fn({
    make,
    model,
    ...(options?.winderId ? { winderId: options.winderId } : {}),
    ...(options?.context ? { context: options.context } : {}),
  });
  return result.data;
}

// ============================================================================
// Lookup Collections
// ============================================================================

const LOOKUP_COLLECTIONS = [
  'lookup_case_materials',
  'lookup_dial_colors',
  'lookup_hand_colors',
  'lookup_strap_materials',
  'lookup_strap_color',
  'lookup_movement_types',
  'lookup_watch_types',
  'lookup_water_resistance',
  'lookup_watch_functions',
  'lookup_crystal_materials',
  'lookup_box_papers',
  'lookup_dial_numerals',
  'lookup_clasp_materials',
  'lookup_clasp_type',
  'lookup_purchase_condition',
  'lookup_dial_materials',
  'lookup_dial_finish',
  'lookup_date_display',
  'lookup_dial_condition',
  'lookup_chronograph_type',
  'lookup_dial_hands',
  'lookup_dial_subdials',
  'lookup_bezel_material',
  'lookup_caseback_type',
  'lookup_crystal_coating',
  'lookup_case_condition',
  'lookup_bracelet_condition',
  'lookup_detailedwatch_condition',
  'lookup_beat_rate',
  'lookup_observed_issue_type',
  'lookup_case_shapes',
  'lookup_watch_occasion',
  'lookup_crown_types',
  'lookup_bezel_types',
  'lookup_winding_directions',
  'lookup_case_finishes',
  'lookup_case_coatings',
  'lookup_case_construction',
  'lookup_pusher_types',
  'lookup_engraving_wear',
  'lookup_movement_manufacturer',
  'lookup_movement_origin',
  'lookup_movement_architecture',
  'lookup_winding_architecture',
  'lookup_regulation',
  'lookup_movement_finishing',
  'lookup_escapement_type',
  'lookup_balance_type',
  'lookup_balance_spring',
  'lookup_antimagnetic_standard',
  'lookup_complications',
  'lookup_lume_presence',
  'lookup_lume_material',
  'lookup_lume_day_color',
  'lookup_lume_glow_color',
  'lookup_dial_marker_type',
  'lookup_dial_logo_type',
  'lookup_dial_text_style',
  'lookup_dial_patterns',
  'lookup_chapter_ring_type',
  'lookup_minute_track_type',
  'lookup_dial_features',
  'lookup_dial_scales',
  'lookup_date_window_frame',
  'lookup_date_wheel_color',
  'lookup_date_window_position',
  'lookup_display_layout',
  'lookup_viewing_angle',
  'lookup_time_systems',
  'lookup_dst_support',
  'lookup_calendar_type',
  'lookup_stopwatch_precision',
  'lookup_alarm_types',
  'lookup_power_source',
  'lookup_sensors',
  'lookup_sync_source',
  'lookup_module_generation',
  'lookup_lcd_type',
  'lookup_light_type',
  'lookup_display_type',
  'lookup_battery_type',
  'lookup_bracelet_construction',
  'lookup_link_type',
  'lookup_end_links',
  'lookup_integration_type',
  'lookup_adjustment_type',
  'lookup_micro_adjustment',
  'lookup_clasp_finish',
  'lookup_clasp_security',
  'lookup_clasp_extension',
  'lookup_strap_style',
  'lookup_strap_padding',
  'lookup_lining_material',
  'lookup_edge_finish',
  'lookup_stitching_color',
  'lookup_stretch_level',
  'lookup_polishing_history',
  'lookup_service_type',
  'lookup_service_provider_type',
  'lookup_service_reason',
  'lookup_service_work_performed',
  'lookup_case_polish_level',
  'lookup_service_dial_condition',
  'lookup_service_hands_condition',
  'lookup_watch_extra_category',
  'lookup_item_originality',
  'lookup_item_condition',
  'lookup_item_acquisition_type',
  'lookup_shock_protection',
  'lookup_purchase_source',
  'lookup_warranty_type',
  'lookup_verified_by',
  'lookup_auth_confidence',
  'lookup_known_replacements',
  'lookup_wrist_presence',
  'lookup_weight_feel',
  'lookup_wear_balance',
  'lookup_rarity_level',
  'lookup_strapbracelet_type',
  'lookup_watch_genders',
  'lookup_overall_condition',
  'lookup_parent_organization',
  // Storage & Watch Winders (Task #30). Registering them here is the whole of
  // what the Lookup Manager needs — it renders an editor per registered name,
  // and getLookupCollectionFull auto-seeds each doc from LOOKUP_INITIAL_VALUES
  // on first read.
  'lookup_storage_locations',
  'lookup_winder_manufacturers',
  'lookup_winder_rotation_directions',
  'lookup_winder_programs',
  'lookup_winder_power_sources',
  'lookup_winder_materials',
  'lookup_winder_price_tiers',
];

export function getLookupCollectionNames(): string[] {
  return LOOKUP_COLLECTIONS;
}

// Helper to compute searchText from reference fields
// Joins non-empty values: brand, reference, name (model), calibre, movementType, diameterMm, family (collection)
export function computeSearchText(
  brandName: string,
  reference: string,
  model: string,
  calibre?: string,
  movementType?: string,
  diameterMm?: number | string,
  collection?: string,
): string {
  const parts = [
    brandName,
    reference,
    model,
    calibre || '',
    movementType || '',
    diameterMm?.toString() || '',
    collection || '',
  ];
  return parts
    .filter((p) => p && p.trim())
    .join(' ')
    .trim();
}

// Helper to get the document ID for a lookup collection
function getLookupDocId(collectionName: string): string {
  // Special cases where the document ID doesn't match the collection name pattern
  if (collectionName === 'lookup_water_resistance') {
    return 'water_resistance_levels';
  }
  if (collectionName === 'lookup_clasp_type') {
    return 'clasp_types';
  }
  if (collectionName === 'lookup_watch_occasion') {
    return 'watch_occasions';
  }
  if (collectionName === 'lookup_detailedwatch_condition') {
    return 'detailed_watch_condition';
  }
  return collectionName.replace('lookup_', '');
}

// Helper to remove undefined values from an object (Firestore doesn't accept undefined)
function removeUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  ) as T;
}

export interface LookupCollectionDocument {
  id: string;
  name: string;
  description?: string;
  values: LookupValue[];
  version?: number;
  isActive: boolean;
  supportsSynonyms: boolean;
  supportsMeasurement: boolean;
  isMultiSelect: boolean;
  supportsGrouping: boolean;
}

/**
 * Session cache for lookup collection docs, keyed by collection name.
 *
 * Lookup docs are the most re-read documents in the admin: the ref editor alone
 * asked for `lookup_watch_types` twice and `lookup_movement_types` three times
 * on a single mount (its own prefetch, an AdaptiveLookupField, and
 * CustomCalibreModal), and every one of the ~48 lookup-backed fields on that
 * form owns its own fetch. React 18 Strict Mode doubles all of it in dev.
 *
 * The PROMISE is cached, not just the value, so a mount-time burst of concurrent
 * callers shares one `getDoc` instead of racing N of them. The cache is dropped
 * wholesale by {@link invalidateLookupCache}, which every lookup mutator below
 * already reaches via `bumpLookupsVersion()` — so an admin editing a lookup sees
 * their own edit immediately. The TTL is a backstop for the one case that has no
 * local write to hang invalidation off: a lookup edited in ANOTHER tab or by
 * another admin.
 */
const LOOKUP_CACHE_TTL_MS = 5 * 60 * 1000;
const lookupCache = new Map<
  string,
  { at: number; promise: Promise<LookupCollectionDocument | null> }
>();

/**
 * Drop cached lookup docs so the next read goes to Firestore. Called by every
 * mutator via `bumpLookupsVersion()`; exported for surfaces that write a lookup
 * some other way (or want to force a refresh).
 *
 * Clears everything rather than one key: a reseed touches several collections at
 * once, and the whole cache is a handful of small documents.
 */
export function invalidateLookupCache(): void {
  lookupCache.clear();
}

export async function getLookupCollectionFull(
  collectionName: string,
): Promise<LookupCollectionDocument | null> {
  const cached = lookupCache.get(collectionName);
  if (cached && Date.now() - cached.at < LOOKUP_CACHE_TTL_MS) {
    return cached.promise;
  }
  const promise = fetchLookupCollectionFull(collectionName);
  lookupCache.set(collectionName, { at: Date.now(), promise });
  // A rejected fetch must not be cached as the answer for the next 5 minutes.
  // (`fetchLookupCollectionFull` swallows its own errors and returns null, so
  // this is belt-and-braces against a future refactor that lets one through.)
  promise.catch(() => {
    if (lookupCache.get(collectionName)?.promise === promise) {
      lookupCache.delete(collectionName);
    }
  });
  return promise;
}

async function fetchLookupCollectionFull(
  collectionName: string,
): Promise<LookupCollectionDocument | null> {
  try {
    const docId = getLookupDocId(collectionName);
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const values = (data.values || []) as LookupValue[];
      return {
        id: data.id || docId,
        name: data.name || collectionName,
        description: data.description,
        values: values.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
        version: data.version,
        isActive: data.isActive ?? true,
        supportsSynonyms: data.supportsSynonyms ?? false,
        supportsMeasurement: data.supportsMeasurement ?? false,
        isMultiSelect: data.isMultiSelect ?? false,
        supportsGrouping: data.supportsGrouping ?? false,
      };
    }

    const seeds = LOOKUP_INITIAL_VALUES[collectionName];
    if (seeds && seeds.length > 0) {
      const seedSlug = (s: string) =>
        s
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
      const seededValues: LookupValue[] = seeds.map((s, idx) => ({
        id: `${seedSlug(s.value)}_seed`,
        value: s.value,
        displayName: s.displayName,
        isActive: true,
        /*
         * Array position, UNLESS the seed states its own.
         *
         * Position is the right default — a seed list is written in the order a
         * curator should see it. But it cannot express a collection whose LIVE
         * order has drifted from its canonical one, and one has:
         * `lookup_storage_locations` must list its values in
         * STORAGE_LOCATION_CATEGORIES order (the shared-types union is the
         * type-level source of truth, and a test pins it), while live Firestore
         * has `at_service` at 8 and `other` at 7 because `at_service` was added
         * after `other` and took the next free position.
         *
         * These seeds exist to MATCH live on a cold start, so an explicit
         * sortOrder wins. Renumbering the live collection to tidy this would
         * reorder the picker in front of users for no gain.
         */
        sortOrder: s.sortOrder ?? idx + 1,
        // Optional seed-time synonyms / properties (e.g. lookup_dial_styles seeds
        // carry detection synonyms + constituent dial-color slugs in properties).
        ...(s.synonyms && s.synonyms.length > 0 ? { synonyms: s.synonyms } : {}),
        ...(s.properties && s.properties.length > 0
          ? { properties: s.properties }
          : {}),
        ...(s.rnQualifier ? { rnQualifier: s.rnQualifier } : {}),
        ...(s.rnReverseLabel ? { rnReverseLabel: s.rnReverseLabel } : {}),
      }));
      const cfg = getLookupCollectionConfig(collectionName);
      const seededDoc = {
        id: docId,
        name: collectionName.replace('lookup_', '').replace(/_/g, ' '),
        values: seededValues,
        version: 1,
        isActive: true,
        // Phase 2 PR1: honor config-level flag overrides at first-seed time.
        // Historical defaults are preserved for any collection whose config
        // leaves the flag undefined (supportsSynonyms=true, others=false), so
        // existing seeded docs are byte-identical to what would have been
        // written before this change.
        supportsSynonyms: cfg?.supportsSynonyms ?? true,
        supportsMeasurement: cfg?.supportsMeasurement ?? false,
        isMultiSelect: cfg?.isMultiSelect ?? false,
        supportsGrouping: cfg?.supportsGrouping ?? false,
      };
      await setDoc(docRef, seededDoc);
      return seededDoc;
    }

    return null;
  } catch (error) {
    console.error('Error fetching lookup collection:', collectionName, error);
    return null;
  }
}

/**
 * Non-destructive admin action: MERGE the canonical acquisition lookup seeds
 * (`lookup_acquisition_types` + `lookup_acquisition_directness`) into whatever
 * is already in Firestore — it never deletes anything.
 *
 * Merge is keyed by the canonical `value` slug (NOT the doc id): a value the
 * admin added through the editor gets a `${value}_${Date.now()}` id, so matching
 * on the slug pairs it with its seed counterpart instead of creating a
 * duplicate. Per value:
 *  - Seed slug already present  -> UPDATE its canonical fields (displayName,
 *    rnQualifier, rnReverseLabel, synonyms, properties) + re-assign its
 *    canonical sortOrder, while preserving the existing id and any extra fields
 *    (description, etc.). Canonical fields the seed leaves blank (e.g.
 *    rnQualifier on the two "direct" directness values) keep whatever was
 *    already stored. rnReverseLabel is seeded for every acquisition TYPE, so a
 *    manual edit there is refreshed from the seed on reseed (same as
 *    displayName / a seeded rnQualifier — symmetric by design).
 *  - Seed slug missing          -> INSERT it.
 *  - Slug present but NOT seeded (admin-added, e.g. "Pays homage to") ->
 *    PRESERVED unchanged, ordered after the seeds.
 *
 * Used by the AdminConfig "Reseed Acquisition Lookups" button. This previously
 * wiped-and-rewrote each collection, which destroyed hand-added values; the
 * merge is idempotent and safe to run repeatedly.
 */
/**
 * Non-destructive MERGE of one lookup collection's canonical seeds
 * (`LOOKUP_INITIAL_VALUES[collectionName]`) into whatever is already in
 * Firestore. Seed slugs are updated-in-place (preserving id + extra fields),
 * missing seeds are inserted, and admin-added values absent from the seed are
 * preserved unchanged after the seeds. Idempotent; never deletes.
 *
 * Shared by every "Reseed / Seed <X> Lookup" admin button.
 */
export async function reseedLookupCollectionFromSeeds(
  collectionName: string,
): Promise<void> {
  const seedSlug = (s: string) =>
    s
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const seeds = LOOKUP_INITIAL_VALUES[collectionName] ?? [];
  const docId = getLookupDocId(collectionName);
  const docRef = doc(db, collectionName, docId);
  const docSnap = await getDoc(docRef);

  const existing = docSnap.exists()
    ? ((docSnap.data().values || []) as LookupValue[])
    : [];
  const existingBySlug = new Map(existing.map((v) => [v.value, v]));

  // 1. Seed values first, in canonical order. Update-in-place if the slug is
  //    already there (preserving id + extra fields), otherwise insert.
  const seededSlugs = new Set<string>();
  const merged: LookupValue[] = seeds.map((s, idx) => {
    seededSlugs.add(s.value);
    const prev = existingBySlug.get(s.value);
    return removeUndefined({
      ...(prev ?? {}),
      id: prev?.id ?? `${seedSlug(s.value)}_seed`,
      value: s.value,
      displayName: s.displayName,
      isActive: prev?.isActive ?? true,
      sortOrder: idx + 1,
      synonyms:
        s.synonyms && s.synonyms.length > 0 ? s.synonyms : prev?.synonyms,
      properties:
        s.properties && s.properties.length > 0
          ? s.properties
          : prev?.properties,
      rnQualifier: s.rnQualifier ?? prev?.rnQualifier,
      rnReverseLabel: s.rnReverseLabel ?? prev?.rnReverseLabel,
    }) as LookupValue;
  });

  // 2. Admin-added values (in Firestore but absent from the seed) preserved
  //    unchanged, appended after the seeds in their existing relative order.
  const extras = existing
    .filter((v) => !seededSlugs.has(v.value))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  extras.forEach((v, i) => {
    merged.push({ ...v, sortOrder: seeds.length + i + 1 });
  });

  if (docSnap.exists()) {
    await updateDoc(docRef, { values: merged });
  } else {
    // Same first-seed shape as getLookupCollectionFull — keep in sync so the
    // "reseed" path and the "auto-seed on first read" path write byte-identical
    // docs (see the comment on the seededDoc block up in
    // getLookupCollectionFull for the historical-default rationale).
    const cfg = getLookupCollectionConfig(collectionName);
    await setDoc(docRef, {
      id: docId,
      name: collectionName.replace('lookup_', '').replace(/_/g, ' '),
      values: merged,
      version: 1,
      isActive: true,
      supportsSynonyms: cfg?.supportsSynonyms ?? true,
      supportsMeasurement: cfg?.supportsMeasurement ?? false,
      isMultiSelect: cfg?.isMultiSelect ?? false,
      supportsGrouping: cfg?.supportsGrouping ?? false,
    });
  }
  await bumpLookupsVersion();
}

export async function reseedAcquisitionLookups(): Promise<void> {
  await reseedLookupCollectionFromSeeds('lookup_acquisition_types');
  await reseedLookupCollectionFromSeeds('lookup_acquisition_directness');
}

/**
 * Seeds/merges the `lookup_movement_decoration` collection with the curated
 * decoration vocab (perlage, Côtes de Genève, anglage, …). Non-destructive —
 * admin-added values are preserved. Backs the AdminConfig "Seed Movement
 * Decoration Lookup" button.
 */
export async function reseedMovementDecorationLookup(): Promise<void> {
  await reseedLookupCollectionFromSeeds('lookup_movement_decoration');
}

/**
 * Seeds/merges the `lookup_winding_architecture` collection with the curated
 * self-winding topology vocab (full rotor, micro-rotor, peripheral, bumper,
 * dual rotor, oscillating weight). Non-destructive — admin-added values are
 * preserved. Backs the AdminConfig "Seed Auto Winding Architecture Lookup"
 * button; the calibre editor also auto-seeds on first read, but the button
 * lets James re-seed after extending the seed list without opening an editor.
 */
export async function reseedWindingArchitectureLookup(): Promise<void> {
  await reseedLookupCollectionFromSeeds('lookup_winding_architecture');
}

/**
 * The three calibre materials/finishing lookups added with shared-types
 * v1.92.0, in the order the seeder runs them. Exported so the AdminConfig
 * section can name them without restating the list.
 */
export const CALIBRE_MATERIALS_LOOKUP_COLLECTIONS = [
  'lookup_rotor_material',
  'lookup_bridge_material',
  'lookup_plate_finishing',
] as const;

/**
 * Seeds/merges the three v1.92.0 calibre materials lookups with their starter
 * vocabularies. Non-destructive — admin-added values are preserved, so it is
 * safe to run repeatedly.
 *
 * Each collection also auto-seeds on first read, so opening the calibre editor
 * is enough to create it. This button exists for the case that ISN'T covered by
 * that: the `enrichCalibre` prompt renders these three as {{LOOKUP_*}} tokens
 * server-side, and a collection nobody has opened yet leaves its token
 * expanding to an empty slug list rather than failing loudly — the AI is then
 * asked to pick from nothing. Running this before the first enrichment (and
 * after extending a seed list) is what avoids that. Same reasoning as
 * {@link reseedStorageAndWinderLookups}.
 */
export async function reseedCalibreMaterialsLookups(): Promise<void> {
  for (const name of CALIBRE_MATERIALS_LOOKUP_COLLECTIONS) {
    await reseedLookupCollectionFromSeeds(name);
  }
}

/**
 * Seed `lookup_dial_features`, the collection behind the ref editor's Dial
 * Features picker and the `{{LOOKUP_DIAL_FEATURES}}` token in enrichReference.
 *
 * Provisioning a lookup belongs HERE, on a button reading the code-side seed
 * list, rather than in a one-off Firestore write: the seed list is reviewable,
 * the button is re-runnable, and a collection provisioned any other way exists
 * in one environment and nowhere else. Non-destructive MERGE — admin-added
 * values and any edited display name survive — so it needs no confirm modal and
 * is safe to press twice.
 */
export async function reseedDialFeaturesLookup(): Promise<void> {
  await reseedLookupCollectionFromSeeds('lookup_dial_features');
}

/**
 * The seven Task #30 lookup collections, in the order the seeder runs them.
 * Exported so the AdminConfig section can name them without restating the list.
 */
export const STORAGE_WINDER_LOOKUP_COLLECTIONS = [
  'lookup_storage_locations',
  'lookup_winder_manufacturers',
  'lookup_winder_rotation_directions',
  'lookup_winder_programs',
  'lookup_winder_power_sources',
  'lookup_winder_materials',
  'lookup_winder_price_tiers',
] as const;

/**
 * Seeds/merges every storage + winder lookup collection (Task #30).
 *
 * `lookup_storage_locations` carries the 7 StorageLocationCategory slugs the RN
 * picker writes to `Watch.storageLocation`; the six `lookup_winder_*`
 * collections back the winder catalog editor's pickers and the `enrichWinder`
 * prompt's {{LOOKUP_*}} tokens.
 *
 * Non-destructive — admin-added values are preserved, so it's safe to run
 * repeatedly. Each collection also auto-seeds on first read (opening its editor
 * or rendering a winder picker is enough); this button exists so James can
 * re-seed after the seed lists are extended, and so the six winder collections
 * exist BEFORE the first `enrichWinder` run — an unseeded collection leaves its
 * {{LOOKUP_*}} token unexpanded in the prompt rather than failing loudly.
 */
export async function reseedStorageAndWinderLookups(): Promise<void> {
  for (const name of STORAGE_WINDER_LOOKUP_COLLECTIONS) {
    await reseedLookupCollectionFromSeeds(name);
  }
}

/**
 * Signal to the Cloud Functions that a lookup collection changed.
 *
 * The AI enrichment functions render lookup slug lists into their prompts and
 * validate AI responses against those same lists, but they memoize each lookup
 * doc in per-instance memory. Since the admin writes lookups straight to
 * Firestore from the browser, a warm function instance had no way to notice — a
 * slug removed here kept being offered to (and returned by) the AI. Every
 * mutator below bumps this counter; ensureLookupCacheFresh() in
 * functions/src/aiEnrich.ts reads it once per AI call and drops its cache when
 * it moves.
 *
 * Best-effort by design: a failure here must never fail the lookup edit that
 * the admin actually asked for. If the write is rejected (e.g. the Firestore
 * rule for admin_config/lookup_state has not been deployed from the watchlock
 * repo yet), the functions fall back to their short TTL instead.
 */
async function bumpLookupsVersion(): Promise<void> {
  // Local edit — drop the client cache first, so the refresh this mutator's
  // caller triggers reads the new document rather than replaying the old one.
  // Do this before the (best-effort, possibly rejected) counter write.
  invalidateLookupCache();
  try {
    await setDoc(
      doc(db, 'admin_config', 'lookup_state'),
      { lookupsVersion: increment(1), lookupsUpdatedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (err) {
    console.warn('Failed to bump admin_config/lookup_state.lookupsVersion:', err);
  }
}

export async function updateLookupCollectionProperties(
  collectionName: string,
  properties: Partial<Omit<LookupCollectionDocument, 'id' | 'values'>>,
): Promise<void> {
  const docId = getLookupDocId(collectionName);
  const docRef = doc(db, collectionName, docId);
  await updateDoc(docRef, properties);
  await bumpLookupsVersion();
}

export async function getLookupCollection(
  collectionName: string,
): Promise<LookupValue[]> {
  const full = await getLookupCollectionFull(collectionName);
  return full?.values || [];
}

export async function addLookupValue(
  collectionName: string,
  value: Omit<LookupValue, 'id'>,
): Promise<string> {
  const docId = getLookupDocId(collectionName);
  const docRef = doc(db, collectionName, docId);
  const docSnap = await getDoc(docRef);

  // Ids follow `<slug>_<timestamp>`; buildLookupValueId slugifies the key and
  // refuses anything outside [a-z0-9_-] so a display name typed into a
  // "Value (internal key)" box can't become a doc-array id verbatim.
  const newId = buildLookupValueId(value.value);
  const newValue: LookupValue = removeUndefined({ ...value, id: newId });

  if (docSnap.exists()) {
    const data = docSnap.data();
    const values = (data.values || []) as LookupValue[];
    values.push(newValue);
    await updateDoc(docRef, { values });
  } else {
    await setDoc(docRef, {
      id: docId,
      name: collectionName.replace('lookup_', '').replace(/_/g, ' '),
      values: [newValue],
      version: 1,
      isActive: true,
    });
  }
  await bumpLookupsVersion();
  return newId;
}

export async function updateLookupValue(
  collectionName: string,
  valueId: string,
  updates: Partial<LookupValue>,
): Promise<void> {
  const docId = getLookupDocId(collectionName);
  const docRef = doc(db, collectionName, docId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Collection not found');
  }

  const data = docSnap.data();
  const values = (data.values || []) as LookupValue[];
  const index = values.findIndex((v) => v.id === valueId);

  if (index === -1) {
    throw new Error('Value not found');
  }

  // Remove undefined values from updates before merging
  const cleanUpdates = removeUndefined(updates);

  values[index] = { ...values[index], ...cleanUpdates };
  await updateDoc(docRef, { values });
  await bumpLookupsVersion();
}

// Apply many per-value patches in a SINGLE Firestore write (one read + one
// updateDoc). Used by bulk tools (e.g. AI description suggestions) so applying
// N rows triggers one re-render instead of N. Unknown ids are ignored.
export async function bulkUpdateLookupValues(
  collectionName: string,
  updates: Array<{ id: string; patch: Partial<LookupValue> }>,
): Promise<void> {
  if (updates.length === 0) return;
  const docId = getLookupDocId(collectionName);
  const docRef = doc(db, collectionName, docId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Collection not found');
  }

  const data = docSnap.data();
  const values = (data.values || []) as LookupValue[];
  const patchById = new Map(
    updates.map((u) => [u.id, removeUndefined(u.patch)]),
  );
  const merged = values.map((v) =>
    patchById.has(v.id) ? { ...v, ...patchById.get(v.id) } : v,
  );

  await updateDoc(docRef, { values: merged });
  await bumpLookupsVersion();
}

// ---------------------------------------------------------------------------
// Dial marker-type / numerals split — reference data migration
// ---------------------------------------------------------------------------
//
// The two lookup collections behind the split (`lookup_dial_marker_type` and
// `lookup_dial_numerals`) are configured BY HAND in the Lookup Manager: both
// multi-select, and the retired `mixed` value set to isActive:false. There is
// deliberately no code path that writes those flags — the Features row already
// writes isMultiSelect through updateLookupCollectionProperties, and the value
// editor already writes isActive, so a bespoke one-shot button would just be a
// second way to do the same write.
//
// The migration below still filters `mixed` out defensively: refs written
// before the value was retired can carry it whatever the lookup now says.

export interface MigrateDialChange {
  refId: string;
  brandId?: string;
  indexesBefore: unknown;
  indexesAfter: string[];
  numeralsBefore: unknown;
  numeralsAfter: string[];
  moved: string[];
  droppedMixed: boolean;
}

export interface MigrateDialIndexesNumeralsResult {
  dryRun: boolean;
  scanned: number;
  wouldUpdate: number;
  updated: number;
  mixedDroppedIndexes: number;
  mixedDroppedNumerals: number;
  movedNumeralsFromIndexes: number;
  /** Slugs both vocabularies contain — never moved, listed so they can be
   *  disambiguated by hand if one turns out to belong to a single field. */
  ambiguousSlugs: string[];
  changes: MigrateDialChange[];
  changesTruncated: boolean;
  errors: Array<{ refId: string; error: string }>;
}

/**
 * Reshape `dialAndHands.indexes` / `.numerals` to string[], move misfiled
 * numeral slugs out of `indexes`, and drop the retired `mixed`. Admin-only,
 * `dryRun` defaults TRUE, idempotent. Calls `migrateDialIndexesNumerals`.
 *
 * Configure the two lookups in the Lookup Manager FIRST: the migration decides
 * what is a numeral by reading `lookup_dial_numerals` and
 * `lookup_dial_marker_type`, so their contents are the authority for the split.
 */
export async function migrateDialIndexesNumerals(
  dryRun = true,
): Promise<MigrateDialIndexesNumeralsResult> {
  const fn = httpsCallable<
    { dryRun: boolean },
    MigrateDialIndexesNumeralsResult
  >(functions, 'migrateDialIndexesNumerals', { timeout: 540000 });
  const res = await fn({ dryRun });
  return res.data;
}

// ---------------------------------------------------------------------------
// Crown type / crystal multi-select split — reference data migration
// ---------------------------------------------------------------------------

export interface MigrateCrownCrystalChange {
  refId: string;
  brandId?: string;
  crownBefore: unknown;
  crownAfter: string[];
  /** True when every crown part resolved to a slug (vs. left as raw text). */
  crownResolved: boolean;
  crystalBefore: unknown;
  crystalAfter: string[];
  crystalResolved: boolean;
}

export interface UnresolvedCrownCrystalRef {
  refId: string;
  brandId?: string;
  /** The parts that matched nothing, verbatim, so they can be looked up. */
  parts: string[];
}

export interface MigrateCrownTypeCrystalResult {
  dryRun: boolean;
  scanned: number;
  wouldUpdate: number;
  updated: number;
  crownCanonicalized: number;
  crystalCanonicalized: number;
  unresolvedCrownRefs: UnresolvedCrownCrystalRef[];
  unresolvedCrystalRefs: UnresolvedCrownCrystalRef[];
  unresolvedTruncated: boolean;
  changes: MigrateCrownCrystalChange[];
  changesTruncated: boolean;
  errors: Array<{ refId: string; error: string }>;
}

/**
 * Reshape `crownType` / `crystal` to string[] and canonicalize each part to a
 * lookup slug. Admin-only, `dryRun` defaults TRUE, idempotent. Calls
 * `migrateCrownTypeCrystal`.
 *
 * Canonicalization is all-or-nothing per field: if any comma-split part fails
 * to resolve, the whole field is left as raw text (wrapped to an array) and the
 * ref is listed in `unresolvedCrownRefs` / `unresolvedCrystalRefs` for manual
 * triage. Add the missing value to the lookup collection and re-run — the
 * second pass resolves it.
 *
 * The two lookup collections ARE the vocabulary this reads, so set them up in
 * the Lookup Manager first.
 */
export async function migrateCrownTypeCrystal(
  dryRun = true,
): Promise<MigrateCrownTypeCrystalResult> {
  const fn = httpsCallable<
    { dryRun: boolean },
    MigrateCrownTypeCrystalResult
  >(functions, 'migrateCrownTypeCrystal', { timeout: 540000 });
  const res = await fn({ dryRun });
  return res.data;
}

// ---------------------------------------------------------------------------
// Calibre balanceType multi-select + micro-rotor cleanup — data migration
// ---------------------------------------------------------------------------
//
// Two transforms in one sweep over `custom_calibres`:
//   A. `data.balanceType` scalar -> string[] (shared-types v1.91.0). A balance
//      is several things at once — a material, a construction pattern, and
//      optionally free-sprung regulation — which one slot could never say.
//   B. `micro_rotor`, seeded onto `lookup_balance_type` by mistake, moves to
//      `data.windingArchitecture` where the same slug already lives.
//
// One pass does both so the ordering is a fact rather than an instruction: B
// always sees the array shape A just produced. Two sweeps could not manage it —
// a scalar `==` query and an `array-contains` query cannot be unioned.
//
// Retiring the `micro_rotor` VALUE on `lookup_balance_type` is deliberately not
// wired here: the Lookup Manager's value editor already writes `isActive`, so a
// button would be a second way to make the same write — the same call the dial
// marker-type split made above. The sweep strips the slug regardless of what
// the lookup says.

export interface CalibreBalanceTypeChange {
  calibreId: string;
  name?: string;
  before: unknown;
  after: string[];
  /** True when the field is being removed rather than written. */
  deleted: boolean;
  /** Set when this calibre also gained `data.windingArchitecture`. */
  windingArchitectureSet?: string;
  /** Number of tier overrides reshaped on this calibre. */
  tierOverridesWrapped: number;
}

export interface CalibreArchitectureConflict {
  calibreId: string;
  name?: string;
  /** The windingArchitecture already stored, which was NOT overwritten. */
  existing: string;
}

export interface MigrateCalibreBalanceTypeResult {
  dryRun: boolean;
  includeMicroRotor: boolean;
  scanned: number;
  wouldUpdate: number;
  updated: number;
  /** Calibres whose base balanceType was written as an array. */
  wrapped: number;
  /** Tier overrides reshaped, across all calibres. */
  tierOverridesWrapped: number;
  /** Calibres carrying `micro_rotor` on the balance list, acted on or not. */
  microRotorFound: number;
  /** Calibres where `micro_rotor` was stripped from the balance list. */
  microRotorStripped: number;
  /** Calibres that gained `data.windingArchitecture: 'micro_rotor'`. */
  microRotorMoved: number;
  /** Calibres whose balanceType was removed entirely (nothing left). */
  balanceTypeDeleted: number;
  /** micro_rotor found but a DIFFERENT architecture already set — review these. */
  windingArchitectureConflicts: CalibreArchitectureConflict[];
  conflictsTruncated: boolean;
  changes: CalibreBalanceTypeChange[];
  changesTruncated: boolean;
  errors: Array<{ calibreId: string; error: string }>;
}

/**
 * Wrap `data.balanceType` to string[] and move the mis-filed `micro_rotor` slug
 * to `data.windingArchitecture`. Admin-only, `dryRun` defaults TRUE, idempotent.
 * Calls `migrateCalibreBalanceType`.
 *
 * `includeMicroRotor: false` runs the wrap alone, leaving the slug in place —
 * the count is still reported, so a wrap-only dry run sizes the remaining work.
 *
 * `windingArchitecture` is never overwritten: a calibre already naming a
 * different architecture keeps it and is listed in
 * `windingArchitectureConflicts` for manual review, though the bogus balance
 * slug is stripped either way.
 */
export async function migrateCalibreBalanceType(
  dryRun = true,
  includeMicroRotor = true,
): Promise<MigrateCalibreBalanceTypeResult> {
  const fn = httpsCallable<
    { dryRun: boolean; includeMicroRotor: boolean },
    MigrateCalibreBalanceTypeResult
  >(functions, 'migrateCalibreBalanceType', { timeout: 540000 });
  const res = await fn({ dryRun, includeMicroRotor });
  return res.data;
}

// ----------------------------------------------------------------------------
// Phase 5d — backfill `calibreId` onto references that carry only the string
//
// Phases 5b and 5c put the FK on every NEW write. Nothing went back for the
// references written before them, so this sweep does: it resolves each ref's
// free-text `calibre` under the same Phase 5a rules the write paths use and
// links what it can.
//
// FILL-EMPTY, like every bulk path here: a reference that already carries a
// `calibreId` is counted and left alone whatever its string says, which is also
// what makes a second run write nothing. It writes `calibreId` and nothing
// else — no legacy movement fields, and deliberately no `updatedAt`, so 47,000
// documents don't jump to the top of every recently-updated ordering to record
// a migration nobody asked to see.
//
// SLICED. `maxWrites` caps the links one call decides and hands back a
// `nextCursor` to continue from; `brandId` scopes the run to one brand. Both
// exist because every reference write fires watchlock's Algolia sync trigger,
// and 47,000 of those at once is the shape that caused the 2026-08-04 read
// spike.
// ----------------------------------------------------------------------------

/** One (brand, calibre string) cell the catalogue could not resolve. */
export interface UnresolvedCalibreRow {
  brandId: string;
  brandName: string;
  calibreString: string;
  refCount: number;
  sampleRefIds: string[];
}

/**
 * A reference already linked to a DIFFERENT calibre than its string names.
 * Reported for review only — the stored FK is never overwritten.
 */
export interface CalibreLinkMismatch {
  refId: string;
  brandId: string;
  calibreString: string;
  /** The `calibreId` already stored, which was NOT overwritten. */
  existingCalibreId: string;
  /** What the free-text string resolves to now. */
  resolvedCalibreId: string;
}

export interface BackfillCalibreLinksResult {
  dryRun: boolean;
  /** Echoed, so a result read later still says what it covered. */
  brandId?: string;
  maxWrites: number;
  /** References read by this call. */
  scanned: number;
  /** Of those, ones with no `calibre` string to work from. */
  withoutCalibre: number;
  /** Already carried a `calibreId` — untouched, and why this is idempotent. */
  alreadyLinked: number;
  /** Calibre string is a known generic placeholder — never links. */
  placeholder: number;
  /** Calibre string names nothing in the catalogue. */
  unresolvable: number;
  /** Links this call would write (dry run). */
  wouldLink: number;
  /** Links this call wrote. */
  linked: number;
  /** Of the links decided, those matched by a calibre's own name. */
  linkedByCanonical: number;
  /** Of the links decided, those matched by one of its aliases. */
  linkedByAka: number;
  /** Present when the slice stopped early — pass back as `startAfterPath`. */
  nextCursor?: string;
  /** Why it stopped, when it did. */
  stoppedReason?: 'maxWrites' | 'deadline';
  unresolvedRows: UnresolvedCalibreRow[];
  unresolvedTruncated: boolean;
  mismatches: CalibreLinkMismatch[];
  mismatchesTruncated: boolean;
  errors: Array<{ refId: string; error: string }>;
}

export interface BackfillCalibreLinksOptions {
  /** Scope the sweep to one brand's references. Omit to sweep the catalogue. */
  brandId?: string;
  /** Resume from a previous call's `nextCursor`. */
  startAfterPath?: string;
  /** Links this call may decide before stopping. Server default 5,000. */
  maxWrites?: number;
}

/**
 * Link references that carry a `calibre` string but no `calibreId`. Admin-only,
 * `dryRun` defaults TRUE, fill-empty, idempotent. Calls `backfillCalibreLinks`.
 *
 * One call handles at most `maxWrites` links and then returns `nextCursor`;
 * pass it back as `startAfterPath` to run the next slice. A dry run counts
 * against the same cap, so its report previews exactly the slice a write run
 * would perform.
 */
export async function backfillCalibreLinks(
  dryRun = true,
  options: BackfillCalibreLinksOptions = {},
): Promise<BackfillCalibreLinksResult> {
  const fn = httpsCallable<
    {
      dryRun: boolean;
      brandId?: string;
      startAfterPath?: string;
      maxWrites?: number;
    },
    BackfillCalibreLinksResult
  >(functions, 'backfillCalibreLinks', { timeout: 540000 });
  const res = await fn({ dryRun, ...options });
  return res.data;
}

export async function deleteLookupValue(
  collectionName: string,
  valueId: string,
): Promise<void> {
  const docId = getLookupDocId(collectionName);
  const docRef = doc(db, collectionName, docId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Collection not found');
  }

  const data = docSnap.data();
  const values = (data.values || []) as LookupValue[];
  const filteredValues = values.filter((v) => v.id !== valueId);

  await updateDoc(docRef, { values: filteredValues });
  await bumpLookupsVersion();
}

export async function reorderLookupValues(
  collectionName: string,
  orderedIds: string[],
): Promise<void> {
  const docId = getLookupDocId(collectionName);
  const docRef = doc(db, collectionName, docId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Collection not found');
  }

  const data = docSnap.data();
  const values = (data.values || []) as LookupValue[];

  // Reorder values based on orderedIds
  const reorderedValues = orderedIds.map((id, index) => {
    const value = values.find((v) => v.id === id);
    if (!value) {
      throw new Error(`Value not found: ${id}`);
    }
    return { ...value, sortOrder: index + 1 };
  });

  await updateDoc(docRef, { values: reorderedValues });
  await bumpLookupsVersion();
}

// ============================================================================
// User Management
// ============================================================================

// Initialize Firebase Functions. Exported so callable clients that live in their
// own service module (e.g. `aiScoringPenaltyDefaults`) share this instance.
export const functions = getFunctions(app);

export async function getAllUsers(): Promise<FirestoreUser[]> {
  const snapshot = await getDocs(collection(db, USERS_COLLECTION));
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      uid: docSnap.id,
      email: data.email,
      displayName: data.displayName,
      isAdmin: data.isAdmin === true || data.IsAdmin === true,
      plan: data.plan || 'free',
      createdAt: data.createdAt,
      lastLoginAt: data.lastLoginAt,
      disabled: data.disabled ?? false,
      accountStatus: data.accountStatus,
      deletionRequestedAt: data.deletionRequestedAt,
      deletionScheduledAt: data.deletionScheduledAt,
      aiUsage: data.aiUsage,
      aiRateLimitMode: data.aiRateLimitMode,
    } as FirestoreUser;
  });
}

export async function getUserById(uid: string): Promise<FirestoreUser | null> {
  const docRef = doc(db, USERS_COLLECTION, uid);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    uid: docSnap.id,
    email: data.email,
    displayName: data.displayName,
    isAdmin: data.isAdmin === true || data.IsAdmin === true,
    plan: data.plan || 'free',
    createdAt: data.createdAt,
    lastLoginAt: data.lastLoginAt,
    disabled: data.disabled ?? false,
    accountStatus: data.accountStatus,
    deletionRequestedAt: data.deletionRequestedAt,
    deletionScheduledAt: data.deletionScheduledAt,
    aiUsage: data.aiUsage,
    aiRateLimitMode: data.aiRateLimitMode,
  } as FirestoreUser;
}

export async function updateUserFields(
  uid: string,
  updates: {
    isAdmin?: boolean;
    isContributor?: boolean;
    plan?: UserPlan;
    submissionDisabled?: boolean;
    /**
     * Pass an explicit `false` to clear it, never `undefined`.
     *
     * This function is an allow-list over `updateDoc`: an `undefined` value
     * skips its branch, the key is never written, and `updateDoc` merges — so a
     * stored `true` would survive being "turned off". `false` is what erases it,
     * exactly as `submissionDisabled` beside it.
     */
    isTestUser?: boolean;
  },
): Promise<void> {
  const docRef = doc(db, USERS_COLLECTION, uid);

  const updateData: Record<string, unknown> = {};

  if (updates.isAdmin !== undefined) {
    updateData.isAdmin = updates.isAdmin;
    // Also remove the old IsAdmin field if it exists
    updateData.IsAdmin = updates.isAdmin;
  }

  if (updates.isContributor !== undefined) {
    updateData.isContributor = updates.isContributor;
  }

  if (updates.plan !== undefined) {
    updateData.plan = updates.plan;
  }

  if (updates.submissionDisabled !== undefined) {
    updateData.submissionDisabled = updates.submissionDisabled;
  }

  if (updates.isTestUser !== undefined) {
    updateData.isTestUser = updates.isTestUser;
  }

  await updateDoc(docRef, updateData);
}

/**
 * Set (or clear) one user's daily AI cap override.
 *
 * `users/{uid}.aiUsage` is server-owned: the owner-update rule on the user
 * document rejects it, the same way it rejects `plan`. This write succeeds
 * because the rules carry a separate unconditional `allow read, write: if
 * isAdmin()` branch on `users/{userId}` — the same branch the existing tier
 * switcher in `updateUserFields` already relies on. No callable is involved.
 *
 * THE DOTTED PATH IS LOAD-BEARING. `aiUsage` also holds `day` and `count`, the
 * live usage counter, and writing `{ aiUsage: { capOverride } }` would replace
 * the whole map and zero the day's usage. watchlock's `incrementAiUsage` merges
 * `{ day, count }` for the mirror-image reason — so that an override survives
 * the counter's rollover.
 *
 * `null` clears the override. `deleteField()` rather than a stored null: the
 * backend reads both as absent, and leaving no key is what "no override" is.
 */
export async function setUserAiCapOverride(
  uid: string,
  value: number | 'unlimited' | null,
): Promise<void> {
  const docRef = doc(db, USERS_COLLECTION, uid);
  await updateDoc(docRef, {
    'aiUsage.capOverride': value === null ? deleteField() : value,
  });
}

/**
 * Set (or clear) one user's AI limiter mode override.
 *
 * A top-level field, so a plain merge is safe here — the dotted-path rule above
 * applies only to `aiUsage`, whose siblings a whole-map write would destroy.
 *
 * NOT ENFORCED YET: `checkAiCap` resolves the mode from RTDB and returns before
 * it reads the user document, so nothing on the server reads this field. PR 2.5
 * hoists that read. Writing it now is safe and inert.
 */
export async function setUserAiRateLimitMode(
  uid: string,
  value: 'credits' | 'daily' | null,
): Promise<void> {
  const docRef = doc(db, USERS_COLLECTION, uid);
  await updateDoc(docRef, {
    aiRateLimitMode: value === null ? deleteField() : value,
  });
}

export async function setUserDisabled(
  uid: string,
  disabled: boolean,
): Promise<void> {
  // The Cloud Function handles both Firebase Auth and the Firestore mirror
  // (disabled flag + accountStatus).
  const setUserDisabledFn = httpsCallable<
    { uid: string; disabled: boolean },
    { success: boolean; error?: string }
  >(functions, 'setUserDisabled');

  const result = await setUserDisabledFn({ uid, disabled });

  if (!result.data.success) {
    throw new Error(result.data.error || 'Failed to update user status');
  }
}

export async function cancelPendingDeletion(uid: string): Promise<void> {
  const fn = httpsCallable<{ uid: string }, { success: boolean; error?: string }>(
    functions,
    'cancelPendingDeletion',
  );
  const result = await fn({ uid });
  if (!result.data.success) {
    throw new Error(result.data.error || 'Failed to cancel pending deletion');
  }
}

/**
 * Admin-only: start the 30-day deletion clock for a user (watchlock PR #761).
 * Sets accountStatus:'pendingDeletion' + the deletion timestamps server-side and
 * writes an audit entry. Super-admin gated server-side. The callable throws an
 * HttpsError on failure; a non-throwing call is treated as success (the result
 * body shape may evolve, so we don't gate on it).
 */
export async function closeAccountOnBehalf(targetUid: string): Promise<void> {
  const fn = httpsCallable<{ targetUid: string }, unknown>(
    functions,
    'closeAccountOnBehalf',
  );
  await fn({ targetUid });
}

/**
 * Admin-only: immediately hard-delete a user and all their data — watches,
 * subcollections, storage, and the user doc (watchlock PR #761). IRREVERSIBLE.
 * Writes an audit entry with the optional `reason`. Super-admin gated
 * server-side. The callable throws an HttpsError on failure; a non-throwing call
 * is treated as success.
 */
export async function purgeAccountNow(
  targetUid: string,
  reason?: string,
): Promise<void> {
  const fn = httpsCallable<{ targetUid: string; reason?: string }, unknown>(
    functions,
    'purgeAccountNow',
  );
  await fn(reason ? { targetUid, reason } : { targetUid });
}

/**
 * Admin-only: remove a purged user's tombstone (watchlock PR #763). A purge
 * leaves the `users` doc at accountStatus:'deleted' as a permanent email
 * blocklist — the `blockDeletedAccountSignup` blocking function rejects any
 * signup whose email matches a tombstoned doc. Deleting that doc frees the email
 * for re-signup. Server-side the callable is super-admin gated and hard-guards
 * accountStatus === 'deleted', so it can never delete a live account. It writes
 * an audit entry and throws an HttpsError on failure; a non-throwing call is
 * treated as success.
 */
export async function deleteUserTombstone(targetUid: string): Promise<void> {
  const fn = httpsCallable<{ targetUid: string }, unknown>(
    functions,
    'deleteUserTombstone',
  );
  await fn({ targetUid });
}

/** Outcome of one step of the E2E signup smoke test. */
export interface E2ETestStep {
  name: string;
  ok: boolean;
  durationMs: number;
  /** Present only when `ok` is false. */
  error?: string;
}

export interface E2ETestResult {
  passed: boolean;
  syntheticEmail: string;
  totalMs: number;
  steps: E2ETestStep[];
  /** Name of the first failing step; absent on a clean run. */
  failedStep?: string;
}

/** One recorded run from `admin_e2e_runs`, as rendered in the history table. */
export interface E2ERunRecord {
  id: string;
  triggeredBy: string;
  triggeredByEmail: string | null;
  at: Date | null;
  syntheticEmail: string;
  passed: boolean;
  totalMs: number;
  steps: E2ETestStep[];
  failedStep: string | null;
}

/**
 * Admin-only: run the end-to-end account-lifecycle smoke test (watchlock PR
 * #786). Creates a throwaway `james+e2e-…@jwsadler.org` account, drives it
 * through signup → purge → tombstone removal, and asserts each stage — most
 * importantly that a purged email is blocked from signing up again, the
 * regression that took production down on 2026-07-24.
 *
 * A FAILED test still resolves successfully with `passed: false` and per-step
 * detail; only a genuine fault (caller isn't a super admin, WEB_API_KEY unset)
 * rejects. So callers should branch on `passed`, not on catch.
 *
 * Takes ~12–25s. Each run sends a real 2FA email to james@jwsadler.org and
 * queues the standard purge notice to support@watchkeeper.tech.
 */
export async function runE2ESignupTest(): Promise<E2ETestResult> {
  const fn = httpsCallable<Record<string, never>, E2ETestResult>(
    functions,
    'runE2ESignupTest',
    { timeout: 300000 },
  );
  const res = await fn({});
  return res.data;
}

/**
 * Read the most recent E2E smoke-test runs for the history table. Rows are
 * written server-side by the callable (Admin SDK) and are read-only to clients,
 * so a failed run can't be quietly erased.
 */
export async function getRecentE2ERuns(max = 20): Promise<E2ERunRecord[]> {
  const snap = await getDocs(
    query(collection(db, 'admin_e2e_runs'), orderBy('at', 'desc'), limit(max)),
  );
  return snap.docs.map((docSnap) => {
    const data = docSnap.data();
    const rawAt = data.at;
    return {
      id: docSnap.id,
      triggeredBy: typeof data.triggeredBy === 'string' ? data.triggeredBy : '',
      triggeredByEmail:
        typeof data.triggeredByEmail === 'string' ? data.triggeredByEmail : null,
      // `at` is a serverTimestamp, so it reads back null on a doc that hasn't
      // round-tripped yet.
      at: rawAt && typeof rawAt.toDate === 'function' ? rawAt.toDate() : null,
      syntheticEmail:
        typeof data.syntheticEmail === 'string' ? data.syntheticEmail : '',
      passed: data.passed === true,
      totalMs: typeof data.totalMs === 'number' ? data.totalMs : 0,
      steps: Array.isArray(data.steps) ? (data.steps as E2ETestStep[]) : [],
      failedStep: typeof data.failedStep === 'string' ? data.failedStep : null,
    };
  });
}

export interface BackfillSubscriptionHistoryResult {
  scanned: number;
  updated: number;
  wouldUpdate: number;
  skippedAlreadySet: number;
  dryRun: boolean;
}

/**
 * One-off, super-admin-gated migration (watchlock PR #783) that seeds
 * `hasEverBeenPaid` on existing user docs from RevenueCat subscription history,
 * so the Curator trial-eligibility gate doesn't offer a trial to users who
 * already pay (or have paid). Idempotent: only ever sets the flag TRUE, never
 * false, and skips docs where it's already set — safe to re-run.
 *
 * `dryRun` (default TRUE here for a safe preview) scans and counts what WOULD be
 * written without committing; on a dry run `updated` is 0 and `wouldUpdate` holds
 * the count. `limit` optionally caps the number of users scanned.
 */
export async function backfillSubscriptionHistory(
  opts: { dryRun?: boolean; limit?: number } = {},
): Promise<BackfillSubscriptionHistoryResult> {
  const fn = httpsCallable<
    { dryRun: boolean; limit?: number },
    BackfillSubscriptionHistoryResult
  >(functions, 'backfillSubscriptionHistory', { timeout: 540000 });
  const res = await fn({ dryRun: opts.dryRun !== false, limit: opts.limit });
  return res.data;
}

export interface PurgeExtractionCacheResult {
  dryRun: boolean;
  scanned: number;
  deleted: number;
  hasMore: boolean;
  samples: string[];
}

/**
 * Delete docs from the `extraction_cache` collection.
 *
 * Needed because `urlToCacheKey` used to truncate its base64 to 128 chars —
 * covering only the first 96 BYTES of `${method}::${url}` — so long URLs sharing
 * a prefix collided on one doc and served each other's product (hit on Omega
 * product URLs, where the SKU sits at the very end). The key is now a full
 * sha256, which makes every pre-existing doc both unreachable and possibly
 * wrong; without a purge they linger until the 30-day TTL.
 *
 * `dryRun` (default TRUE) counts and samples without deleting. A large
 * collection drains over several calls — re-run while `hasMore` is true.
 */
export async function purgeExtractionCache(
  opts: { dryRun?: boolean } = {},
): Promise<PurgeExtractionCacheResult> {
  const fn = httpsCallable<{ dryRun: boolean }, PurgeExtractionCacheResult>(
    functions,
    'purgeExtractionCache',
    { timeout: 540000 },
  );
  const res = await fn({ dryRun: opts.dryRun !== false });
  return res.data;
}

/**
 * Snapshot of recent Anthropic API spend, from the admin-gated
 * `getAnthropicCreditBalance` callable (Cost Report API).
 *
 * NOTE: the Anthropic Admin API exposes no remaining-credit-balance endpoint, so
 * `balance`/`runwayDays` are always null and `balanceAvailable` is false — the UI
 * links out to Plans & Billing for the raw balance. `usageLast30d`/`dailyBurn`
 * come from the Cost Report and are the useful signal (null only when the report
 * returned no buckets). All monetary fields are in whole `currency` units.
 */
export interface AnthropicCreditBalanceResult {
  balance: number | null;
  balanceAvailable: boolean;
  currency: string;
  usageLast30d: number | null;
  dailyBurn: number | null;
  runwayDays: number | null;
  /** Admin-set monthly spend target (whole currency units), or null if unset. */
  monthlyBudgetTargetUsd: number | null;
  /** `max(0, target - usageLast30d)`, or null when target unset / no data. */
  budgetRemainingUsd: number | null;
  /** `usageLast30d / target * 100`, or null when target unset / no data. */
  budgetUsedPercent: number | null;
  periodStart: string;
  periodEnd: string;
  endpoint: string;
  lastUpdated: string;
}

export async function getAnthropicCreditBalance(): Promise<AnthropicCreditBalanceResult> {
  const fn = httpsCallable<unknown, AnthropicCreditBalanceResult>(
    functions,
    'getAnthropicCreditBalance',
    { timeout: 60000 },
  );
  const res = await fn({});
  return res.data;
}

/** One selectable Claude model, as reported by Anthropic's `GET /v1/models`. */
export interface AnthropicModelInfo {
  /** The exact string stored at `admin_config/ai_prompts` → `prompts.{key}.model`. */
  id: string;
  displayName: string;
  /** RFC 3339 release datetime. The list arrives sorted on this, newest first. */
  createdAt: string;
  maxInputTokens: number | null;
  maxTokens: number | null;
}

export interface ListAnthropicModelsResult {
  models: AnthropicModelInfo[];
  fetchedAt: string;
}

/**
 * Live list of Claude models from the admin-gated `listAnthropicModels` callable
 * (a server-side proxy for Anthropic's Models API — the API key is a server
 * secret and never reaches the browser).
 *
 * Callers should treat a failure as non-fatal and fall back to a local list: the
 * model dropdown must stay usable when Anthropic is unreachable.
 */
export async function listAnthropicModels(): Promise<ListAnthropicModelsResult> {
  const fn = httpsCallable<unknown, ListAnthropicModelsResult>(
    functions,
    'listAnthropicModels',
    { timeout: 30000 },
  );
  const res = await fn({});
  return res.data;
}

const ANTHROPIC_CONFIG_DOC_ID = 'anthropic_config';
const ANTHROPIC_STATE_DOC_ID = 'anthropic_state';

/**
 * Persist the admin-set monthly Anthropic budget target (Option 1, proactive
 * signal). Written to `admin_config/anthropic_config`; the
 * `getAnthropicCreditBalance` callable reads it back to derive budget usage.
 * Pass null/0 to clear the target (turns the budget panel off).
 */
export async function setAnthropicMonthlyBudget(
  targetUsd: number | null,
): Promise<void> {
  await setDoc(
    doc(db, 'admin_config', ANTHROPIC_CONFIG_DOC_ID),
    {
      monthlyBudgetTargetUsd:
        typeof targetUsd === 'number' && Number.isFinite(targetUsd) && targetUsd > 0
          ? targetUsd
          : null,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid ?? null,
    },
    { merge: true },
  );
}

/**
 * Reactive credit-exhausted state (Option 2). Latched server-side by
 * `anthropicCreditState.ts` whenever an Anthropic call 400s with "credit balance
 * is too low", auto-cleared on the next success. Mapped from Firestore
 * Timestamps to JS Dates for the UI.
 */
export interface AnthropicCreditState {
  exhausted: boolean;
  since: Date | null;
  lastAttemptAt: Date | null;
  lastError: string | null;
}

function mapAnthropicCreditState(
  data: Record<string, unknown> | undefined,
): AnthropicCreditState {
  const since = data?.since;
  const lastAttemptAt = data?.lastAttemptAt;
  return {
    exhausted: data?.creditExhausted === true,
    since: since instanceof Timestamp ? since.toDate() : null,
    lastAttemptAt: lastAttemptAt instanceof Timestamp ? lastAttemptAt.toDate() : null,
    lastError: typeof data?.lastError === 'string' ? data.lastError : null,
  };
}

/** Live subscription to the Anthropic credit-exhausted banner state. */
export function subscribeToAnthropicCreditState(
  callback: (state: AnthropicCreditState) => void,
): () => void {
  return onSnapshot(
    doc(db, 'admin_config', ANTHROPIC_STATE_DOC_ID),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback({ exhausted: false, since: null, lastAttemptAt: null, lastError: null });
        return;
      }
      callback(mapAnthropicCreditState(snapshot.data()));
    },
    (err) => {
      console.error('subscribeToAnthropicCreditState failed:', err);
      callback({ exhausted: false, since: null, lastAttemptAt: null, lastError: null });
    },
  );
}

const OPENAI_CONFIG_DOC_ID = 'openai_config';

/** One UTC day of OpenAI spend. `amountUsd` is already in dollars — OpenAI's
 * Costs API returns dollars where Anthropic's cost report returns cents. */
export interface OpenAiCostDay {
  /** `YYYY-MM-DD`, UTC. */
  date: string;
  amountUsd: number;
}

/** One UTC day of AI call volume from the `ai_usage_events` audit trail. */
export interface OpenAiVolumeDay {
  date: string;
  total: number;
  byCallType: Record<string, number>;
}

/** Structured failure for one half of the panel. The other half still renders —
 * the cost series needs an OpenAI Admin key, the audit trail does not. */
export interface OpenAiUsageError {
  /** The HttpsError code this would have thrown as, e.g. 'failed-precondition'
   * when the OPENAI_ADMIN_KEY secret has not been created yet. */
  code: string;
  message: string;
}

export interface OpenAiTokenTotals {
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  modelRequests: number;
  hasData: boolean;
}

export interface OpenAiTopUser {
  userId: string;
  count: number;
  /** Email, else display name, else null when the user document is gone. */
  label: string | null;
}

export interface OpenAiCostView {
  available: boolean;
  error: OpenAiUsageError | null;
  currency: string;
  days: OpenAiCostDay[];
  /** Null when the Costs API returned no buckets — distinct from a real $0. */
  totalUsd: number | null;
  dailyBurnUsd: number | null;
  peakDayUsd: number | null;
  /** Always false: OpenAI publishes no remaining-credit-balance endpoint. */
  balanceAvailable: boolean;
  monthlyBudgetTargetUsd: number | null;
  budgetRemainingUsd: number | null;
  budgetUsedPercent: number | null;
  tokens: OpenAiTokenTotals | null;
}

export interface OpenAiVolumeView {
  available: boolean;
  error: OpenAiUsageError | null;
  days: OpenAiVolumeDay[];
  totalCalls: number;
  byCallType: Record<string, number>;
  byTier: Record<string, number>;
  topUsers: OpenAiTopUser[];
  /** Non-zero means the admin app's call-type list has drifted from watchlock's
   * and the breakdown is under-counting. */
  unknownCallTypes: number;
  distinctUsers: number;
  /** True when the audit-trail scan hit its cap — every figure is a floor. */
  truncated: boolean;
  scanned: number;
  scanCap: number;
}

/**
 * Snapshot behind the Configuration → OpenAI API panel, from the admin-gated
 * `getOpenAiUsage` callable.
 *
 * Two independent halves. `costs` is what OpenAI charged the org (Costs API +
 * completions token counters) and needs the `OPENAI_ADMIN_KEY` secret; `volume`
 * is what users actually did, read from the `ai_usage_events` audit trail, and
 * needs nothing. Either can fail on its own — check `available` before reading
 * a half, and render its `error` when it's false.
 */
export interface OpenAiUsageResult {
  window: { days: number; startDate: string; endDate: string };
  costs: OpenAiCostView;
  volume: OpenAiVolumeView;
  /** True when served from the server-side 15-minute snapshot cache. */
  cached: boolean;
  fetchedAt: string;
  endpoints: { costs: string; completions: string };
}

/**
 * Fetch the OpenAI usage snapshot. `force` bypasses the server-side cache — the
 * panel passes it for an explicit Refresh and omits it on the auto-load, so
 * re-opening the tab doesn't re-scan the audit trail.
 *
 * Long timeout: the audit-trail pass reads up to 50k documents on a busy month.
 */
export async function getOpenAiUsage(
  opts: { force?: boolean } = {},
): Promise<OpenAiUsageResult> {
  const fn = httpsCallable<{ force: boolean }, OpenAiUsageResult>(
    functions,
    'getOpenAiUsage',
    { timeout: 300000 },
  );
  const res = await fn({ force: opts.force === true });
  return res.data;
}

/**
 * Persist the admin-set monthly OpenAI budget target. Written straight to
 * `admin_config/openai_config` rather than through a callable, mirroring
 * {@link setAnthropicMonthlyBudget} — `admin_config/{configId}` already allows
 * admin writes, so a callable would add a hop and no safety. `getOpenAiUsage`
 * reads it back to derive budget usage. Pass null/0 to clear the target.
 */
export async function setOpenAiMonthlyBudget(
  targetUsd: number | null,
): Promise<void> {
  await setDoc(
    doc(db, 'admin_config', OPENAI_CONFIG_DOC_ID),
    {
      monthlyBudgetTargetUsd:
        typeof targetUsd === 'number' && Number.isFinite(targetUsd) && targetUsd > 0
          ? targetUsd
          : null,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid ?? null,
    },
    { merge: true },
  );
}

/**
 * AI credit balance for a user, as returned by the admin `getUserAiCreditStatus`
 * and `grantAiCredits` callables (watchlock PRs #773/#774). `bonusCredits` is a
 * persistent field on the user doc spent BEFORE the monthly pool, so it's already
 * folded into `remaining`. `resetsAt` arrives as an ISO string and is surfaced as
 * a Date to callers (mirroring getAiCreditStatus).
 */
export interface AiCreditStatus {
  remaining: number;
  monthlyLimit: number;
  /** Remaining one-time intro grant (0 for paid tiers). */
  introCreditsRemaining: number;
  showIntroCta: boolean;
  tier: string;
  resetsAt: Date;
  /** Remaining admin-granted bonus credits (spent before the monthly pool). */
  bonusCreditsRemaining: number;
}

/** Wire shape of AiCreditStatus — `resetsAt` is an ISO string on the wire. */
type AiCreditStatusWire = Omit<AiCreditStatus, 'resetsAt'> & { resetsAt: string };

const toAiCreditStatus = (data: AiCreditStatusWire): AiCreditStatus => ({
  ...data,
  resetsAt: new Date(data.resetsAt),
});

/**
 * Admin-only: read a user's AI credit status (watchlock PR #773). Super-admin
 * gated server-side; a 403 surfaces as an HttpsError with a permission message.
 */
export async function getUserAiCreditStatus(
  targetUid: string,
): Promise<AiCreditStatus> {
  const fn = httpsCallable<{ targetUid: string }, AiCreditStatusWire>(
    functions,
    'getUserAiCreditStatus',
  );
  const result = await fn({ targetUid });
  return toAiCreditStatus(result.data);
}

/**
 * Admin-only: grant `amount` bonus AI credits to a user (watchlock PR #773).
 * Server enforces MAX_GRANT_PER_CALL = 25 and a required non-empty `reason` that
 * is written to the permanent `admin_audit/` log. Super-admin gated server-side.
 * Returns the previous and new credit state (both `resetsAt` remapped to Date).
 */
export async function grantAiCredits(
  targetUid: string,
  amount: number,
  reason: string,
): Promise<{
  ok: boolean;
  previousState: AiCreditStatus;
  newState: AiCreditStatus;
}> {
  const fn = httpsCallable<
    { targetUid: string; amount: number; reason: string },
    {
      ok: boolean;
      previousState: AiCreditStatusWire;
      newState: AiCreditStatusWire;
    }
  >(functions, 'grantAiCredits');
  const result = await fn({ targetUid, amount, reason });
  return {
    ok: result.data.ok,
    previousState: toAiCreditStatus(result.data.previousState),
    newState: toAiCreditStatus(result.data.newState),
  };
}

// ---------------------------------------------------------------------------
// AI Sandbox (admin tool) — invoke the REAL recognize / search AI backends with
// `sandboxMode: true` so super-admins can test prompts against live plumbing
// WITHOUT spending an RN AI credit. Backend: watchlock PR #778. Consumed by
// src/components/AISandbox. Response shapes mirror the watchlock interfaces
// (not in shared-types); every optional field is guarded at the read site.
// ---------------------------------------------------------------------------

/** Per-candidate attributes the vision model extracts (all optional). */
export interface SandboxExtractedAttributes {
  brand?: string;
  model?: string;
  reference?: string;
  collection?: string;
  dialColor?: string;
  caseMaterial?: string;
  caseShape?: string;
  bezelType?: string;
  movementType?: string;
  complications?: string[];
  approxCaseSizeMm?: number;
  handStyle?: string;
  strapMaterial?: string;
  visibleText?: string[];
  overallConfidence?: number;
  // Added to the identify extraction schema by watchlock PR #808. `dialNumerals`
  // and `handsColor` stay SINGLE: PR #878 fixed a cardinality trap in the
  // extraction prompt by pinning them to one dominant reading, so the shapes
  // below are what the callable actually returns even though the catalogue
  // fields behind them are arrays.
  dialNumerals?: string;
  subdials?: number;
  dateWindow?: string;
  strapType?: string;
  bezelColor?: string;
  caseFinish?: string;
  handsColor?: string;
  crownGuards?: boolean;
  /**
   * Dial-surface finish. Extracted (and SCORED, at weight 0.06) since the
   * original identify build, but it was never mirrored here — so the sandbox's
   * extraction grid dropped it even though the extracted-vs-ref table labelled
   * it. Listed alongside the 2026-08-10 block below rather than with the fields
   * it shipped with, because that is when it was noticed.
   */
  dialFinish?: string;
  // ---- Dial detail (watchlock PR #878, 2026-08-10) --------------------------
  // The eleven `DialInfo` fields the identify prompt had never asked for. Names
  // and cardinality copied from `ExtractedAttributes` in the deployed
  // functions/src/identifyExtraction.ts — NOT from shared-types, which differs:
  // `dateWheelTextColor` is a multi-select on `DialInfo` but the identify
  // extraction asks for one dominant reading, so it arrives as a single slug.
  // Only the two genuinely multi-valued fields are arrays.
  /** WHAT each subdial does — `lookup_dial_subdials` slugs. Multi. */
  subdialTypes?: string[];
  /** Measurement scales on dial or bezel — `lookup_dial_scales` slugs. Multi. */
  dialScales?: string[];
  /** Structural/decorative dial attributes — `lookup_dial_features` slugs. Multi. */
  dialFeatures?: string[];
  /** The ring between dial and crystal — a `lookup_chapter_ring_type` slug. */
  chapterRing?: string;
  /** Style of the edge minute/second markings — a `lookup_minute_track_type` slug. */
  minuteTrack?: string;
  /** How the brand mark is applied — a `lookup_dial_logo_type` slug. */
  logoType?: string;
  /**
   * Where lume is applied — a `lookup_lume_presence` slug, one of `none` |
   * `hands_only` | `markers_only` | `hands_and_markers` | `full_dial`. Gates the
   * other three lume fields at the display layer.
   */
  lumePresence?: string;
  /** What the lume is — a `lookup_lume_material` slug. */
  lumeMaterial?: string;
  /** The lume's unlit daylight colour — a `lookup_lume_day_color` slug. */
  lumeDayColor?: string;
  /** The colour the lume glows — a `lookup_lume_glow_color` slug. */
  lumeGlowColor?: string;
  /** The date disc's own colour — a `lookup_date_wheel_color` slug. */
  dateWheelColor?: string;
  /** The colour of the numerals on the date disc — a `lookup_text_colors` slug. */
  dateWheelTextColor?: string;
}

/**
 * How one attribute slot scored for a candidate: what the vision model pulled
 * off the photo (`extracted`) against what the catalogue reference holds
 * (`candidate`), plus the scorer's verdict. Slot keys mirror
 * `SandboxExtractedAttributes` keys. `penaltyReason` is treated as an open set
 * at the read site — an unrecognised value renders neutral rather than blank.
 */
export interface SandboxScoreBreakdownSlot {
  extracted: string | number | boolean | null;
  candidate: string | number | boolean | null;
  weight: number;
  provided: boolean;
  /**
   * Whether the REFERENCE carried a value for this slot at all.
   *
   * `provided` is the conjunction of both sides having one, so a blank "On ref"
   * cell had three causes rendered identically: nothing read off the photo,
   * nothing on the reference, or an attribute the indexer has not projected.
   * Those want three different responses, and telling them apart is what sent a
   * projection audit chasing a scorer bug. Optional — absent on responses from
   * before watchlock added it, where the column falls back to its old
   * value-or-dash rendering.
   */
  candidatePresent?: boolean;
  /** 0..1 — whether the slot matched. */
  score: number;
  /** The slot's positive contribution to the blended score. */
  contribution: number;
  /** 0 or negative. */
  penalty: number;
  penaltyReason:
    | 'match'
    | 'near'
    | 'mismatch'
    | 'not-extracted'
    | 'gated-off';
}

/**
 * Per-candidate scoring trace returned when the backend runs in `sandboxMode`
 * (watchlock PR #812). Absent on older responses, so every read is guarded.
 */
export interface SandboxScoreBreakdown {
  slots: Record<string, SandboxScoreBreakdownSlot>;
  renormalized: number;
  complicationBonus: number;
  modelExactBonusApplied: boolean;
  modelExactBonus: number;
  ocrBonus: number;
  rankPrior: number;
  penaltyTotal: number;
  penaltyCapped: boolean;
  finalBlended: number;
}

/** A scored catalogue match from `recognizeWatchFromImage`. */
export interface SandboxImageMatch {
  referenceId: string;
  confidence: number;
  matchLabel: 'strong' | 'possible' | 'weak';
  extractedAttributes?: SandboxExtractedAttributes;
  scoreBreakdown?: SandboxScoreBreakdown;
  brandId?: string;
  brandName?: string;
  reference?: string;
  model?: string;
  productName?: string;
  imageUrl?: string;
  webPromoted?: boolean;
}

/** A single web page the AI cited as evidence for a `SandboxWebMatch`. */
export interface SandboxWebMatchSource {
  title?: string;
  url?: string;
}

/**
 * A reference the AI turned up via its web-search fallback — i.e. one that is
 * NOT in our catalogue, so it has no `referenceId` to link to. Every field is
 * optional: this is unvalidated backend JSON and the shape is owned by
 * watchlock, not shared-types.
 */
export interface SandboxWebMatch {
  brand?: string;
  brandId?: string;
  reference?: string;
  referenceNormalized?: string;
  modelDescription?: string;
  /**
   * A picture of what was found, from the reverse image pass. Absent on
   * text-pass findings, which have no image to offer — so its presence is also
   * the quickest read on which pass produced a row.
   */
  thumbnailUrl?: string;
  confidence?: number;
  sources?: SandboxWebMatchSource[];
  /**
   * Which web pass produced this row.
   *
   * `both` is the strongest signal the flow emits: the text pass searches for
   * the WORDS read off the photograph and the reverse image pass asks what the
   * OBJECT looks like, so agreement between them is two independent methods
   * reaching one answer.
   *
   * Optional, like everything here — responses predating the field omit it, and
   * the badge is simply not rendered.
   */
  discoveredVia?: WebMatchProvenance;
  isWebDiscovered?: boolean;
}

/** Raw response from `recognizeWatchFromImage`. */
export interface SandboxImageResult {
  matches: SandboxImageMatch[];
  webMatches?: SandboxWebMatch[];
  imagePath: string;
  noMatch: boolean;
  usedIntro: boolean;
  usedBonus: boolean;
}

/** A scored catalogue match from `searchReferencesByDescription`. */
export interface SandboxTextMatch {
  referenceId: string;
  confidence: number;
  matchLabel: 'strong' | 'possible' | 'weak';
  brand: string;
  brandId: string;
  model: string;
  refNumber: string;
  imageUrl?: string;
  reasons?: string[];
  webPromoted?: boolean;
}

/** Raw response from `searchReferencesByDescription` (HTTP endpoint). */
export interface SandboxTextResult {
  status: string;
  matches: SandboxTextMatch[];
  webMatches?: SandboxWebMatch[];
  statusNote?: string;
  noMatch?: boolean;
  usedIntro?: boolean;
  usedBonus?: boolean;
}

/**
 * Upload an image to the caller's own `users/{uid}/uploads/…` prefix — the only
 * location `recognizeWatchFromImage` will read (it validates the {uid} segment
 * equals the authenticated caller). Returns the storage path to pass to the
 * callable.
 *
 * The path is shaped to satisfy the single `storage.rules` rule matching the
 * `/uploads/` prefix: `users/{uid}/uploads/{yyyy}/{mm}/{dd}/{assetId}/original.jpg`
 * — filename must be exactly `original.jpg`, image content type, <10MB. The
 * `sandbox-` assetId prefix keeps these distinguishable from real user uploads.
 */
export async function uploadSandboxRecognitionImage(file: File): Promise<string> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not authenticated.');
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
  const now = new Date();
  const yyyy = String(now.getFullYear());
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const path = `users/${uid}/uploads/${yyyy}/${mm}/${dd}/sandbox-${id}/original.jpg`;
  await uploadBytes(ref(storage, path), file, {
    contentType: file.type || 'image/jpeg',
  });
  return path;
}

/**
 * Sandbox-invoke `recognizeWatchFromImage` (onCall) with `sandboxMode: true`.
 * Super-admin gated + credit-skipped server-side (watchlock PR #778).
 */
export async function sandboxRecognizeImage(
  imagePath: string,
  hint: string,
): Promise<SandboxImageResult> {
  const fn = httpsCallable<
    { imagePath: string; hint?: string; sandboxMode: boolean },
    SandboxImageResult
  >(functions, 'recognizeWatchFromImage');
  const trimmed = hint.trim();
  const result = await fn({
    imagePath,
    ...(trimmed ? { hint: trimmed } : {}),
    sandboxMode: true,
  });
  return result.data;
}

/**
 * Sandbox-invoke `searchReferencesByDescription` (HTTP onRequest — NOT a
 * callable) with `sandboxMode: true`. Bearer-token auth; super-admin gated +
 * credit-skipped server-side. Throws with the backend `error`/`code` on non-2xx
 * (e.g. 403 when the enrichment RTDB flag is off, 400 for a too-short query).
 */
export async function sandboxSearchByDescription(
  brand: string,
  description: string,
): Promise<SandboxTextResult> {
  const current = auth.currentUser;
  if (!current) throw new Error('Not authenticated.');
  const token = await current.getIdToken();
  const url = `https://us-central1-${projectId}.cloudfunctions.net/searchReferencesByDescription`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ brand, description, sandboxMode: true }),
  });
  const data = (await res.json().catch(() => ({}))) as
    | (SandboxTextResult & { error?: string; code?: string })
    | Record<string, never>;
  if (!res.ok) {
    const msg =
      ('error' in data && data.error) ||
      ('code' in data && data.code) ||
      `HTTP ${res.status}`;
    throw new Error(String(msg));
  }
  return data as SandboxTextResult;
}

/** Input for a persisted AI Sandbox test row. */
export interface AiSandboxLogInput {
  mode: 'image' | 'text';
  input: Record<string, unknown>;
  response: unknown;
  usedBonus?: boolean;
  usedIntro?: boolean;
  durationMs: number;
}

/** A read-back AI Sandbox test row (`at` remapped to Date, null if pending). */
export interface AiSandboxLogRecord extends AiSandboxLogInput {
  id: string;
  adminUid: string;
  adminEmail?: string;
  at: Date | null;
}

const AI_SANDBOX_LOGS_COLLECTION = 'ai_sandbox_logs';

/**
 * Persist a sandbox test to `ai_sandbox_logs` (admin-create per Firestore
 * rules — watchlock PR #778). Deep-cleans input/response through a JSON
 * round-trip so no `undefined` (which Firestore rejects) sneaks into a nested
 * field. Best-effort — callers should not let a log failure break the test.
 */
export async function logAiSandboxTest(entry: AiSandboxLogInput): Promise<void> {
  const current = auth.currentUser;
  if (!current) return;
  const clean = (v: unknown): unknown =>
    v === undefined ? null : JSON.parse(JSON.stringify(v ?? null));
  await addDoc(collection(db, AI_SANDBOX_LOGS_COLLECTION), {
    adminUid: current.uid,
    ...(current.email ? { adminEmail: current.email } : {}),
    at: serverTimestamp(),
    mode: entry.mode,
    input: clean(entry.input),
    response: clean(entry.response),
    ...(entry.usedBonus !== undefined ? { usedBonus: entry.usedBonus } : {}),
    ...(entry.usedIntro !== undefined ? { usedIntro: entry.usedIntro } : {}),
    durationMs: entry.durationMs,
  });
}

/** Fetch the most recent sandbox tests (admin-read per Firestore rules). */
export async function getRecentAiSandboxTests(
  max = 20,
): Promise<AiSandboxLogRecord[]> {
  const q = query(
    collection(db, AI_SANDBOX_LOGS_COLLECTION),
    orderBy('at', 'desc'),
    limit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data() as Record<string, unknown>;
    const at = data.at as { toDate?: () => Date } | undefined;
    return {
      id: d.id,
      adminUid: (data.adminUid as string) ?? '',
      adminEmail: data.adminEmail as string | undefined,
      at: at?.toDate ? at.toDate() : null,
      mode: (data.mode as 'image' | 'text') ?? 'image',
      input: (data.input as Record<string, unknown>) ?? {},
      response: data.response,
      usedBonus: data.usedBonus as boolean | undefined,
      usedIntro: data.usedIntro as boolean | undefined,
      durationMs: (data.durationMs as number) ?? 0,
    };
  });
}

/** Result of the createBrandUser callable (Brand Portal Phase 1.2.5 stopgap). */
export interface CreateBrandUserResult {
  uid: string;
  email: string;
  /** True when the Auth user already existed (membership added to existing user). */
  existed: boolean;
  /** Present only when a new Auth user was created. Shown once, then unrecoverable. */
  tempPassword?: string;
}

/**
 * Admin-only: create (or look up) a brand user and grant them a brand
 * membership. Stopgap that replaces the manual Firestore + Auth console
 * seeding from Phase 1.1. Super-admin gated server-side. On a brand-new user
 * the result carries a one-time `tempPassword` to share out of band.
 */
export async function createBrandUser(params: {
  email: string;
  brandId: string;
  role: BrandMembershipRole;
}): Promise<CreateBrandUserResult> {
  const fn = httpsCallable<typeof params, CreateBrandUserResult>(
    functions,
    'createBrandUser',
  );
  const result = await fn(params);
  return result.data;
}

// ============================================================================
// Brand Portal invites (Phase 1.3b)
// Callables shipped on watchlock: createBrandInvite, consumeBrandInvite,
// cancelBrandInvite (1.3a) + listBrandInvites (Option A). All authz/email gating
// lives server-side; these are thin client wrappers. The `brandInvites`
// collection is NOT client-readable (its token is a bearer credential), so the
// list is fetched via the listBrandInvites callable rather than a direct query.
// ============================================================================

export type BrandInviteStatus =
  | 'pending'
  | 'consumed'
  | 'expired'
  | 'cancelled';

/**
 * One `brandInvites/{id}` document. The doc id doubles as the cancel `inviteId`.
 * `token` is the secret used in the magic link; the backend may store it as a
 * field and/or use the doc id as the token (design doc: `brandInvites/{token}`).
 * Optional fields are tolerated so a partial backend shape never breaks the list.
 */
export interface BrandInvite {
  id: string;
  brandId: string;
  email: string;
  role: BrandMembershipRole;
  status: BrandInviteStatus;
  invitedBy?: string;
  invitedByEmail?: string;
  token?: string;
  createdAt?: Timestamp;
  expiresAt?: Timestamp;
  consumedAt?: Timestamp;
  consumedBy?: string;
}

/** Result of `createBrandInvite`. Shape is tolerant of the backend's return. */
export interface CreateBrandInviteResult {
  inviteId?: string;
  token?: string;
  /** A ready-to-send accept link, if the backend builds one. */
  link?: string;
  email?: string;
  expiresAt?: number | string;
}

/** Result of `consumeBrandInvite` — drives the post-accept redirect. */
export interface ConsumeBrandInviteResult {
  brandId: string;
  brandName?: string;
  role?: BrandMembershipRole;
}

/**
 * Super-admin: create a brand invite. On success the backend records a pending
 * `brandInvites` doc (and, depending on deployment, emails the invitee). Returns
 * whatever token/link the backend generated so the UI can surface a shareable
 * accept link.
 */
export async function createBrandInvite(params: {
  email: string;
  brandId: string;
  role: BrandMembershipRole;
}): Promise<CreateBrandInviteResult> {
  const fn = httpsCallable<typeof params, CreateBrandInviteResult>(
    functions,
    'createBrandInvite',
  );
  const result = await fn(params);
  return result.data ?? {};
}

/** Super-admin: cancel a pending invite by its document id. */
export async function cancelBrandInvite(inviteId: string): Promise<void> {
  const fn = httpsCallable<{ inviteId: string }, { success?: boolean }>(
    functions,
    'cancelBrandInvite',
  );
  await fn({ inviteId });
}

/**
 * Consume an invite token for the signed-in user (accept-invite landing page).
 * Grants the brand membership server-side. Throws a `FunctionsError` whose
 * `message` the landing page maps to friendly states (verify-email, wrong
 * account, expired/invalid).
 */
export async function consumeBrandInvite(
  token: string,
): Promise<ConsumeBrandInviteResult> {
  const fn = httpsCallable<{ token: string }, ConsumeBrandInviteResult>(
    functions,
    'consumeBrandInvite',
  );
  const result = await fn({ token });
  return result.data;
}

/** Raw invite row as returned by the `listBrandInvites` callable. */
interface ListBrandInviteRow {
  id: string;
  email: string;
  brandId: string;
  brandName?: string;
  role: BrandMembershipRole;
  status: BrandInviteStatus;
  invitedBy?: string;
  invitedByEmail?: string;
  createdBy?: string;
  // Timestamps come back as ISO strings; the token is present only for
  // still-pending invites (resolved/cancelled tokens are filtered server-side).
  token?: string;
  createdAt?: string;
  expiresAt?: string;
  consumedAt?: string;
  consumedBy?: string;
}

interface ListBrandInvitesResult {
  invites: ListBrandInviteRow[];
}

/** ISO string → Firestore `Timestamp` (so the UI's `relativeFrom` keeps working). */
function isoToTimestamp(iso?: string): Timestamp | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? undefined : Timestamp.fromDate(date);
}

/**
 * Read recent brand invites (super-admin only) via the `listBrandInvites`
 * watchlock callable. The `brandInvites` collection is deliberately NOT
 * client-readable — its `token` field is the magic-link bearer credential — so
 * the read goes through a callable that uses the Admin SDK and only returns the
 * token for still-pending invites. The caller partitions into pending vs
 * historical client-side. ISO timestamps from the callable are re-wrapped as
 * Firestore `Timestamp`s so existing relative-time rendering is unchanged.
 */
export async function getRecentBrandInvites(
  max: number = 200,
): Promise<BrandInvite[]> {
  const fn = httpsCallable<
    { limit: number; status: 'all' },
    ListBrandInvitesResult
  >(functions, 'listBrandInvites');
  const result = await fn({ limit: max, status: 'all' });
  const rows = result.data?.invites ?? [];
  return rows.map((row) => ({
    id: row.id,
    brandId: row.brandId,
    email: row.email,
    role: row.role,
    status: row.status ?? 'pending',
    invitedBy: row.invitedBy,
    invitedByEmail: row.invitedByEmail,
    token: row.token,
    createdAt: isoToTimestamp(row.createdAt),
    expiresAt: isoToTimestamp(row.expiresAt),
    consumedAt: isoToTimestamp(row.consumedAt),
    consumedBy: row.consumedBy,
  }));
}

// ============================================================================
// Brand Portal — brand owner self-edit (Phase 2.1)
// `updateBrandFromPortal` is a watchlock callable that lets a brand owner
// (brandAdmin OR brandMember) patch their own brand record. The server-side
// whitelist is the source of truth; the client list below is a UX courtesy.
// ============================================================================

/**
 * The brand fields a brand owner may edit via the portal. These map to the
 * canonical `watchBrands` schema names (the watchlock callable also accepts the
 * design brief's `foundedYear`/`alsoKnownAs` aliases, but we send the real
 * names here). The callable silently drops anything outside its whitelist.
 */
export interface BrandPortalEditableFields {
  displayName?: string;
  description?: string;
  country?: string;
  founded?: number | null;
  // Canonical schema field is `mainWebsite`; `website` is still accepted by the
  // callable as a backward-compat alias (Phase 2.2 fixed the 2.1 drift).
  website?: string;
  mainWebsite?: string;
  logo?: string;
  alternativeNames?: string[];
  showProductLinks?: boolean;
  useAuthoredDescription?: boolean;
  information?: string;
  mainAddress?: string;
  abbreviations?: string[];
  windingDirections?: string[];
  parentOrg?: string;
  logoSourceUrl?: string;
  heroImage?: string;
  heroImageSourceUrl?: string;
  useHeroImage?: boolean;
}

/** Result of `updateBrandFromPortal`. */
export interface UpdateBrandFromPortalResult {
  success: boolean;
  fieldsUpdated: string[];
}

/**
 * Brand owner: patch the brand record through the controlled callable. Only
 * whitelisted fields are written server-side; non-editable fields are dropped.
 * Throws a `FunctionsError` on permission / validation failure.
 */
export async function updateBrandFromPortal(
  brandId: string,
  updates: BrandPortalEditableFields,
): Promise<UpdateBrandFromPortalResult> {
  const fn = httpsCallable<
    { brandId: string; updates: BrandPortalEditableFields },
    UpdateBrandFromPortalResult
  >(functions, 'updateBrandFromPortal');
  const result = await fn({ brandId, updates });
  return result.data;
}

// ============================================================================
// Brand Portal — brand owner reference (watch model) self-edit (Phase 2.2)
// `updateReferenceFromPortal` is a watchlock callable that lets a brand owner
// (brandAdmin OR brandMember) patch a reference under their own brand. The
// server-side whitelist is the source of truth; the client list below is a UX
// courtesy. New image BYTES upload client-side to Cloud Storage (the Phase 2.2
// storage.rules change permits brand members); the resulting paths/URLs are
// then written to the doc via this callable.
// ============================================================================

/**
 * The reference fields a brand owner may edit via the portal. These map to the
 * canonical `watchBrands/{brandId}/references/{referenceId}` schema names. The
 * callable silently drops anything outside its whitelist (brandId, editorial/
 * curation flags, aiDescription, searchText, referenceNormalized, createdAt, …).
 * (`occasion` became brand-owner editable in Phase 2.2c.)
 *
 * NESTED OBJECTS (`case`, `production`, `strap`, `dialAndHands`, `electronics`,
 * `movement`) are WHOLE-REPLACED by Firestore `update()` — load the existing
 * object, mutate it, and send the COMPLETE object back, or unsent subkeys are
 * lost.
 */
export interface ReferencePortalEditableFields {
  productName?: string;
  model?: string;
  collection?: string;
  series?: string;
  description?: string;
  productUrl?: string;
  watchType?: string;
  // Boolean flag in the live schema (not a string label).
  specialEdition?: boolean;
  color?: string;
  dial?: string;
  glass?: string;
  crystal?: string;
  bezel?: string;
  bezelType?: string;
  crownType?: string;
  nickname?: string;
  // The displayable reference number. Editing this recomputes
  // `referenceNormalized` server-side.
  reference?: string;
  calibre?: string;
  movement?: Record<string, unknown>;
  movementType?: string;
  functions?: string[];
  features?: string[];
  links?: Array<{ label?: string; url: string }>;
  case?: Record<string, unknown>;
  production?: Record<string, unknown>;
  strap?: Record<string, unknown>;
  dialAndHands?: Record<string, unknown>;
  electronics?: Record<string, unknown>;
  images?: Array<Record<string, unknown>>;
  imageUrls?: string[];
  useAuthoredDescription?: boolean;
  // Phase 2.2c additions (brand-owner editable):
  knownDiscontinuation?: boolean;
  // Select-from-lookup (lookup_watch_occasion); persisted as a comma-joined
  // string. Was previously super-admin-only; now select-no-create.
  occasion?: string;
}

/** Result of `updateReferenceFromPortal`. */
export interface UpdateReferenceFromPortalResult {
  success: boolean;
  fieldsUpdated: string[];
}

/**
 * Brand owner: patch a reference (watch model) through the controlled callable.
 * Only whitelisted fields are written server-side; non-editable fields are
 * dropped. Throws a `FunctionsError` on permission / validation failure.
 */
export async function updateReferenceFromPortal(
  brandId: string,
  referenceId: string,
  updates: ReferencePortalEditableFields,
): Promise<UpdateReferenceFromPortalResult> {
  const fn = httpsCallable<
    {
      brandId: string;
      referenceId: string;
      updates: ReferencePortalEditableFields;
    },
    UpdateReferenceFromPortalResult
  >(functions, 'updateReferenceFromPortal');
  const result = await fn({ brandId, referenceId, updates });
  return result.data;
}

// ============================================================================
// `createReferenceFromPortal` is a watchlock callable that lets a brand owner
// (brandAdmin OR brandMember) CREATE a brand-new reference under their own
// brand. Same controlled-mutation pattern as `updateReferenceFromPortal`: it
// shares the server-side whitelist + searchText formula. Only the ref number is
// server-hard-required; everything else is optional and fillable later via the
// edit panel. The doc ID is generated server-side mirroring the admin editor
// scheme, so portal-created refs are indistinguishable from admin-created ones.
// ============================================================================

/**
 * The reference fields a brand owner may set when CREATING a reference. Same
 * whitelist as {@link ReferencePortalEditableFields}, but the displayable
 * `reference` number is REQUIRED (it seeds the doc ID + searchText). Server-
 * computed fields (brandId, brandName, referenceNormalized, searchText,
 * createdAt, createdBy, …) are NOT accepted here — they're set by the callable.
 */
export interface ReferencePortalCreatableFields
  extends Omit<ReferencePortalEditableFields, 'reference'> {
  reference: string;
}

/** Result of `createReferenceFromPortal`. */
export interface CreateReferenceFromPortalResult {
  /** The new reference's document ID — use it to transition into edit mode. */
  refId: string;
  /** The written doc (with resolved server timestamps + id) for edit-mode hand-off. */
  reference: WatchReference;
}

/**
 * Brand owner: create a new reference (watch model) through the controlled
 * callable. Only whitelisted fields are written server-side; the ref number is
 * required. Throws a `FunctionsError` — notably `already-exists` (carrying
 * `details.existingReferenceId`) when a ref with the same number already exists
 * for the brand, so the caller can offer a "go edit it instead" affordance.
 */
export async function createReferenceFromPortal(
  brandId: string,
  fields: ReferencePortalCreatableFields,
): Promise<CreateReferenceFromPortalResult> {
  const fn = httpsCallable<
    {
      brandId: string;
      reference: ReferencePortalCreatableFields;
    },
    CreateReferenceFromPortalResult
  >(functions, 'createReferenceFromPortal');
  const result = await fn({ brandId, reference: fields });
  return result.data;
}

// ============================================================================
// Entity requests — Brand Portal "Request new value" workflow (Phase 2.5).
// Brand owners request new catalogue entities (calibres, lookup values) when an
// existing value isn't available; admins resolve them. Three watchlock
// callables back this, same controlled-mutation posture as the reference
// portal callables: direct Firestore writes to `entityRequests` stay closed in
// the rules; all mutations funnel through the callables. Client reads are
// scoped (own brand + admin). Consumed by chunks ii–vi.
//
// Manufacturer requests are a deferred fast-follow — v1 is calibre + lookup.
// ============================================================================

/** Discriminator for which kind of catalogue entity is being requested. */
export type EntityRequestType = 'calibre' | 'lookup';

/** Open vs resolved lifecycle of a request. */
export type EntityRequestStatus =
  | 'pending'
  | 'needs_info'
  | 'approved'
  | 'rejected';

/** Calibre request payload — `name` required, the rest optional. */
export interface CalibreRequestPayload {
  name: string;
  manufacturer?: string;
  movementType?: string;
  notes?: string;
}

/** Generic-lookup request payload — `collection` + `value` required. */
export interface LookupRequestPayload {
  /** e.g. 'lookup_occasions'. */
  collection: string;
  /** The proposed new value. */
  value: string;
  notes?: string;
}

/** Where the request was raised from, so an admin sees the situation. */
export interface EntityRequestContext {
  /** e.g. 'movement.calibreId' or 'lookups.occasion'. */
  sourceField?: string;
  /** Set when the request came from a reference edit. */
  sourceReferenceId?: string;
}

/**
 * An entity request doc (`entityRequests/{requestId}`). Mirrors the Firestore
 * shape written by the watchlock `submitEntityRequest` / `resolveEntityRequest`
 * callables. `payload` is discriminated by `type`. Timestamps are Firestore
 * `Timestamp`s on read.
 */
export interface EntityRequest {
  id: string;
  type: EntityRequestType;
  brandId: string;
  submittedBy: string;
  status: EntityRequestStatus;
  payload: CalibreRequestPayload | LookupRequestPayload;
  context?: EntityRequestContext;
  // Resolution
  resolvedAt?: Timestamp;
  resolvedBy?: string;
  resolvedEntityId?: string;
  rejectReason?: string;
  // Audit
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** A message in a request's thread (`entityRequests/{id}/messages/{msgId}`). */
export interface EntityRequestMessage {
  id: string;
  uid: string;
  body: string;
  authorRole: 'brand' | 'admin';
  createdAt?: Timestamp;
}

/**
 * Brand owner: open a request for a new calibre/lookup value under their own
 * brand. The server validates + whitelists the payload, enforces the open-
 * request rate limit (20/brand), and stamps `status: 'pending'`. Throws a
 * `FunctionsError` — notably `resource-exhausted` when the brand has too many
 * open requests, or `invalid-argument` on a bad payload.
 */
export async function submitEntityRequest(input: {
  brandId: string;
  type: EntityRequestType;
  payload: CalibreRequestPayload | LookupRequestPayload;
  context?: EntityRequestContext;
}): Promise<{ requestId: string; request: EntityRequest }> {
  const fn = httpsCallable<typeof input, { requestId: string; request: EntityRequest }>(
    functions,
    'submitEntityRequest',
  );
  const result = await fn(input);
  return result.data;
}

/**
 * Post a message to a request's thread. Either a brand member of the request's
 * brand OR a super admin may reply (the server determines `authorRole`). A
 * brand reply to a `needs_info` request reopens it to `pending`.
 */
export async function replyToEntityRequest(input: {
  requestId: string;
  body: string;
}): Promise<{ messageId: string }> {
  const fn = httpsCallable<typeof input, { messageId: string }>(
    functions,
    'replyToEntityRequest',
  );
  const result = await fn(input);
  return result.data;
}

/**
 * Admin-only: record the outcome of a request. The admin creates the actual
 * entity in the client first (CustomCalibreModal / LookupValueEditor), then
 * calls this with the created entity's id. `approve` requires `resolvedEntityId`;
 * `reject` requires `rejectReason` (both enforced server-side). An optional
 * `message` is also posted to the thread as an admin reply.
 */
export async function resolveEntityRequest(input: {
  requestId: string;
  decision: 'approve' | 'reject' | 'needs_info';
  resolvedEntityId?: string;
  rejectReason?: string;
  message?: string;
}): Promise<{ ok: true }> {
  const fn = httpsCallable<typeof input, { ok: true }>(
    functions,
    'resolveEntityRequest',
  );
  const result = await fn(input);
  return result.data;
}

export function subscribeToUsers(
  callback: (users: FirestoreUser[]) => void,
): () => void {
  return onSnapshot(collection(db, USERS_COLLECTION), (snapshot) => {
    const users = snapshot.docs.map((docSnap) => mapFirestoreUser(docSnap.id, docSnap.data()));
    callback(users);
  });
}

// ============================================================================
// Orphan User Management
// ============================================================================

export interface OrphanUser {
  uid: string;
  email?: string;
  displayName?: string;
  createdAt?: unknown;
}

export async function getOrphanUsers(): Promise<OrphanUser[]> {
  const getOrphanUsersFn = httpsCallable<
    Record<string, never>,
    { success: boolean; orphanUsers: OrphanUser[]; count: number }
  >(functions, 'getOrphanUsers');

  const result = await getOrphanUsersFn({});

  if (!result.data.success) {
    throw new Error('Failed to get orphan users');
  }

  return result.data.orphanUsers;
}

export async function deleteOrphanUser(uid: string): Promise<void> {
  const deleteOrphanUserFn = httpsCallable<
    { uid: string },
    { success: boolean; error?: string }
  >(functions, 'deleteOrphanUser');

  const result = await deleteOrphanUserFn({ uid });

  if (!result.data.success) {
    throw new Error(result.data.error || 'Failed to delete orphan user');
  }
}

// ============================================================================
// Firebase Auth Metadata (overlay)
// ============================================================================

/** Firebase Auth metadata for a user, fetched via the getUserAuthMetadata Cloud Function. */
export interface AuthMetadata {
  /** metadata.lastSignInTime — authoritative "last login" (ISO string). */
  lastSignInTime?: string;
  /** metadata.lastRefreshTime — last token refresh / activity (ISO string). */
  lastRefreshTime?: string;
  /** metadata.creationTime — when the Auth account was created (ISO string). */
  creationTime?: string;
  /** Whether the user's email is verified. */
  emailVerified: boolean;
  /** Whether the Firebase Auth account is disabled (distinct from the app-level disable). */
  authDisabled: boolean;
  /** Sign-in provider IDs (e.g. "google.com", "password", "apple.com"), sorted + unique. */
  providerIds: string[];
}

/**
 * Fetch Firebase Auth metadata for the given UIDs via the getUserAuthMetadata
 * Cloud Function. UIDs without a matching Auth account are omitted from the
 * returned map (no error). Returns an empty map for an empty input.
 */
export async function getUserAuthMetadata(
  uids: string[],
): Promise<Record<string, AuthMetadata>> {
  if (uids.length === 0) return {};

  const fn = httpsCallable<
    { uids: string[] },
    { success: boolean; metadata: Record<string, AuthMetadata>; error?: string }
  >(functions, 'getUserAuthMetadata');

  const result = await fn({ uids });

  if (!result.data.success) {
    throw new Error(result.data.error || 'Failed to get user auth metadata');
  }

  return result.data.metadata;
}

// ============================================================================
// Admin Email
// ============================================================================

const EMAIL_SENDS_COLLECTION = 'email_sends';

/**
 * Which consent bucket a broadcast belongs to.
 *
 * `transactional` is intentionally absent. The server rejects it: it is the one
 * category no user can opt out of, so the only reason to pick it on a bulk
 * composer would be to reach people who have already said no.
 */
export type EmailCategory = 'marketing' | 'product';

/** Who an email send targets: every consenting user, or an explicit list. */
export type EmailTarget = 'all' | { userIds: string[] };

export interface SendEmailParams {
  subject: string;
  /** Plain text. Sent as-is, and the basis of the HTML part when none is given. */
  body: string;
  /** Optional hand-written HTML. Passed through untouched when present. */
  htmlBody?: string;
  target: EmailTarget;
  category: EmailCategory;
  /** Send-to-self. Bypasses the duplicate throttle and is tagged in the audit log. */
  isTest?: boolean;
}

export interface SendEmailResult {
  sendId: string;
  /** Users the target resolved to, before consent was applied. */
  resolvedUserCount: number;
  /** Messages actually queued for delivery. */
  queuedCount: number;
  /** Resolved users dropped for lack of consent (or a closed account). */
  suppressedCount: number;
  /** Resolved users with no email address on their doc. */
  noEmailCount: number;
  category: EmailCategory;
  isTest: boolean;
}

/**
 * Invoke the `sendEmailToUsers` callable (watchlock). The server resolves the
 * target, drops anyone who has not consented to `category`, and writes one
 * `emailQueue` doc per surviving recipient — the compliance footer and the
 * consent re-check happen at delivery, not here.
 */
export async function callSendEmailToUsers(
  params: SendEmailParams,
): Promise<SendEmailResult> {
  const fn = httpsCallable<SendEmailParams, SendEmailResult>(
    functions,
    'sendEmailToUsers',
  );
  const result = await fn(params);
  return result.data;
}

/** One row of the `email_sends` audit log. `sentAt` is a Firestore Timestamp. */
export interface EmailSendLog {
  id: string;
  sentAt?: Timestamp;
  sender?: string;
  subject?: string;
  body?: string;
  htmlBody?: string;
  category?: EmailCategory;
  isTest?: boolean;
  target?: 'all' | { userIds?: string[] };
  resolvedUserCount?: number;
  queuedCount?: number;
  suppressedCount?: number;
  noEmailCount?: number;
  status?: string;
}

/** Most recent broadcasts, newest first. */
export async function getEmailSendLogs(
  limitCount = 20,
): Promise<EmailSendLog[]> {
  const q = query(
    collection(db, EMAIL_SENDS_COLLECTION),
    orderBy('sentAt', 'desc'),
    limit(limitCount),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as EmailSendLog);
}

// ============================================================================
// Push Device State
// ============================================================================

/** One registered device. Carries no token value — see the callable's comment. */
export interface PushDevice {
  /** 'ios' | 'android', written by the RN client as `Platform.OS`. */
  platform?: string;
  /** ISO string. Rewritten on every app launch, so it doubles as a liveness hint. */
  updatedAt?: string;
  /** Populated once the RN change ships; absent on older clients. */
  deviceModel?: string;
  osVersion?: string;
  appVersion?: string;
}

/** Aggregate push state for one user, as returned by getUserPushDeviceState. */
export interface PushDeviceState {
  tokenCount: number;
  /** Sorted, unique platforms across this user's devices. */
  platforms: string[];
  newestUpdatedAt?: string;
  devices: PushDevice[];
}

/**
 * Fetch push-device state via the `getUserPushDeviceState` Cloud Function.
 *
 * This CANNOT be a direct Firestore query: the rule on `users/{uid}/fcmTokens`
 * (watchlock firestore.rules) is owner-only, with no admin branch, so the admin
 * client is denied outright. The callable reads them with the Admin SDK.
 *
 * A user with NO registered devices is omitted from the returned map. Absent
 * means "no live device", not "not fetched" — the callable always resolves
 * every uid it was given — so callers should treat absent as zero.
 *
 * Passing no uids fetches every user that owns at least one token.
 */
export async function getUserPushDeviceState(
  uids?: string[],
): Promise<Record<string, PushDeviceState>> {
  if (uids && uids.length === 0) return {};

  const fn = httpsCallable<
    { uids?: string[] },
    { success: boolean; state: Record<string, PushDeviceState>; error?: string }
  >(functions, 'getUserPushDeviceState');

  const result = await fn(uids ? { uids } : {});

  if (!result.data.success) {
    throw new Error(result.data.error || 'Failed to get push device state');
  }

  return result.data.state;
}

/**
 * Count a user's watches. These live in the TOP-LEVEL `watches` collection with
 * a `userId` field (see watchlock's watchStore) — NOT in `users/{uid}/watches`,
 * which holds per-watch asset metadata (photos, docs), not the watches.
 *
 * Uses a server-side aggregation (`getCountFromServer`), so it returns only a
 * number — no watch documents are read and no field values are touched. Watch
 * field values are encrypted per-user by the RN app / watchlock, but the
 * documents themselves are ordinary Firestore docs, so counting them needs no
 * decryption.
 *
 * NOTE: this is gated by the Firestore rules in the separate `watchlock` repo.
 * Admin `list` access to the top-level `watches` collection must be granted
 * there or this throws a permission-denied error — callers should degrade
 * gracefully.
 */
export async function getUserWatchCount(uid: string): Promise<number> {
  const q = query(collection(db, 'watches'), where('userId', '==', uid));
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

/**
 * Fetch watch counts for many users concurrently (bounded fan-out). Resolves to
 * a `uid -> count` map. A uid whose count query fails (e.g. rules not yet
 * granted) is omitted from the map rather than rejecting the whole batch, so the
 * UI can show "—" for just those rows. `onError` fires once per failure for
 * optional surfacing/telemetry.
 */
export async function getUserWatchCounts(
  uids: string[],
  onError?: (uid: string, error: unknown) => void,
): Promise<Record<string, number>> {
  const CONCURRENCY = 8;
  const result: Record<string, number> = {};
  let cursor = 0;

  async function worker() {
    while (cursor < uids.length) {
      const uid = uids[cursor++];
      try {
        result[uid] = await getUserWatchCount(uid);
      } catch (error) {
        onError?.(uid, error);
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, uids.length) }, worker),
  );
  return result;
}

// ============================================================================
// Generic Pages
// ============================================================================

import type { GenericPage } from '../types';

const GENERIC_PAGES_COLLECTION = 'generic_pages';

export async function getGenericPages(): Promise<GenericPage[]> {
  const snapshot = await getDocs(collection(db, GENERIC_PAGES_COLLECTION));
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as GenericPage[];
}

export async function getGenericPage(id: string): Promise<GenericPage | null> {
  const docRef = doc(db, GENERIC_PAGES_COLLECTION, id);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as GenericPage;
}

type GenericPageVideo = { url: string; title?: string };
type GenericPageWriteData = {
  title?: string;
  content?: string;
  enabled?: boolean;
  headerVideo?: GenericPageVideo | null;
  videos?: GenericPageVideo[];
};

export async function createGenericPage(
  id: string,
  data: { title: string; content: string; enabled: boolean } & Omit<GenericPageWriteData, 'title' | 'content' | 'enabled'>,
): Promise<void> {
  const { headerVideo, videos, ...rest } = data;
  const payload: Record<string, unknown> = {
    ...rest,
    lastUpdated: Timestamp.now(),
  };
  if (headerVideo) payload.headerVideo = headerVideo;
  if (videos && videos.length > 0) payload.videos = videos;
  await setDoc(doc(db, GENERIC_PAGES_COLLECTION, id), payload);
}

export async function updateGenericPage(
  id: string,
  data: GenericPageWriteData,
): Promise<void> {
  const { headerVideo, videos, ...rest } = data;
  const payload: Record<string, unknown> = {
    ...rest,
    lastUpdated: Timestamp.now(),
  };
  // Allow explicit null to delete the field
  if (headerVideo !== undefined) payload.headerVideo = headerVideo ?? deleteField();
  if (videos !== undefined) payload.videos = videos;
  await updateDoc(doc(db, GENERIC_PAGES_COLLECTION, id), payload);
}

export async function deleteGenericPage(id: string): Promise<void> {
  await deleteDoc(doc(db, GENERIC_PAGES_COLLECTION, id));
}

// ============================================================================
// Flagged Words
// ============================================================================

import type { FlaggedWords } from '../types';

const FLAGGED_WORDS_COLLECTION = 'flaggedWords';
const FLAGGED_WORDS_DOC_ID = 'config';

export async function getFlaggedWords(): Promise<FlaggedWords | null> {
  const docRef = doc(db, FLAGGED_WORDS_COLLECTION, FLAGGED_WORDS_DOC_ID);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return snapshot.data() as FlaggedWords;
}

export async function updateFlaggedWords(
  words: Record<string, string>,
): Promise<void> {
  const docRef = doc(db, FLAGGED_WORDS_COLLECTION, FLAGGED_WORDS_DOC_ID);
  const snapshot = await getDoc(docRef);
  const currentVersion = snapshot.exists() ? snapshot.data().version || 0 : 0;

  await setDoc(docRef, {
    words,
    updatedAt: Timestamp.now(),
    version: currentVersion + 1,
  });
}

export async function addFlaggedWord(
  word: string,
  replacement: string,
): Promise<void> {
  const current = await getFlaggedWords();
  const words = current?.words || {};
  words[word] = replacement;
  await updateFlaggedWords(words);
}

export async function removeFlaggedWord(word: string): Promise<void> {
  const current = await getFlaggedWords();
  if (!current?.words) return;
  const words = { ...current.words };
  delete words[word];
  await updateFlaggedWords(words);
}

// ============================================================================
// Technical Reports & Feedback Reports
// ============================================================================

import type { TechnicalReport, FeedbackReport } from '../types';

const TECHNICAL_REPORTS_COLLECTION = 'technicalReports';
const FEEDBACK_REPORTS_COLLECTION = 'feedbackReports';

export async function getTechnicalReports(): Promise<TechnicalReport[]> {
  const q = query(
    collection(db, TECHNICAL_REPORTS_COLLECTION),
    orderBy('timestamp', 'desc'),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as TechnicalReport[];
}

export async function updateTechnicalReport(
  id: string,
  updates: Partial<
    Pick<TechnicalReport, 'status' | 'jiraTicket' | 'adminNotes' | 'closedAt'>
  >,
): Promise<void> {
  const docRef = doc(db, TECHNICAL_REPORTS_COLLECTION, id);
  const cleanUpdates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      cleanUpdates[key] = value;
    }
  }
  await updateDoc(docRef, cleanUpdates);
}

export async function deleteTechnicalReport(id: string): Promise<void> {
  await deleteDoc(doc(db, TECHNICAL_REPORTS_COLLECTION, id));
}

export async function getFeedbackReports(): Promise<FeedbackReport[]> {
  const q = query(
    collection(db, FEEDBACK_REPORTS_COLLECTION),
    orderBy('timestamp', 'desc'),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as FeedbackReport[];
}

export async function updateFeedbackReport(
  id: string,
  updates: Partial<
    Pick<FeedbackReport, 'status' | 'jiraTicket' | 'adminNotes' | 'closedAt'>
  >,
): Promise<void> {
  const docRef = doc(db, FEEDBACK_REPORTS_COLLECTION, id);
  const cleanUpdates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      cleanUpdates[key] = value;
    }
  }
  await updateDoc(docRef, cleanUpdates);
}

export async function deleteFeedbackReport(id: string): Promise<void> {
  await deleteDoc(doc(db, FEEDBACK_REPORTS_COLLECTION, id));
}

export interface FeedbackScreenshotUrl {
  url: string;
  /** Epoch ms at which `url` stops working. */
  expiresAt: number;
}

/**
 * Sign a shake-report screenshot for viewing in the admin console.
 *
 * Screenshots are admin-only objects, so there is no download URL to store on
 * the document — `screenshotPath` is a bucket path and the server mints a 1h v4
 * signed URL for it on demand. The callable is `assertSuperAdmin`-gated and
 * validates the path shape, so it rejects with `permission-denied` for a
 * non-super-admin, `not-found` once an object has been deleted, and
 * `invalid-argument` for anything that is not a feedback-screenshots path.
 * Callers should surface those rather than swallowing them.
 *
 * NOTE the request field is `path`, not `screenshotPath` — the document field
 * and the callable argument are deliberately named differently upstream.
 */
export async function getFeedbackScreenshotUrl(
  path: string,
): Promise<FeedbackScreenshotUrl> {
  const fn = httpsCallable<{ path: string }, FeedbackScreenshotUrl>(
    functions,
    'getFeedbackScreenshotUrl',
  );
  const result = await fn({ path });
  return result.data;
}

// ============================================================================
// Jira Integration (via Cloud Functions)
// ============================================================================

export interface JiraConfigStatus {
  configured: boolean;
  baseUrl?: string | null;
  projectKey?: string | null;
  email?: string | null;
}

export interface JiraIssueResult {
  success: boolean;
  key: string;
  url: string;
}

export interface JiraIssueDetails {
  success: boolean;
  key: string;
  summary: string;
  status: string;
  assignee: string | null;
  priority: string | null;
  updated: string;
  url: string;
}

export interface JiraIssueType {
  id: string;
  name: string;
}

export async function getJiraConfigStatus(): Promise<JiraConfigStatus> {
  const fn = httpsCallable<unknown, JiraConfigStatus>(
    functions,
    'getJiraConfig_status',
  );
  const result = await fn({});
  return result.data;
}

export async function saveJiraConfig(config: {
  email: string;
  apiToken: string;
  baseUrl: string;
  projectKey: string;
}): Promise<void> {
  const fn = httpsCallable<typeof config, { success: boolean }>(
    functions,
    'saveJiraConfig',
  );
  await fn(config);
}

export async function createJiraIssue(params: {
  reportId: string;
  reportCollection: 'technicalReports' | 'feedbackReports';
  summary: string;
  description: string;
  issueType?: string;
}): Promise<JiraIssueResult> {
  const fn = httpsCallable<typeof params, JiraIssueResult>(
    functions,
    'createJiraIssue',
  );
  const result = await fn(params);
  return result.data;
}

export async function getJiraIssue(
  issueKey: string,
): Promise<JiraIssueDetails> {
  const fn = httpsCallable<{ issueKey: string }, JiraIssueDetails>(
    functions,
    'getJiraIssue',
  );
  const result = await fn({ issueKey });
  return result.data;
}

export async function getJiraIssueTypes(): Promise<JiraIssueType[]> {
  const fn = httpsCallable<
    unknown,
    { success: boolean; issueTypes: JiraIssueType[] }
  >(functions, 'getJiraIssueTypes');
  const result = await fn({});
  return result.data.issueTypes;
}

// ============================================================================
// Testing Tasks (Jira Integration)
// ============================================================================

export interface JiraUser {
  accountId: string;
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
}

export interface JiraPriority {
  id: string;
  name: string;
  iconUrl: string | null;
}

export interface TestTask {
  key: string;
  summary: string;
  status: string;
  description: string | null;
  assignee: string | null;
  assigneeId: string | null;
  assigneeAvatar: string | null;
  created: string;
  priority: string | null;
  priorityId: string | null;
  priorityIcon: string | null;
  labels: string[];
  url: string;
}

export async function getJiraProjectUsers(): Promise<JiraUser[]> {
  const fn = httpsCallable<unknown, { success: boolean; users: JiraUser[] }>(functions, 'getJiraProjectUsers');
  const result = await fn({});
  return result.data.users;
}

export async function getJiraPriorities(): Promise<JiraPriority[]> {
  const fn = httpsCallable<unknown, { success: boolean; priorities: JiraPriority[] }>(functions, 'getJiraPriorities');
  const result = await fn({});
  return result.data.priorities;
}

export async function createTestTask(params: {
  title: string;
  body?: string;
  issueType?: string;
  assigneeId?: string;
  priorityId?: string;
  additionalLabels?: string[];
}): Promise<{ success: boolean; key: string; url: string }> {
  const fn = httpsCallable<typeof params, { success: boolean; key: string; url: string }>(functions, 'createTestTask');
  const result = await fn(params);
  return result.data;
}

export interface TestTasksResponse {
  tasks: TestTask[];
  nextPageToken: string | null;
  hasMore: boolean;
  maxResults: number;
}

export async function getTestTasks(params?: { nextPageToken?: string; maxResults?: number; sortOrder?: string; includeClosed?: boolean }): Promise<TestTasksResponse> {
  const fn = httpsCallable<typeof params, { success: boolean; tasks: TestTask[]; nextPageToken: string | null; hasMore: boolean; maxResults: number }>(functions, 'getTestTasks');
  const result = await fn(params || {});
  return {
    tasks: result.data.tasks,
    nextPageToken: result.data.nextPageToken,
    hasMore: result.data.hasMore,
    maxResults: result.data.maxResults,
  };
}

export async function updateTestTask(params: {
  issueKey: string;
  title?: string;
  body?: string;
  assigneeId?: string;
  priorityId?: string;
}): Promise<{ success: boolean }> {
  const fn = httpsCallable<typeof params, { success: boolean }>(functions, 'updateTestTask');
  const result = await fn(params);
  return result.data;
}

export async function deleteTestTask(issueKey: string): Promise<{ success: boolean }> {
  const fn = httpsCallable<{ issueKey: string }, { success: boolean }>(functions, 'deleteTestTask');
  const result = await fn({ issueKey });
  return result.data;
}

// =============================================================================
// Caliber Corner Lookup
// =============================================================================

export interface CalibreData {
  movementType?: string;
  frequencyVph?: number;
  jewels?: number;
  powerReserveHours?: number;
  /**
   * Number of positions the calibre is regulated in (e.g. 3, 5, 6). Higher =
   * tighter regulation. Distinct from the pocket-watch `adjustments` lookup.
   * Mirrors `CalibreData.positionsAdjusted` in @watchkeeper/shared-types.
   */
  positionsAdjusted?: number;
  /** Lower bound of daily accuracy in seconds/day (signed; negative = slow, e.g. COSC -4). */
  accuracyLowerSecPerDay?: number;
  /** Upper bound of daily accuracy in seconds/day (signed; positive = fast, e.g. COSC +6). */
  accuracyUpperSecPerDay?: number;
  /**
   * Interpretation flag for the two accuracy fields. When `false`/unset
   * (default), they are SIGNED asymmetric bounds (COSC: lower=-4, upper=+6 →
   * "-4/+6 sec/day"). When `true`, they are a symmetric spread spec: `lower` =
   * best-case spread (unsigned), `upper` = worst-case spread (unsigned,
   * `lower <= upper`); collapses to "±20 sec/day" when equal, else "±10 to
   * ±12 sec/day". Mirrors `CalibreData.accuracySymmetric` in
   * @watchkeeper/shared-types (v1.33.0).
   */
  accuracySymmetric?: boolean;
  diameterMm?: number;
  heightMm?: number;
  hacking?: boolean;
  manualWinding?: boolean;
  quickSetDate?: boolean;
  windingDirection?: string;
  baseMovement?: string;
  isCosc?: boolean;
  isGlashuetteRegulation?: boolean;
  isGenevaSeal?: boolean;
  isGrandSeikoStandard?: boolean;
  isMETAS?: boolean;
  isChronometer?: boolean;
  isGMT?: boolean;
  isChronograph?: boolean;
  chronograph?: string[];
  functions?: string[];
  date?: string[];
  hands?: string[];
  additionals?: string[];
  astronomical?: string[];
  acoustic?: string[];
  // Extended fields
  liftAngleDegrees?: number;
  isInHouse?: boolean;
  /**
   * When true, this calibre is a movement-family placeholder (e.g. "Felsa
   * Bidynator", "Glashütte Spezimatic") rather than a specific calibre
   * reference. Acts as a catch-all for vague historical attributions where the
   * specific reference isn't known. Defaults to false/absent. Mirrors
   * `CalibreData.isFamily` in @watchkeeper/shared-types.
   */
  isFamily?: boolean;
  /**
   * When true, this calibre is a "catch-all" placeholder — not a real distinct
   * movement, but a bucket for refs whose calibre string is generic
   * ("Unspecified Quartz", "Automatic", etc.). Excluded from cross-manufacturer
   * derivation logic (deriveManufacturerBrands, reclassifyCoDevelopedCalibres)
   * and from the client calibre resolvers so its refs don't pollute
   * brand↔manufacturer association data. Distinct from `isFamily` (a real
   * movement family that SHOULD derive). Defaults to false/absent.
   *
   * TODO: mirror as `CalibreData.isCatchAll` in @watchkeeper/shared-types
   * (proposed v1.37.0) — kept as a local field for now for faster iteration.
   */
  isCatchAll?: boolean;
  countryOfManufacture?: string;
  antiShockSystem?: string;
  rotorType?: string;
  columnWheel?: boolean;
  serviceIntervalYears?: number;
  lignes?: string;
  regulatorSystem?: string;
  casingDiameterMm?: number;
  manualWindingDirection?: string;
  hairspring?: string;
  doubleBarrel?: boolean;
  escapement?: string;
  /**
   * Balance-wheel attributes, MULTI-select. The slugs cover three orthogonal
   * axes and a well-specified calibre carries at most one from each:
   *   - Material: `glucydur`, `brass`, `silicon`, `gyromax`, `nickel`
   *   - Construction: `variable_inertia`, `screw_balance`, `monometallic`,
   *     `bimetallic`
   *   - Regulation architecture: `free-sprung`, when the balance has no
   *     regulator index (Chronometer grade and above)
   * A Glucydur balance in a Rolex Chronometer-grade movement is therefore
   * `['glucydur', 'variable_inertia', 'free-sprung']`. Backed by the
   * `lookup_balance_type` lookup (isMultiSelect) — stores lookup slugs.
   * Mirrors `CalibreData.balanceType` in @watchkeeper/shared-types (v1.91.0).
   *
   * Was a single string before v1.91.0. Records written before the one-shot
   * `migrateCalibreBalanceType` sweep may still hold that scalar, so every read
   * goes through `parseMultiLookupValues` rather than trusting the type.
   */
  balanceType?: string[];
  /**
   * Movement construction architecture (e.g. full bridge, three-quarter plate,
   * modular, integrated). Backed by the `lookup_movement_architecture` lookup —
   * stores the lookup slug. Mirrors `CalibreData.movementArchitecture` in
   * @watchkeeper/shared-types (v1.39.0).
   */
  movementArchitecture?: string;
  /**
   * Self-winding system topology (full rotor, micro-rotor, peripheral, bumper,
   * dual rotor, generic oscillating weight). Backed by the
   * `lookup_winding_architecture` lookup — stores the lookup slug. Only
   * meaningful when the selected `movementType` carries the `isAuto` property;
   * absent on manual/quartz calibres. Distinct from `rotorType`, which carries
   * the rotor's bearing/suspension or material detail. Base-movement property —
   * deliberately NOT tier-overridable and NOT cached onto the reference.
   * Mirrors `CalibreData.windingArchitecture` in @watchkeeper/shared-types
   * (v1.45.0).
   */
  windingArchitecture?: string;
  /** Number of going/train bridges. Precise count — omit unless verified. Mirrors v1.39.0. */
  numberOfBridges?: number;
  /** Number of mainspring barrels (e.g. 1, 2). Precise count — omit unless verified. Mirrors v1.39.0. */
  barrelCount?: number;
  // Materials
  /**
   * Material of the automatic winding rotor / oscillating weight (e.g. `steel`,
   * `brass`, `18k_rose_gold`, `platinum`, `tungsten`). Backed by the
   * `lookup_rotor_material` lookup (single-select) — stores the lookup slug.
   * What the rotor is MADE OF; its construction lives in `windingArchitecture`
   * and its bearing/suspension style in `rotorType`. Only meaningful on
   * automatic calibres. Mirrors `CalibreData.rotorMaterial` in
   * @watchkeeper/shared-types (v1.92.0).
   */
  rotorMaterial?: string;
  /**
   * Material of the movement's bridges — and, where the two share a material,
   * the mainplate (e.g. `german_silver`, `nickel_silver`, `brass`, `steel`).
   * Backed by the `lookup_bridge_material` lookup (single-select) — stores the
   * lookup slug. What the parts are MADE OF; the surface treatment applied on
   * top lives in `plateFinishing` and `decorations`. Omitted rather than
   * guessed when the bridges and mainplate differ. Mirrors
   * `CalibreData.bridgeMaterial` in @watchkeeper/shared-types (v1.92.0).
   */
  bridgeMaterial?: string;
  /**
   * Case materials this calibre is OFFERED in (e.g. `steel`, `titanium`,
   * `18k_rose_gold`, `platinum`, `ceramic`). Backed by the
   * `lookup_case_materials` lookup — stores lookup slugs.
   *
   * Availability, not composition: it constrains which references can
   * legitimately carry this calibre, so a movement sold only in precious metal
   * reads `['18k_rose_gold', 'platinum']`. Distinct from the per-reference
   * `case.material` (what one watch's case actually is) and from
   * `WatchReference.caseMaterialVariants` (what a parent's children happen to
   * use). Omitted when the calibre is unrestricted rather than listing every
   * material. Mirrors `CalibreData.caseMaterialsAvailable` in
   * @watchkeeper/shared-types (v1.92.0).
   *
   * NOTE the collection name: shared-types' docstring says
   * `lookup_case_material`, but the collection that exists in this admin (and
   * backs the reference-level case material) is the PLURAL
   * `lookup_case_materials`. That collection is `isMultiSelect: false` because
   * the reference field is single-select — the multi-ness here rides on the
   * binding (LookupMultiSelect) and the prompt prose, not the config flag, the
   * same arrangement `dateWheelTextColor` has with `lookup_text_colors`.
   */
  caseMaterialsAvailable?: string[];
  // Decoration & finishing
  /**
   * Degree of openworking / skeletonisation: `'full'` (fully skeletonised),
   * `'partial'` (open-heart / partial aperture), `'none'` (solid). Omit when
   * unknown. Mirrors `CalibreData.skeletonization` in @watchkeeper/shared-types
   * (v1.39.0).
   */
  skeletonization?: 'full' | 'partial' | 'none';
  /**
   * Overall level/quality of movement finishing. Backed by the
   * `lookup_movement_finishing` lookup — stores the lookup slug. Mirrors
   * `CalibreData.movementFinishing` in @watchkeeper/shared-types (v1.39.0).
   */
  movementFinishing?: string;
  /**
   * PRIMARY finishing treatment of the mainplate — the single surface
   * treatment that defines its look (e.g. `sunburst`, `frosted`,
   * `sandblasted`, `perlage_mainplate`, `cotes_de_geneve_mainplate`). Backed by
   * the `lookup_plate_finishing` lookup (single-select) — stores the lookup
   * slug.
   *
   * Narrower than `movementFinishing`, which grades the movement's overall
   * finishing QUALITY, and distinct from `decorations`, which lists every
   * decorative technique present without ranking them. Holds the treatment a
   * spec sheet would name first; the rest belong in `decorations`. Mirrors
   * `CalibreData.plateFinishing` in @watchkeeper/shared-types (v1.92.0).
   */
  plateFinishing?: string;
  /**
   * Specific decorative techniques on the movement (e.g. `perlage`,
   * `cotes_de_geneve`, `anglage`, `blued_screws`, `engraving`, `guilloche`).
   * Backed by the `lookup_movement_decoration` lookup (multi-select) — stores
   * lookup slugs; the AI may also propose values not yet in the seed vocab. The
   * dedicated `rhodiumPlated` / `giltFinish` booleans below stay separately
   * queryable. Mirrors `CalibreData.decorations` in @watchkeeper/shared-types
   * (v1.39.0).
   */
  decorations?: string[];
  /** True when the movement's components carry a rhodium plating/finish. Mirrors v1.39.0. */
  rhodiumPlated?: boolean;
  /** True when the movement carries a gilt (gilded) finish. Mirrors v1.39.0. */
  giltFinish?: boolean;
  // Battery-powered movement fields
  batteryType?: string;
  batteryLifeYears?: number;
  // Durability
  shockResistanceG?: number;
  antiMagneticAm?: number;
  antiMagneticGauss?: number;
  images?: CalibreImage[];
  /**
   * Tier variants (e.g. base / Elaboré / Top / Chronometer). When absent or
   * empty, this calibre has no tiers. Mirrors `CalibreData.tiers` in
   * @watchkeeper/shared-types (v1.30.0) — kept in this LOCAL dup until the
   * admin `CalibreData` derives from the shared type.
   */
  tiers?: CalibreTier[];
  /**
   * Display name for the synthetic "Base" row that heads the tier list in the
   * calibre editor (e.g. "Standard", "Base grade"). Display-only — falls back to
   * "Base" when unset. Mirrors `CalibreData.baseTierName` in
   * @watchkeeper/shared-types (v1.35.0).
   */
  baseTierName?: string;
}

/**
 * Subset of {@link CalibreData} fields a tier may override. Identity fields
 * (canonical name, manufacturer, baseMovement, movementType) are NEVER
 * overridable. Mirrors `CalibreTierOverrides` in @watchkeeper/shared-types
 * (v1.30.0).
 */
export interface CalibreTierOverrides {
  // Timing / regulation
  isChronometer?: boolean;
  isCosc?: boolean;
  isGlashuetteRegulation?: boolean;
  isGenevaSeal?: boolean;
  isGrandSeikoStandard?: boolean;
  isMETAS?: boolean;
  // Component materials
  regulatorSystem?: string;
  hairspring?: string;
  rotorType?: string;
  escapement?: string;
  /**
   * Anti-shock system for this tier (e.g. "Incabloc", "KIF"). Overrides the
   * base `CalibreData.antiShockSystem` field; backed by the same
   * `lookup_shock_protection` collection. Mirrors
   * `CalibreTierOverrides.antiShockSystem` in @watchkeeper/shared-types
   * (v1.34.0).
   */
  antiShockSystem?: string;
  /**
   * Rotor / oscillating weight material for this tier (e.g. `steel`,
   * `18k_rose_gold`, `platinum`, `tungsten`). Backed by the
   * `lookup_rotor_material` lookup (single-select) — stores the lookup slug,
   * same shape as the base `CalibreData.rotorMaterial` field. REPLACES the base
   * value when set: a Top tier fitted with a gold rotor over the base's steel
   * one carries `'18k_rose_gold'`. Absent means no change from base. Mirrors
   * `CalibreTierOverrides.rotorMaterial` in @watchkeeper/shared-types
   * (v1.92.0).
   */
  rotorMaterial?: string;
  /**
   * Bridge (and, where they share a material, mainplate) material for this tier
   * (e.g. `german_silver`, `nickel_silver`, `brass`, `18k_rose_gold`). Backed
   * by the `lookup_bridge_material` lookup (single-select) — stores the lookup
   * slug, same shape as the base `CalibreData.bridgeMaterial` field. REPLACES
   * the base value when set — the common case for a tier swapping
   * rhodium-plated brass bridges for German silver or solid gold. Absent means
   * no change from base. Mirrors `CalibreTierOverrides.bridgeMaterial` in
   * @watchkeeper/shared-types (v1.92.0).
   */
  bridgeMaterial?: string;
  /**
   * Case materials this TIER is offered in (e.g. `18k_rose_gold`, `platinum`),
   * as `lookup_case_materials` slugs — same shape as the base
   * `CalibreData.caseMaterialsAvailable` field. REPLACES the base list
   * wholesale rather than merging into it, so a tier must list its FULL set: a
   * precious-metal-only tier of a calibre otherwise sold in steel carries
   * `['18k_rose_gold', 'platinum']`, not just the additions. An empty array
   * strips the base restriction entirely. Mirrors
   * `CalibreTierOverrides.caseMaterialsAvailable` in @watchkeeper/shared-types
   * (v1.92.0).
   */
  caseMaterialsAvailable?: string[];
  // Complications
  /**
   * Whether this tier carries a date complication, overriding the base
   * calibre's `date[]` presence. `true` — the tier adds a date the base lacks;
   * `false` — the tier drops the date the base carries. Absent leaves the base
   * `date[]` untouched. Coarse presence only: it cannot name the date TYPE, so
   * a tier that adds a date the base has no entry for renders unqualified.
   * Mirrors `CalibreTierOverrides.hasDate` in @watchkeeper/shared-types
   * (v1.90.0).
   */
  hasDate?: boolean;
  /**
   * Balance-wheel attributes for this tier, as `lookup_balance_type` slugs —
   * same shape as the base `CalibreData.balanceType` field. REPLACES the base
   * list wholesale rather than merging into it, so a tier must list its FULL
   * balance attribute set: an ETA 2824 Top tier upgrading from base
   * `['glucydur']` carries `['glucydur', 'free-sprung']`, both slugs. An empty
   * array strips the base's balance metadata entirely. Mirrors
   * `CalibreTierOverrides.balanceType` in @watchkeeper/shared-types (v1.91.0).
   */
  balanceType?: string[];
  // Decoration & finishing
  /**
   * Primary mainplate finishing treatment for this tier (e.g. `sunburst`,
   * `frosted`, `sandblasted`, `perlage_mainplate`,
   * `cotes_de_geneve_mainplate`). Backed by the `lookup_plate_finishing` lookup
   * (single-select) — stores the lookup slug, same shape as the base
   * `CalibreData.plateFinishing` field. REPLACES the base value when set;
   * absent means no change from base. Names the ONE defining treatment — every
   * other technique the tier adds belongs in `decorations` below. Mirrors
   * `CalibreTierOverrides.plateFinishing` in @watchkeeper/shared-types
   * (v1.92.0).
   */
  plateFinishing?: string;
  /**
   * Decorative techniques this tier carries (e.g. `perlage`,
   * `cotes_de_geneve`, `anglage`, `blued_screws`). Backed by the
   * `lookup_movement_decoration` lookup (multi-select) — stores lookup slugs,
   * same shape as the base `CalibreData.decorations` field. REPLACES the base
   * array wholesale rather than merging into it, so a tier must list its full
   * set; an empty array strips the base decoration entirely. Mirrors
   * `CalibreTierOverrides.decorations` in @watchkeeper/shared-types (v1.90.0).
   */
  decorations?: string[];
  // Specs
  jewels?: number;
  powerReserveHours?: number;
  /** Number of positions the tier is regulated in (e.g. 3, 5, 6). */
  positionsAdjusted?: number;
  /** Lower bound of daily accuracy in seconds/day (signed; negative = slow). */
  accuracyLowerSecPerDay?: number;
  /** Upper bound of daily accuracy in seconds/day (signed; positive = fast). */
  accuracyUpperSecPerDay?: number;
  /**
   * Interpretation flag for the tier's two accuracy fields — same semantics as
   * `CalibreData.accuracySymmetric`. Independent of the base: a symmetric base
   * calibre may carry an asymmetric Chronometer tier, or vice versa. Mirrors
   * `CalibreTierOverrides.accuracySymmetric` in @watchkeeper/shared-types
   * (v1.33.0).
   */
  accuracySymmetric?: boolean;
  hacking?: boolean;
  quickSetDate?: boolean;
  // Durability (Phase 3, v1.94.0)
  /**
   * Shock resistance in G-force for this tier (e.g. 5000). REPLACES the base
   * `CalibreData.shockResistanceG` when set. Absent means no change from base.
   * Common case: a tier-level shock upgrade like Ball's Amortiser® anti-shock
   * system raises tolerance above the base movement's rating. Mirrors
   * `CalibreTierOverrides.shockResistanceG` in @watchkeeper/shared-types
   * (v1.94.0).
   */
  shockResistanceG?: number;
  /**
   * Anti-magnetic rating in A/m for this tier (e.g. 4800, 80000). REPLACES the
   * base `CalibreData.antiMagneticAm` when set. Absent means no change from
   * base. Common case: a brand-specific tier adding a soft-iron inner case
   * (Ball Engineer II, IWC Ingenieur) that lifts the movement's effective
   * magnetic tolerance well beyond the base calibre's rating. Mirrors
   * `CalibreTierOverrides.antiMagneticAm` in @watchkeeper/shared-types
   * (v1.94.0).
   */
  antiMagneticAm?: number;
  /**
   * Anti-magnetic rating in Gauss for this tier (e.g. 60, 1000). REPLACES the
   * base `CalibreData.antiMagneticGauss` when set. Absent means no change from
   * base. Mirrors `antiMagneticAm` in the alternate unit — populate whichever
   * the source cites; consumers may derive one from the other (1 Gauss ≈
   * 80 A/m) but the stored shape mirrors base exactly. Mirrors
   * `CalibreTierOverrides.antiMagneticGauss` in @watchkeeper/shared-types
   * (v1.94.0).
   */
  antiMagneticGauss?: number;
}

/**
 * A tier variant of a base calibre (e.g. Sellita SW200-1 base / Elaboré / Top /
 * Chronometer). `overrides` is applied on top of the base calibre spec; any
 * field left `undefined` inherits the base value. Mirrors `CalibreTier` in
 * @watchkeeper/shared-types (v1.30.0).
 */
export interface CalibreTier {
  /** Stable id — referenced from `WatchReference.calibreTierId`. */
  id: string;
  /** Display name (e.g. "Elaboré", "Top", "Chronometer"). */
  name: string;
  /** Optional short description of what makes this tier distinct. */
  description?: string;
  /**
   * `'general'` = a manufacturer-defined grade (Standard/Elaboré/Top/…),
   * universal across brands. `'brand-specific'` = a modification a specific
   * brand applies on the base (requires `brandId`). Absent → treated as
   * `'general'` for back-compat. Mirrors `CalibreTier` in shared-types v1.37.0.
   */
  tierType?: 'general' | 'brand-specific';
  /** FK into watchBrands. Required when tierType === 'brand-specific'. */
  brandId?: string;
  /**
   * The tier's own movement manufacturer(s) when they differ from the base
   * calibre's (Towson's Benzinger-decorated Valjoux: valjoux + [benzinger]).
   * Same semantics as on CustomCalibreEntry. Absent = the base's. Mirrors
   * shared-types v1.136.0.
   */
  manufacturerId?: string;
  additionalManufacturerIds?: string[];
  /** Tier-specific images, as on CustomCalibreEntry. Mirrors shared-types v1.136.0. */
  images?: CalibreImage[];
  /** Field overrides for this tier, applied on top of the base calibre. */
  overrides: CalibreTierOverrides;
}

/**
 * Derive reference-level `functions` (lookup_watch_functions) from calibre complication data.
 * Maps calibre boolean flags and complication arrays → lookup IDs that match the
 * lookup_watch_functions / lookup_complications collections used in the RN app.
 *
 * @param d - Calibre data with complication arrays and boolean flags
 * @param existingFunctions - Optional existing functions array to merge with (avoids losing manually-set values)
 * @returns Deduplicated array of function lookup IDs
 */
export function deriveFunctionsFromCalibre(d: CalibreData, existingFunctions?: string[]): string[] {
  const fns = new Set<string>(existingFunctions || []);

  // Boolean flags → top-level functions
  if (d.isChronograph) fns.add('chronograph');
  if (d.isGMT) fns.add('gmt');

  // Date array → individual functions
  if (d.date?.length) {
    for (const v of d.date) {
      const lower = v.toLowerCase();
      if (/perpetual\s*calendar/.test(lower)) fns.add('perpetual_calendar');
      else if (/annual\s*calendar/.test(lower)) fns.add('annual_calendar');
      else if (/day[\s-]?date/.test(lower)) fns.add('day_date');
      else if (/\bdate\b/.test(lower)) fns.add('date');
      // Any date-related entry implies at least "date"
      if (!fns.has('perpetual_calendar') && !fns.has('annual_calendar') && !fns.has('day_date')) {
        fns.add('date');
      }
    }
  }

  // Chronograph array → specific chronograph functions
  if (d.chronograph?.length) {
    fns.add('chronograph'); // Always add base chronograph if array has items
    for (const v of d.chronograph) {
      const lower = v.toLowerCase();
      if (/flyback/.test(lower)) fns.add('flyback');
      if (/split[\s-]?seconds|rattrapante/.test(lower)) fns.add('split_seconds');
    }
  }

  // Astronomical array
  if (d.astronomical?.length) {
    for (const v of d.astronomical) {
      const lower = v.toLowerCase();
      if (/moon\s*phase/.test(lower)) fns.add('moon_phase');
      if (/equation\s*of\s*time/.test(lower)) fns.add('equation_of_time');
      if (/sunrise|sunset/.test(lower)) fns.add('sunrise_sunset');
    }
  }

  // Acoustic array
  if (d.acoustic?.length) {
    for (const v of d.acoustic) {
      const lower = v.toLowerCase();
      if (/minute\s*repeat/.test(lower)) fns.add('minute_repeater');
      if (/\balarm\b/.test(lower)) fns.add('alarm');
      // grande/petite sonnerie → minute_repeater (closest match in lookup)
    }
  }

  // Additionals array
  if (d.additionals?.length) {
    for (const v of d.additionals) {
      const lower = v.toLowerCase();
      if (/tourbillon/.test(lower)) fns.add('tourbillon');
      if (/power\s*reserve/.test(lower)) fns.add('power_reserve');
      if (/world\s*time/.test(lower)) fns.add('world_timer');
      if (/jumping\s*hour/.test(lower)) fns.add('jumping_hour');
      if (/retrograde/.test(lower)) fns.add('retrograde');
      if (/dead[\s-]?seconds?|dead[\s-]?beat/.test(lower)) fns.add('dead_seconds');
      if (/regatta/.test(lower)) fns.add('regatta');
    }
  }

  return Array.from(fns);
}

export interface CalibreImage {
  storagePath?: string;
  url?: string; // Legacy URL-based images (pre-Storage migration)
  source: string;
  caption?: string;
}

export interface CalibreLookupResult {
  success: boolean;
  data?: CalibreData;
  url: string;
  error?: string;
  source?: 'custom' | 'cache' | 'calibercorner';
  normalizedName?: string;
}

// Custom Calibre Database
const CUSTOM_CALIBRES_COLLECTION = 'custom_calibres';

/** Rich alias metadata for calibre "also known as" entries */
export interface CalibreAliasDetail {
  name: string;
  /** When set, this alias only applies when matching references from this brand */
  brandId?: string;
  /** When true, this alias is shown on the Explore calibre detail screen in the RN app */
  showInExplore?: boolean;
}

export interface CustomCalibreEntry {
  id: string;
  name: string;
  /**
   * Canonical watchBrand doc id (lowercase slug). Authoritative going forward.
   * Renderers fall back to brandName while the backfill completes.
   */
  brandId?: string;
  /** Legacy free-text brand string. Read-only fallback during transition. */
  brandName?: string;
  /** Flat string array for Firestore array-contains queries (backward compatible) */
  alsoKnownAs?: string[];
  /** Rich alias metadata — kept in sync with alsoKnownAs */
  aliasDetails?: CalibreAliasDetail[];
  /** FK to movement_manufacturers/{id} — primary "made by" maker. */
  manufacturerId?: string | null;
  /**
   * FKs to movement_manufacturers/{id} for co-developers of a jointly-authored
   * calibre (e.g. UWD 33.1 = primary UWD + additional Sinn). Excludes the
   * primary manufacturerId. RN surfaces the calibre under EACH of these too.
   */
  additionalManufacturerIds?: string[] | null;
  /**
   * FK to movement_manufacturers/{id} — the maker that ORIGINALLY made this
   * calibre, when it differs from the current primary (ETA 6498-1: primary
   * eta, original unitas). Not a co-developer. Mirrors shared-types v1.143.0.
   */
  originalManufacturerId?: string | null;
  /**
   * FK → calibre_families/{id}. Groups this calibre under a movement family
   * (e.g. Sellita SW200). Managed via the manufacturer detail's Families panel.
   * Mirrors `CalibreEntry.familyId` in @watchkeeper/shared-types.
   */
  familyId?: string | null;
  // --- Calibre lineage (mirrors CalibreEntry, shared-types v1.141.0) ---
  // Written only through services/calibreLineage.ts, immediately, never by the
  // calibre form's Save — the same contract as `familyId`.
  /** FK → calibre_lineages/{id}: the revision line this calibre belongs to. */
  calibreLineageId?: string;
  /** The revision as the maker writes it: "-2", "A", ".1". */
  revisionLabel?: string;
  activeFromYear?: number;
  /** Absent = still produced. Cleared with deleteField, never null. */
  activeUntilYear?: number;
  /** Explicit current revision of its line (D3). At most one per lineage. */
  isCurrent?: boolean;
  /** The bare lineage name rather than a real revision. Never current. */
  isLineagePlaceholder?: boolean;
  /**
   * Admin-only provenance of activeFromYear/activeUntilYear (lineage Phase 5).
   * 'ai' = applied from a suggestCalibreLineage row, so a later suggestion may
   * revise them. Absent = curator-entered, which the AI guard never changes.
   * Not in shared-types: RN does not read it.
   */
  activeDatesSource?: 'ai';
  activeDatesConfidence?: 'high' | 'medium' | 'low';
  /** AI-generated description covering history, notable references, significance */
  description?: string;
  data: CalibreData;
  source?: string; // 'calibercorner' | 'manual'
  sourceUrl?: string;
  images?: CalibreImage[];
  /**
   * Soft-delete / alias flag. True when this calibre has been reclassified as a
   * brand-specific tier of another calibre — hide from pickers/search/admin
   * lists unless "show deprecated" is toggled. Never hard-deleted; the redirect
   * is a permanent alias. Mirrors `CalibreEntry` in shared-types v1.37.0.
   */
  deprecated?: boolean;
  /** When deprecated, the calibre reads resolve to (see `resolveCalibre`). */
  redirectToCalibreId?: string;
  /** When deprecated, the tier ON the redirect target this data now lives as. */
  redirectToTierId?: string;
  /**
   * How many WatchReferences point at this calibre. Denormalised onto the doc
   * server-side by the `maintainCalibreReferenceCount` trigger (watchlock) and
   * mirrored onto the Algolia record — never written from the admin. Absent on
   * calibres the trigger has not visited yet, which is why the listing treats a
   * missing value as 0 rather than as "unknown".
   */
  referenceCount?: number;
  /**
   * The split of {@link referenceCount}, also server-maintained: references
   * linked by `calibreId`, and references that match by name/alias text only.
   * Absent until a recount has run under the union semantics — treat absence
   * as "not computed", never as 0.
   */
  linkedReferenceCount?: number;
  unlinkedReferenceCount?: number;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * Resolved (calibre, tier) pair after following a deprecation redirect.
 * `redirected` is true when the input calibre was deprecated and we followed
 * its `redirectToCalibreId`/`redirectToTierId` to `calibre`/`tierId`.
 */
export interface ResolvedCalibre {
  calibre: CustomCalibreEntry;
  /** The tier id to apply on the resolved calibre, if the redirect carried one. */
  tierId?: string;
  redirected: boolean;
  /** The original (deprecated) calibre we redirected FROM, when redirected. */
  source?: CustomCalibreEntry;
}

/**
 * Follow a calibre's deprecation redirect to the live target (calibre + tier).
 *
 * Depth is capped at ONE hop: the migration guarantees a redirect always points
 * at a live (non-deprecated) calibre, so chains never form in normal use. If we
 * nonetheless land on a second deprecated calibre we STOP and warn rather than
 * follow further — an anomaly is surfaced, never an infinite loop.
 *
 * Pass either a calibre entry (no fetch) or an id (one server read). Returns
 * null only when an id argument doesn't resolve to any calibre.
 */
export async function resolveCalibre(
  input: CustomCalibreEntry | string,
): Promise<ResolvedCalibre | null> {
  const entry =
    typeof input === 'string' ? await getCustomCalibreById(input) : input;
  if (!entry) return null;

  if (!entry.deprecated || !entry.redirectToCalibreId) {
    return { calibre: entry, redirected: false };
  }

  const target = await getCustomCalibreById(entry.redirectToCalibreId);
  if (!target) {
    console.warn(
      `resolveCalibre: ${entry.id} redirects to missing calibre ${entry.redirectToCalibreId}`,
    );
    return { calibre: entry, redirected: false, source: entry };
  }
  if (target.deprecated) {
    console.warn(
      `resolveCalibre: redirect chain detected — ${entry.id} → ${target.id} (also deprecated). Stopping at depth 1.`,
    );
  }
  return {
    calibre: target,
    tierId: entry.redirectToTierId,
    redirected: true,
    source: entry,
  };
}

/**
 * Append (or replace, by id) a single tier on a calibre's inline `data.tiers`
 * via a targeted read-modify-write — used by the reclassify wizard to create the
 * brand-specific tier on the target calibre before the migration callable
 * re-points refs onto it. Idempotent: re-running with the same tier id replaces
 * rather than duplicates. Returns the tier id written.
 *
 * Targeted update (only `data.tiers`) so sibling calibre fields are untouched;
 * the write fires watchlock's onCustomCalibreWrite → Algolia reindex.
 */
export async function appendCalibreTier(
  calibreId: string,
  tier: CalibreTier,
): Promise<string> {
  const ref = doc(db, CUSTOM_CALIBRES_COLLECTION, calibreId);
  const snap = await getDocFromServer(ref);
  if (!snap.exists()) {
    throw new Error(`Calibre ${calibreId} not found`);
  }
  const data = (snap.data()?.data ?? {}) as CalibreData;
  const tiers = Array.isArray(data.tiers) ? [...data.tiers] : [];
  const idx = tiers.findIndex((t) => t.id === tier.id);
  if (idx >= 0) tiers[idx] = tier;
  else tiers.push(tier);
  await updateDoc(ref, { 'data.tiers': tiers });
  return tier.id;
}

/**
 * Build the custom_calibres document id for a calibre name (+ optional brand).
 *
 * Deliberately does NOT use normalizeCalibreKey: that folds "caliber"→"calibre",
 * and a doc id is a storage key, not a comparison key. Folding here would give
 * every existing `...-caliber-...` doc a different id than the one it lives at.
 * Spelling-insensitivity belongs in the name/AKA comparisons, which is where the
 * fold is applied.
 *
 * Only ever used to mint the id for a NEW calibre. Editing an existing one never
 * re-derives its id: the id slug is allowed to drift from the display name (a
 * `...-caliber-2824-2` doc named "Calibre 2824-2" is expected and fine), because
 * re-keying an edit meant delete + re-create, which dropped fields the create
 * payload doesn't carry and orphaned image storagePaths and reference FKs.
 *
 * DOES fold Unicode dashes onto ASCII "-", via the same helper the comparison
 * key uses. That is not a spelling fold, it is a SEPARATOR fold: the
 * `[^a-z0-9\s-]` strip below treats U+2011 as punctuation and DELETES it, so
 * "CH 29\u2011535 PS Q" minted `ch-29535-ps-q` while the hand-typed
 * "CH 29-535 PS Q" minted `ch-29-535-ps-q` — two ids for one movement. Because
 * this only ever mints ids for NEW calibres, folding here re-keys nothing that
 * already exists; it just stops the id-space accreting more of the defect.
 */
export function buildCalibreDocId(name: string, brandName?: string): string {
  const slug = foldUnicodeDashes(name).toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  return brandName
    ? `${brandName.toLowerCase().replace(/[^a-z0-9]/g, '-')}--${slug}`
    : slug;
}

export async function getCustomCalibres(
  fromServer = false,
  includeDeprecated = false,
): Promise<CustomCalibreEntry[]> {
  const q = query(collection(db, CUSTOM_CALIBRES_COLLECTION), orderBy('name'));
  const snapshot = fromServer ? await getDocsFromServer(q) : await getDocs(q);
  const all = snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as CustomCalibreEntry[];
  // Deprecated calibres are redirect aliases (reclassified into a tier of another
  // calibre) — hidden from pickers/lists by default. A caller passes
  // includeDeprecated to surface them (e.g. a "show deprecated" toggle or audit).
  // Client-side filter: a Firestore `deprecated != true` query would wrongly
  // exclude the (vast majority) docs that lack the field entirely.
  return includeDeprecated ? all : all.filter((c) => c.deprecated !== true);
}

/**
 * Fetch a single custom calibre by doc id with a forced server read. Used when
 * opening the editor for one calibre from another surface (e.g. the movement-type
 * backfill / description-audit page) where we need the freshest copy — not a
 * possibly-stale client-cached snapshot — so edits round-trip correctly.
 */
export async function getCustomCalibreById(
  id: string,
): Promise<CustomCalibreEntry | null> {
  const snap = await getDocFromServer(doc(db, CUSTOM_CALIBRES_COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as CustomCalibreEntry;
}

/**
 * Append one image to a calibre's gallery.
 *
 * A TARGETED write — `updateDoc` on the single `images` field — rather than a
 * trip through `saveCustomCalibre`, which takes the calibre's name and its whole
 * `CalibreData` and writes a dozen fields besides. The caller here is the
 * REFERENCE editor, which holds no authoritative copy of the calibre document;
 * handing a partial one to a whole-document writer is how fields go missing.
 *
 * Writes the TOP-LEVEL `images`, which is what `saveCustomCalibre` persists and
 * what the calibre editor prefers on read (`entry.images || entry.data?.images`)
 * — `data.images` is a pre-migration fallback and must not be written.
 *
 * `current` is the caller's freshly-read image array, so the cap is enforced
 * against what is actually stored rather than against a copy from whenever the
 * reference editor happened to open.
 */
export async function appendCalibreImage(
  calibreId: string,
  current: CalibreImage[],
  image: CalibreImage,
): Promise<CalibreImage[]> {
  const next = [...current, image];
  await updateDoc(doc(db, CUSTOM_CALIBRES_COLLECTION, calibreId), {
    images: next,
    updatedAt: serverTimestamp(),
  });
  return next;
}

/**
 * Load a handful of calibres by doc id, in parallel.
 *
 * The companion to `searchCalibreIdsByName`: the index narrows ~1,822 calibres
 * to a few candidates, this reads only those documents, and the caller runs the
 * authoritative brand-scope/alias match against them. Missing ids and failed
 * reads are skipped rather than thrown — a stale index entry pointing at a
 * deleted calibre must not break the editor.
 *
 * Deliberately a cached `getDoc`, not `getDocFromServer`: this feeds a
 * read-only "does this calibre exist / which one is it" check, so replaying a
 * document already in the SDK cache is free and correct. Use
 * {@link getCustomCalibreById} when you are about to EDIT the calibre.
 */
export async function getCustomCalibresByIds(
  ids: string[],
): Promise<CustomCalibreEntry[]> {
  if (ids.length === 0) return [];
  const snaps = await Promise.all(
    ids.map((id) =>
      getDoc(doc(db, CUSTOM_CALIBRES_COLLECTION, id)).catch(() => null),
    ),
  );
  return snaps
    .filter((s): s is NonNullable<typeof s> => !!s && s.exists())
    .map((s) => ({ id: s.id, ...s.data() }) as CustomCalibreEntry);
}

// Strip undefined values from an object (Firestore rejects them)
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  ) as Partial<T>;
}

/**
 * Check if a calibre with a genuinely colliding name/alias already exists.
 *
 * Brand scope is honoured. Each comparable "token" is a name (the calibre's own
 * name, scoped to its brandId, or an alias scoped to its own optional brandId).
 * Two same-named tokens collide UNLESS both are brand-scoped to DIFFERENT
 * brands — a universal (unscoped) token matches every brand, and same-brand
 * tokens collide. So:
 *   - universal ↔ universal (same name)         → collision
 *   - universal ↔ brand-scoped (same name)      → collision
 *   - brand-scoped X ↔ brand-scoped X (same)    → collision
 *   - brand-scoped X ↔ brand-scoped Y (same)    → NOT a collision
 *
 * Returns the match details (including the specific matched name) or null.
 * This is warn-only signal for the UI — it never mutates or merges anything.
 */
export async function checkCalibreConflict(
  name: string,
  brandId: string | undefined,
  aliasDetails: CalibreAliasDetail[] | undefined,
  excludeDocId?: string,
): Promise<{ id: string; name: string; matchedName: string; matchedOn: string } | null> {
  const normalize = normalizeCalibreKey;

  interface ScopeToken {
    display: string;
    norm: string;
    brandId: string | undefined;
  }

  const buildTokens = (
    entryName: string,
    entryBrandId: string | undefined,
    details: CalibreAliasDetail[] | undefined,
    flatAliases: string[] | undefined,
  ): ScopeToken[] => {
    const tokens: ScopeToken[] = [];
    const seen = new Set<string>();
    const push = (raw: string, scope: string | undefined) => {
      const display = (raw || '').trim();
      if (!display) return;
      const norm = normalize(display);
      if (seen.has(norm)) return;
      seen.add(norm);
      tokens.push({ display, norm, brandId: scope || undefined });
    };
    push(entryName, entryBrandId);
    // Prefer rich aliasDetails (they carry brand scope); fall back to the flat
    // alsoKnownAs array (treated as universal) for any alias without a detail.
    for (const d of details || []) push(d.name, d.brandId);
    for (const a of flatAliases || []) push(a, undefined);
    return tokens;
  };

  const newTokens = buildTokens(name, brandId, aliasDetails, undefined);

  const collides = (a: ScopeToken, b: ScopeToken): boolean => {
    if (a.norm !== b.norm) return false;
    // Only genuinely different brands escape a collision.
    if (a.brandId && b.brandId && a.brandId !== b.brandId) return false;
    return true;
  };

  const allEntries = await getDocs(collection(db, CUSTOM_CALIBRES_COLLECTION));
  for (const d of allEntries.docs) {
    if (excludeDocId && d.id === excludeDocId) continue;
    const entry = d.data();
    const entryName = (entry.name as string) || '';
    const entryTokens = buildTokens(
      entryName,
      (entry.brandId as string | undefined) || undefined,
      entry.aliasDetails as CalibreAliasDetail[] | undefined,
      entry.alsoKnownAs as string[] | undefined,
    );
    const entryNameNorm = normalize(entryName);
    for (const nt of newTokens) {
      for (const et of entryTokens) {
        if (!collides(nt, et)) continue;
        const isName = et.norm === entryNameNorm;
        return {
          id: d.id,
          name: entryName,
          matchedName: nt.display,
          matchedOn: isName
            ? `name "${entryName}"`
            : `alias "${et.display}" on "${entryName}"`,
        };
      }
    }
  }
  return null;
}

export async function saveCustomCalibre(
  name: string,
  data: CalibreData,
  brandName?: string,
  alsoKnownAs?: string[],
  // The four `| null` parameters are the clearable ones. `undefined` still means
  // "leave the stored value alone" for every caller that doesn't own the whole
  // document; the editor passes null (or an empty array) to erase a field.
  images?: CalibreImage[] | null,
  sourceUrl?: string | null,
  isEdit?: boolean,
  description?: string | null,
  forceOverwriteId?: string,
  forceCreate?: boolean,
  manufacturerId?: string,
  aliasDetails?: CalibreAliasDetail[],
  brandId?: string | null,
  source?: string,
  additionalManufacturerIds?: string[],
  // `undefined` = leave alone; null or '' = clear. Same contract as the co-developers.
  originalManufacturerId?: string | null,
): Promise<string> {
  const normalize = normalizeCalibreKey;
  const nameLower = normalize(name);
  const newAliases = alsoKnownAs || [];

  // Skip all conflict detection if forceCreate is true
  let existingDocRef: ReturnType<typeof doc> | null = null;
  let existingData: Record<string, unknown> | null = null;

  if (forceCreate) {
    // User explicitly chose to create a new entry — skip to create path
  } else if (forceOverwriteId) {
    existingDocRef = doc(db, CUSTOM_CALIBRES_COLLECTION, forceOverwriteId);
    const snap = await getDoc(existingDocRef);
    if (!snap.exists()) {
      // The caller is updating a specific doc that has since vanished (deleted
      // in another tab/session). Falling through to the create path would write
      // a partial copy at a DIFFERENT, name-derived id — exactly the re-keying
      // that loses familyId/deprecated/redirectTo*/createdAt and orphans image
      // storagePaths. Fail loudly instead.
      throw new Error(
        `Calibre "${forceOverwriteId}" no longer exists — it may have been deleted elsewhere. Reload before saving.`,
      );
    }
    existingData = snap.data() as Record<string, unknown>;
  } else if (isEdit) {
    // When editing, find by matching names (existing behavior)
    const allNames = [nameLower, ...newAliases.map(a => normalize(a))];
    const allEntries = await getDocs(collection(db, CUSTOM_CALIBRES_COLLECTION));
    for (const d of allEntries.docs) {
      const entry = d.data();
      const entryName = normalize(entry.name || '');
      const entryAliases: string[] = (entry.alsoKnownAs || []).map((a: string) => normalize(a));
      if (allNames.includes(entryName) || allNames.some(n => entryAliases.includes(n))) {
        existingDocRef = doc(db, CUSTOM_CALIBRES_COLLECTION, d.id);
        existingData = entry;
        break;
      }
    }
  }

  const resolvedManufacturerId = manufacturerId && manufacturerId.trim() ? manufacturerId.trim() : null;
  // Co-developers: trim, drop empties + the primary (a manufacturer is never
  // listed in both), dedupe. `undefined` arg → leave the field untouched (other
  // callers that don't manage co-authorship); a passed (possibly empty) array →
  // write the cleaned list, or null to clear it.
  const resolvedAdditionalManufacturerIds: string[] | null | undefined =
    additionalManufacturerIds === undefined
      ? undefined
      : (() => {
          const cleaned = [
            ...new Set(
              additionalManufacturerIds
                .map((id) => (id || '').trim())
                .filter((id) => id && id !== resolvedManufacturerId),
            ),
          ];
          return cleaned.length > 0 ? cleaned : null;
        })();

  // Original maker: trimmed; equal to the primary means "not different" → null.
  const resolvedOriginalManufacturerId: string | null | undefined =
    originalManufacturerId === undefined
      ? undefined
      : (() => {
          const id = (originalManufacturerId ?? '').trim();
          return id && id !== resolvedManufacturerId ? id : null;
        })();

  if (existingDocRef && existingData) {
    let finalAliases: string[];

    if (isEdit) {
      finalAliases = newAliases;
    } else {
      const currentAliases: string[] = (existingData.alsoKnownAs as string[]) || [];
      // Both sides of every comparison below must live in the SAME key space.
      // These two were built with a raw toLowerCase() but tested against
      // normalize(...), so a stored "Caliber X" never matched an incoming
      // "Calibre X" (nor a decomposed accent a composed one) and the duplicate
      // was appended.
      const currentAliasesLower = new Set(currentAliases.map((a: string) => normalize(a)));
      const currentNameLower = normalize((existingData.name as string) || '');
      finalAliases = [...currentAliases];
      for (const alias of newAliases) {
        if (normalize(alias) !== currentNameLower && !currentAliasesLower.has(normalize(alias))) {
          finalAliases.push(alias);
          currentAliasesLower.add(normalize(alias));
        }
      }
      if (nameLower !== currentNameLower && !currentAliasesLower.has(nameLower)) {
        finalAliases.push(name);
      }
    }

    let finalAliasDetails: CalibreAliasDetail[] | null;
    if (isEdit) {
      finalAliasDetails = aliasDetails && aliasDetails.length > 0 ? aliasDetails : null;
    } else {
      const currentDetails: CalibreAliasDetail[] = (existingData.aliasDetails as CalibreAliasDetail[]) || [];
      const newDetails = aliasDetails || [];
      const existingNames = new Set(currentDetails.map(d => normalize(d.name)));
      const merged = [...currentDetails];
      for (const nd of newDetails) {
        if (!existingNames.has(normalize(nd.name))) {
          merged.push(nd);
          existingNames.add(normalize(nd.name));
        }
      }
      finalAliasDetails = merged.length > 0 ? merged : null;
    }

    const cleanData = stripUndefined(data as unknown as Record<string, unknown>);
    const updatePayload: Record<string, unknown> = {
      // An edit is authoritative over the display name: a rename (incl. the
      // Caliber → Calibre canonicalisation) updates this doc in place rather
      // than re-keying it, so the new name has to be written here or the save
      // would appear to work and revert on reload. Gated on isEdit because the
      // no-isEdit branch above is a MERGE into a name/alias match — there the
      // incoming name is folded into alsoKnownAs and the stored name must stay.
      ...(isEdit ? { name, brandName: brandName || null } : {}),
      data: cleanData,
      alsoKnownAs: finalAliases.length > 0 ? finalAliases : null,
      aliasDetails: finalAliasDetails,
      manufacturerId: resolvedManufacturerId,
      ...(resolvedAdditionalManufacturerIds !== undefined
        ? { additionalManufacturerIds: resolvedAdditionalManufacturerIds }
        : {}),
      ...(resolvedOriginalManufacturerId !== undefined
        ? { originalManufacturerId: resolvedOriginalManufacturerId }
        : {}),
      ...(source ? { source } : {}),
      // brandId / description / sourceUrl / images: the four fields the editor
      // can EMPTY. Each used to be gated on being truthy or merely defined, so
      // a cleared one dropped out of the write and `updateDoc`'s merge left the
      // old value in place. `calibreClearableUpdate` writes an explicit null for
      // a cleared field and still omits the key for callers that pass undefined
      // because they don't manage the field — see that module for the contract.
      ...calibreClearableUpdate({ brandId, description, sourceUrl, images }),
      updatedAt: serverTimestamp(),
    };
    await updateDoc(existingDocRef, stripUndefined(updatePayload));
    return existingDocRef.id;
  }

  // No existing entry — create new (or merge into existing if doc ID matches).
  // Prefer brandId (canonical slug) for the doc id slug when available.
  const docId = buildCalibreDocId(name, brandId || brandName);
  const docRef = doc(db, CUSTOM_CALIBRES_COLLECTION, docId);
  const cleanData = stripUndefined(data as unknown as Record<string, unknown>);

  // Check if doc already exists — if so, merge aliases instead of overwriting
  const existingSnap = await getDoc(docRef);
  let mergedAliases: string[] | null = newAliases.length > 0 ? newAliases : null;
  if (existingSnap.exists()) {
    const existingEntry = existingSnap.data();
    const currentAliases: string[] = (existingEntry.alsoKnownAs as string[]) || [];
    const currentNameLower = normalize((existingEntry.name as string) || '');
    const mergedSet = new Set(currentAliases.map(a => normalize(a)));
    const merged = [...currentAliases];
    for (const alias of newAliases) {
      if (normalize(alias) !== currentNameLower && !mergedSet.has(normalize(alias))) {
        merged.push(alias);
        mergedSet.add(normalize(alias));
      }
    }
    mergedAliases = merged.length > 0 ? merged : null;
  }

  const createPayload: Record<string, unknown> = {
    name,
    brandId: brandId || null,
    brandName: brandName || null,
    manufacturerId: resolvedManufacturerId,
    ...(resolvedAdditionalManufacturerIds !== undefined
      ? { additionalManufacturerIds: resolvedAdditionalManufacturerIds }
      : {}),
    ...(resolvedOriginalManufacturerId ? { originalManufacturerId: resolvedOriginalManufacturerId } : {}),
    alsoKnownAs: mergedAliases,
    aliasDetails: aliasDetails && aliasDetails.length > 0 ? aliasDetails : null,
    description: description || null,
    data: cleanData,
    images: images && images.length > 0 ? images : null,
    ...(sourceUrl ? { sourceUrl } : {}),
    ...(source ? { source } : {}),
    updatedAt: serverTimestamp(),
  };
  await setDoc(docRef, stripUndefined(createPayload), { merge: true });
  // Set createdAt only on first write
  const existing = await getDoc(docRef);
  if (!existing.data()?.createdAt) {
    await updateDoc(docRef, { createdAt: serverTimestamp() });
  }
  return docId;
}

export async function deleteCustomCalibre(docId: string): Promise<void> {
  await deleteDoc(doc(db, CUSTOM_CALIBRES_COLLECTION, docId));
}

export async function lookupCalibre(calibreName: string, brandName?: string): Promise<CalibreLookupResult> {
  const fn = httpsCallable<{ calibreName: string; brandName?: string }, CalibreLookupResult>(functions, 'lookupCalibre');
  const result = await fn({ calibreName, brandName });
  return result.data;
}

// ============================================================================
// Generic product URL extraction
// ============================================================================

export interface ExtractedProduct {
  name?: string;
  description?: string;
  reference?: string;
  model?: string;
  collection?: string;
  price?: string;
  currency?: string;
  imgSrc?: string;
  movement?: string;
  movementType?: string;
  /** Loose display/form-category hint (e.g. "ana-digi", "smartwatch"); the client canonicalises via resolveWatchType. */
  watchType?: string;
  /** Digital-watch module identifier (electronic equivalent of a calibre), e.g. Casio "3229". */
  module?: string;
  caseSize?: string;
  caseHeight?: string;
  waterRes?: string;
  caseMaterial?: string;
  caseShape?: string;
  /** Crystal material AND features (cyclops lens, domed, flat), comma-joined. */
  crystal?: string;
  /** Crown attachment / sealing system / shape, comma-joined. */
  crownType?: string;
  dialColor?: string;
  dialFinish?: string;
  /** Marker TYPE — how the markers are made (applied/printed/sandwich/…). */
  dialIndexes?: string;
  /** Marker STYLE — what the markers are (arabic/roman/stick/dot/baton). */
  dialNumerals?: string;
  hands?: string;
  handsColor?: string;
  /**
   * WHAT each sub-register does, mirroring `DialInfo.subdialTypes`.
   * MULTI-select, so a comma-joined string rather than one slug; the AI route
   * may answer an array and the importer's `toJoined` normalises either shape.
   *
   * No counterpart to the `subdials` COUNT: that is a number rather than a
   * slug and the importer has never carried it.
   */
  subdialTypes?: string;
  /**
   * Chapter ring & dial furniture, mirroring
   * `DialInfo.{chapterRing,minuteTrack,dialScales,logoType}`. One single-select
   * slug each except `dialScales`, which is multi-select and arrives
   * comma-joined.
   *
   * None of the four gates another, so a page can answer any subset. In
   * practice `dialScales` is the one a product page states outright; the ring,
   * the track and the logo treatment are visual details that normally come from
   * the enrich callable reading a dial image.
   */
  chapterRing?: string;
  minuteTrack?: string;
  dialScales?: string;
  /**
   * Structural / decorative dial attributes, comma-joined — the counterpart to
   * `dialFinish`: that is how the surface is treated, this is what has been
   * done to, or set into, the dial. Lands at `dialAndHands.dialFeatures`.
   */
  dialFeatures?: string;
  logoType?: string;
  /**
   * Lume detail, mirroring
   * `DialInfo.{lumePresence,lumeMaterial,lumeDayColor,lumeGlowColor}`. One
   * single-select slug each; `lumePresence` gates the other three.
   *
   * In practice a product page answers `lumeMaterial` and nothing else — the
   * two colours normally come from the enrich callable reading a lume-tagged
   * image, which is the only shot taken in the dark.
   */
  lumePresence?: string;
  lumeMaterial?: string;
  lumeDayColor?: string;
  lumeGlowColor?: string;
  /**
   * Date complication, mirroring
   * `DialInfo.{dateDisplay,dateWindowPosition,dateWindowFrame,dateWheelColor,dateWheelTextColor}`.
   * `dateDisplay` gates the other four: a pointer_date or subsidiary_dial has
   * no aperture, so it carries no position/frame/wheel colour.
   *
   * One slug each except `dateWheelTextColor` — multi-select, comma-joined by
   * the extractor, and comma-joined is also how it is stored.
   */
  dateDisplay?: string;
  dateWindowPosition?: string;
  dateWindowFrame?: string;
  dateWheelColor?: string;
  dateWheelTextColor?: string;
  bezelType?: string;
  /**
   * Bezel material and bezel colour(s). `bezelMaterial` lands on the ref's
   * `bezel` field — the historical name for the material. `bezelColors` is
   * multi-select but arrives as one comma-joined string: the callable collapses
   * the model's array, and the import mapper re-splits it.
   */
  bezelMaterial?: string;
  bezelColors?: string;
  lugWidth?: string;
  lugToLug?: string;
  strapMaterial?: string;
  strapColor?: string;
  jewels?: string;
  powerReserve?: string;
  functions?: string;
  extractionMethod: 'json-ld' | 'html-specs' | 'og-tags' | 'mixed' | 'ai';
  fieldsFound: number;
  url: string;
  fieldSources?: Record<string, 'json-ld' | 'html-specs' | 'og-tags' | 'description' | 'ai'>;
}

/**
 * Custom error thrown when both server and browser fetch fail,
 * signalling the UI to open the paste-HTML modal.
 */
export class NeedsManualHtmlError extends Error {
  constructor(public url: string, public reason: string) {
    super(`Site unreachable automatically — manual HTML paste needed for ${url}`);
    this.name = 'NeedsManualHtmlError';
  }
}

/**
 * Message shown when a listing/collection page itself is unreachable. Distinct
 * from the per-product case: there is no paste-HTML flow for listing pages, so
 * the way forward is to supply product URLs directly and let the per-product
 * paste fallback handle those.
 */
export const UNREACHABLE_LISTING_MESSAGE =
  "Blocked by the site's bot protection — neither the Cloudflare proxy nor our servers could load this page. "
  + 'Add the product URLs directly (or a JSON listing URL) instead; any product page that also fails can be pasted in by hand.';

/**
 * True when an extraction technically "succeeded" but yielded nothing usable —
 * e.g. an image-heavy product page (like ginault.com/expeditioner) that has no
 * parseable name, image, price, reference, or spec fields. Used to upgrade an
 * empty result into a NeedsManualHtmlError so the manual-paste fallback kicks in.
 *
 * Conservative on purpose: a product with even a single meaningful field
 * (e.g. just a name) is considered useful and passes through.
 */
export function isEmptyExtraction(p: ExtractedProduct | null | undefined): boolean {
  if (!p) return true;
  const hasName = !!p.name?.trim();
  const hasImage = !!p.imgSrc?.trim();
  const hasPrice = !!p.price?.trim();
  const hasReference = !!p.reference?.trim();
  // fieldsFound counts the meaningful spec fields the extractor populated.
  const hasFields = (p.fieldsFound ?? 0) > 0;
  return !hasName && !hasImage && !hasPrice && !hasReference && !hasFields;
}

// Cloudflare Worker proxy for fetching HTML from sites that block cloud IPs
const CF_PROXY_URL = import.meta.env.VITE_CF_PROXY_URL || '';
const CF_PROXY_SECRET = import.meta.env.VITE_CF_PROXY_SECRET || '';

/**
 * Hosts that ship an empty SPA shell to a plain fetch (or sit behind a
 * sophisticated Cloudflare bot challenge), so we skip the wasted plain-HTML
 * attempt and go straight to browser-rendered mode. Matched as a substring of
 * the hostname, so `chrono24` covers chrono24.com / .ca / .de / etc.
 * Data-driven so it's trivial to extend.
 */
const KNOWN_JS_RENDERED_HOSTS = [
  'chrono24.',
];

function hostNeedsBrowserRender(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return KNOWN_JS_RENDERED_HOSTS.some((needle) => host.includes(needle));
  } catch {
    return false;
  }
}

/**
 * Hosts where the Cloudflare Worker fails entirely — both `html` AND `browser`
 * modes come back 502, because the bot detection blocks the Worker's egress
 * IPs even for headless-browser rendering. For these we skip the Worker
 * outright and go straight to the Cloud Function fetch, which does get
 * through. Saves 20-30s of doomed attempts (and console noise) per URL.
 *
 * Matched as a hostname substring, same as KNOWN_JS_RENDERED_HOSTS, so one
 * entry covers every regional TLD (omegawatches.com / .co.uk / …).
 */
const KNOWN_WORKER_BLOCKED_HOSTS = [
  'omegawatches.',
];

function hostBypassesWorker(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return KNOWN_WORKER_BLOCKED_HOSTS.some((needle) => host.includes(needle));
  } catch {
    return false;
  }
}

/**
 * Heuristic: is this HTML unusable for extraction? True when the response is
 * just a SPA shell or a Cloudflare bot-challenge interstitial rather than the
 * real page content. Callers use this to decide whether to retry in browser
 * (JS-rendered) mode.
 */
export function isUselessHtml(html: string | null | undefined): boolean {
  if (!html) return true;

  // Too small to be a real product page — almost certainly a shell.
  if (html.length < 1024) return true;

  // Cloudflare / bot-challenge interstitial markers.
  const challengeMarkers = [
    'Just a moment...',
    'Checking your browser',
    'cf-mitigated',
    '__cf_chl_',
    'Attention Required! | Cloudflare',
  ];
  if (challengeMarkers.some((marker) => html.includes(marker))) return true;

  // Title-based detection.
  const titleMatch = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  const title = titleMatch ? titleMatch[1].trim() : '';
  if (title === 'Just a moment...') return true;

  // No <title> AND no meaningful body content → likely an empty shell.
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const bodyText = bodyMatch ? bodyMatch[1].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim() : '';
  if (!title && bodyText.length < 200) return true;

  return false;
}

/**
 * POST to the Cloudflare Worker proxy in a given mode and return the rendered
 * HTML, or null on any failure. `mode: 'browser'` drives a real headless
 * browser (Browser Rendering) so JS-rendered SPAs return real content.
 */
async function callCloudflareProxy(
  url: string,
  mode: 'html' | 'browser',
  waitFor?: string,
): Promise<string | null> {
  if (!CF_PROXY_URL) return null;
  try {
    const resp = await fetch(CF_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Proxy-Secret': CF_PROXY_SECRET,
      },
      body: JSON.stringify({ url, mode, ...(waitFor ? { waitFor } : {}) }),
    });
    if (resp.ok) {
      const data = await resp.json() as { html?: string };
      if (data.html) return data.html;
      return null;
    }
    console.warn(`[extract] Cloudflare proxy (${mode}) returned ${resp.status}`);
    return null;
  } catch (err) {
    console.warn(`[extract] Cloudflare proxy (${mode}) failed:`, err);
    return null;
  }
}

/** Result of {@link fetchViaCloudflareProxyDetailed}. */
export interface ProxyFetchOutcome {
  /** The fetched HTML, or null when no strategy produced any. */
  html: string | null;
  /**
   * True when every automated strategy failed to REACH the origin — the Worker
   * was blocked and the Cloud Function fetch timed out, was refused, or died.
   * Distinguishes "no machine we control can load this page" (→ manual HTML
   * paste is the only way forward) from "we loaded it but got nothing useful"
   * (→ worth retrying / parsing differently).
   */
  unreachable: boolean;
}

/**
 * True when a callable rejection means the page could not be reached at all —
 * the function was killed by its deadline, the fetch inside it timed out, or
 * the transport died. A function killed mid-request never emits CORS headers,
 * so these also surface in the browser as bare "Failed to fetch" / CORS errors;
 * both shapes are matched here.
 */
function isUnreachableError(err: unknown): boolean {
  const code = typeof (err as { code?: unknown } | null)?.code === 'string'
    ? ((err as { code: string }).code)
    : '';
  const bare = code.replace(/^functions\//, '');
  if (bare === 'deadline-exceeded' || bare === 'unavailable' || bare === 'internal' || bare === 'cancelled') {
    return true;
  }
  const message = err instanceof Error ? err.message : String(err ?? '');
  return /deadline|timed? ?out|failed to fetch|networkerror|cors|\b50[234]\b/i.test(message);
}

/**
 * Fetch a page's body through the `fetchHtml` Cloud Function only — no
 * Cloudflare Worker, no browser render. For responses whose raw body is all we
 * need but which send no CORS headers: Square Online storefronts (the
 * `__BOOTSTRAP_STATE__` blob) and the WooCommerce Store API (JSON — the
 * callable returns any body as text).
 *
 * `profile: 'api'` asks the function to send an honest API-client User-Agent
 * instead of posing as Chrome — see functions/src/fetchHtmlHeaders.ts. A
 * function deployed before that option existed ignores it and poses as Chrome.
 *
 * Throws a {@link FetchHtmlError} carrying the origin's HTTP status (0 when it
 * could not be reached at all).
 */
export async function fetchHtmlServerSide(
  url: string,
  options: { profile?: FetchHtmlProfile } = {},
): Promise<string> {
  const fn = httpsCallable<
    { url: string; profile?: FetchHtmlProfile },
    { html?: string | null; status?: number; unreachable?: boolean; error?: string }
  >(functions, 'fetchHtml', { timeout: 120000 });
  const result = await fn({ url, ...(options.profile ? { profile: options.profile } : {}) });
  const html = result.data?.html;
  if (html) return html;
  const status = result.data?.status ?? 0;
  throw new FetchHtmlError(
    status
      ? `The page returned HTTP ${status}`
      : `The page could not be reached${result.data?.error ? ` (${result.data.error})` : ''}`,
    status,
  );
}

/**
 * A URL's body through the WK Cloudflare Worker's plain-fetch mode — a
 * different egress from the `fetchHtml` function. Throws FetchHtmlError with
 * the upstream status the Worker reports ("Upstream returned HTTP 403"), so a
 * caller can tell a host refusing us from the Worker itself failing.
 */
export async function fetchViaCloudflareWorkerText(url: string): Promise<string> {
  if (!CF_PROXY_URL) throw new FetchHtmlError('Cloudflare proxy not configured', 0);
  const resp = await fetch(CF_PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Proxy-Secret': CF_PROXY_SECRET },
    body: JSON.stringify({ url, mode: 'html' }),
  });
  const data = (await resp.json().catch(() => ({}))) as { html?: string; error?: string };
  if (resp.ok && data.html) return data.html;
  const upstream = Number(/HTTP (\d{3})/.exec(data.error ?? '')?.[1]);
  throw new FetchHtmlError(
    `Cloudflare proxy: ${data.error ?? `HTTP ${resp.status}`}`,
    Number.isFinite(upstream) ? upstream : resp.status,
  );
}

/**
 * Body of a public store API (WooCommerce Store API), fetched server-side
 * through an escalation ladder: the standard fetchHtml request, then — only if
 * the host refuses it — an honest API-client request, then the Cloudflare
 * Worker. Policy and evidence: services/storeApiFetch.ts.
 */
export const fetchStoreApiText = createStoreApiFetcher(
  fetchHtmlServerSide,
  CF_PROXY_URL ? fetchViaCloudflareWorkerText : undefined,
);

/**
 * Fetch HTML for a URL through every automated strategy in turn (Cloudflare
 * Worker plain → Worker browser-render → Cloud Function), reporting whether the
 * origin turned out to be unreachable. {@link fetchViaCloudflareProxy} is the
 * html-only wrapper for callers that don't need the distinction.
 */
export async function fetchViaCloudflareProxyDetailed(url: string): Promise<ProxyFetchOutcome> {
  // Hosts the Worker can't reach at all skip straight to the Cloud Function
  // fallback below rather than burning both Worker modes on a guaranteed 502.
  const skipWorker = hostBypassesWorker(url);
  if (skipWorker) {
    console.log(`[extract] ${new URL(url).hostname} is a known-Worker-blocked host — going straight to Cloud Function fetch`);
  }

  if (CF_PROXY_URL && !skipWorker) {
    // Known JS-rendered hosts skip the wasted plain-HTML attempt.
    if (hostNeedsBrowserRender(url)) {
      console.log(`[extract] ${new URL(url).hostname} is a known JS-rendered host — using browser mode`);
      const rendered = await callCloudflareProxy(url, 'browser');
      if (rendered && !isUselessHtml(rendered)) return { html: rendered, unreachable: false };
    } else {
      const html = await callCloudflareProxy(url, 'html');
      if (html && !isUselessHtml(html)) return { html, unreachable: false };

      // Plain HTML was a shell / bot challenge — retry with browser rendering.
      if (html) {
        console.log('[extract] Plain HTML looked unusable, retrying via Cloudflare browser mode');
      }
      const rendered = await callCloudflareProxy(url, 'browser');
      if (rendered && !isUselessHtml(rendered)) return { html: rendered, unreachable: false };
      // Fall back to whatever plain HTML we got rather than dropping to the CF fn empty-handed.
      if (html) return { html, unreachable: false };
    }
    console.warn('[extract] Cloudflare proxy yielded no usable HTML, trying Cloud Function fallback');
  }

  // Fallback: use Cloud Function
  try {
    const fn = httpsCallable<
      { url: string },
      { html?: string | null; status?: number; unreachable?: boolean; error?: string }
    >(
      functions,
      'fetchHtml',
      // 120s: matches the function's own deadline. At the old 30s the client
      // gave up while the server was still fetching, so a slow-but-survivable
      // host looked identical to a hard block.
      { timeout: 120000 },
    );
    const result = await fn({ url });
    const html = result.data?.html ?? null;
    if (html) return { html, unreachable: false };
    const unreachable = result.data?.unreachable === true;
    if (unreachable) {
      console.warn(
        `[extract] Cloud Function could not reach ${url} (status ${result.data?.status ?? 0})`,
        result.data?.error ?? '',
      );
    }
    return { html: null, unreachable };
  } catch (err) {
    console.warn('[extract] Cloud Function fallback also failed:', err);
    return { html: null, unreachable: isUnreachableError(err) };
  }
}

export async function fetchViaCloudflareProxy(url: string): Promise<string | null> {
  return (await fetchViaCloudflareProxyDetailed(url)).html;
}

/**
 * Fetch a URL via the Cloudflare Worker's browser-rendering mode directly,
 * for callers that already know the page needs JS execution (skips the plain
 * fetch + uselessness check). Returns the JS-rendered HTML or null.
 *
 * @param waitFor optional: 'networkidle' | 'load' | a CSS selector to wait for.
 */
export async function fetchViaCloudflareBrowser(url: string, waitFor?: string): Promise<string | null> {
  return callCloudflareProxy(url, 'browser', waitFor);
}

import type { GenericExtractionMethod } from '../types';

/**
 * Extract product data from a URL. Races multiple strategies in parallel:
 * - Cloud Function (Google Cloud IPs — does fetch + parse + AI in one trip)
 * - Cloudflare Worker proxy (edge IPs — rarely blocked) + parseProductHtml
 * First successful result wins. Falls back to paste modal if all fail.
 *
 * The URL is always the key regardless of which method succeeds.
 *
 * `imageSelector` is the source's product-page `image` CSS override. It must be
 * passed to BOTH server paths: whichever of the Cloud Function / Cloudflare
 * race wins does the parsing, so plumbing it through only one of them would
 * make the override apply intermittently.
 */
export async function extractProductData(
  url: string,
  skipCache = false,
  method: GenericExtractionMethod = 'auto',
  imageSelector?: string,
): Promise<ExtractedProduct> {
  // Race: Cloud Function (does everything server-side) vs Cloudflare proxy (just fetches HTML)
  // Important: we DON'T call parseProductHtml in the race — only if CF wins and Cloud Function lost.
  // This avoids double AI calls.

  const cfHtmlHolder: { value: string | null } = { value: null };
  // Set when every automated fetch strategy failed to reach the origin, so the
  // final error can say "manual paste is the only way in" rather than the
  // generic "all extraction methods failed".
  const unreachableHolder = { value: false };

  const cssSelectorOverrides = imageSelector ? { image: imageSelector } : undefined;

  const cloudFunctionPromise = (async (): Promise<ExtractedProduct> => {
    const fn = httpsCallable<
      {
        url: string;
        skipCache?: boolean;
        method?: GenericExtractionMethod;
        cssSelectorOverrides?: { image?: string };
      },
      ExtractedProduct
    >(
      functions,
      'extractProductData',
      // 120s: bot-protected hosts fall through to the Cloud Function doing its
      // own fetch + parse + AI pass, which needs more headroom than 70s gave.
      { timeout: 120000 },
    );
    const result = await fn({ url, skipCache, method, cssSelectorOverrides });
    console.log('[extract] Cloud Function succeeded');
    return result.data;
  })();

  // Cloudflare just fetches the HTML in parallel (fast, no AI cost)
  const cfHtmlPromise = fetchViaCloudflareProxyDetailed(url).then((outcome) => {
    cfHtmlHolder.value = outcome.html;
    unreachableHolder.value = outcome.unreachable;
    return outcome.html;
  });

  // Try Cloud Function first — give it a chance to succeed
  try {
    const result = await cloudFunctionPromise;
    // An empty result (image-heavy page, nothing parseable) is treated as a
    // failure so we fall through to the other strategies rather than returning junk.
    if (!isEmptyExtraction(result)) return result;
    console.warn('[extract] Cloud Function returned an empty product, trying Cloudflare HTML...');
  } catch (cfErr) {
    console.warn('[extract] Cloud Function failed, checking Cloudflare HTML...', cfErr);
  }

  // Cloud Function failed — use the pre-fetched Cloudflare HTML if available
  if (!cfHtmlHolder.value) {
    // Wait for CF if it hasn't finished yet
    try {
      await cfHtmlPromise;
    } catch {
      console.warn('[extract] Cloudflare proxy also failed');
    }
  }

  // fetchViaCloudflareProxy already transparently upgrades shells/bot-challenges
  // to browser-rendered HTML, so by here cfHtmlHolder.value should be real
  // content; guard against an unusable shell slipping through.
  if (cfHtmlHolder.value && !isUselessHtml(cfHtmlHolder.value)) {
    console.log(`[extract] Using Cloudflare HTML (${cfHtmlHolder.value.length} chars), parsing server-side...`);
    try {
      const parsed = await parseProductHtml(cfHtmlHolder.value, url, skipCache, method, imageSelector);
      if (!isEmptyExtraction(parsed)) return parsed;
      console.warn('[extract] parseProductHtml returned an empty product, trying browser fetch...');
    } catch (parseErr) {
      console.warn('[extract] parseProductHtml failed:', parseErr);
    }
  }

  // Last resort: browser-side fetch (works if site allows CORS)
  try {
    const resp = await fetch(url, {
      credentials: 'omit',
      headers: { Accept: 'text/html,application/xhtml+xml,*/*' },
    });
    if (resp.ok) {
      const html = await resp.text();
      if (html.length > 100) {
        console.log(`[extract] Browser fetch succeeded (${html.length} chars), parsing server-side...`);
        const parsed = await parseProductHtml(html, url, skipCache, method, imageSelector);
        if (!isEmptyExtraction(parsed)) return parsed;
        // Parsed fine but the page has no usable product data — hand off to manual paste.
        throw new NeedsManualHtmlError(url, 'Extraction returned no products');
      }
    }
  } catch (corsErr) {
    if (corsErr instanceof NeedsManualHtmlError) throw corsErr;
    console.warn('[extract] Browser fetch blocked (likely CORS):', corsErr);
  }

  // All strategies failed
  throw new NeedsManualHtmlError(
    url,
    unreachableHolder.value
      ? 'Every automated fetch was blocked or timed out — the site is unreachable from our servers'
      : 'All extraction methods failed',
  );
}

/** Progress info passed to {@link extractProductDataWithBackoff}'s callback. */
export interface ExtractAttemptInfo {
  /** 1-based attempt number. 1 = first try; ≥2 = a retry. */
  attempt: number;
  /** Delay (ms) that preceded this attempt. 0 for the first attempt. */
  waitMs: number;
  /**
   * Which phase of the attempt this event marks:
   * - `'waiting'` — the backoff delay is running before the fetch fires.
   * - `'fetching'` — the delay is done and the actual fetch is in flight.
   *
   * A retry fires both events (waiting → fetching); the first attempt has no
   * delay so it fires `'fetching'` only.
   */
  phase: 'waiting' | 'fetching';
}

/**
 * Thrown by {@link extractProductDataWithBackoff} when the caller's
 * `shouldCancel` predicate goes true. Distinct from NeedsManualHtmlError so the
 * UI can tell "you stopped this" apart from "the site blocked us" — a stopped
 * URL is neither a failure nor a candidate for automatic retry.
 */
export class ExtractionCancelledError extends Error {
  constructor(public url: string) {
    super(`Extraction stopped for ${url}`);
    this.name = 'ExtractionCancelledError';
  }
}

/**
 * Cancellable sleep. The backoff waits reach 256s, so a plain `setTimeout`
 * promise makes Stop feel dead for minutes — the loop can only notice a cancel
 * between URLs. Polling every 250ms bounds the stop latency regardless of how
 * long the remaining wait is. Resolves `true` when the wait completed,
 * `false` when it was cut short by `shouldCancel`.
 */
async function cancellableDelay(
  waitMs: number,
  shouldCancel?: () => boolean,
): Promise<boolean> {
  if (!shouldCancel) {
    await new Promise<void>((resolve) => setTimeout(resolve, waitMs));
    return true;
  }
  const POLL_MS = 250;
  const deadline = Date.now() + waitMs;
  while (Date.now() < deadline) {
    if (shouldCancel()) return false;
    const remaining = deadline - Date.now();
    await new Promise<void>((resolve) =>
      setTimeout(resolve, Math.min(POLL_MS, remaining)),
    );
  }
  return !shouldCancel();
}

/**
 * Wraps {@link extractProductData} with exponential-backoff retry for
 * rate-limited sites (Cartier, Vacheron Constantin, …) — the pattern proven in
 * James's older .NET Core scraper.
 *
 * A delay is inserted ONLY after a failed attempt; a successful fetch fires
 * immediately. Each call starts fresh with no backoff carried in from a previous
 * URL, so callers drive one URL at a time sequentially and every URL gets its
 * full retry budget.
 *
 * Retry schedule (delay before each retry): 2s, 4s, 8s, 16s, 32s, 64s, 128s,
 * 256s — up to 8 retries (9 attempts total, ~510s worst case). After the final retry fails the
 * last error is rethrown unchanged, so the caller's existing catch classifies it
 * exactly as before (NeedsManualHtmlError → manual HTML paste; anything else →
 * hard failure). The manual-paste fallback therefore only kicks in once a URL has
 * exhausted every retry, not on the first failure.
 *
 * @param onAttempt Notified before each attempt (and before its preceding delay)
 *                  so the UI can show live progress.
 * @param shouldCancel Polled before each attempt AND during the backoff wait.
 *                  Once true, throws {@link ExtractionCancelledError} instead of
 *                  burning the remaining retry budget. Without this a Stop
 *                  button can't interrupt a 256s wait.
 * @param imageSelector The source's product-page `image` CSS override, passed
 *                  straight through to {@link extractProductData}. Appended last
 *                  deliberately — inserting it mid-signature would silently
 *                  shift onAttempt/shouldCancel at the existing call sites.
 */
export async function extractProductDataWithBackoff(
  url: string,
  skipCache = false,
  method: GenericExtractionMethod = 'auto',
  onAttempt?: (info: ExtractAttemptInfo) => void,
  shouldCancel?: () => boolean,
  imageSelector?: string,
): Promise<ExtractedProduct> {
  const MAX_RETRIES = 8;
  const BASE_DELAY_MS = 2000;
  let attempt = 0;
  for (;;) {
    if (shouldCancel?.()) throw new ExtractionCancelledError(url);
    // Delay only precedes retries; the very first attempt fires immediately.
    const waitMs = attempt > 0 ? BASE_DELAY_MS * 2 ** (attempt - 1) : 0; // 2s,4s,8s,16s,32s,64s,128s,256s
    if (waitMs > 0) {
      // Signal the wait, run it, then flip to the fetching phase so the UI can
      // show the slow fetch itself isn't more waiting.
      onAttempt?.({ attempt: attempt + 1, waitMs, phase: 'waiting' });
      const completed = await cancellableDelay(waitMs, shouldCancel);
      if (!completed) throw new ExtractionCancelledError(url);
    }
    onAttempt?.({ attempt: attempt + 1, waitMs, phase: 'fetching' });
    try {
      return await extractProductData(url, skipCache, method, imageSelector);
    } catch (err) {
      // A cancel that landed mid-fetch shouldn't be swallowed into a retry.
      if (shouldCancel?.()) throw new ExtractionCancelledError(url);
      attempt += 1;
      if (attempt > MAX_RETRIES) throw err; // hard fail — caller classifies the last error
    }
  }
}

/**
 * Parse raw HTML server-side. Used as fallback when the HTML was obtained
 * via paste or other means. URL is kept as the key/identifier.
 */
export async function parseProductHtml(
  html: string,
  url: string,
  skipCache = false,
  method: GenericExtractionMethod = 'auto',
  imageSelector?: string,
): Promise<ExtractedProduct> {
  const fn = httpsCallable<
    {
      html: string;
      url: string;
      skipCache?: boolean;
      method?: GenericExtractionMethod;
      cssSelectorOverrides?: { image?: string };
    },
    ExtractedProduct
  >(
    functions,
    'parseProductHtml',
    { timeout: 70000 },
  );
  const result = await fn({
    html,
    url,
    skipCache,
    method,
    cssSelectorOverrides: imageSelector ? { image: imageSelector } : undefined,
  });
  return result.data;
}

export interface ExtractedProductUrls {
  urls: string[];
  method: 'css-selector' | 'dom-links' | 'json-ld-images' | 'meta-tags' | 'none' | 'json-api';
}

export interface ExtractProductUrlsOverrides {
  productLink?: string;
}

/**
 * Given a collection/listing page URL, extract individual product page URLs.
 * Used as a fallback when sites rewrite the DOM (e.g. Tag Heuer's Kleecks overlay)
 * and standard product extraction yields nothing.
 *
 * If `cssSelectorOverrides.productLink` is provided, that selector is tried first
 * on the server before the standard JSON-LD/DOM/meta heuristics.
 */
/**
 * Fetch a JSON listing endpoint (e.g. AEM search_result.search.json) via the
 * Cloudflare proxy and parse product URLs from it, paginating on
 * `numberOfPages` (capped at 50). Mirrors the server parser; lives client-side
 * because the proxy (which bypasses Akamai's block of Google-Cloud IPs) is only
 * reachable from here. Returns absolute product-page URLs.
 */
async function fetchJsonListingViaProxy(jsonListingUrl: string): Promise<string[]> {
  const seen = new Set<string>();
  const out: string[] = [];
  let totalPages = 1;
  for (let page = 1; page <= totalPages && page <= 50; page++) {
    const pageUrl = (() => {
      const u = new URL(jsonListingUrl);
      u.searchParams.set('page', String(page));
      return u.toString();
    })();
    const body = await fetchViaCloudflareProxy(pageUrl);
    if (!body) break;
    let json: any;
    try {
      json = JSON.parse(body);
    } catch {
      break;
    }
    // Shape: { all: { hits: { hits: [...], numberOfPages } } } — "all" is the
    // default bucket; fall back to the first bucket with a hits container.
    const buckets = [json?.all, ...(json && typeof json === 'object' ? Object.values(json) : [])];
    let container: any = null;
    for (const b of buckets) {
      if (b && typeof b === 'object' && Array.isArray(b.hits?.hits)) {
        container = b.hits;
        break;
      }
    }
    if (!container) break;
    totalPages = Number(container.numberOfPages) || 1;
    const hits: any[] = container.hits;
    for (const h of hits) {
      // Prefer `_source['mapped-path']` (public .html URL) over `_source.path`
      // (internal AEM repo path).
      const mapped = h?._source?.['mapped-path'];
      if (typeof mapped === 'string' && mapped.trim()) {
        try {
          const abs = new URL(mapped.trim(), jsonListingUrl).toString();
          if (!seen.has(abs)) {
            seen.add(abs);
            out.push(abs);
          }
        } catch {
          /* skip */
        }
      }
    }
    if (hits.length === 0) break;
  }
  return out;
}

export async function extractProductUrls(
  url: string,
  cssSelectorOverrides?: ExtractProductUrlsOverrides,
  jsonListingUrl?: string,
): Promise<ExtractedProductUrls> {
  // JSON listing override — fetch the JSON endpoint via the proxy and parse
  // product URLs from it. Preferred for JS-rendered collection pages. Falls
  // back to the server (which can also honour jsonListingUrl) if the proxy
  // yields nothing.
  const jlu = jsonListingUrl?.trim();
  if (jlu) {
    try {
      const urls = dedupeProductUrls(await fetchJsonListingViaProxy(jlu));
      if (urls.length > 0) return { urls, method: 'json-api' };
    } catch (err) {
      console.warn('[extractProductUrls] JSON listing via proxy failed, falling back to server:', err);
    }
  }

  // 120s: matches extractProductData — the server-side fetch path for
  // bot-protected hosts needs the headroom.
  const fn = httpsCallable<
    {
      url: string;
      html?: string;
      cssSelectorOverrides?: ExtractProductUrlsOverrides;
      jsonListingUrl?: string;
    },
    ExtractedProductUrls
  >(functions, 'extractProductUrls', { timeout: 120000 });

  // Fetch HTML via Cloudflare worker first — bypasses Akamai/Cloudflare bot
  // detection that blocks Google Cloud egress IPs (e.g. alange-soehne.com).
  // If the proxy fails, fall back to letting the Cloud Function fetch directly.
  // (Skip the HTML pre-fetch when using a JSON listing — no HTML needed.)
  let html: string | undefined;
  let unreachable = false;
  if (!jlu) {
    try {
      const outcome = await fetchViaCloudflareProxyDetailed(url);
      if (outcome.html && outcome.html.length > 100) html = outcome.html;
      else unreachable = outcome.unreachable;
    } catch (err) {
      console.warn('[extractProductUrls] Cloudflare proxy fetch failed, falling back to server fetch:', err);
      unreachable = isUnreachableError(err);
    }
  }

  try {
    const result = await fn({ url, html, cssSelectorOverrides, jsonListingUrl: jlu });
    // The server dedupes by exact string; this also collapses URLs that differ
    // only by tracking params, trailing slash, or case.
    return { ...result.data, urls: dedupeProductUrls(result.data?.urls ?? []) };
  } catch (err) {
    // With no pre-fetched HTML the Cloud Function has to fetch the listing page
    // itself — the last automated option. If that also times out (or the proxy
    // path already reported the origin unreachable), nothing server-side will
    // ever load this page, so raise the manual-paste signal instead of leaking
    // a bare deadline-exceeded/CORS error the caller can't act on.
    if (!html && (unreachable || isUnreachableError(err))) {
      throw new NeedsManualHtmlError(
        url,
        'Listing page unreachable — the Cloudflare proxy and the server fetch were both blocked or timed out',
      );
    }
    throw err;
  }
}

// Console helpers for testing extraction
if (typeof window !== 'undefined') {
  (window as any).testExtract = async (url: string) => {
    console.log(`Testing extraction for: ${url}`);
    try {
      const result = await extractProductData(url);
      console.log('Extraction result:', result);
      console.table(
        Object.entries(result.fieldSources || {}).map(([field, source]) => ({
          field,
          source,
          value: (result as any)[field],
        }))
      );
      return result;
    } catch (err) {
      if (err instanceof NeedsManualHtmlError) {
        console.warn('Needs manual HTML paste. Use: testParseHtml(html, url)');
      }
      throw err;
    }
  };

  (window as any).testParseHtml = async (html: string, url: string) => {
    console.log(`Parsing HTML for: ${url} (${html.length} chars)`);
    try {
      const result = await parseProductHtml(html, url);
      console.log('Parse result:', result);
      console.table(
        Object.entries(result.fieldSources || {}).map(([field, source]) => ({
          field,
          source,
          value: (result as any)[field],
        }))
      );
      return result;
    } catch (err) {
      console.error('Parse failed:', err);
      throw err;
    }
  };
}

// AI enrichment — uses Claude to suggest values for empty reference fields
export interface AIEnrichmentResult {
  suggestions: Record<string, unknown>;
  confidence: Record<string, string>;
  /**
   * Year suggestions the callable dropped for predating the brand's founded
   * year (functions/src/enrichYearFloor.ts). Absent when nothing was dropped.
   */
  yearFloorRejections?: Array<{
    field: 'releaseYear' | 'startYear' | 'endYear';
    value: unknown;
    year: number;
    founded: number;
    reason: string;
  }>;
}

export async function enrichReferenceWithAI(
  referenceData: Record<string, unknown>,
  // The linked calibre's tier variants, when the ref has a calibre link and that
  // calibre defines tiers[]. Fed into the enrichReference {{CALIBRE_TIERS}}
  // prompt section so the model can suggest which tier applies to this ref.
  //
  // CALLER CONTRACT: pass a BRAND-SCOPED list — run the calibre's `tiers[]`
  // through `tiersForBrand(tiers, brandId)` first. A shared base movement
  // accumulates brand-specific tiers from every brand that modifies it, and
  // whatever lands here is what the model is offered: hand it the raw array and
  // it will occasionally pick another brand's tier off a loose textual cue.
  calibreTiers?: CalibreTier[],
  // Optional extra context gathered by the pre-enrichment dialog: text scraped
  // from the ref's product URL (sourceUrlContent), free-text notes typed by the
  // user (additionalContext), and the ref's product images with their tags
  // (taggedImages). All are handled server-side and omitted when empty.
  //
  // taggedImages makes the run MULTIMODAL: the callable fetches each image's
  // bytes and sends them as labelled image blocks, so the prompt can route a
  // field to the image that shows it — dialColor off the `dial` shot, crownType
  // off the `crown` shot. Send the primary image as tags ['primary'] and every
  // gallery image with its curator tags (an untagged image still helps — send
  // tags: []). Costs up to ~$0.005 per image; `maxImages` caps the spend
  // (default 5). Unfetchable URLs are skipped server-side and a run where every
  // image fails degrades to text-only, so callers never pre-validate them.
  options?: {
    sourceUrlContent?: string;
    additionalContext?: string;
    taggedImages?: Array<{ url: string; tags: string[] }>;
    maxImages?: number;
    /**
     * One-off model override from the pre-enrichment dialog. Omitted means the
     * model configured for enrichReference, which is what every bulk caller
     * wants; the callable falls back to it for anything it does not recognise.
     */
    model?: string;
    /**
     * 'variant' asks ONLY for the six variant-level fields (dialColor,
     * dialFinish, dialStyle, caseMaterial, strapMaterial, calibre), with
     * `parentData` (the parent's payload, same shape as referenceData) as
     * settled context and `variantLabel` (the shop's option name) as evidence.
     * The callable cuts the answer to those six keys. Omitted = full prompt.
     */
    mode?: 'full' | 'variant';
    parentData?: Record<string, unknown>;
    variantLabel?: string;
    /**
     * 'metadata' runs the enrichReferenceLight prompt (its own model, Haiku by
     * default): releaseYear, startYear, endYear and description only. The
     * callable sends no images or tiers and cuts the answer to those four
     * keys. Omitted = the full enrichReference profile.
     */
    profile?: 'full' | 'metadata';
    /** The ref's doc id — logged server-side, never sent to the model. */
    refId?: string;
  },
): Promise<AIEnrichmentResult> {
  const fn = httpsCallable<
    {
      referenceData: Record<string, unknown>;
      profile?: 'full' | 'metadata';
      refId?: string;
      mode?: 'full' | 'variant';
      parentData?: Record<string, unknown>;
      variantLabel?: string;
      calibreTiers?: CalibreTier[];
      sourceUrlContent?: string;
      additionalContext?: string;
      taggedImages?: Array<{ url: string; tags: string[] }>;
      maxImages?: number;
      model?: string;
    },
    AIEnrichmentResult
    // 120s to match the callable's own timeout — an image-enriched run fetches
    // every image's bytes server-side before a slower vision inference.
  >(functions, 'enrichReference', { timeout: 120000 });
  const sourceUrlContent = options?.sourceUrlContent?.trim();
  const additionalContext = options?.additionalContext?.trim();
  const taggedImages = (options?.taggedImages ?? []).filter((i) => i?.url?.trim());
  const result = await fn({
    referenceData,
    ...(calibreTiers && calibreTiers.length > 0 ? { calibreTiers } : {}),
    ...(sourceUrlContent ? { sourceUrlContent } : {}),
    ...(additionalContext ? { additionalContext } : {}),
    ...(taggedImages.length > 0 ? { taggedImages } : {}),
    ...(typeof options?.maxImages === 'number' ? { maxImages: options.maxImages } : {}),
    ...(options?.model?.trim() ? { model: options.model.trim() } : {}),
    ...(options?.profile === 'metadata' ? { profile: 'metadata' as const } : {}),
    ...(options?.refId ? { refId: options.refId } : {}),
    ...(options?.mode === 'variant'
      ? {
          mode: 'variant' as const,
          ...(options.parentData ? { parentData: options.parentData } : {}),
          ...(options.variantLabel?.trim() ? { variantLabel: options.variantLabel.trim() } : {}),
        }
      : {}),
  });
  return result.data;
}

// AI lookup-description suggestions — uses Claude to draft the `description`
// field for each value in a lookup collection (consumed by the Lookup Manager
// "Suggest descriptions with AI" button). Returns a slug -> description map.
export interface LookupDescriptionSuggestions {
  suggestions: Record<string, string>;
  // Map of value slug -> suggested rnQualifier phrase. Only present for
  // lookup_acquisition_directness; absent/empty for every other collection.
  rnQualifiers?: Record<string, string>;
  // Map of value slug -> suggested rnReverseLabel. Only present for
  // lookup_acquisition_types; absent/empty for every other collection.
  rnReverseLabels?: Record<string, string>;
  skipped: number;
  total: number;
}

export async function suggestLookupDescriptions(
  collectionName: string,
  options?: { collectionDisplayName?: string; onlyEmpty?: boolean },
): Promise<LookupDescriptionSuggestions> {
  const fn = httpsCallable<
    { collectionName: string; collectionDisplayName?: string; onlyEmpty?: boolean },
    LookupDescriptionSuggestions
  >(functions, 'suggestLookupDescriptions', { timeout: 120000 });
  const result = await fn({ collectionName, ...options });
  return result.data;
}

// AI calibre enrichment — uses Claude to identify a calibre from a (possibly
// mangled, partial, or non-English) name and return canonical name + specs.
/**
 * An AI-proposed calibre tier variant. Mirrors the enrichCalibre Cloud
 * Function's tier output shape — no `id` (the admin app assigns a
 * crypto.randomUUID when the user accepts the tier). `overrides` is the
 * whitelisted {@link CalibreTierOverrides} subset.
 */
export interface AIProposedCalibreTier {
  name: string;
  description?: string;
  overrides: CalibreTierOverrides;
}

export interface AICalibreEnrichmentResult {
  canonicalName: string;
  specs: Record<string, unknown>;
  description?: string;
  confidence: string;
  notes?: string;
  sourceUrls?: string[];
  /** AI-proposed tier variants — present only for calibres with real tiers. */
  tiers?: AIProposedCalibreTier[];
}

/**
 * Context the enrichment accepts alongside the calibre name.
 *
 * `existingTierNames` / `familyTierVocab` are the tier-name vocabulary the
 * curator already holds — the calibre's own tiers and its movement family's
 * canonical list. The callable folds both into the PREFER-these block of the
 * user message so the model reuses an established name rather than coining a
 * synonym for the same grade. Callers must scope `existingTierNames` to the
 * brand in play (`tiersForBrand`); passing a shared base movement's whole
 * `tiers[]` would offer the model another brand's tier names.
 *
 * Both are optional and frequently absent: the surfaces that enrich an
 * unmatched calibre STRING have no calibre document to read tiers off.
 */
export interface CalibreEnrichmentContext {
  brandName?: string;
  model?: string;
  description?: string;
  existingData?: Record<string, unknown>;
  referenceInfo?: string;
  existingTierNames?: string[];
  familyTierVocab?: string[];
  /**
   * Structured tier vocabulary. With either id the server loads the
   * manufacturer's list (or its nearest ancestor's) and the family's, and writes
   * MANUFACTURER / FAMILY / EXISTING tier-name lines; without both it sends the
   * two legacy lines. `brandId` is the brand in play — only that brand's brand
   * tiers are sent.
   */
  manufacturerId?: string;
  familyId?: string;
  brandId?: string;
  /** The calibre document, when there is one: a revision-line member gets a REVISION LINE block. */
  calibreId?: string;
}

export async function enrichCalibreWithAI(
  calibreName: string,
  context?: CalibreEnrichmentContext,
): Promise<AICalibreEnrichmentResult> {
  const fn = httpsCallable<
    { calibreName: string } & CalibreEnrichmentContext,
    AICalibreEnrichmentResult
  >(functions, 'enrichCalibre', { timeout: 120000 });
  // Empty arrays are dropped rather than sent: the callable treats an absent
  // list and an empty one identically, and omitting keeps the payload honest
  // about which surfaces actually have tier context.
  const trimmed: CalibreEnrichmentContext = { ...context };
  if (!trimmed.existingTierNames?.length) delete trimmed.existingTierNames;
  if (!trimmed.familyTierVocab?.length) delete trimmed.familyTierVocab;
  for (const k of ['manufacturerId', 'familyId', 'brandId', 'calibreId'] as const) {
    if (!trimmed[k]?.trim()) delete trimmed[k];
  }
  const result = await fn({ calibreName, ...trimmed });
  return result.data;
}

// AI brand enrichment — uses Claude to generate brand description, founded year, detect Shopify
export interface AIBrandEnrichmentResult {
  suggestions: Record<string, unknown>;
  confidence: Record<string, 'high' | 'medium' | 'low'>;
  isShopify?: boolean;
  /** Description proposals the callable withheld because they dropped a stored link. */
  descriptionLinkLoss?: DescriptionLinkLoss[];
}

export interface BrandDataForEnrichment {
  name: string;
  displayName?: string;
  country?: string;
  founded?: number;
  mainWebsite?: string;
  mainAddress?: string;
  description?: string;
  /** The stored rich description (HTML), so the model can keep its in-app links. */
  descriptionRich?: string;
  aiDescription?: string;
  information?: string;
  /**
   * Hint to the model on whether the brand is already configured as Shopify.
   * Derived from the canonical import-sources config (brandHasShopifySource),
   * NOT the legacy shopifyEnabled brand field. Field name kept as-is for the
   * enrichBrand Cloud Function contract.
   */
  shopifyEnabled?: boolean;
  /** Current dial-glyph description, so the model doesn't re-propose it. */
  glyphDescription?: string;
  // What the editor already holds for the fields the callable can now propose.
  // Hints only: they tell the model what is settled and what to look for.
  city?: string;
  stateRegion?: string;
  assemblyLocation?: string;
  designLocation?: string;
  instagramUrl?: string;
  ecommercePlatform?: string;
  isMicroBrand?: boolean;
  founder?: { name?: string };
  parentOrg?: string;
  status?: string;
  yearRevived?: number;
  ownershipType?: string;
  predecessorBrand?: string;
}

/**
 * Enrich a brand with AI. A full enrichment researches the brand with web
 * search, so it can take well over a minute — the client timeout sits just
 * above the callable's own 180s.
 *
 * @param websiteHtml @deprecated Ignored by the callable, which now does its
 *   own research. Still accepted, and still sent when given, so callers written
 *   against the old signature keep working for one release; pass `undefined`.
 * @param focusFields Narrows the call to a subset of fields (currently only
 *   `['glyphDescription']`, used by the inline Generate button in the brand
 *   editor). Omit for a full brand enrichment.
 */
/** ✨ on the bulk-import review grid: a reference's release year, from one web search. Never writes. */
export async function lookupRefReleaseYearWithAI(input: {
  brandId: string;
  refName?: string;
  refNumber?: string;
}): Promise<{ year?: number; confidence: 'high' | 'medium' | 'low'; evidence?: string }> {
  const fn = httpsCallable<typeof input, { year?: number; confidence: 'high' | 'medium' | 'low'; evidence?: string }>(
    functions,
    'lookupRefReleaseYear',
    { timeout: 70000 },
  );
  return (await fn(input)).data;
}

/** Admin analytics events (allow-listed server-side in functions/src/adminEvents.ts). */
export type AdminEventName =
  | 'calibre_revision_resolver_choice'
  | 'calibre_revision_reassigned'
  | 'variant_group_created'
  | 'variant_group_ungrouped';
export interface AdminEvent {
  name: AdminEventName;
  params: Record<string, string | number | boolean | null>;
}

/**
 * Log admin analytics events to `analytics_events` (via the logAdminEvents
 * callable). Best-effort: sent in chunks of 500, and a failure is logged,
 * never thrown — analytics must not block an import or a reassign.
 */
export async function logAdminEvents(events: AdminEvent[]): Promise<void> {
  if (events.length === 0) return;
  const fn = httpsCallable<{ events: AdminEvent[] }, { written: number; dropped: number }>(functions, 'logAdminEvents');
  try {
    for (let i = 0; i < events.length; i += 500) await fn({ events: events.slice(i, i + 500) });
  } catch (err) {
    console.warn('logAdminEvents failed (ignored):', err);
  }
}

export async function enrichBrandWithAI(
  brandData: BrandDataForEnrichment,
  websiteHtml?: string,
  focusFields?: string[],
  /** The curator's free-text hint from the Enrich dialog. */
  additionalContext?: string,
): Promise<AIBrandEnrichmentResult> {
  const fn = httpsCallable<
    {
      brandData: BrandDataForEnrichment;
      websiteHtml?: string;
      focusFields?: string[];
      additionalContext?: string;
    },
    AIBrandEnrichmentResult
  >(functions, 'enrichBrand', { timeout: 190000 });
  const hint = additionalContext?.trim();
  const result = await fn({ brandData, websiteHtml, focusFields, ...(hint ? { additionalContext: hint } : {}) });
  return result.data;
}

// AI movement manufacturer enrichment — same pattern as enrichBrand; longer timeout
// because the function runs at 540s server-side for slower model calls.
export interface AIMovementManufacturerEnrichmentResult {
  suggestions: Record<string, unknown>;
  confidence: Record<string, 'high' | 'medium' | 'low'>;
  /** Description proposals the callable withheld because they dropped a stored link. */
  descriptionLinkLoss?: DescriptionLinkLoss[];
}

export interface MovementManufacturerDataForEnrichment {
  name: string;
  displayName?: string;
  description?: string;
  /** The stored rich description (HTML), so the model can keep its in-app links. */
  descriptionRich?: string;
  aiDescription?: string;
  country?: string;
  founded?: number;
  website?: string;
  parentOrg?: string;
  city?: string;
  stateRegion?: string;
  assemblyLocation?: string;
  instagramUrl?: string;
  founder?: { name?: string };
  ownershipType?: 'independent' | 'family-owned' | 'private-equity' | 'public' | 'subsidiary';
  status?: 'active' | 'dormant' | 'defunct' | 'revived' | 'absorbed';
  yearRevived?: number;
  yearCeased?: number;
  predecessorManufacturer?: string;
}

/** One AI-proposed timeline event, before the curator reviews it. */
export interface AIProposedTimelineEvent {
  date: string;
  title: string;
  /** Rich-text HTML — the callable wraps the model's plain prose in <p>. */
  description: string;
  category?: string;
  sourceUrl?: string;
  /** Only ever `{ source, aiSuggestion }` — enrichment never supplies bytes. */
  image?: { source: string; aiSuggestion: string };
  /** 0..1, mapped from the model's band by the callable. */
  aiConfidence: number;
}

export interface AIHistoryResult {
  events: AIProposedTimelineEvent[];
  /** Events the callable discarded as unusable (no date, no title, over cap). */
  dropped: number;
}

/** @deprecated Prefer {@link AIHistoryResult} — brands return the same shape. */
export type AIManufacturerHistoryResult = AIHistoryResult;

/**
 * Propose a timeline for one brand. Same shape and same 540s budget as the
 * manufacturer equivalent — a 30-event history is a long generation.
 */
export async function enrichBrandHistoryWithAI(input: {
  brandId: string;
  additionalContext?: string[];
  sourceUrlContent?: string;
}): Promise<AIHistoryResult> {
  const fn = httpsCallable<typeof input, AIHistoryResult>(
    functions,
    'enrichBrandHistory',
    { timeout: 540000 },
  );
  const result = await fn(input);
  return result.data;
}

/**
 * Propose a timeline for one manufacturer. 540s timeout, matching the callable:
 * a 30-event history is a long generation.
 */
export async function enrichManufacturerHistoryWithAI(input: {
  manufacturerId: string;
  additionalContext?: string[];
  sourceUrlContent?: string;
}): Promise<AIHistoryResult> {
  const fn = httpsCallable<typeof input, AIHistoryResult>(
    functions,
    'enrichManufacturerHistory',
    { timeout: 540000 },
  );
  const result = await fn(input);
  return result.data;
}

/**
 * Propose a timeline for one parent organization. Same 540s timeout as the brand
 * and manufacturer siblings — a 30-event history is a long generation.
 */
export async function enrichParentOrgHistoryWithAI(input: {
  parentOrgId: string;
  additionalContext?: string[];
  sourceUrlContent?: string;
}): Promise<AIHistoryResult> {
  const fn = httpsCallable<typeof input, AIHistoryResult>(
    functions,
    'enrichParentOrgHistory',
    { timeout: 540000 },
  );
  const result = await fn(input);
  return result.data;
}

/** One AI-proposed ownership event, before its target has been resolved to an id. */
export interface AIOwnershipEvent {
  date: string;
  type:
    | 'acquired'
    | 'divested'
    | 'acquired_by'
    | 'divested_from'
    | 'founded'
    | 'absorbed'
    | 'renamed'
    | 'other';
  targetType: 'brand' | 'manufacturer' | 'parentOrg';
  /**
   * The company NAME the model proposed. Never an id: the model cannot know a
   * document slug, so the review dialog resolves this against the catalogue and
   * an event whose target does not resolve cannot be applied.
   */
  targetName: string;
  previousOwnerName?: string;
  description?: string;
  sourceUrl?: string;
  /** 0..1, mapped from the model's high/medium/low. */
  aiConfidence: number;
}

export interface AIOwnershipEventsResult {
  events: AIOwnershipEvent[];
  /** Events the callable dropped as unusable — no date, no target, bad enum. */
  dropped: number;
}

/**
 * Propose structured ownership events for one parent organization. 540s, like
 * the history callables: up to 40 events is a long generation.
 */
export async function enrichParentOrgOwnershipEventsWithAI(input: {
  parentOrgId: string;
  additionalContext?: string[];
  sourceUrlContent?: string;
}): Promise<AIOwnershipEventsResult> {
  const fn = httpsCallable<typeof input, AIOwnershipEventsResult>(
    functions,
    'enrichParentOrgOwnershipEvents',
    { timeout: 540000 },
  );
  const result = await fn(input);
  return result.data;
}

/** Per-field suggestions for a parent organization, with confidences. */
export interface AIParentOrgEnrichmentResult {
  suggestions: Record<string, unknown>;
  confidence: Record<string, 'high' | 'medium' | 'low'>;
  /** Description proposals the callable withheld because they dropped a stored link. */
  descriptionLinkLoss?: DescriptionLinkLoss[];
}

/** Propose corporate basics for one parent organization. */
export async function enrichParentOrgWithAI(input: {
  orgData: {
    name: string;
    displayName?: string;
    description?: string;
    country?: string;
    founded?: number;
    website?: string;
    alternativeNames?: string[];
    abbreviations?: string[];
    status?: string;
  };
  /** Pasted research, one block per source — treated as ground truth. */
  additionalContext?: string[];
}): Promise<AIParentOrgEnrichmentResult> {
  const fn = httpsCallable<typeof input, AIParentOrgEnrichmentResult>(
    functions,
    'enrichParentOrg',
    // Matches the callable's 120s: it researches with web_search first.
    { timeout: 120000 },
  );
  const result = await fn(input);
  return result.data;
}

export async function enrichMovementManufacturerWithAI(input: {
  manufacturerId?: string;
  manufacturerData?: MovementManufacturerDataForEnrichment;
  /** The curator's free-text hint from the Enrich dialog. */
  additionalContext?: string;
}): Promise<AIMovementManufacturerEnrichmentResult> {
  const fn = httpsCallable<
    { manufacturerId?: string; manufacturerData?: MovementManufacturerDataForEnrichment; additionalContext?: string },
    AIMovementManufacturerEnrichmentResult
  >(functions, 'enrichMovementManufacturer', { timeout: 540000 });
  const result = await fn(input);
  return result.data;
}

// Calibre slug mappings — maps user input (e.g. "ETA 7750") to known-good slug (e.g. "eta-valjoux-caliber-7750")
const CALIBRE_MAPPINGS_DOC = 'admin_config';
const CALIBRE_MAPPINGS_ID = 'calibre_slug_mappings';

export interface CalibreSlugMappings {
  [input: string]: string; // input text -> slug
}

export async function getCalibreSlugMappings(): Promise<CalibreSlugMappings> {
  const docRef = doc(db, CALIBRE_MAPPINGS_DOC, CALIBRE_MAPPINGS_ID);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return {};
  return (snap.data()?.mappings || {}) as CalibreSlugMappings;
}

export async function addCalibreSlugMapping(inputText: string, slug: string): Promise<void> {
  const docRef = doc(db, CALIBRE_MAPPINGS_DOC, CALIBRE_MAPPINGS_ID);
  await setDoc(docRef, {
    mappings: { [inputText]: slug },
    updatedAt: serverTimestamp(),
  }, { merge: true });

  // Auto-sync: trigger a lookup to pull CalibreCorner data into the local DB
  try {
    await lookupCalibre(inputText);
  } catch {
    // Non-fatal — mapping saved even if sync fails
  }
}

export async function deleteCalibreSlugMapping(inputText: string): Promise<void> {
  // Can't use dot-notation for keys containing dots (e.g. "Cal. ETA 2824")
  // Read the full mappings, remove the key, and write back
  const docRef = doc(db, CALIBRE_MAPPINGS_DOC, CALIBRE_MAPPINGS_ID);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return;
  const current = (snap.data()?.mappings || {}) as Record<string, string>;
  delete current[inputText];
  await setDoc(docRef, { mappings: current, updatedAt: serverTimestamp() }, { merge: true });
}

// =============================================================================
// Articles (Firestore)
// =============================================================================

const ARTICLES_COLLECTION = 'articles';

export async function getArticles(): Promise<Article[]> {
  const q = query(
    collection(db, ARTICLES_COLLECTION),
    orderBy('publishedDate', 'desc'),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as Article[];
}

export async function createArticle(
  data: Omit<Article, 'id'>,
): Promise<string> {
  const docRef = doc(collection(db, ARTICLES_COLLECTION));
  await setDoc(docRef, data);
  return docRef.id;
}

export async function updateArticle(
  id: string,
  data: Partial<Omit<Article, 'id'>>,
): Promise<void> {
  await updateDoc(doc(db, ARTICLES_COLLECTION, id), {
    ...data,
    updatedDate: Timestamp.now(),
  });
}

/**
 * Announce an article to every subscribed user — push, in-app and email.
 *
 * Server-side (watchlock `functions/src/articleNotifications.ts`), because the
 * send needs the Admin SDK to read every user and their preferences. The
 * callable is admin-gated and idempotent: it refuses an article it has already
 * announced, one that is disabled or in test mode, and one whose feature flag is
 * off for the app. Those refusals arrive as `functions/failed-precondition` with
 * a message written for the person who pressed the button, so callers should
 * surface `error.message` rather than a generic failure.
 *
 * `notifiedCount` is how many users were actually reached. Zero is a real
 * outcome — everyone opted out — and in that case the server deliberately does
 * NOT mark the article as announced, so the action stays available.
 */
export async function sendArticleNotification(
  articleId: string,
): Promise<{ notifiedCount: number }> {
  const fn = httpsCallable<
    { articleId: string },
    { success: boolean; notifiedCount: number }
  >(functions, 'sendArticleNotification');
  const res = await fn({ articleId });
  return { notifiedCount: res.data.notifiedCount };
}

export async function deleteArticle(id: string): Promise<void> {
  await deleteDoc(doc(db, ARTICLES_COLLECTION, id));
}

/**
 * One article by Firestore doc id. Returns null when it does not exist —
 * callers link articles by id and are expected to tolerate a stale one.
 */
export async function getArticleById(id: string): Promise<Article | null> {
  const snapshot = await getDoc(doc(db, ARTICLES_COLLECTION, id));
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as Article;
}

export async function getArticlesByAuthor(uid: string): Promise<Article[]> {
  const q = query(
    collection(db, ARTICLES_COLLECTION),
    where('createdByUid', '==', uid),
    orderBy('publishedDate', 'desc'),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as Article[];
}


// =============================================================================
// Import Field Rules (per-brand, persisted)
// =============================================================================

export interface PersistedFieldRule {
  sourceField: string;
  condition: string;
  value: string;
  targetField: string;
  targetValue: string;
  enabled: boolean;
}

export interface PersistedExtractionRule {
  sourceField: string;
  pattern: string;
  replaceWith?: string;
  targetField?: 'calibre' | 'movement' | 'module';
}

export interface PersistedExclusionRule {
  sourceField: string;
  condition: string;
  value: string;
  enabled: boolean;
}

export interface ImportConfig {
  rules: PersistedFieldRule[];
  extractionRules: PersistedExtractionRule[];
  exclusionRules: PersistedExclusionRule[];
}

export async function getImportConfigForBrand(brandId: string): Promise<ImportConfig> {
  const docRef = doc(db, 'admin_config', `import_rules_${brandId}`);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return { rules: [], extractionRules: [], exclusionRules: [] };
  const data = snap.data();
  return {
    rules: (data?.rules || []) as PersistedFieldRule[],
    extractionRules: (data?.extractionRules || []) as PersistedExtractionRule[],
    exclusionRules: (data?.exclusionRules || []) as PersistedExclusionRule[],
  };
}

// Backward compat wrapper
export async function getImportRulesForBrand(brandId: string): Promise<PersistedFieldRule[]> {
  const config = await getImportConfigForBrand(brandId);
  return config.rules;
}

export async function saveImportConfigForBrand(
  brandId: string,
  rules: PersistedFieldRule[],
  extractionRules: PersistedExtractionRule[],
  exclusionRules: PersistedExclusionRule[] = [],
): Promise<void> {
  const docRef = doc(db, 'admin_config', `import_rules_${brandId}`);
  await setDoc(docRef, { rules, extractionRules, exclusionRules, updatedAt: serverTimestamp() }, { merge: true });
}

// Backward compat wrapper
export async function saveImportRulesForBrand(brandId: string, rules: PersistedFieldRule[]): Promise<void> {
  const docRef = doc(db, 'admin_config', `import_rules_${brandId}`);
  await setDoc(docRef, { rules, updatedAt: serverTimestamp() }, { merge: true });
}

// ============================================================================
// Brand Import Sources (unified per-brand import config)
// ============================================================================

function generateImportSourceId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `src_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function legacyBrandToImportSources(brand: WatchBrand): ImportSourceConfig[] {
  const sources: ImportSourceConfig[] = [];

  const hasShopify = Boolean(brand.shopifyEnabled || brand.shopifyUrl);
  if (hasShopify) {
    const shopify: ShopifyImportSource = {
      id: generateImportSourceId(),
      kind: 'shopify',
      enabled: brand.shopifyEnabled !== false,
      storeUrl: brand.shopifyUrl || '',
      excludedProductTypes: brand.excludedProductTypes || [],
    };
    sources.push(shopify);
  }

  const productUrls = brand.productUrls || [];
  if (productUrls.length > 0 || brand.importSource === 'generic') {
    const generic: GenericUrlImportSource = {
      id: generateImportSourceId(),
      kind: 'generic',
      enabled: true,
      collectionPageUrls: [],
      productUrls,
      extractionMethod: 'auto',
    };
    sources.push(generic);
  }

  return sources;
}

/**
 * Read the brand's import sources doc. If it doesn't exist yet, lazily migrate
 * from the legacy fields on the brand doc, persist the new doc, and return it.
 * Returns null if the brand itself doesn't exist.
 */
export async function getBrandImportSources(
  brandId: string,
): Promise<BrandImportSources | null> {
  const docRef = doc(db, BRAND_IMPORT_SOURCES_COLLECTION, brandId);
  const snap = await getDoc(docRef);
  if (snap.exists()) {
    const data = snap.data();
    return {
      brandId,
      sources: (data?.sources || []) as ImportSourceConfig[],
      updatedAt: data?.updatedAt,
    };
  }

  // Lazy migration from legacy brand fields
  const brand = await getWatchBrandById(brandId);
  if (!brand) return null;
  const sources = legacyBrandToImportSources(brand);
  const migrated: BrandImportSources = { brandId, sources };
  await saveBrandImportSources(brandId, sources);
  return migrated;
}

/**
 * Read every brand's configured import sources in a single collection query,
 * keyed by brandId. Used by the Import Sources page to filter the brand list by
 * source kind (e.g. "Shopify only") without fetching each brand's doc one by
 * one. Brands that have never been migrated have no doc here — callers should
 * fall back to the legacy brand fields (shopifyEnabled / shopifyUrl) for those.
 */
export async function getAllBrandImportSources(): Promise<
  Map<string, ImportSourceConfig[]>
> {
  const snapshot = await getDocs(collection(db, BRAND_IMPORT_SOURCES_COLLECTION));
  const byBrand = new Map<string, ImportSourceConfig[]>();
  snapshot.forEach((d) => {
    const data = d.data();
    byBrand.set(d.id, (data?.sources || []) as ImportSourceConfig[]);
  });
  return byBrand;
}

/**
 * Single source of truth for "is this a Shopify brand?" in display/filter
 * surfaces. Canonical signal is a configured source of kind 'shopify' in the
 * brand's import-sources doc (loaded via getAllBrandImportSources). For brands
 * that have never been migrated to a sources doc, fall back to the legacy
 * shopifyEnabled / shopifyUrl fields so they still render correctly.
 *
 * Pass `sourcesByBrand: null` to skip the canonical check entirely (e.g. before
 * the map has loaded) — falls back to legacy fields only.
 */
export function brandHasShopifySource(
  brand: Pick<WatchBrand, 'id' | 'shopifyEnabled' | 'shopifyUrl'>,
  sourcesByBrand: Map<string, ImportSourceConfig[]> | null,
): boolean {
  const sources = sourcesByBrand?.get(brand.id);
  if (sources) return sources.some((s) => s.kind === 'shopify');
  return Boolean(brand.shopifyEnabled || brand.shopifyUrl);
}

export async function saveBrandImportSources(
  brandId: string,
  sources: ImportSourceConfig[],
): Promise<void> {
  const docRef = doc(db, BRAND_IMPORT_SOURCES_COLLECTION, brandId);
  const cleanSources = sources.map((s) =>
    stripUndefined(s as unknown as Record<string, unknown>),
  );
  await setDoc(
    docRef,
    { brandId, sources: cleanSources, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

/**
 * Append an import run record to the runs subcollection and update the
 * matching source's lastRun summary on the parent doc.
 */
export async function appendImportRun(
  brandId: string,
  sourceId: string,
  sourceKind: ImportSourceConfig['kind'],
  summary: Omit<ImportRunSummary, 'at'> & { triggeredBy?: string },
): Promise<string> {
  const runsRef = collection(
    db,
    BRAND_IMPORT_SOURCES_COLLECTION,
    brandId,
    BRAND_IMPORT_RUNS_SUBCOLLECTION,
  );
  // Parent/variant breakdown — included only when provided (older/non-variant
  // runs omit these). Firestore rejects undefined, so spread conditionally.
  const breakdown = {
    ...(summary.parentsImported !== undefined ? { parentsImported: summary.parentsImported } : {}),
    ...(summary.variantsImported !== undefined ? { variantsImported: summary.variantsImported } : {}),
    ...(summary.parentsUpdated !== undefined ? { parentsUpdated: summary.parentsUpdated } : {}),
    ...(summary.variantsUpdated !== undefined ? { variantsUpdated: summary.variantsUpdated } : {}),
  };

  const runDoc = await addDoc(runsRef, {
    sourceId,
    sourceKind,
    at: serverTimestamp(),
    status: summary.status,
    importedCount: summary.importedCount,
    updatedCount: summary.updatedCount,
    errorMessage: summary.errorMessage ?? null,
    triggeredBy: summary.triggeredBy ?? null,
    ...breakdown,
  });

  // Update lastRun on the matching source in the parent doc
  const existing = await getBrandImportSources(brandId);
  if (existing) {
    const updatedSources = existing.sources.map((s) =>
      s.id === sourceId
        ? {
            ...s,
            lastRun: {
              at: Timestamp.now(),
              status: summary.status,
              importedCount: summary.importedCount,
              updatedCount: summary.updatedCount,
              ...(summary.errorMessage ? { errorMessage: summary.errorMessage } : {}),
              ...breakdown,
            },
          }
        : s,
    );
    await saveBrandImportSources(brandId, updatedSources);
  }

  return runDoc.id;
}

export async function getImportRunsForBrand(
  brandId: string,
  max = 25,
): Promise<ImportRunRecord[]> {
  const runsRef = collection(
    db,
    BRAND_IMPORT_SOURCES_COLLECTION,
    brandId,
    BRAND_IMPORT_RUNS_SUBCOLLECTION,
  );
  const q = query(runsRef, orderBy('at', 'desc'), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      sourceId: data.sourceId,
      sourceKind: data.sourceKind,
      at: data.at,
      status: data.status,
      importedCount: data.importedCount ?? 0,
      updatedCount: data.updatedCount ?? 0,
      errorMessage: data.errorMessage ?? undefined,
      triggeredBy: data.triggeredBy ?? undefined,
      parentsImported: data.parentsImported ?? undefined,
      variantsImported: data.variantsImported ?? undefined,
      parentsUpdated: data.parentsUpdated ?? undefined,
      variantsUpdated: data.variantsUpdated ?? undefined,
    } as ImportRunRecord;
  });
}

export function createEmptyImportSource(
  kind: ImportSourceConfig['kind'],
): ImportSourceConfig {
  const base = { id: generateImportSourceId(), enabled: true };
  switch (kind) {
    case 'shopify':
      return {
        ...base,
        kind: 'shopify',
        storeUrl: '',
        excludedProductTypes: [],
        ignoreParentChildRelationships: false,
      };
    case 'generic':
      return {
        ...base,
        kind: 'generic',
        collectionPageUrls: [],
        productUrls: [],
        extractionMethod: 'auto',
      };
    case 'json-feed':
      return { ...base, kind: 'json-feed' };
    case 'manual':
      return { ...base, kind: 'manual' };
    case 'square-online':
      return { ...base, kind: 'square-online', categoryUrl: '' };
    case 'woocommerce':
      return { ...base, kind: 'woocommerce', categoryUrl: '' };
  }
}

/**
 * Fetch an image as a Blob, falling back to a Cloud Function proxy for CORS-blocked URLs.
 */
export async function fetchImageBlob(url: string): Promise<Blob> {
  return fetchImageBlobWith(url, {
    fetch: (...args) => fetch(...args),
    cloudflare: CF_PROXY_URL ? { url: CF_PROXY_URL, secret: CF_PROXY_SECRET } : undefined,
    proxyImage: async (target) => {
      const proxyImageFn = httpsCallable<{ url: string }, { base64: string; contentType: string }>(
        functions,
        'proxyImage'
      );
      return (await proxyImageFn({ url: target })).data;
    },
    log: (message, detail) => console.log(message, detail ?? ''),
  });
}

/** Extract a safe file extension from an image URL. Falls back to 'jpg'. */
export function getImageExtension(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split('/').pop() || '';
    const dotIdx = lastSegment.lastIndexOf('.');
    if (dotIdx > 0) {
      const ext = lastSegment.slice(dotIdx + 1).toLowerCase();
      if (/^(jpg|jpeg|png|gif|webp|avif|svg|bmp|tiff?)$/.test(ext)) return ext;
    }
  } catch { /* ignore */ }
  return 'jpg';
}

/**
 * Record the source image URL that failed to fetch for a reference so it can be
 * recovered later (via "Retry missing images" or a self-healing re-import).
 */
export async function setReferencePendingImageUrl(
  brandId: string,
  referenceId: string,
  sourceUrl: string,
): Promise<void> {
  const docRef = doc(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION, referenceId);
  await updateDoc(docRef, { pendingImageUrl: sourceUrl, updatedAt: Timestamp.now() });
}

/** Clear the pending-image recovery marker once an image has been attached. */
export async function clearReferencePendingImageUrl(
  brandId: string,
  referenceId: string,
): Promise<void> {
  const docRef = doc(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION, referenceId);
  await updateDoc(docRef, { pendingImageUrl: deleteField(), updatedAt: Timestamp.now() });
}

/**
 * Largest image we'll pull in from a URL.
 *
 * Neither Storage rules nor `uploadReferenceImage` cap this, so without a check
 * a mistyped URL pointing at a video or a print-resolution TIFF uploads happily
 * and only shows up later as a slow reference. Product photography doesn't come
 * close to this, so a hit is nearly always the wrong URL — which is the useful
 * thing to tell the curator.
 */
export const MAX_REFERENCE_IMAGE_BYTES = 20 * 1024 * 1024;

export { ReferenceImageFetchError };

/**
 * Fetch an image from a source URL and attach it to a reference: runs the full
 * {@link fetchImageBlob} 3-strategy chain (direct → Cloudflare worker → the
 * `proxyImage` callable, so a CORS-blocked brand site still works) →
 * {@link uploadReferenceImage}, then sets `images[0]` and clears the
 * `pendingImageUrl` marker.
 *
 * The written image carries `sourceUrl` — this URL is exactly the provenance
 * the field is for — and NO tags: `images[0]` is the whole watch, so the
 * gallery vocabulary doesn't apply to it (same rule as
 * `promoteGalleryImageToMain`).
 *
 * Throws if the fetch fails (caller keeps `pendingImageUrl` in place to retry).
 * A failure after the bytes land in Storage deletes them again rather than
 * leaving an orphan no screen can see.
 *
 * Returns the image record written, so a caller holding it in local state
 * doesn't have to re-read the doc.
 */
export async function attachReferenceImageFromUrl(
  brandId: string,
  referenceId: string,
  sourceUrl: string,
): Promise<ImageInfo> {
  const blob = await fetchImageBlob(sourceUrl);

  // A 404 that answers with an HTML error page is still a successful fetch, so
  // guard on the type rather than uploading the page as if it were a photo.
  // An empty type means the server declined to say; the upload is still worth
  // attempting, as it always has been.
  if (blob.type && !blob.type.startsWith('image/')) {
    throw new ReferenceImageFetchError(
      `That URL returned ${blob.type}, not an image`,
    );
  }
  if (blob.size > MAX_REFERENCE_IMAGE_BYTES) {
    throw new ReferenceImageFetchError(
      `That image is ${(blob.size / 1024 / 1024).toFixed(1)} MB — the limit is ${MAX_REFERENCE_IMAGE_BYTES / 1024 / 1024} MB`,
    );
  }

  const ext = getImageExtension(sourceUrl);
  const file = new File([blob], `image.${ext}`, { type: blob.type || 'image/jpeg' });
  const storagePath = await uploadReferenceImage(brandId, referenceId, file);
  const image: ImageInfo = {
    storagePath,
    contentType: file.type,
    index: 0,
    sourceUrl,
  };
  const docRef = doc(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION, referenceId);
  try {
    await updateDoc(docRef, {
      images: [image],
      pendingImageUrl: deleteField(),
      updatedAt: Timestamp.now(),
    });
  } catch (err) {
    // Nothing references these bytes now, so drop them. Best effort: the write
    // failure is what the caller needs to hear about.
    try {
      await deleteReferenceImage(storagePath);
    } catch {
      console.warn(`Left an orphaned upload at ${storagePath}`);
    }
    throw err;
  }
  return image;
}

/**
 * Returns references that need image recovery: those carrying a non-empty
 * `pendingImageUrl` (set when a bulk-import image fetch failed) AND still
 * missing an image. The `!= null` query relies on Firestore's automatic
 * single-field index; emptiness of `images` is filtered client-side since
 * Firestore can't query array emptiness directly.
 */
export async function getReferencesPendingImageRetry(
  brandId: string,
): Promise<WatchReference[]> {
  const q = query(
    collection(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION),
    where('pendingImageUrl', '!=', null),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((docSnap) => ({ id: docSnap.id, brandId, ...docSnap.data() }) as WatchReference)
    .filter((r) => !!r.pendingImageUrl && (!r.images || r.images.length === 0));
}

// ============================================================================
// AI Prompts Configuration
// ============================================================================

export type AIPromptType =
  | 'enrichReference'
  | 'enrichReferenceVariant'
  | 'enrichReferenceLight'
  | 'enrichBrand'
  | 'enrichCalibre'
  | 'enrichMovementManufacturer'
  | 'moduleEnrichment'
  | 'enrichWinder'
  | 'suggestLookupDescriptions'
  | 'suggestCalibreFamily'
  | 'suggestManufacturerTierVocabulary'
  | 'suggestCalibreLineage'
  | 'lookupRefReleaseYear'
  | 'enrichManufacturerHistory'
  | 'enrichBrandHistory'
  | 'enrichParentOrg'
  | 'enrichParentOrgHistory'
  | 'enrichParentOrgOwnershipEvents';

export interface AIPromptConfig {
  id: AIPromptType;
  name: string;
  description: string;
  systemPrompt: string;
  model?: string;
  inputFields?: Array<{
    name: string;
    description: string;
  }>;
  outputFields: Array<{
    name: string;
    type: string;
    description: string;
  }>;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

const AI_PROMPTS_DOC = 'admin_config';
const AI_PROMPTS_ID = 'ai_prompts';

export async function getAIPrompts(): Promise<Record<AIPromptType, AIPromptConfig>> {
  const docRef = doc(db, AI_PROMPTS_DOC, AI_PROMPTS_ID);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return {} as Record<AIPromptType, AIPromptConfig>;
  const data = snap.data();
  return (data?.prompts || {}) as Record<AIPromptType, AIPromptConfig>;
}

export async function getAIPrompt(promptType: AIPromptType): Promise<AIPromptConfig | null> {
  const prompts = await getAIPrompts();
  return prompts[promptType] || null;
}

export async function saveAIPrompt(promptType: AIPromptType, config: Partial<AIPromptConfig>): Promise<void> {
  const docRef = doc(db, AI_PROMPTS_DOC, AI_PROMPTS_ID);
  await setDoc(docRef, {
    prompts: {
      [promptType]: {
        ...config,
        id: promptType,
        updatedAt: serverTimestamp(),
      },
    },
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ---------------------------------------------------------------------------
// AI Prompt version history
//
// Every prompt save snapshots the *previous* live text into a subcollection at
// admin_config/ai_prompts/versions/{autoId}. Snapshots are immutable and let an
// admin view / restore earlier prompt language after an overwrite.
// ---------------------------------------------------------------------------

const AI_PROMPT_VERSIONS_SUBCOLLECTION = 'versions';

export interface AIPromptVersion {
  id: string;
  /** Which prompt this snapshot belongs to (matches AIPromptType). */
  promptKey: AIPromptType;
  /** The full previous systemPrompt text, captured before it was overwritten. */
  promptText: string;
  savedAt: Timestamp | null;
  /** Auth uid of the user who triggered the save that produced this snapshot. */
  savedBy: string;
  savedByEmail?: string;
  /** Optional user-entered note describing what changed. */
  note?: string;
}

/**
 * Write an immutable snapshot of a prompt's previous text into the version
 * history subcollection. Call this with the *live* value BEFORE overwriting it.
 */
export async function snapshotAIPromptVersion(
  promptKey: AIPromptType,
  promptText: string,
  note?: string,
): Promise<void> {
  const versionsRef = collection(
    db,
    AI_PROMPTS_DOC,
    AI_PROMPTS_ID,
    AI_PROMPT_VERSIONS_SUBCOLLECTION,
  );
  const uid = auth.currentUser?.uid ?? 'unknown';
  const email = auth.currentUser?.email ?? null;
  const trimmedNote = note?.trim();
  await addDoc(versionsRef, {
    promptKey,
    promptText,
    savedAt: serverTimestamp(),
    savedBy: uid,
    // Firestore rejects undefined — only include optional fields when present.
    ...(email ? { savedByEmail: email } : {}),
    ...(trimmedNote ? { note: trimmedNote } : {}),
  });
}

/**
 * List all version snapshots for a single prompt, newest first.
 *
 * Orders by savedAt (a single-field, auto-indexed query) and filters by
 * promptKey client-side so no composite index deploy is required.
 */
export async function listAIPromptVersions(promptKey: AIPromptType): Promise<AIPromptVersion[]> {
  const versionsRef = collection(
    db,
    AI_PROMPTS_DOC,
    AI_PROMPTS_ID,
    AI_PROMPT_VERSIONS_SUBCOLLECTION,
  );
  const snap = await getDocs(query(versionsRef, orderBy('savedAt', 'desc')));
  return snap.docs
    .map((d): AIPromptVersion => {
      const data = d.data();
      return {
        id: d.id,
        promptKey: data.promptKey as AIPromptType,
        promptText: (data.promptText as string) ?? '',
        savedAt: (data.savedAt as Timestamp | null) ?? null,
        savedBy: (data.savedBy as string) ?? 'unknown',
        savedByEmail: data.savedByEmail as string | undefined,
        note: data.note as string | undefined,
      };
    })
    .filter((v) => v.promptKey === promptKey);
}

// ============================================================================
// Lookup Algolia Exclusions
// ============================================================================

const LOOKUP_EXCLUSIONS_COLLECTION = 'lookup_exclusions';

export async function getExclusions(lookupType: string): Promise<string[]> {
  const docId = lookupType.replace(/^lookup_/, '');
  const snap = await getDoc(doc(db, LOOKUP_EXCLUSIONS_COLLECTION, docId));
  return snap.exists() ? (snap.data().excludedValues || []) : [];
}

export async function setExclusions(lookupType: string, values: string[]): Promise<void> {
  const docId = lookupType.replace(/^lookup_/, '');
  await setDoc(
    doc(db, LOOKUP_EXCLUSIONS_COLLECTION, docId),
    { excludedValues: values },
    { merge: true },
  );
}

// ============================================================================
// Curated Collections
// ============================================================================

const CURATED_COLLECTIONS_COLLECTION = 'curatedCollections';

export async function getCuratedCollections(): Promise<CuratedCollection[]> {
  const snapshot = await getDocs(collection(db, CURATED_COLLECTIONS_COLLECTION));
  const collections = snapshot.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      ...data,
      filterCriteria: deserializeFilterCriteria(data.filterCriteria),
    };
  }) as CuratedCollection[];
  collections.sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999));
  return collections;
}

export async function saveCuratedCollection(
  id: string | null,
  data: Partial<CuratedCollection>,
): Promise<string> {
  const { id: _ignored, ...rest } = data;
  // Strip undefined values — Firestore rejects them
  const clean: Record<string, any> = Object.fromEntries(
    Object.entries(rest).filter(([, v]) => v !== undefined)
  );
  // Firestore doesn't support nested arrays — serialize facetFilters as JSON string
  if (clean.filterCriteria && typeof clean.filterCriteria === 'object' && clean.filterCriteria.facetFilters) {
    clean.filterCriteria = {
      ...clean.filterCriteria,
      facetFilters: JSON.stringify(clean.filterCriteria.facetFilters),
    };
  }
  const now = Timestamp.now();
  if (id) {
    const docRef = doc(db, CURATED_COLLECTIONS_COLLECTION, id);
    await updateDoc(docRef, { ...clean, updatedAt: now });
    return id;
  }
  const docRef = await addDoc(collection(db, CURATED_COLLECTIONS_COLLECTION), {
    ...clean,
    createdAt: now,
    updatedAt: now,
  });
  return docRef.id;
}

/** Deserialize facetFilters from Firestore (stored as JSON string) */
function deserializeFilterCriteria(criteria: any): CuratedCollection['filterCriteria'] {
  if (!criteria) return undefined;
  return {
    ...criteria,
    facetFilters: typeof criteria.facetFilters === 'string'
      ? JSON.parse(criteria.facetFilters)
      : criteria.facetFilters,
  };
}

export async function deleteCuratedCollection(id: string): Promise<void> {
  const docRef = doc(db, CURATED_COLLECTIONS_COLLECTION, id);
  await deleteDoc(docRef);
}

// ============================================================================
// Movement Manufacturers (Phase 2: first-class entity)
// ============================================================================

const MOVEMENT_MANUFACTURERS_COLLECTION = 'movement_manufacturers';

/** Mirrors the WatchBrand slug derivation (createWatchBrand). */
export function deriveManufacturerSlug(name: string): string {
  return deriveBrandSlug(name);
}

/**
 * The whole manufacturer collection, name-ordered, as WHOLE documents.
 *
 * Prefer {@link getManufacturerCatalogue} — this reads all 176 documents from
 * Firestore and only three surfaces still need to. Kept (and still exported)
 * because those three genuinely do: the fields they read live nowhere else. See
 * `MANUFACTURER_FIELDS_NOT_INDEXED` for the list and the reasons.
 */
export async function getMovementManufacturers(): Promise<MovementManufacturer[]> {
  const q = query(collection(db, MOVEMENT_MANUFACTURERS_COLLECTION), orderBy('name'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as MovementManufacturer[];
}

/**
 * The manufacturer catalogue as every dropdown and `id → name` map in the admin
 * consumes it: from `watchkeeper_manufacturers`, falling back to the Firestore
 * collection read when Algolia is unconfigured or unreachable.
 *
 * Fails SOFT, unlike the calibre and brand listings. Those throw on an Algolia
 * error so their caller can decide; here the fallback is unconditional, because
 * every consumer of this function is a picker or a label resolver where the
 * failure mode is a dropdown with no options or an id rendered as a raw slug —
 * a wrong answer rather than a slow one.
 *
 * The return type is deliberately NARROWER than `MovementManufacturer`: the
 * index carries no `brandIdsManualInclude`, `brandIdsDerivedFromRefs`,
 * `brandExclusions`, `status` or `heritageBrandIds`, so a caller that needs one
 * of those gets a compile error pointing it back at
 * `getMovementManufacturers()` instead of a silently-undefined field. The
 * Firestore fallback returns whole documents that would satisfy those reads by
 * accident, which is exactly why the type — not the runtime value — is what
 * enforces the boundary.
 */
export async function getManufacturerCatalogue(): Promise<
  ManufacturerCatalogueEntry[]
> {
  if (isAlgoliaConfigured()) {
    try {
      // The overlay is what makes a just-created manufacturer visible before
      // the Algolia trigger has indexed it — see
      // `manufacturerCatalogueOverlay.ts`. Applied on the Firestore fallback
      // too, so both paths agree about membership.
      return applyManufacturerOverlay(await fetchManufacturerCatalogue());
    } catch (err) {
      console.warn(
        '[manufacturers] Algolia catalogue fetch failed, falling back to Firestore',
        err,
      );
    }
  }
  return applyManufacturerOverlay(await getMovementManufacturers());
}

export async function getMovementManufacturerById(
  id: string,
): Promise<MovementManufacturer | null> {
  const docRef = doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as MovementManufacturer;
}

export async function createMovementManufacturer(
  m: Omit<MovementManufacturer, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const id = deriveManufacturerSlug(m.name);
  if (!id) {
    throw new Error('Manufacturer name must contain alphanumerics');
  }
  const docRef = doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id);
  const existing = await getDoc(docRef);
  if (existing.exists()) {
    throw new Error(`Manufacturer with ID "${id}" already exists`);
  }
  const now = Timestamp.now();
  await setDoc(docRef, {
    ...stripUndefined(m as Record<string, unknown>),
    createdAt: now,
    updatedAt: now,
  });

  // The document exists NOW; `watchkeeper_manufacturers` will not know about it
  // for another second or two. Everything below closes that gap: the overlay
  // makes any catalogue fetch include it meanwhile, and the notification tells
  // surfaces already mounted elsewhere — the calibre listing, an open module
  // editor, the movement panel — to refetch instead of waiting for a remount
  // that, for most of them, only a page reload would cause.
  recordCreatedManufacturer(toCatalogueEntry(id, m, now));
  notifyManufacturerCatalogueChanged();
  return id;
}

/**
 * Project a manufacturer being written onto the shape the catalogue serves.
 *
 * Field by field rather than a spread: `MovementManufacturer` is WIDER than
 * `ManufacturerCatalogueEntry` (description, aiDescription, needsReview and the
 * image-provenance fields are all unindexed — see
 * `MANUFACTURER_FIELDS_NOT_INDEXED`), and a spread would put them on an overlay
 * entry, making it answer reads that a fetched entry answers as `undefined`.
 * The whole point of the narrow type is that those reads fail to compile.
 *
 * Undefined values are left OFF rather than set to `undefined`, matching
 * `manufacturerHitToEntry`, so an overlaid entry and an indexed one agree about
 * which keys are present.
 */
function toCatalogueEntry(
  id: string,
  m: Omit<MovementManufacturer, 'id' | 'createdAt' | 'updatedAt'>,
  now: Timestamp,
): ManufacturerCatalogueEntry {
  const entry: ManufacturerCatalogueEntry = {
    id,
    name: m.name,
    createdAt: now,
    updatedAt: now,
  };
  if (m.displayName) entry.displayName = m.displayName;
  if (m.alternativeNames?.length) entry.alternativeNames = m.alternativeNames;
  if (m.abbreviations?.length) entry.abbreviations = m.abbreviations;
  if (m.country) entry.country = m.country;
  if (m.parentOrg) entry.parentOrg = m.parentOrg;
  if (m.parentOrgId) entry.parentOrgId = m.parentOrgId;
  if (m.parentManufacturerId) entry.parentManufacturerId = m.parentManufacturerId;
  if (m.website) entry.website = m.website;
  if (m.logo) entry.logo = m.logo;
  if (m.heroImage) entry.heroImage = m.heroImage;
  // Carried across including `false` — `classifyCalibreGeneric` turns on the
  // difference between an explicit false and an absent flag.
  if (typeof m.isGeneric === 'boolean') entry.isGeneric = m.isGeneric;
  if (m.primaryBrandId) entry.primaryBrandId = m.primaryBrandId;
  if (typeof m.founded === 'number') entry.founded = m.founded;
  if (m.status) entry.status = m.status;
  if (m.brandIdsManualInclude?.length) {
    entry.brandIdsManualInclude = m.brandIdsManualInclude;
  }
  if (m.brandIdsDerivedFromRefs?.length) {
    entry.brandIdsDerivedFromRefs = m.brandIdsDerivedFromRefs;
  }
  if (m.brandExclusions?.length) entry.brandExclusions = m.brandExclusions;
  if (m.heritageBrandIds?.length) entry.heritageBrandIds = m.heritageBrandIds;
  return entry;
}

export async function updateMovementManufacturer(
  id: string,
  updates: Partial<Omit<MovementManufacturer, 'id'>>,
): Promise<void> {
  await updateDoc(doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id), {
    ...stripUndefined(updates as Record<string, unknown>),
    updatedAt: Timestamp.now(),
  });
}

export async function deleteMovementManufacturer(id: string): Promise<void> {
  await deleteDoc(doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id));
  // The mirror of the create case: the index serves the deleted record until
  // the trigger runs, so without this a list refreshed immediately after a
  // delete would show the row it had just removed.
  recordDeletedManufacturer(id);
  notifyManufacturerCatalogueChanged();
}

// ============================================================================
// Parent organizations — first-class entity, Phase 2 (admin CRUD + seed)
// ============================================================================
//
// `parent_organizations/{id}` mirrors `movement_manufacturers`: the doc id IS
// the slug, the admin reads and writes it directly, and nothing outside the
// admin reads it yet. Brand and manufacturer `parentOrg` strings are NOT touched
// here — pointing them at these docs is Phase 3.
//
// No Algolia index and no catalogue overlay, unlike manufacturers: the
// collection is a couple of dozen documents, so every consumer reads Firestore.
//
// STORAGE: every upload below lands at exactly
// `parent_organizations/{id}/{field}/{file}` — one path segment for `{field}` —
// because that is the shape of the watchlock storage rule that allows the
// write. A deeper path would 403, as `brands/**` once did.

export const PARENT_ORGANIZATIONS_COLLECTION = 'parent_organizations';

/** Every parent organization, name-ordered. */
export async function getParentOrganizations(): Promise<ParentOrganization[]> {
  const q = query(collection(db, PARENT_ORGANIZATIONS_COLLECTION), orderBy('name'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as ParentOrganization[];
}

/**
 * Brands and manufacturers whose `parentOrgId` FOREIGN KEY points at this
 * organization.
 *
 * Returns nothing today, on purpose. `parentOrgId` exists on both types and is
 * written by nobody until Phase 3's backfill — it is not even an attribute on
 * either Algolia index, which is why this is the one part of the portfolio that
 * cannot come from search. Two equality queries on a field no document carries
 * cost a query each and return zero docs, and on the day the backfill runs the
 * portfolio starts filling in with no further change here.
 *
 * Single-field equality, so Firestore's automatic index covers it — no
 * composite index to deploy.
 */
export async function getEntitiesByParentOrgId(parentOrgId: string): Promise<{
  brands: Array<{ id: string; name: string }>;
  manufacturers: Array<{ id: string; name: string }>;
}> {
  if (!parentOrgId) return { brands: [], manufacturers: [] };
  const read = async (collectionName: string) => {
    const snapshot = await getDocs(
      query(collection(db, collectionName), where('parentOrgId', '==', parentOrgId)),
    );
    return snapshot.docs.map((d) => {
      const data = d.data() as { name?: string; displayName?: string };
      return { id: d.id, name: data.name || data.displayName || d.id };
    });
  };
  const [brands, manufacturers] = await Promise.all([
    read(WATCH_BRANDS_COLLECTION),
    read(MOVEMENT_MANUFACTURERS_COLLECTION),
  ]);
  return { brands, manufacturers };
}

export async function getParentOrganizationById(id: string): Promise<ParentOrganization | null> {
  const snap = await getDoc(doc(db, PARENT_ORGANIZATIONS_COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as ParentOrganization;
}

/**
 * Create a parent organization under an explicit slug id.
 *
 * CREATE-ONLY: an existing document is never overwritten. Returns `false`
 * rather than throwing when the id is taken, because the seed treats "already
 * there" as the idempotent no-op it is — a second run creates nothing and a
 * curator's edits survive it.
 */
export async function createParentOrganization(
  id: string,
  org: Omit<ParentOrganization, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<boolean> {
  if (!id) throw new Error('A parent organization needs an id');
  const docRef = doc(db, PARENT_ORGANIZATIONS_COLLECTION, id);
  if ((await getDoc(docRef)).exists()) return false;
  const now = Timestamp.now();
  await setDoc(docRef, {
    ...stripUndefined(org as Record<string, unknown>),
    createdAt: now,
    updatedAt: now,
  });
  return true;
}

/**
 * The patch shape {@link updateParentOrganization} accepts. `null` clears a
 * field.
 *
 * Why null rather than undefined: `stripUndefined` drops undefined keys and
 * `updateDoc` merges, so an undefined value would leave the OLD value in place
 * — a cleared website that looks saved and isn't. `null` is turned into a
 * `deleteField()` sentinel, which actually removes the key.
 */
export type ParentOrganizationPatch = {
  [K in keyof Omit<ParentOrganization, 'id' | 'createdAt' | 'updatedAt'>]?:
    | Omit<ParentOrganization, 'id' | 'createdAt' | 'updatedAt'>[K]
    | null;
};

export async function updateParentOrganization(
  id: string,
  updates: ParentOrganizationPatch,
): Promise<void> {
  const payload: Record<string, unknown> = { updatedAt: Timestamp.now() };
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) continue;
    if (value === null) {
      payload[key] = deleteField();
    } else if (key === 'ownershipEvents' && Array.isArray(value)) {
      payload[key] = (value as ParentOrgOwnershipEvent[]).map(cleanOwnershipEvent);
    } else {
      payload[key] = value;
    }
  }
  await updateDoc(doc(db, PARENT_ORGANIZATIONS_COLLECTION, id), payload);
}

/**
 * One ownership event with every undefined key removed, image included.
 *
 * `stripUndefined` is shallow and the events are written as a whole array, so an
 * event built as `{ sourceUrl: undefined }` would reach `updateDoc` intact and
 * Firestore would reject the entire save — the `historyHeroImage.url` bug (#573)
 * one level deeper.
 */
function cleanOwnershipEvent(event: ParentOrgOwnershipEvent): ParentOrgOwnershipEvent {
  const cleaned = withoutUndefined(event);
  if (cleaned.image) cleaned.image = withoutUndefined(cleaned.image);
  return cleaned;
}

export async function deleteParentOrganization(id: string): Promise<void> {
  await deleteDoc(doc(db, PARENT_ORGANIZATIONS_COLLECTION, id));
}

/**
 * Upload a parent organization's logo and return its public download URL —
 * stored as a URL, exactly as manufacturer and brand logos are, because it is
 * rendered directly.
 */
export async function uploadParentOrganizationLogo(
  parentOrgId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  const storagePath = await uploadTimestampedImage(
    `${PARENT_ORGANIZATIONS_COLLECTION}/${parentOrgId}/logo/`,
    file,
    fileName,
  );
  return getDownloadURL(ref(storage, storagePath));
}

/** Upload a parent org's history hero image; returns its storage path. */
export async function uploadParentOrganizationHistoryHeroImage(
  parentOrgId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  return uploadTimestampedImage(
    `${PARENT_ORGANIZATIONS_COLLECTION}/${parentOrgId}/history-hero/`,
    file,
    fileName,
  );
}

/** Upload one timeline-event image; returns its storage path. */
export async function uploadParentOrganizationTimelineImage(
  parentOrgId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  return uploadTimestampedImage(
    `${PARENT_ORGANIZATIONS_COLLECTION}/${parentOrgId}/timeline/`,
    file,
    fileName,
  );
}

/** Upload one ownership-event image; returns its storage path. */
export async function uploadParentOrganizationOwnershipImage(
  parentOrgId: string,
  file: File | Blob,
  fileName?: string,
): Promise<string> {
  return uploadTimestampedImage(
    `${PARENT_ORGANIZATIONS_COLLECTION}/${parentOrgId}/ownership-events/`,
    file,
    fileName,
  );
}

// --- Manufacturer Data Health quick actions (Phase 3b) ----------------------
// Single-field, immediate writes fired from the per-manufacturer Data Health
// section's action buttons. Each uses a FieldValue sentinel (arrayUnion /
// arrayRemove) or a plain field set so concurrent edits merge rather than
// clobber, bumps updatedAt, and invalidates the manufacturer index cache.
// Callers do optimistic UI; these just persist.

/** Add a brand to `brandIdsManualInclude` (idempotent via arrayUnion). */
export async function promoteManufacturerBrand(
  id: string,
  brandId: string,
): Promise<void> {
  const value = brandId.trim();
  if (!value) return;
  await updateDoc(doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id), {
    brandIdsManualInclude: arrayUnion(value),
    updatedAt: Timestamp.now(),
  });
  invalidateManufacturerIndex();
}

/** Remove a brand from `brandIdsManualInclude` (idempotent via arrayRemove). */
export async function demoteManufacturerBrand(
  id: string,
  brandId: string,
): Promise<void> {
  const value = brandId.trim();
  if (!value) return;
  await updateDoc(doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id), {
    brandIdsManualInclude: arrayRemove(value),
    updatedAt: Timestamp.now(),
  });
  invalidateManufacturerIndex();
}

/** Add a brand to `brandExclusions` (idempotent via arrayUnion). */
export async function excludeManufacturerBrand(
  id: string,
  brandId: string,
): Promise<void> {
  const value = brandId.trim();
  if (!value) return;
  await updateDoc(doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id), {
    brandExclusions: arrayUnion(value),
    updatedAt: Timestamp.now(),
  });
  invalidateManufacturerIndex();
}

/** Remove a brand from `brandExclusions` (idempotent via arrayRemove). */
export async function unexcludeManufacturerBrand(
  id: string,
  brandId: string,
): Promise<void> {
  const value = brandId.trim();
  if (!value) return;
  await updateDoc(doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id), {
    brandExclusions: arrayRemove(value),
    updatedAt: Timestamp.now(),
  });
  invalidateManufacturerIndex();
}

/** Set (or clear, when brandId is empty) the primary brand. */
export async function setManufacturerPrimaryBrand(
  id: string,
  brandId: string,
): Promise<void> {
  const value = brandId.trim();
  await updateDoc(doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id), {
    primaryBrandId: value ? value : deleteField(),
    updatedAt: Timestamp.now(),
  });
  invalidateManufacturerIndex();
}

/** Set the `isGeneric` classification flag on a single manufacturer. */
export async function setManufacturerIsGeneric(
  id: string,
  isGeneric: boolean,
): Promise<void> {
  await updateDoc(doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id), {
    isGeneric,
    updatedAt: Timestamp.now(),
  });
  invalidateManufacturerIndex();
}

/** Count of custom_calibres carrying this manufacturerId (after backfill). */
export async function getCalibresCountForManufacturer(
  manufacturerId: string,
): Promise<number> {
  const q = query(
    collection(db, CUSTOM_CALIBRES_COLLECTION),
    where('manufacturerId', '==', manufacturerId),
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

/**
 * Live count of references linked to a calibre by `calibreId`, across every
 * brand (`collectionGroup('references')`). Read before a rename so the editor
 * can warn; the stored `referenceCount` is not recomputed after merges, so it
 * is not trusted here.
 */
export async function getReferenceCountForCalibre(calibreId: string): Promise<number> {
  const q = query(collectionGroup(db, REFERENCES_SUBCOLLECTION), where('calibreId', '==', calibreId));
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

/**
 * Batched calibre-count lookup keyed by manufacturerId. One Firestore read of
 * the full `custom_calibres` collection, tallied client-side. Suitable while
 * the calibres corpus is small (hundreds); switch to a server-side aggregation
 * if it grows to thousands+.
 */
export async function getCalibreCountsByManufacturerId(): Promise<Map<string, number>> {
  const snap = await getDocs(collection(db, CUSTOM_CALIBRES_COLLECTION));
  const counts = new Map<string, number>();
  for (const d of snap.docs) {
    const mid = (d.data() as { manufacturerId?: string | null }).manufacturerId;
    if (!mid) continue;
    counts.set(mid, (counts.get(mid) ?? 0) + 1);
  }
  return counts;
}

/**
 * No-op retained for callers that previously invalidated the in-process
 * manufacturer-string→id cache. The cache was dropped in Phase 5 when
 * `calibre.manufacturer` (string) was removed in favor of `manufacturerId`.
 */
export function invalidateManufacturerIndex(): void {
  // intentionally empty
}

// =============================================================================
// Calibre Families (Phase 3) — movement-family groupings under a manufacturer.
// Firestore: calibre_families/{id}. Synced to the `watchkeeper_calibre_families`
// Algolia index server-side via the syncCalibreFamilyToAlgolia trigger.
// =============================================================================

const CALIBRE_FAMILIES_COLLECTION = 'calibre_families';

/**
 * Cross-manufacturer lineage pointer (e.g. Sellita SW200 inspired by ETA 2824).
 * Points at another family OR a specific calibre. Mirrors the shared-types
 * `CalibreFamilyInspiredBy` shape exactly — we persist only the structured FK,
 * not any display label (labels are resolved on read).
 */
export interface CalibreFamilyInspiredBy {
  type: 'family' | 'calibre';
  /** When type === 'family' */
  familyId?: string;
  /** When type === 'calibre' */
  calibreId?: string;
  /** Always set, for display + the consumer to resolve a label. */
  manufacturerId: string;
}

/**
 * Functional-competitor pointer — a family this family competes with in the same
 * tier / use case / spec class (market positioning, not lineage; cf.
 * `CalibreFamilyInspiredBy`). Same structural FK shape; a family holds an array
 * since it usually competes with several rivals. Mirrors the shared-types
 * `CalibreFamilyFunctionalCompetitor` exactly — only the FK is persisted, the
 * display label is resolved on read.
 */
export interface CalibreFamilyFunctionalCompetitor {
  type: 'family' | 'calibre';
  /** When type === 'family' */
  familyId?: string;
  /** When type === 'calibre' */
  calibreId?: string;
  /** Always set, for display + the consumer to resolve a label. */
  manufacturerId: string;
}

export interface CalibreFamilyEntry {
  id: string;
  manufacturerId: string;
  name: string;
  displayName: string;
  slug: string;
  description?: string;
  baseCalibreId?: string;
  inspiredBy?: CalibreFamilyInspiredBy;
  functionalCompetitor?: CalibreFamilyFunctionalCompetitor[];
  /**
   * Canonical tier name vocabulary for this family. Member calibres draw
   * tier names from this list to prevent drift ("Chronomètre" vs
   * "Chronometer" across siblings). Empty/undefined = free-text tier
   * names on member calibres (backwards-compatible).
   */
  tierNames?: string[];
  /**
   * Structured family tier vocabulary (shared-types v1.140.0): additions to the
   * manufacturer's list for this family, incl. brand tiers (`brandId`). Written
   * only by {@link mutateFamilyTierVocabulary}, never by the family form's Save.
   */
  tierVocabulary?: TierVocabularyEntry[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** All calibre families for a manufacturer, sorted by name (client-side). */
export async function getCalibreFamiliesForManufacturer(
  manufacturerId: string,
): Promise<CalibreFamilyEntry[]> {
  const q = query(
    collection(db, CALIBRE_FAMILIES_COLLECTION),
    where('manufacturerId', '==', manufacturerId),
  );
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as CalibreFamilyEntry[];
  return rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * Calibres whose primary `manufacturerId` is this manufacturer. Drives the base
 * calibre picker, member list, and member-add picker on the Families panel.
 * (Joint-authorship `additionalManufacturerIds` are intentionally excluded —
 * family membership tracks the primary maker, matching the count helpers.)
 */
export async function getCalibresForManufacturer(
  manufacturerId: string,
): Promise<CustomCalibreEntry[]> {
  const q = query(
    collection(db, CUSTOM_CALIBRES_COLLECTION),
    where('manufacturerId', '==', manufacturerId),
  );
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as CustomCalibreEntry[];
  return rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * Every calibre a manufacturer contributes to — its primary `manufacturerId`
 * matches OR its `additionalManufacturerIds` co-developer array contains the id.
 * Two queries merged + deduped (Firestore can't OR across fields), mirroring
 * {@link getElectronicModulesForManufacturer}. This is the calibre set the
 * `deriveManufacturerBrands` derivation folds a ref's brand into, so it's the set
 * the Data Health brand drill-down scans against.
 */
export async function getCalibresForManufacturerIncludingCoDeveloped(
  manufacturerId: string,
): Promise<CustomCalibreEntry[]> {
  const [primarySnap, additionalSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, CUSTOM_CALIBRES_COLLECTION),
        where('manufacturerId', '==', manufacturerId),
      ),
    ),
    getDocs(
      query(
        collection(db, CUSTOM_CALIBRES_COLLECTION),
        where('additionalManufacturerIds', 'array-contains', manufacturerId),
      ),
    ),
  ]);
  const byId = new Map<string, CustomCalibreEntry>();
  for (const d of [...primarySnap.docs, ...additionalSnap.docs]) {
    byId.set(d.id, { id: d.id, ...d.data() } as CustomCalibreEntry);
  }
  return [...byId.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/** Single family by doc id (used to resolve an inspired-by label on edit-open). */
export async function getCalibreFamilyById(
  id: string,
): Promise<CalibreFamilyEntry | null> {
  const snap = await getDoc(doc(db, CALIBRE_FAMILIES_COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as CalibreFamilyEntry;
}

export interface CalibreFamilyDraft {
  manufacturerId: string;
  name: string;
  displayName: string;
  description?: string;
  baseCalibreId?: string;
  inspiredBy?: CalibreFamilyInspiredBy;
  functionalCompetitor?: CalibreFamilyFunctionalCompetitor[];
  /** Canonical tier name vocabulary for the family (see CalibreFamilyEntry). */
  tierNames?: string[];
}

/**
 * Create or update a calibre family. Slugifies `name`, defaults `displayName`
 * to "<name> Family", and server-validates name-uniqueness within the
 * manufacturer (rejects if another family in the same manufacturer already owns
 * the name). On edit, optional fields left empty are cleared via deleteField so
 * the doc doesn't retain stale values. Returns the family doc id.
 *
 * When `opts.claimBaseAsMember` is set, the family's `baseCalibreId` calibre is
 * also made a *member* of this family (its `familyId` is pointed here) in the
 * SAME batch as the family write — so a base calibre can never be shown as the
 * base without actually belonging to the family. Because `familyId` is singular,
 * this also removes the calibre from any prior family; the editor confirms that
 * move with the user before requesting it.
 */
export async function saveCalibreFamily(
  draft: CalibreFamilyDraft,
  existingId?: string,
  opts?: { claimBaseAsMember?: boolean },
): Promise<string> {
  const name = draft.name.trim();
  if (!name) throw new Error('Family name is required');
  if (!draft.manufacturerId) throw new Error('Manufacturer is required');

  const slug = deriveManufacturerSlug(name);
  const displayName = draft.displayName.trim() || `${name} Family`;

  // Uniqueness check within the manufacturer (case-sensitive exact match on the
  // canonical name). Reject if a *different* family already owns it.
  const dupQ = query(
    collection(db, CALIBRE_FAMILIES_COLLECTION),
    where('manufacturerId', '==', draft.manufacturerId),
    where('name', '==', name),
  );
  const dupSnap = await getDocs(dupQ);
  const conflict = dupSnap.docs.find((d) => d.id !== existingId);
  if (conflict) {
    throw new Error(`A family named "${name}" already exists for this manufacturer`);
  }

  // The family write and the optional base-membership write commit atomically in
  // one batch, so the base calibre can never be pointed at a family that failed
  // to persist (and vice versa).
  const batch = writeBatch(db);
  let familyId: string;

  if (existingId) {
    // Merge-write set fields; explicitly clear cleared-out optionals.
    batch.set(
      doc(db, CALIBRE_FAMILIES_COLLECTION, existingId),
      {
        manufacturerId: draft.manufacturerId,
        name,
        displayName,
        slug,
        description: draft.description?.trim() ? draft.description.trim() : deleteField(),
        baseCalibreId: draft.baseCalibreId || deleteField(),
        inspiredBy: draft.inspiredBy ?? deleteField(),
        functionalCompetitor: draft.functionalCompetitor?.length
          ? draft.functionalCompetitor
          : deleteField(),
        tierNames: draft.tierNames?.length ? draft.tierNames : deleteField(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    familyId = existingId;
  } else {
    const ref = doc(collection(db, CALIBRE_FAMILIES_COLLECTION));
    batch.set(
      ref,
      stripUndefined({
        manufacturerId: draft.manufacturerId,
        name,
        displayName,
        slug,
        description: draft.description?.trim() || undefined,
        baseCalibreId: draft.baseCalibreId || undefined,
        inspiredBy: draft.inspiredBy ?? undefined,
        functionalCompetitor: draft.functionalCompetitor?.length
          ? draft.functionalCompetitor
          : undefined,
        tierNames: draft.tierNames?.length ? draft.tierNames : undefined,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
    );
    familyId = ref.id;
  }

  // Ensure the chosen base calibre is also a member of this family. `familyId`
  // is singular, so this moves it here from any prior family.
  if (opts?.claimBaseAsMember && draft.baseCalibreId) {
    batch.update(doc(db, CUSTOM_CALIBRES_COLLECTION, draft.baseCalibreId), {
      familyId,
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();
  return familyId;
}

// ============================================================================
// Tier-name vocabulary (manufacturer + family) — design memo P1b
// ============================================================================
//
// Both lists save immediately, one change per transaction (D4), the way the
// timeline does: re-read the stored list, apply the change, re-validate, write.
// Neither form's main Save touches `tierVocabulary`, so the promote prompt and
// the AI dialog can write to a list from outside its form without a held-back
// copy overwriting them.

type TierVocabularyChange = (current: TierVocabularyEntry[]) => TierVocabularyEntry[];

type TierVocabularyListener = (owner: 'manufacturer' | 'family', id: string, next: TierVocabularyEntry[]) => void;
const tierVocabularyListeners = new Set<TierVocabularyListener>();

/**
 * Hear about every vocabulary write in this tab. The manufacturer section and
 * the family editor sit on one page and both write the manufacturer list (the
 * promote prompt), so each keeps the other current through this.
 */
export function onTierVocabularyChanged(listener: TierVocabularyListener): () => void {
  tierVocabularyListeners.add(listener);
  return () => tierVocabularyListeners.delete(listener);
}

async function mutateTierVocabulary(
  owner: 'manufacturer' | 'family',
  collectionName: string,
  id: string,
  change: TierVocabularyChange,
  reserved?: () => Promise<TierVocabularyEntry[]>,
): Promise<TierVocabularyEntry[]> {
  const reservedEntries = reserved ? await reserved() : [];
  const ref = doc(db, collectionName, id);
  const next = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error(`${owner === 'manufacturer' ? 'Manufacturer' : 'Family'} ${id} no longer exists`);
    const current = (snap.data().tierVocabulary as TierVocabularyEntry[] | undefined) ?? [];
    const updated = change(current);
    const { errors } = validateTierVocabulary(updated, reservedEntries);
    if (errors.length > 0) throw new Error(errors[0]);
    tx.update(ref, {
      tierVocabulary: updated.length > 0 ? updated : deleteField(),
      updatedAt: owner === 'manufacturer' ? Timestamp.now() : serverTimestamp(),
    });
    return updated;
  });
  for (const l of tierVocabularyListeners) l(owner, id, next);
  return next;
}

/** One transactional change to `movement_manufacturers/{id}.tierVocabulary`. */
export function mutateManufacturerTierVocabulary(
  manufacturerId: string,
  change: TierVocabularyChange,
): Promise<TierVocabularyEntry[]> {
  return mutateTierVocabulary('manufacturer', MOVEMENT_MANUFACTURERS_COLLECTION, manufacturerId, change);
}

/**
 * One transactional change to `calibre_families/{id}.tierVocabulary`. The
 * manufacturer's generic names are validated against too, so a family BRAND
 * tier can never take a grade's name.
 */
export function mutateFamilyTierVocabulary(
  familyId: string,
  manufacturerId: string,
  change: TierVocabularyChange,
): Promise<TierVocabularyEntry[]> {
  return mutateTierVocabulary('family', CALIBRE_FAMILIES_COLLECTION, familyId, change, () =>
    getManufacturerTierVocabulary(manufacturerId),
  );
}

/** The manufacturer's own stored list (no inheritance). */
export async function getManufacturerTierVocabulary(manufacturerId: string): Promise<TierVocabularyEntry[]> {
  return (await getManufacturerTierNamesContext(manufacturerId)).entries;
}

/** Display name + own stored list — what the family editor's promote prompt needs. */
export async function getManufacturerTierNamesContext(
  manufacturerId: string,
): Promise<{ name: string; entries: TierVocabularyEntry[] }> {
  const snap = await getDoc(doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, manufacturerId));
  const d = snap.exists() ? snap.data() : undefined;
  return {
    name: (d?.displayName as string | undefined) || (d?.name as string | undefined) || manufacturerId,
    entries: (d?.tierVocabulary as TierVocabularyEntry[] | undefined) ?? [],
  };
}

export type TierVocabConfidence = 'high' | 'medium' | 'low';

/** One row of the `suggestManufacturerTierVocabulary` response (already checked server-side). */
export interface SuggestedTierVocabEntry {
  canonicalName: string;
  akas: string[];
  rank?: number;
  description?: string;
  /** Set ⇒ the row only adds `akas` to that existing entry. */
  matchesExistingEntryId?: string;
  /** The model's spelling when it differs from the matched entry — a note, never applied. */
  preferredSpelling?: string;
  evidence: { sourceUrls: string[]; catalogueNames: string[] };
  confidence: TierVocabConfidence;
  /** Catalogue spellings this row covers. */
  covers: string[];
}

export interface SuggestTierVocabResult {
  publishesGradeLadder: boolean;
  tiers: SuggestedTierVocabEntry[];
  notes?: string;
  uncoveredSpellings: string[];
  droppedCount: number;
}

/**
 * ✨ Suggest tier names. Never writes — the review dialog's Merge & Save does.
 * Fails with "AI prompt … not found" until the slot is saved in Configuration →
 * AI Prompts.
 */
// ---------------------------------------------------------------------------
// suggestCalibreLineage (calibre lineage Phase 5)
// ---------------------------------------------------------------------------
// Response shape mirrors functions/src/calibreLineageSuggest.ts; already
// validated and curator-guarded server-side. The admin guards again
// (services/lineageSuggestionGuard.ts) against the values it has loaded.

export type LineageSuggestionConfidence = 'high' | 'medium' | 'low';

export interface LineageSuggestionMember {
  calibreId: string;
  revisionLabel?: string;
  order: number;
  activeFromYear?: number;
  activeUntilYear?: number;
  confidence: LineageSuggestionConfidence;
  evidence: string;
}

export type LineageCuratorField =
  | 'calibreLineageId'
  | 'revisionLabel'
  | 'activeFromYear'
  | 'activeUntilYear'
  | 'isCurrent'
  | 'isLineagePlaceholder';

export interface LineageCuratorConflict {
  calibreId: string;
  field: LineageCuratorField;
  curatorValue: string | number | boolean | null;
  proposedValue: string | number | boolean | null;
}

export interface SuggestCalibreLineageResult {
  verdict: 'revision-line' | 'tiers' | 'peers' | 'mixed';
  verdictReason: string;
  baseLabel?: string;
  /** A sentence for the line description naming its predecessor/successor number. */
  successorNote?: string;
  members: LineageSuggestionMember[];
  current: { calibreId: string; confidence: LineageSuggestionConfidence; reasoning: string } | null;
  knownMissingRevisions: { label: string; activeFromYear?: number; activeUntilYear?: number; evidence: string }[];
  stemAliases: string[];
  curatorConflicts: LineageCuratorConflict[];
  /**
   * Further SEPARATE lines from the same candidates (parallel variants such as
   * ETA 6498-2 beside 6498 / 6498-1). Each is a NEW line. Optional: absent
   * from a function deployed before this field existed.
   */
  additionalLines?: LineageLineProposal[];
}

export interface LineageLineProposal {
  baseLabel?: string;
  lineReason: string;
  successorNote?: string;
  members: LineageSuggestionMember[];
  current: SuggestCalibreLineageResult['current'];
  curatorConflicts: LineageCuratorConflict[];
}

export async function suggestCalibreLineageWithAI(input: {
  manufacturerId: string;
  lineageId?: string;
  calibreIds?: string[];
  additionalContext?: string;
}): Promise<SuggestCalibreLineageResult> {
  const fn = httpsCallable<typeof input, SuggestCalibreLineageResult>(functions, 'suggestCalibreLineage', {
    timeout: 120000,
  });
  const res = await fn(input);
  return res.data;
}

export async function suggestManufacturerTierVocabularyWithAI(input: {
  manufacturerId: string;
  additionalContext?: string;
}): Promise<SuggestTierVocabResult> {
  const fn = httpsCallable<typeof input, SuggestTierVocabResult>(
    functions,
    'suggestManufacturerTierVocabulary',
    { timeout: 120000 },
  );
  const res = await fn(input);
  return res.data;
}

/**
 * Delete a family and clear `familyId` off every member calibre in one batch.
 * Pass the current member ids (the caller already has them loaded).
 */
export async function deleteCalibreFamily(
  familyId: string,
  memberCalibreIds: string[],
): Promise<void> {
  const batch = writeBatch(db);
  for (const cid of memberCalibreIds) {
    batch.update(doc(db, CUSTOM_CALIBRES_COLLECTION, cid), {
      familyId: deleteField(),
      updatedAt: serverTimestamp(),
    });
  }
  batch.delete(doc(db, CALIBRE_FAMILIES_COLLECTION, familyId));
  await batch.commit();
}

/**
 * Assign (familyId set) or remove (familyId null → deleteField) a calibre's
 * family membership. Member writes happen immediately, independent of the
 * family's own Save.
 */
export async function setCalibreFamilyMembership(
  calibreId: string,
  familyId: string | null,
): Promise<void> {
  await updateDoc(doc(db, CUSTOM_CALIBRES_COLLECTION, calibreId), {
    familyId: familyId ?? deleteField(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Add a calibre to one of its manufacturer's families from the *calibre* editor
 * — the mirror of the family editor's member picker. Membership is tracked
 * solely by `familyId` on the calibre doc (there is no `memberIds` array on the
 * family — the family editor derives its members by querying calibres), so the
 * reciprocal write is just setting `familyId`. When `asBase` is set we also point
 * the family's `baseCalibreId` at this calibre (replacing any existing base).
 * Both writes commit atomically in one batch.
 */
export async function addCalibreToFamily(
  calibreId: string,
  familyId: string,
  opts?: { asBase?: boolean },
): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(db, CUSTOM_CALIBRES_COLLECTION, calibreId), {
    familyId,
    updatedAt: serverTimestamp(),
  });
  if (opts?.asBase) {
    batch.update(doc(db, CALIBRE_FAMILIES_COLLECTION, familyId), {
      baseCalibreId: calibreId,
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
}

/**
 * Remove a calibre from its family (calibre-editor side). Clears the calibre's
 * `familyId` and, if this calibre was the family's `baseCalibreId`, clears that
 * too — the family is left with no base until an admin picks a new one. The base
 * check reads the family doc first, then both writes commit atomically.
 */
export async function removeCalibreFromFamily(
  calibreId: string,
  familyId: string,
): Promise<void> {
  const fam = await getCalibreFamilyById(familyId);
  const batch = writeBatch(db);
  batch.update(doc(db, CUSTOM_CALIBRES_COLLECTION, calibreId), {
    familyId: deleteField(),
    updatedAt: serverTimestamp(),
  });
  if (fam?.baseCalibreId === calibreId) {
    batch.update(doc(db, CALIBRE_FAMILIES_COLLECTION, familyId), {
      baseCalibreId: deleteField(),
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
}

export type CalibreFamilyConfidence = 'high' | 'medium' | 'low';

export interface SuggestCalibreFamilyInput {
  manufacturerId: string;
  familyName: string;
  memberCalibreIds?: string[];
  /**
   * The curator's current draft, sent so the model can defer to it rather than
   * re-proposing a generic guess over researched values. The callable renders
   * these into a CURATOR VALUES block the system prompt treats as ground truth.
   * All optional — a brand-new family sends none of them.
   */
  existingDescription?: string;
  /** Resolved display name of the chosen base calibre, not its id. */
  existingBaseCalibre?: string;
  /** Resolved display label of the inspired-by reference, not its id. */
  existingInspiredBy?: string;
  /** Resolved display labels of competitors already on the family. */
  existingCompetitors?: string[];
}

/**
 * Shape of the `suggestCalibreFamily` callable's response (Phase 3). Each field
 * is independently suggested with its own confidence. `inspiredBy.hint` is a
 * raw string for v1 — the admin renders it as a label and the user picks a real
 * reference via the inspired-by picker. Fields are typed loosely / optionally
 * and parsed defensively in the UI to tolerate response-shape drift.
 */
export interface SuggestCalibreFamilyResult {
  description?: { value: string; confidence?: CalibreFamilyConfidence };
  baseCalibre?: {
    calibreId?: string;
    name?: string;
    confidence?: CalibreFamilyConfidence;
  };
  inspiredBy?: { hint: string; confidence?: CalibreFamilyConfidence };
  /**
   * Functional competitors the AI proposes (Phase 3 / functional-competitor).
   * Always-present array (defaults `[]`) from the callable. Suggestions usually
   * arrive as free-text `name` + `manufacturer` with no resolved id, so the admin
   * resolves each to a structured FK via the inspired-by Algolia typeahead before
   * adding it. Typed loosely / optionally and parsed defensively to tolerate
   * response-shape drift.
   */
  functionalCompetitor?: FunctionalCompetitorSuggestion[];
}

/**
 * One AI-proposed functional competitor. The callable may already resolve a
 * `familyId` / `calibreId`, but typically returns only free-text `name` +
 * `manufacturer` for the admin to resolve against the Algolia index.
 */
export interface FunctionalCompetitorSuggestion {
  familyId?: string;
  calibreId?: string;
  name?: string;
  manufacturer?: string;
  confidence?: CalibreFamilyConfidence;
  reasoning?: string;
}

/**
 * Ask Claude to suggest a family's description, base calibre, and inspired-by
 * lineage. Does not write — the admin applies suggestions per-field, then saves.
 */
export async function suggestCalibreFamily(
  input: SuggestCalibreFamilyInput,
): Promise<SuggestCalibreFamilyResult> {
  const fn = httpsCallable<SuggestCalibreFamilyInput, SuggestCalibreFamilyResult>(
    functions,
    'suggestCalibreFamily',
    { timeout: 120000 },
  );
  const res = await fn(input);
  return res.data;
}

export type CalibreManufacturerIdAuditResult = {
  calibresScanned: number;
  calibresMissingManufacturerId: number;
  calibresWithManufacturerStringButNoId: number;
  calibresWithoutManufacturerString: number;
  topManufacturerStrings: Array<{ value: string; count: number }>;
};

export async function callAuditCalibresMissingManufacturerId(): Promise<CalibreManufacturerIdAuditResult> {
  const fn = httpsCallable<unknown, CalibreManufacturerIdAuditResult>(
    functions,
    'auditCalibresMissingManufacturerId',
    { timeout: 540000 },
  );
  const result = await fn({});
  return result.data;
}

export type RefsUnresolvedCalibreRow = {
  brandId: string;
  brandDisplayName: string;
  calibreString: string;
  refCount: number;
  sampleRefIds: string[];
};

export type RefsUnresolvedCalibreByBrand = {
  brandDisplayName: string;
  totalUnresolved: number;
  distinctCalibres: number;
};

export type RefsUnresolvedCalibreAuditResult = {
  refsScanned: number;
  refsWithoutCalibre: number;
  refsResolved: number;
  refsUnresolved: number;
  /** Deprecated mirror — kept one release. Prefer rows[] for new code. */
  topUnresolvedCalibres: Array<{ value: string; count: number }>;
  rows: RefsUnresolvedCalibreRow[];
  byBrand: Record<string, RefsUnresolvedCalibreByBrand>;
};

export async function callAuditRefsUnresolvedCalibre(): Promise<RefsUnresolvedCalibreAuditResult> {
  const fn = httpsCallable<unknown, RefsUnresolvedCalibreAuditResult>(
    functions,
    'auditRefsUnresolvedCalibre',
    { timeout: 540000 },
  );
  const result = await fn({});
  return result.data;
}

export type CalibresUnresolvedBrandIdRow = {
  calibreId: string;
  calibreDisplayName: string;
  brandIdRaw: string;
  brandNameLegacy: string;
  suggestedBrandId?: string;
  suggestedBrandDisplayName?: string;
};

export type CalibresUnresolvedBrandIdAuditResult = {
  scanned: number;
  alreadyResolved: number;
  unresolved: number;
  rows: CalibresUnresolvedBrandIdRow[];
};

export async function callAuditCalibresWithUnresolvedBrandId(): Promise<CalibresUnresolvedBrandIdAuditResult> {
  const fn = httpsCallable<unknown, CalibresUnresolvedBrandIdAuditResult>(
    functions,
    'auditCalibresWithUnresolvedBrandId',
    { timeout: 540000 },
  );
  const result = await fn({});
  return result.data;
}

export type ResyncAllReferencesToAlgoliaResult = {
  resynced: number;
  orphansRemoved: number;
};

export async function callResyncAllReferencesToAlgolia(
  options: { fullRebuild?: boolean } = {},
): Promise<ResyncAllReferencesToAlgoliaResult> {
  const fn = httpsCallable<{ fullRebuild?: boolean }, ResyncAllReferencesToAlgoliaResult>(
    functions,
    'resyncAllReferencesToAlgolia',
    { timeout: 540000 },
  );
  const result = await fn({ fullRebuild: options.fullRebuild === true });
  return result.data;
}

export type PushAlgoliaSettingsResult = {
  changed: boolean;
  attributesForFaceting: string[];
};

export async function callPushAlgoliaRefIndexSettings(): Promise<PushAlgoliaSettingsResult> {
  const fn = httpsCallable<unknown, PushAlgoliaSettingsResult>(
    functions,
    'pushAlgoliaRefIndexSettings',
    { timeout: 60000 },
  );
  const result = await fn({});
  return result.data;
}

// --- Entity Algolia indexes (brands / manufacturers / calibres / orgs) ------

export type EntityIndexBackfillResult = {
  index: string;
  scanned: number;
  indexed: number;
  fullRebuild?: boolean;
};

export type EntityIndexSettingsResult = {
  index: string;
  updated: boolean;
};

async function callEntityIndexBackfill(
  name:
    | 'backfillBrandsToAlgolia'
    | 'backfillManufacturersToAlgolia'
    | 'backfillCalibresToAlgolia'
    | 'backfillCalibreFamiliesToAlgolia'
    | 'backfillParentOrgsToAlgolia'
    | 'backfillWatchReferenceFamilyIds',
  fullRebuild = false,
): Promise<EntityIndexBackfillResult> {
  const fn = httpsCallable<{ fullRebuild?: boolean }, EntityIndexBackfillResult>(
    functions,
    name,
    { timeout: 540000 },
  );
  return (await fn({ fullRebuild: fullRebuild === true })).data;
}

async function callEntityIndexSettings(
  name:
    | 'pushAlgoliaBrandIndexSettings'
    | 'pushAlgoliaManufacturerIndexSettings'
    | 'pushAlgoliaCalibreIndexSettings',
): Promise<EntityIndexSettingsResult> {
  const fn = httpsCallable<unknown, EntityIndexSettingsResult>(functions, name, {
    timeout: 60000,
  });
  return (await fn({})).data;
}

export const callBackfillBrandsToAlgolia = (opts?: { fullRebuild?: boolean }) =>
  callEntityIndexBackfill('backfillBrandsToAlgolia', opts?.fullRebuild);
export const callBackfillManufacturersToAlgolia = (opts?: { fullRebuild?: boolean }) =>
  callEntityIndexBackfill('backfillManufacturersToAlgolia', opts?.fullRebuild);
export const callBackfillCalibresToAlgolia = (opts?: { fullRebuild?: boolean }) =>
  callEntityIndexBackfill('backfillCalibresToAlgolia', opts?.fullRebuild);
export const callBackfillCalibreFamiliesToAlgolia = (opts?: {
  fullRebuild?: boolean;
}) => callEntityIndexBackfill('backfillCalibreFamiliesToAlgolia', opts?.fullRebuild);
export const callBackfillParentOrgsToAlgolia = (opts?: { fullRebuild?: boolean }) =>
  callEntityIndexBackfill('backfillParentOrgsToAlgolia', opts?.fullRebuild);
export const callBackfillWatchReferenceFamilyIds = (opts?: {
  fullRebuild?: boolean;
}) => callEntityIndexBackfill('backfillWatchReferenceFamilyIds', opts?.fullRebuild);

export const callPushAlgoliaBrandIndexSettings = () =>
  callEntityIndexSettings('pushAlgoliaBrandIndexSettings');
export const callPushAlgoliaManufacturerIndexSettings = () =>
  callEntityIndexSettings('pushAlgoliaManufacturerIndexSettings');
export const callPushAlgoliaCalibreIndexSettings = () =>
  callEntityIndexSettings('pushAlgoliaCalibreIndexSettings');

// --- Cleanup callables (Phase 2.5) -----------------------------------------

export interface DeleteManufacturersResult {
  entitiesDeleted: number;
  calibresCleared: number;
}

export async function callDeleteMovementManufacturers(
  ids: string[],
): Promise<DeleteManufacturersResult> {
  const fn = httpsCallable<{ ids: string[] }, DeleteManufacturersResult>(
    functions,
    'deleteMovementManufacturers',
    { timeout: 540000 },
  );
  const result = await fn({ ids });
  invalidateManufacturerIndex();
  // Same membership change as the single-document delete, so the same overlay
  // tombstones and the same broadcast — the callable's own writes reach Algolia
  // on the trigger's schedule, not this response's.
  for (const id of ids) recordDeletedManufacturer(id);
  notifyManufacturerCatalogueChanged();
  return result.data;
}

export interface MergeManufacturersResult {
  canonicalId: string;
  calibresUpdated: number;
  entitiesDeleted: number;
  alternativeNamesAdded: number;
}

export async function callMergeMovementManufacturers(
  canonicalId: string,
  mergedIds: string[],
): Promise<MergeManufacturersResult> {
  const fn = httpsCallable<
    { canonicalId: string; mergedIds: string[] },
    MergeManufacturersResult
  >(functions, 'mergeMovementManufacturers', { timeout: 540000 });
  const result = await fn({ canonicalId, mergedIds });
  invalidateManufacturerIndex();
  // A merge deletes the merged-away manufacturers, so it is a membership change
  // too — the same treatment as a delete.
  for (const id of mergedIds) recordDeletedManufacturer(id);
  notifyManufacturerCatalogueChanged();
  return result.data;
}

/**
 * Set `isGeneric` on multiple manufacturer docs in a single Firestore batch.
 * Used by the bulk Mark as Generic / Mark as In-House actions.
 */
export async function bulkSetManufacturerGeneric(
  ids: string[],
  isGeneric: boolean,
): Promise<void> {
  if (ids.length === 0) return;
  const BATCH_SIZE = 400;
  const now = Timestamp.now();
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const slice = ids.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);
    for (const id of slice) {
      batch.update(doc(db, MOVEMENT_MANUFACTURERS_COLLECTION, id), {
        isGeneric,
        updatedAt: now,
      });
    }
    await batch.commit();
  }
  invalidateManufacturerIndex();
}

// --- Brand → refs propagation -----------------------------------------------

export type RepropagateBrandToRefsResult = {
  touched: number;
  /** References under the brand (watchlock returns it beside `touched`). */
  scanned?: number;
  updated?: number;
};

/**
 * Admin-only drift-recovery callable: re-runs the brand→refs propagation
 * sweep for a single brand (touches updatedAt on every ref under the brand,
 * which fans out to the existing syncReferenceToAlgolia trigger). Idempotent.
 */
export async function callRepropagateBrandToRefs(
  brandId: string,
): Promise<RepropagateBrandToRefsResult> {
  const fn = httpsCallable<{ brandId: string }, RepropagateBrandToRefsResult>(
    functions,
    'repropagateBrandToRefs',
    { timeout: 540000 },
  );
  const result = await fn({ brandId });
  return result.data;
}

/**
 * Stamp `manufacturerId` (FK) on multiple custom calibre docs in batched
 * Firestore writes.
 */
export async function bulkSetCalibreManufacturer(
  calibreIds: string[],
  manufacturerId: string,
): Promise<void> {
  if (calibreIds.length === 0) return;
  const entity = await getMovementManufacturerById(manufacturerId);
  if (!entity) {
    throw new Error(`Manufacturer not found: ${manufacturerId}`);
  }
  const BATCH_SIZE = 400;
  const now = Timestamp.now();
  for (let i = 0; i < calibreIds.length; i += BATCH_SIZE) {
    const slice = calibreIds.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);
    for (const id of slice) {
      batch.update(doc(db, CUSTOM_CALIBRES_COLLECTION, id), {
        manufacturerId: entity.id,
        updatedAt: now,
      });
    }
    await batch.commit();
  }
}

/** One calibre's brand assignment, as planned by `planCalibreBrandUpdates`. */
export interface BulkCalibreBrandWrite {
  id: string;
  /** Canonical watchBrand doc id. Never empty — a plan with no target isn't written. */
  brandId: string;
  /**
   * The brand's display name, for the legacy denormalised `brandName`. Written
   * ONLY when the calibre carries no `brandName` today: a stale free-text value
   * is somebody's data, and `brandId` is what every renderer prefers anyway
   * (`calibreRowBrandKey`), so filling the gap is worth a write and clobbering
   * an existing string is not.
   */
  brandName?: string;
}

/**
 * Stamp `brandId` (and, where empty, `brandName`) on multiple custom calibre
 * docs in batched Firestore writes.
 *
 * Sibling to {@link bulkSetCalibreManufacturer} and deliberately the same shape:
 * batched client writes, no callable, and no decisions of its own. Every skip
 * rule — already-set, conflicting, no derivable brand — is applied by
 * `planCalibreBrandUpdates` before anything reaches here, so this writes exactly
 * the rows it is handed. Unlike Set Manufacturer each row carries its OWN brand,
 * because the target is derived per calibre from its manufacturer set.
 */
export async function bulkSetCalibreBrand(
  writes: BulkCalibreBrandWrite[],
): Promise<void> {
  if (writes.length === 0) return;
  const BATCH_SIZE = 400;
  const now = Timestamp.now();
  for (let i = 0; i < writes.length; i += BATCH_SIZE) {
    const slice = writes.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);
    for (const w of slice) {
      batch.update(doc(db, CUSTOM_CALIBRES_COLLECTION, w.id), {
        brandId: w.brandId,
        ...(w.brandName ? { brandName: w.brandName } : {}),
        updatedAt: now,
      });
    }
    await batch.commit();
  }
}

/** One calibre's rewritten alias list, as planned by `planCalibreAkaBrandUpdates`. */
export interface BulkCalibreAliasBrandWrite {
  id: string;
  /** The WHOLE array — filled entries plus every untouched one. */
  aliasDetails: CalibreAliasDetail[];
}

/**
 * Rewrite `aliasDetails` on multiple custom calibre docs in batched writes.
 *
 * The array is replaced wholesale rather than patched by index, which is why the
 * caller must have built it from a HYDRATED document: `aliasDetails` is not an
 * indexed attribute, so a plan built from a listing row would write an empty
 * list over a calibre's aliases. `alsoKnownAs` is deliberately untouched — the
 * alias NAMES don't change here, only their brand scope.
 */
export async function bulkSetCalibreAliasBrands(
  writes: BulkCalibreAliasBrandWrite[],
): Promise<void> {
  if (writes.length === 0) return;
  const BATCH_SIZE = 400;
  const now = Timestamp.now();
  for (let i = 0; i < writes.length; i += BATCH_SIZE) {
    const slice = writes.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);
    for (const w of slice) {
      batch.update(doc(db, CUSTOM_CALIBRES_COLLECTION, w.id), {
        aliasDetails: w.aliasDetails.length > 0 ? w.aliasDetails : null,
        updatedAt: now,
      });
    }
    await batch.commit();
  }
}

/**
 * Calibre / calibre-image `source` vocabularies.
 *
 * Defined in `calibreSources.ts` (a module free of this one's imports, so the
 * rule they encode can be tested without booting Firebase) and re-exported here
 * so every existing `services/firebase` import path keeps working.
 */
export {
  CALIBRE_IMAGE_SOURCES,
  CALIBRE_ENTITY_SOURCES,
  CALIBRE_LEVEL_ATTRIBUTION_SOURCES,
  CALIBRE_IMAGE_ATTRIBUTION_SOURCES,
  CALIBRE_ATTRIBUTION_SOURCES,
} from './calibreSources';
export type {
  CalibreImageSource,
  CalibreEntitySource,
  CalibreAttributionSource,
} from './calibreSources';

/**
 * Default attribution for a calibre (and its images) based on context:
 * an in-house movement (the brand makes its own movement, i.e. `brandId` equals
 * `manufacturerId`) is attributed to the `brand`; everything else to the
 * `manufacturer`. Mirrors the rule applied by the one-shot `custom`-source
 * migration so new entries and migrated entries land on the same value.
 */
export function resolveDefaultCalibreSource(
  brandId?: string | null,
  manufacturerId?: string | null,
): CalibreAttributionSource {
  const b = brandId?.trim();
  const m = manufacturerId?.trim();
  return b && m && b === m ? 'brand' : 'manufacturer';
}

export interface BulkSetImageSourceResult {
  /** Calibres whose images were successfully rewritten. */
  calibresUpdated: number;
  /** Total individual images touched across all updated calibres. */
  imagesUpdated: number;
  /** Calibres skipped because they had no images. */
  calibresSkipped: number;
  /** Calibres whose write failed; not rolled back — surface for retry. */
  failedIds: string[];
}

/**
 * Set `source` on every image of multiple custom calibres. Writes one doc at a
 * time (rather than an atomic batch) so a mid-run failure leaves prior writes
 * committed and we can report exactly which calibres failed. Calibres with no
 * images are skipped, not errored.
 */
export async function bulkSetCalibreImageSource(
  calibres: Array<{ id: string; images?: CalibreImage[] }>,
  source: CalibreImageSource,
): Promise<BulkSetImageSourceResult> {
  const result: BulkSetImageSourceResult = {
    calibresUpdated: 0,
    imagesUpdated: 0,
    calibresSkipped: 0,
    failedIds: [],
  };
  const now = Timestamp.now();
  for (const c of calibres) {
    const images = c.images ?? [];
    if (images.length === 0) {
      result.calibresSkipped++;
      continue;
    }
    const nextImages = images.map((img) => ({ ...img, source }));
    try {
      await updateDoc(doc(db, CUSTOM_CALIBRES_COLLECTION, c.id), {
        images: nextImages,
        updatedAt: now,
      });
      result.calibresUpdated++;
      result.imagesUpdated += nextImages.length;
    } catch (err) {
      console.error(`bulkSetCalibreImageSource failed for ${c.id}:`, err);
      result.failedIds.push(c.id);
    }
  }
  return result;
}

export interface BulkSetSourceResult {
  /** Calibres whose entity-level `source` was successfully written. */
  calibresUpdated: number;
  /** Calibres whose write failed; not rolled back — surface for retry. */
  failedIds: string[];
}

/**
 * Set the entity-level `source` field on multiple custom calibres (the calibre
 * doc's own `source`, NOT per-image source — see `bulkSetCalibreImageSource`).
 * The RN app reads this as cascade tier 3 in image attribution. Writes one doc
 * at a time (rather than an atomic batch) so a mid-run failure leaves prior
 * writes committed and we can report exactly which calibres failed. Every
 * selected calibre is eligible — there is no "no images" skip here.
 */
export async function bulkSetCalibreSource(
  calibreIds: string[],
  // Narrower than the image equivalent ON PURPOSE — `watchkeeper` is not a
  // calibre-level attribution, and this signature is what stops one reaching
  // the field in bulk.
  source: CalibreEntitySource,
): Promise<BulkSetSourceResult> {
  const result: BulkSetSourceResult = {
    calibresUpdated: 0,
    failedIds: [],
  };
  const now = Timestamp.now();
  for (const id of calibreIds) {
    try {
      await updateDoc(doc(db, CUSTOM_CALIBRES_COLLECTION, id), {
        source,
        updatedAt: now,
      });
      result.calibresUpdated++;
    } catch (err) {
      console.error(`bulkSetCalibreSource failed for ${id}:`, err);
      result.failedIds.push(id);
    }
  }
  return result;
}

export interface MigrateCustomSourceResult {
  /** Total custom_calibres docs examined. */
  calibresScanned: number;
  /** Docs whose entity-level `source` was `'custom'`. */
  calibresWithCustomSource: number;
  /** Individual images (across all docs) whose `source` was `'custom'`. */
  imagesWithCustomSource: number;
  /** Docs actually written (entity source and/or image sources rewritten). */
  calibresUpdated: number;
  /** Of the rewritten entity sources, how many became `'brand'`. */
  toBrand: number;
  /** Of the rewritten entity sources, how many became `'manufacturer'`. */
  toManufacturer: number;
  /** Total images rewritten away from `'custom'`. */
  imagesUpdated: number;
  /** Docs whose write failed; surfaced for retry. */
  errors: { id: string; error: string }[];
}

/**
 * One-shot migration: find every custom calibre whose entity-level `source` —
 * or any of whose images' `source` — is the legacy `'custom'` value (which the
 * RN app renders no attribution for) and reassign it to `'brand'` /
 * `'manufacturer'` per {@link resolveDefaultCalibreSource}. Only `'custom'`
 * values are touched; legitimate provenance values (`manual`, `calibrecorner`,
 * `user_submitted`, already-set `brand`/`manufacturer`) are left untouched.
 *
 * `dryRun` counts what would change without writing. Writes one doc at a time so
 * a mid-run failure leaves prior writes committed and we report exactly which
 * docs failed.
 */
export async function migrateCustomCalibreSources(
  opts: { dryRun: boolean },
): Promise<MigrateCustomSourceResult> {
  const result: MigrateCustomSourceResult = {
    calibresScanned: 0,
    calibresWithCustomSource: 0,
    imagesWithCustomSource: 0,
    calibresUpdated: 0,
    toBrand: 0,
    toManufacturer: 0,
    imagesUpdated: 0,
    errors: [],
  };
  const entries = await getCustomCalibres(true);
  const now = Timestamp.now();
  for (const entry of entries) {
    result.calibresScanned++;
    const target = resolveDefaultCalibreSource(entry.brandId, entry.manufacturerId);

    const entitySourceIsCustom = entry.source === 'custom';
    const images = entry.images ?? [];
    const customImageCount = images.filter((img) => img.source === 'custom').length;

    if (!entitySourceIsCustom && customImageCount === 0) continue;

    if (entitySourceIsCustom) result.calibresWithCustomSource++;
    result.imagesWithCustomSource += customImageCount;

    if (opts.dryRun) {
      result.calibresUpdated++;
      if (entitySourceIsCustom) {
        if (target === 'brand') result.toBrand++;
        else result.toManufacturer++;
      }
      result.imagesUpdated += customImageCount;
      continue;
    }

    const update: Record<string, unknown> = { updatedAt: now };
    if (entitySourceIsCustom) update.source = target;
    if (customImageCount > 0) {
      update.images = images.map((img) =>
        img.source === 'custom' ? { ...img, source: target } : img,
      );
    }
    try {
      await updateDoc(doc(db, CUSTOM_CALIBRES_COLLECTION, entry.id), update);
      result.calibresUpdated++;
      if (entitySourceIsCustom) {
        if (target === 'brand') result.toBrand++;
        else result.toManufacturer++;
      }
      result.imagesUpdated += customImageCount;
    } catch (err) {
      console.error(`migrateCustomCalibreSources failed for ${entry.id}:`, err);
      result.errors.push({
        id: entry.id,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
  return result;
}

// ============================================================================
// Image recognition (watchlock callables) — phase i.
//
// Thin wrappers over the `recognizeWatchFromImage` + `getAiCreditStatus`
// watchlock callables, mirroring the portal-callable pattern above. The RN app
// calls these callables directly; these admin wrappers exist so the brand-portal
// admin can drive/debug recognition + read AI credit status. Photo BYTES upload
// client-side to `users/{uid}/uploads/…` BEFORE calling recognize (the callable
// reads the image from Storage and validates it belongs to the caller).
// ============================================================================

/** Attributes gpt-4o extracted from the photo (debug/tooltip; all best-effort). */
export interface RecognizedAttributes {
  brand?: string;
  model?: string;
  reference?: string;
  collection?: string;
  dialColor?: string;
  caseMaterial?: string;
  caseShape?: string;
  bezelType?: string;
  movementType?: string;
  complications?: string[];
  approxCaseSizeMm?: number;
  handStyle?: string;
  strapMaterial?: string;
  visibleText?: string[];
  overallConfidence?: number;
}

export interface RecognizedMatch {
  referenceId: string;
  confidence: number; // 0-1, blended from vision + ocr + algolia
  matchLabel: 'strong' | 'possible' | 'weak';
  extractedAttributes: RecognizedAttributes;
  brandId?: string;
  brandName?: string;
  reference?: string;
  model?: string;
  productName?: string;
  imageUrl?: string;
}

/**
 * Identify a watch from an already-uploaded photo. `imagePath` is the Storage
 * path the image was uploaded to, e.g. `users/{uid}/uploads/{reqId}.jpg` — it
 * must belong to the signed-in user. Returns the top matches plus the echoed
 * `imagePath` (chunk iv uses it for the "use your photo" option).
 */
export async function recognizeWatchFromImage(input: {
  imagePath: string;
}): Promise<{ matches: RecognizedMatch[]; imagePath: string }> {
  const fn = httpsCallable<
    { imagePath: string },
    { matches: RecognizedMatch[]; imagePath: string }
  >(functions, 'recognizeWatchFromImage');
  const result = await fn(input);
  return result.data;
}

/**
 * Read-only view of the caller's unified AI credit pool (shared by image
 * recognition + AI Insights). Powers the "N free identifications left" UI.
 */
export async function getAiCreditStatus(): Promise<{
  remaining: number;
  monthlyLimit: number;
  /** Remaining one-time intro grant (0 for paid tiers). */
  introCreditsRemaining: number;
  /** Server-computed gate for the "try AI" CTA: intro left + zero watches + free. */
  showIntroCta: boolean;
  tier: 'free' | 'paid';
  resetsAt: Date;
}> {
  const fn = httpsCallable<
    Record<string, never>,
    {
      remaining: number;
      monthlyLimit: number;
      introCreditsRemaining: number;
      showIntroCta: boolean;
      tier: 'free' | 'paid';
      resetsAt: string;
    }
  >(functions, 'getAiCreditStatus');
  const result = await fn({});
  // Server returns `resetsAt` as an ISO string; surface it as a Date to callers.
  return { ...result.data, resetsAt: new Date(result.data.resetsAt) };
}

// ============================================================================
// Per-brand enrichment runs — last-run tracking for the Enrichment Tasks panel
// ============================================================================

/** Map field on the brand doc holding the latest run per task:
 *  watchBrands/{brandId}.enrichmentRuns[taskKey]. Stored on the brand doc
 *  (not a subcollection) so writes go through the brand-doc update path the
 *  deployed Firestore rules already permit — a new subcollection is
 *  default-denied. */
const ENRICHMENT_RUNS_FIELD = 'enrichmentRuns';

/** Stable keys for the post-import enrichment tasks consolidated in the
 *  per-brand Enrichment Tasks panel (see EnrichmentTasksPanel). Used as the
 *  map key under watchBrands/{brandId}.enrichmentRuns so each task keeps
 *  exactly one "last run" record. */
export type EnrichmentTaskKey =
  | 'occasion'
  | 'calibre'
  | 'module'
  | 'description'
  | 'aiEnrich'
  | 'cleanup'
  | 'dialColor'
  | 'variantAggregates'
  | 'repropagateRefs';

export interface EnrichmentRunRecord {
  /** When the task last ran for this brand (client time, matches updatedAt). */
  ranAt: Timestamp | null;
  /** Admin email/uid that ran it (null when unknown). */
  ranBy: string | null;
  /** Whether the run completed without errors. */
  ok: boolean;
  /** One-line human summary, e.g. "Updated 12 refs". */
  summary: string;
}

/**
 * Record the latest run of an enrichment task for a brand. Stored as one entry
 * per task key in the `enrichmentRuns` map on the brand doc (overwrites the
 * previous run) — we only ever surface the *last* run, so a log would be
 * overkill. Lives on the brand doc rather than a subcollection because the
 * brand-doc write path is already allowed by the deployed Firestore rules;
 * a new subcollection is default-denied (was the "Failed to mark done" bug).
 */
export async function recordEnrichmentRun(
  brandId: string,
  taskKey: EnrichmentTaskKey,
  run: { ok: boolean; summary: string; ranBy?: string | null },
): Promise<void> {
  const docRef = doc(db, WATCH_BRANDS_COLLECTION, brandId);
  // Dot-path key merges this one task's entry into the enrichmentRuns map
  // without clobbering the other tasks. Client Timestamp (not serverTimestamp)
  // — matches updatedAt elsewhere and is valid nested inside a map value.
  await updateDoc(docRef, {
    [`${ENRICHMENT_RUNS_FIELD}.${taskKey}`]: {
      ok: run.ok,
      summary: run.summary,
      ranBy: run.ranBy ?? auth.currentUser?.email ?? auth.currentUser?.uid ?? null,
      ranAt: Timestamp.now(),
    },
  });
}

/** Load all enrichment-run records for a brand, keyed by task key. Missing keys
 *  simply aren't present (task never run for this brand). */
export async function getEnrichmentRunsForBrand(
  brandId: string,
): Promise<Partial<Record<EnrichmentTaskKey, EnrichmentRunRecord>>> {
  const snap = await getDoc(doc(db, WATCH_BRANDS_COLLECTION, brandId));
  const raw =
    (snap.exists()
      ? (snap.data()[ENRICHMENT_RUNS_FIELD] as Record<string, unknown> | undefined)
      : undefined) ?? {};
  const out: Partial<Record<EnrichmentTaskKey, EnrichmentRunRecord>> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!value || typeof value !== 'object') continue;
    const data = value as Record<string, unknown>;
    out[key as EnrichmentTaskKey] = {
      ranAt: (data.ranAt as Timestamp) ?? null,
      ranBy: (data.ranBy as string) ?? null,
      ok: data.ok !== false,
      summary: (data.summary as string) ?? '',
    };
  }
  return out;
}

// ============================================================================
// Description rewrite — shared headless runner
// ----------------------------------------------------------------------------
// Single source of truth for the AI description-rewrite logic, reused by the
// standalone Bulk Description Rewrite tool (per-item UI) and the per-brand
// Enrichment Tasks panel (aggregate progress). The snapshot denylist below is
// domain-critical — keep it here, not duplicated in callers.
// ============================================================================

/**
 * A ref is eligible for AI description rewrite when its description has NOT
 * already been AI-rewritten and wasn't hand-edited by an admin (manual edits are
 * preserved). Covers raw imported text not yet rewritten AND empty/missing
 * descriptions (the AI writes one from the spec data). `descriptionSource`
 * absent/undefined ⇒ legacy import ⇒ eligible.
 */
export function isDescriptionRewriteCandidate(ref: WatchReference): boolean {
  return referenceNeedsAiEnrichment(ref);
}

import { buildDescriptionEnrichSnapshot } from './descriptionEnrichSnapshot';
import type { DescriptionLinkLoss } from './descriptionLinkGuard';

export { buildDescriptionEnrichSnapshot };

export interface DescriptionRewriteRunResult {
  updated: number;
  failed: number;
  total: number;
  stopped: boolean;
}

// Bounded parallelism — keeps us within the enrichReference Cloud Function /
// Anthropic rate limits while still finishing a brand quickly.
const DESCRIPTION_REWRITE_CONCURRENCY = 4;

/**
 * Headless AI description rewrite for an entire brand: scan candidate refs, run
 * the enrichReference rule on each (bounded concurrency), and persist the new
 * description. Returns aggregate counts. Mirrors the loop in the standalone Bulk
 * Description Rewrite tool — both share the candidate/snapshot helpers above.
 *
 * @param opts.override   re-run on ALL refs (ignore the skip filter)
 * @param opts.onProgress called after each ref completes with (done, total)
 * @param opts.shouldStop polled between refs; return true to abort early
 */
export async function runDescriptionRewriteForBrand(
  brandId: string,
  brandName: string,
  opts: {
    override?: boolean;
    onProgress?: (done: number, total: number) => void;
    shouldStop?: () => boolean;
  } = {},
): Promise<DescriptionRewriteRunResult> {
  const { override = false, onProgress, shouldStop } = opts;
  // Doc-id-keyed: a ref-string-keyed map collapses docs sharing a `reference`,
  // silently dropping one of every such pair from both the candidate list and
  // the sibling ground truth.
  const map = await getExistingReferenceMapById(brandId);
  const allRefs = Array.from(map.values());
  // Variants (parentReferenceId set) are NEVER eligible, override or not —
  // they're not consumer-facing (Algolia + RN Explore skip them), so
  // AI-rewriting their descriptions wastes tokens and risks variant-vs-parent
  // drift. Parents-only, matching the server-side backfillAIDescriptions rule.
  const candidates = allRefs
    .filter((ref) => !ref.parentReferenceId && (override || isDescriptionRewriteCandidate(ref)))
    .sort((a, b) => (a.reference || '').localeCompare(b.reference || ''));

  const total = candidates.length;
  let updated = 0;
  let failed = 0;
  let done = 0;
  let cursor = 0;

  const worker = async () => {
    while (true) {
      if (shouldStop?.()) return;
      const i = cursor;
      cursor += 1;
      if (i >= candidates.length) return;
      const ref = candidates[i];

      // Defensive idempotency short-circuit (candidates already exclude these,
      // unless override — where re-running AI-rewritten refs is the whole point).
      if (!override && ref.descriptionSource === 'ai') {
        failed += 1;
        done += 1;
        onProgress?.(done, total);
        continue;
      }

      try {
        const siblingReferences = siblingReferencesFrom(allRefs, ref);
        const snapshot = buildDescriptionEnrichSnapshot(ref, brandName, siblingReferences);
        const result = await enrichReferenceWithAI(snapshot);
        const raw = result.suggestions?.description;
        const newDesc = typeof raw === 'string' ? raw.trim() : '';
        if (!newDesc) throw new Error('AI returned no description');
        await updateDoc(
          doc(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION, ref.id),
          {
            description: newDesc,
            descriptionSource: 'ai',
            descriptionAiRewrittenAt: serverTimestamp(),
            // Write-once snapshot of the text being replaced, as the other two
            // rewrite tools take — this path used to overwrite without it, so a
            // ref rewritten here lost its original for good.
            ...(!ref.originalDescription && ref.description
              ? { originalDescription: ref.description }
              : {}),
            updatedAt: serverTimestamp(),
          },
        );
        updated += 1;
      } catch (err) {
        failed += 1;
        console.error(`Description rewrite failed for ${ref.reference}:`, err);
      } finally {
        done += 1;
        onProgress?.(done, total);
      }
    }
  };

  await Promise.all(
    Array.from({ length: DESCRIPTION_REWRITE_CONCURRENCY }, () => worker()),
  );

  return { updated, failed, total, stopped: shouldStop?.() ?? false };
}

// ============================================================================
// Push Notifications
// ============================================================================

const PUSH_NOTIFICATION_LOGS_COLLECTION = 'push_notification_logs';

/** Who a push send targets: every user, or an explicit list of user ids. */
export type PushTarget = 'all' | { userIds: string[] };

export interface SendPushParams {
  title: string;
  body: string;
  target: PushTarget;
  data?: Record<string, string>;
  /**
   * Optional deep-link path/URL the notification tap navigates to (e.g.
   * `/watch/<id>`, `/subscription`, or an external `https://…` URL). Built from
   * the shared deep-link target catalog (see components/Banners/deepLinkTargets).
   * Server-side it lands in the FCM `data.deepLink` payload + the in-app doc.
   */
  deepLink?: string;
  /**
   * Marks a "send test to myself" send. Server-side this bypasses the duplicate
   * throttle and tags the audit-log row so it never blocks a later real
   * broadcast of the same title+body.
   */
  isTest?: boolean;
}

/** Send outcome for one platform within a single broadcast. */
export interface PushPlatformBreakdown {
  success: number;
  failure: number;
  pruned: number;
}

/**
 * Per-platform tallies keyed by the token's `platform` — 'ios' | 'android', plus
 * 'unknown' for tokens written before the client set one. Recorded during the
 * send because it cannot be reconstructed afterwards: a dead token is
 * hard-deleted the moment FCM reports it, and `platform` lives only on that doc.
 */
export type PushPlatformBreakdownMap = Record<string, PushPlatformBreakdown>;

export interface SendPushResult {
  recipientUserCount: number;
  /** In-app notification docs written (one per recipient). */
  notificationsWritten: number;
  /** FCM messages accepted by the messaging service. */
  fcmSuccessCount: number;
  /** FCM messages rejected (dead/invalid tokens etc.). */
  fcmFailureCount: number;
  prunedTokenCount: number;
  /**
   * OPTIONAL because the deployed callable may predate it. Absent means "this
   * send did not record a breakdown", which is NOT the same as all-zeroes —
   * never default it to `{}` at the boundary or an old send starts claiming
   * zero iOS failures.
   */
  platformBreakdown?: PushPlatformBreakdownMap;
  /**
   * Recipients that got the in-app notification but no push, because they have
   * no registered device. Optional for the same reason as above.
   */
  recipientsWithNoDevice?: number;
  logId: string;
}

/**
 * Shape of an audit-log entry written to `push_notification_logs/{autoId}` by
 * the `sendPushToUsers` callable. `sentAt` is a Firestore Timestamp.
 */
export interface PushNotificationLog {
  id: string;
  sentBy: string;
  sentByEmail: string;
  sentAt: Timestamp | null;
  title: string;
  body: string;
  target: PushTarget;
  recipientUserCount: number;
  notificationsWritten: number;
  fcmSuccessCount: number;
  fcmFailureCount: number;
  prunedTokenCount: number;
  /**
   * Present only on sends made after the breakdown shipped. Rows written before
   * that carry NO breakdown, and the reader must leave it undefined rather than
   * substituting `{}` — an old send genuinely has no per-platform data, and
   * rendering it as "0 iOS failures" would be a fabricated answer.
   */
  platformBreakdown?: PushPlatformBreakdownMap;
  /** Present only on sends made after the breakdown shipped. See above. */
  recipientsWithNoDevice?: number;
}

/**
 * Invoke the `sendPushToUsers` callable (built in parallel in watchlock). Fans
 * out a push notification to either all users or a specific list, and returns
 * delivery counts plus the id of the audit-log entry it wrote.
 */
export async function callSendPushToUsers(
  params: SendPushParams,
): Promise<SendPushResult> {
  const fn = httpsCallable<SendPushParams, SendPushResult>(
    functions,
    'sendPushToUsers',
  );
  const result = await fn(params);
  return result.data;
}

/**
 * Narrow an untyped Firestore value to a platform-breakdown map. Anything not
 * shaped like one is treated as absent — a malformed row should render as
 * "no breakdown recorded", never as a partial set of counts.
 */
function isPlatformBreakdownMap(value: unknown): value is PushPlatformBreakdownMap {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every(
    (v) =>
      !!v &&
      typeof v === 'object' &&
      typeof (v as PushPlatformBreakdown).success === 'number' &&
      typeof (v as PushPlatformBreakdown).failure === 'number' &&
      typeof (v as PushPlatformBreakdown).pruned === 'number',
  );
}

/** Recent push-notification audit-log entries, newest first. */
export async function getPushNotificationLogs(
  limitCount = 20,
): Promise<PushNotificationLog[]> {
  const q = query(
    collection(db, PUSH_NOTIFICATION_LOGS_COLLECTION),
    orderBy('sentAt', 'desc'),
    limit(limitCount),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      sentBy: data.sentBy ?? '',
      sentByEmail: data.sentByEmail ?? '',
      sentAt: (data.sentAt as Timestamp | undefined) ?? null,
      title: data.title ?? '',
      body: data.body ?? '',
      target: data.target ?? 'all',
      recipientUserCount: data.recipientUserCount ?? 0,
      notificationsWritten: data.notificationsWritten ?? 0,
      fcmSuccessCount: data.fcmSuccessCount ?? 0,
      fcmFailureCount: data.fcmFailureCount ?? 0,
      prunedTokenCount: data.prunedTokenCount ?? 0,
      // Deliberately NOT `?? {}` / `?? 0`. These fields are absent on every row
      // written before the breakdown shipped, and defaulting them would turn
      // "we never measured this" into "we measured zero" — the UI distinguishes
      // the two, so the boundary has to preserve the distinction.
      platformBreakdown: isPlatformBreakdownMap(data.platformBreakdown)
        ? data.platformBreakdown
        : undefined,
      recipientsWithNoDevice:
        typeof data.recipientsWithNoDevice === 'number'
          ? data.recipientsWithNoDevice
          : undefined,
    } as PushNotificationLog;
  });
}

// ============================================================================
// Admin Banners (admin_banners collection — sibling to watchlock #764)
//
// Admin writes the doc directly via the client SDK (admin custom-claim path,
// same as electronicModules); rules from watchlock #764 already permit admin
// write + authed read for enabled banners. The RN app renders these; keep the
// field shape verbatim.
// ============================================================================

const ADMIN_BANNERS_COLLECTION = 'admin_banners';

/**
 * Realtime subscription to all banners, newest-first (`createdAt desc`). Returns
 * the onSnapshot unsubscribe fn. Mirrors `subscribeToPendingApprovals`.
 */
export function subscribeToBanners(
  callback: (banners: AdminBanner[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(
    collection(db, ADMIN_BANNERS_COLLECTION),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const banners = snapshot.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as AdminBanner,
      );
      callback(banners);
    },
    (err) => {
      console.error('subscribeToBanners failed:', err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

/**
 * Build the Firestore payload shared by create/update. Optional fields are
 * written as `null` when cleared (rather than omitted) so an edit that empties a
 * field clears it on the doc. `dismissible` is force-`false` for `urgent`
 * severity (belt-and-braces with the editor UI). `dismissCooldownDays` is
 * coerced to a non-negative integer.
 */
function buildBannerPayload(input: AdminBannerInput): Record<string, unknown> {
  const title = input.title.trim();
  if (!title) throw new Error('Banner title is required');
  const body = input.body.trim();
  if (!body) throw new Error('Banner body is required');

  const dismissible = input.severity === 'urgent' ? false : input.dismissible;
  const cooldownRaw = Number(input.dismissCooldownDays);
  const dismissCooldownDays =
    Number.isFinite(cooldownRaw) && cooldownRaw > 0 ? Math.floor(cooldownRaw) : 0;

  const deepLinkRaw = (input.deepLink ?? '').trim();
  const ctaLabelRaw = (input.ctaLabel ?? '').trim();

  return {
    enabled: input.enabled,
    title,
    body,
    severity: input.severity,
    startsAt: input.startsAt ? Timestamp.fromDate(input.startsAt) : null,
    endsAt: input.endsAt ? Timestamp.fromDate(input.endsAt) : null,
    dismissible,
    dismissCooldownDays,
    targetAudience: input.targetAudience,
    deepLink: deepLinkRaw ? deepLinkRaw : null,
    ctaLabel: ctaLabelRaw ? ctaLabelRaw : null,
    updatedAt: serverTimestamp(),
  };
}

/**
 * Create a new banner. Stamps `authoredBy` (current admin uid), `createdAt`,
 * `updatedAt`. Returns the new doc id.
 */
export async function createBanner(
  input: AdminBannerInput,
  authoredBy: string,
): Promise<string> {
  const payload = buildBannerPayload(input);
  const docRef = await addDoc(collection(db, ADMIN_BANNERS_COLLECTION), {
    ...payload,
    authoredBy: authoredBy || null,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * Update an existing banner. Re-stamps `authoredBy` + `updatedAt`; leaves
 * `createdAt` untouched.
 */
export async function updateBanner(
  id: string,
  input: AdminBannerInput,
  authoredBy: string,
): Promise<void> {
  const payload = buildBannerPayload(input);
  await updateDoc(doc(db, ADMIN_BANNERS_COLLECTION, id), {
    ...payload,
    authoredBy: authoredBy || null,
  });
}

/** Inline enable/disable toggle for the list — writes only `enabled` + audit. */
export async function setBannerEnabled(
  id: string,
  enabled: boolean,
  authoredBy: string,
): Promise<void> {
  await updateDoc(doc(db, ADMIN_BANNERS_COLLECTION, id), {
    enabled,
    authoredBy: authoredBy || null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteBanner(id: string): Promise<void> {
  await deleteDoc(doc(db, ADMIN_BANNERS_COLLECTION, id));
}

// ---------------------------------------------------------------------------
// Roadmap
// ---------------------------------------------------------------------------

const ROADMAP_COLLECTION = 'roadmap';
const ROADMAP_COMMENTS_SUBCOLLECTION = 'comments';
const ROADMAP_ATTACHMENTS_SUBCOLLECTION = 'attachments';

/**
 * Fetch roadmap items via the super-admin-gated `listRoadmapItems` callable.
 *
 * Server-side filters are supported but the admin panel deliberately doesn't
 * use them: the corpus is small enough (the callable defaults to 500) that one
 * fetch plus client-side filtering beats a round trip per filter change.
 *
 * Date fields arrive as serialised Timestamps, NOT `Date` — run them through
 * `toDateSafe()` from `utils/roadmapDates` before use.
 */
export async function listRoadmapItems(
  filters: ListRoadmapItemsInput = {},
): Promise<RoadmapItem[]> {
  const fn = httpsCallable<ListRoadmapItemsInput, { items: RoadmapItem[] }>(
    functions,
    'listRoadmapItems',
  );

  // Only send keys that are actually set, so an empty filter object reads as
  // "everything" rather than as a set of empty-array filters.
  const payload: ListRoadmapItemsInput = {};
  if (filters.status && filters.status.length > 0) payload.status = filters.status;
  if (filters.priority && filters.priority.length > 0) {
    payload.priority = filters.priority;
  }
  if (filters.tags && filters.tags.length > 0) payload.tags = filters.tags;
  if (typeof filters.limit === 'number') payload.limit = filters.limit;

  const res = await fn(payload);
  return res.data?.items ?? [];
}

/**
 * Create or update a roadmap item through the `saveRoadmapItem` callable.
 *
 * Resolution is server-side: `id` present → update; else `sourceMemoryFile`
 * set → upsert on the match; else create. Returns the doc id plus whether a new
 * doc was created.
 */
export async function saveRoadmapItem(
  input: SaveRoadmapItemInput,
): Promise<SaveRoadmapItemOutput> {
  const fn = httpsCallable<SaveRoadmapItemInput, SaveRoadmapItemOutput>(
    functions,
    'saveRoadmapItem',
  );
  const res = await fn(input);
  return res.data;
}

/** Append a comment to a roadmap item via the `addRoadmapComment` callable. */
export async function addRoadmapComment(
  roadmapId: string,
  text: string,
  author: RoadmapAuthor = 'james',
): Promise<string> {
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Comment text is required');

  const fn = httpsCallable<
    { roadmapId: string; author: RoadmapAuthor; text: string },
    AddRoadmapCommentOutput
  >(functions, 'addRoadmapComment');
  const res = await fn({ roadmapId, author, text: trimmed });
  return res.data?.id ?? '';
}

/**
 * Realtime comment thread for one roadmap item, oldest first.
 *
 * Read straight from Firestore rather than through a callable: the deployed
 * rules already cover `roadmap/{id}/comments`, and onSnapshot means a posted
 * comment appears without a manual refetch.
 */
export function subscribeToRoadmapComments(
  roadmapId: string,
  callback: (comments: RoadmapComment[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(
    collection(
      db,
      ROADMAP_COLLECTION,
      roadmapId,
      ROADMAP_COMMENTS_SUBCOLLECTION,
    ),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const comments = snapshot.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as RoadmapComment,
      );
      callback(comments);
    },
    (err) => {
      console.error('subscribeToRoadmapComments failed:', err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

// ---------------------------------------------------------------------------
// Roadmap attachments
// ---------------------------------------------------------------------------

/** Images only for now. Mirrored by the `image/*` check in storage.rules. */
export const ROADMAP_ATTACHMENT_ACCEPT = 'image/*';

/** Hard cap per file. Mirrored by the size check in storage.rules. */
export const ROADMAP_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

/**
 * Pick the extension for the stored object.
 *
 * The original filename wins when it carries a plausible one, so a
 * round-tripped download keeps the name the uploader recognises. Otherwise
 * fall back to the mime subtype ('image/png' -> 'png'), and to 'bin' if even
 * that is missing — an extensionless object is awkward to inspect in the
 * Firebase console.
 */
function roadmapAttachmentExtension(filename: string, contentType: string): string {
  const fromName = filename.includes('.') ? filename.split('.').pop() ?? '' : '';
  if (/^[a-zA-Z0-9]{1,5}$/.test(fromName)) return fromName.toLowerCase();

  const subtype = contentType.split('/')[1] ?? '';
  const cleaned = subtype.split('+')[0];
  if (/^[a-zA-Z0-9]{1,5}$/.test(cleaned)) return cleaned.toLowerCase();

  return 'bin';
}

/**
 * Upload one image to `roadmap/{itemId}/attachments/` and index it in Firestore.
 *
 * Deliberately not a callable: routing 25 MB of image bytes through a function
 * would mean base64 in a JSON payload. The client writes straight to Storage
 * (rules gate on isAdmin() and re-assert the type/size caps), then writes the
 * metadata doc.
 *
 * The doc id is minted FIRST and reused as the object's basename, so the two
 * halves are always reconcilable by id alone. If the Storage upload succeeds
 * but the Firestore write fails, the caller sees the throw and the object is
 * left unreferenced — the same orphan-preferring trade-off the delete callable
 * makes, and the reason `deleteRoadmapAttachment` tolerates a missing object.
 *
 * `onProgress` receives 0-100. Size and type are validated here rather than
 * only at the input element, since a drag-drop bypasses `accept`.
 */
export async function uploadRoadmapAttachment(
  roadmapId: string,
  file: File,
  options: {
    author?: RoadmapAuthor;
    onProgress?: (percent: number) => void;
  } = {},
): Promise<RoadmapAttachment> {
  const { author = 'james', onProgress } = options;

  if (!roadmapId) throw new Error('roadmapId is required');

  if (!file.type.startsWith('image/')) {
    throw new Error(`"${file.name}" is not an image`);
  }
  if (file.size > ROADMAP_ATTACHMENT_MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    throw new Error(`"${file.name}" is ${mb} MB — the limit is 25 MB`);
  }

  const attachmentRef = doc(
    collection(db, ROADMAP_COLLECTION, roadmapId, ROADMAP_ATTACHMENTS_SUBCOLLECTION),
  );
  const attachmentId = attachmentRef.id;
  const ext = roadmapAttachmentExtension(file.name, file.type);
  const storagePath = `${ROADMAP_COLLECTION}/${roadmapId}/${ROADMAP_ATTACHMENTS_SUBCOLLECTION}/${attachmentId}.${ext}`;

  const task = uploadBytesResumable(ref(storage, storagePath), file, {
    contentType: file.type,
  });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => {
        if (!onProgress || snapshot.totalBytes === 0) return;
        onProgress(
          Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
        );
      },
      (err) => reject(err),
      () => resolve(),
    );
  });

  const downloadUrl = await getDownloadURL(task.snapshot.ref);

  const payload = {
    id: attachmentId,
    filename: file.name,
    contentType: file.type,
    size: file.size,
    storagePath,
    downloadUrl,
    uploadedAt: serverTimestamp(),
    uploadedBy: author,
  };
  await setDoc(attachmentRef, payload);

  // Returned for an optimistic render; the onSnapshot listener replaces this
  // with the server copy (real `uploadedAt`) a moment later.
  return { ...payload, uploadedAt: new Date() } as RoadmapAttachment;
}

/**
 * Realtime attachment list for one roadmap item, oldest first.
 *
 * Read straight from Firestore for the same reason as the comment thread:
 * rules already cover the subcollection, and onSnapshot means an upload or
 * delete lands in the grid without a manual refetch.
 */
export function subscribeToRoadmapAttachments(
  roadmapId: string,
  callback: (attachments: RoadmapAttachment[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(
    collection(
      db,
      ROADMAP_COLLECTION,
      roadmapId,
      ROADMAP_ATTACHMENTS_SUBCOLLECTION,
    ),
    orderBy('uploadedAt', 'asc'),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const attachments = snapshot.docs.map(
        (docSnap) =>
          ({ id: docSnap.id, ...docSnap.data() }) as RoadmapAttachment,
      );
      callback(attachments);
    },
    (err) => {
      console.error('subscribeToRoadmapAttachments failed:', err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

/**
 * Delete an attachment through the `deleteRoadmapAttachment` callable.
 *
 * Not a client-side deleteObject + deleteDoc pair: those are two writes that
 * can half-fail independently, and the callable orders them so a failure can
 * only ever strand an unreferenced object, never a doc pointing at missing
 * bytes. `storageDeleted: false` in the result means exactly that happened.
 */
export async function deleteRoadmapAttachment(
  roadmapId: string,
  attachmentId: string,
): Promise<DeleteRoadmapAttachmentOutput> {
  const fn = httpsCallable<
    { roadmapId: string; attachmentId: string },
    DeleteRoadmapAttachmentOutput
  >(functions, 'deleteRoadmapAttachment');
  const res = await fn({ roadmapId, attachmentId });
  return res.data ?? { storageDeleted: false };
}
